import { appEn } from './app-strings';
import { appEnPlatform } from './app-strings-platform';
import { appEnBook } from './app-strings-book';
import { appEnExtras } from './app-strings-extras';
import { appEnBookings } from './app-strings-bookings';
import { appEnSearch } from './app-strings-search';
import { appEnBusiness } from './app-strings-business';
import { appEnOffers } from './app-strings-offers';
import { appEnAccount } from './app-strings-account';
import { appEnHome } from './app-strings-home';
import { appEnUi } from './app-strings-ui';
import { appEnShell } from './app-strings-shell';
import { siteChromeEn } from './app-strings-chrome';
import { blogPageEn } from './app-strings-blog';
import { lrMerchantEn } from './lr-merchant-en';
import { lrAdminEn } from './lr-admin-en';
// English dictionary — the canonical key set (default language is English per
// CLAUDE.md). Every new UI string adds BOTH an `en` and a `bn` key; parity is
// guaranteed at compile time (bn.ts is typed `Record<MessageKey, string>`) and at
// test time (parity.test.ts). Seeded here: the 10 booking statuses (DB-17) and
// common actions.
export const en = {
  // Booking status labels — the ten-status machine (DBP §7.1 / DB-17).
  // ---- listing map (map/listings-map.tsx, Maps admin settings) ----
  'maps.view': 'View',
  'maps.zoom_in': 'Zoom in',
  'maps.zoom_out': 'Zoom out',
  'maps.cluster_label': '{n} listings',
  'maps.unavailable': 'The map is unavailable right now.',
  'maps.consent': 'Accept cookies to load the map.',
  'booking.status.pending': 'Pending',
  'booking.status.payment_verification': 'Payment verification',
  'booking.status.confirmed': 'Confirmed',
  'booking.status.in_progress': 'In progress',
  'booking.status.completed': 'Completed',
  'booking.status.cancelled': 'Cancelled',
  'booking.status.no_show': 'No-show',
  'booking.status.rejected': 'Rejected',
  'booking.status.expired': 'Expired',
  'booking.status.refunded': 'Refunded',

  // Common actions.
  'action.book': 'Book',
  'action.cancel': 'Cancel',
  'action.reschedule': 'Reschedule',
  'action.confirm': 'Confirm',
  'action.reject': 'Reject',
  'action.save': 'Save',
  'action.retry': 'Retry',
  'action.search': 'Search',
  'action.continue': 'Continue',
  'action.back': 'Back',

  // Website shell / navigation (surface: website discovery, T27).
  'nav.for_business': 'For business',
  'nav.help': 'Help',
  'nav.home': 'Home',
  'app.language': 'Language',
  'lang.en': 'English',
  'lang.bn': 'বাংলা',
  // Abbreviated forms for the header toggle. A language endonym does not change
  // with the active dictionary, so these are identical in en.ts and bn.ts.
  'lang.en_short': 'EN',
  'lang.bn_short': 'বাং',
  'location.all': 'All areas',

  // Common cross-screen strings.
  'common.loading': 'Loading…',
  'common.signing_out': 'Signing out…',
  'common.logging_out': 'Logging out…',
  'common.error_title': 'Something went wrong',
  'common.error_body': 'We couldn’t load this right now.',
  'common.empty': 'Nothing here yet',
  'common.verified': 'Verified',
  'common.prev': 'Previous',
  'common.next': 'Next',
  'common.free_reservation': 'Free reservation',
  'common.saved': 'Saved',
  'common.instant': 'Instant',
  'common.sponsored': 'Sponsored',
  'common.from': 'From',
  'common.reviews': 'reviews',
  'common.rating': 'Rating',

  // Home screen (FR-C1).
  'home.hero_subtitle': 'Hotels, doctors, salons, turfs, venues and more — one search away.',
  'home.areas': 'Browse by area',
  'home.featured': 'Featured',
  'home.see_all': 'See all',

  // Hero search pill — three segments (service / location / when).
  'home.search.service': 'All services',
  'home.search.location': 'Current location',
  'home.search.when': 'Any time',
  'home.search.city_area_placeholder': 'Search city or area…',
  'home.search.cats_header': 'Categories',
  'home.search.no_matches_for': 'No matches for',
  'home.search.try_category': 'Try a category, or check the spelling.',
  'home.search.today': 'Today',
  'home.search.tomorrow': 'Tomorrow',
  'home.search.any_date': 'Any date',
  'home.search.when_morning': 'Morning 9am–12pm',
  'home.search.when_afternoon': 'Afternoon 12pm–5pm',
  'home.search.when_evening': 'Evening 5pm–12am',
  'home.search.apply': 'Apply',

  // Hero ticker + app pill.
  'home.bookings_today': 'bookings made today',
  'home.get_app': 'Get the app',
  'home.scan_to_download': 'Scan to download',

  // Rails.
  'home.rail.recommended': 'Recommended',

  // App-download section.
  'home.app.available_on': 'Available on',
  'home.app.coming_soon': 'Coming soon',
  'home.app.scan_to_get_notified': 'Scan to get notified',
  'home.app.notify_me': 'Notify me',
  'home.app.preview': 'Preview the page',

  // Trust / platform stats.
  'home.trust.subtitle': 'One platform for every service. Trusted across the country.',
  'home.trust.lakh': 'lakh',
  'home.trust.districts_covered': 'districts covered',
  'home.trust.professionals': 'professionals',
  'home.trust.demo_figures': 'Demo figures for illustration',

  // For-business showcase.
  'home.for_business.body':
    'Grow your business with {region}’s booking platform. Calendar, bookings, payments to your own bKash or Nagad, and clients — all in one place.',
  'home.for_business.cta': 'Find out more',
  'home.for_business.excellent': 'Excellent 5/5',

  // Labels inside the decorative merchant-calendar / phone mockups.
  'home.mock.working_staff': 'Working staff',
  'home.mock.add_new': 'Add new',
  'home.mock.open_now': 'Open now',
  'home.mock.instant_booking': 'Instant booking',
  'home.mock.bkash_accepted': 'bKash accepted',
  'home.mock.details': 'Details',
  'home.mock.type': 'Type',
  'home.mock.services_available': '{n} services available',
  'home.mock.businesses_nearby': '{n} businesses nearby',

  // Reviews carousel.
  'home.reviews.title': 'What customers say',
  'home.reviews.on': 'on {biz}',

  // Browse-by-area directory link — "<category> in <area>".
  // Short category labels used only in the browse-by-area directory (the design
  // curates five: Salons, Doctors, Turfs, Restaurants, Hotels). Kept separate from
  // the full category names so a link reads "Salons in <area>", not "Salons & Wellness…".
  'home.dir_cat.restaurant': 'Restaurants',
  'home.dir_cat.coworking': 'Co-working',

  // Search results (FR-C2).
  'search.results_count': 'results',
  'search.filters': 'Filters',
  'search.sort': 'Sort',
  'search.sort.rating': 'Top rated',
  'search.filter.price': 'Price range',
  'search.filter.payment': 'Payment method',
  'search.filter.any': 'Any',
  'search.clear': 'Clear all',
  'search.no_results': 'No matches found',
  'search.instant_confirm': 'Instant confirmation',
  'search.map': 'Map',
  'search.list': 'List',
  'search.map_placeholder': 'map placeholder — swap with Maps SDK',
  'search.filter.category': 'Category',
  'search.filter.all_categories': 'All categories',
  'search.filter.city': 'City',
  'search.price.any': 'Any price',

  // Category screen.
  'category.explore': 'Explore',

  // Business page (FR-C3).
  'biz.book_now': 'Book now',
  'biz.about': 'About',
  'biz.services': 'Services',
  'biz.staff': 'Team',
  'biz.hours': 'Opening hours',
  'biz.amenities': 'Amenities',
  'biz.policy': 'Cancellation policy',
  'biz.share': 'Share',
  'biz.no_reviews': 'No reviews yet',
  // Unclaimed hotel listing (FR-B17, FR-A25).
  'biz.uc.close_photos': 'Close',
  'biz.show_all_photos': 'Show all {n} photos',
  'biz.photo_open': 'Open photo {n} of {total}',
  'biz.photo_prev': 'Previous photo',
  'biz.photo_next': 'Next photo',
  'biz.video': 'Video tour',

  // Reels (FR-C29): short vertical videos from YouTube, played in a fullscreen vertical viewer.
  'biz.reels': 'Reels',
  'reels.title': 'Reels.',
  'reels.sub': 'Short videos from businesses & creators — tap to watch',
  'reels.open': 'Play reel: {name}',
  'reels.close': 'Close',
  'reels.next': 'Next reel',
  'reels.sound_on': 'Sound on',
  'reels.sound_off': 'Muted — tap for sound',
  'reels.watch_youtube': 'Watch on YouTube',
  'reels.view_offer': 'View offer',
  'reels.view_business': 'View business',
  'reels.by': 'by {creator}',
  'reels.offer_chip': 'Offer',
  'reels.share': 'Share',
  'reels.link_copied': 'Link copied',
  'reels.copy_failed': "Couldn't copy the link",
  'reels.creative': 'Creative',
  'reels.reel_n': 'Reel {n}',
  'reels.hide_ui': 'Hide interface',
  'reels.show_ui': 'Show interface',
  'reels.save': 'Save',
  'reels.saved_toast': 'Saved to My list',
  'reels.unsaved_toast': 'Removed from My list',
  'reels.share_title': 'Share this reel',
  'reels.share_via': 'Share via {platform}',
  'reels.copy_link': 'Copy link',
  'reels.copied': 'Copied',
  'reels.pause': 'Pause',
  'share.title': 'Share this listing',
  'share.via': 'Share via {channel}',
  'share.copy_link': 'Copy link',
  'share.copied': 'Link copied',
  'share.copy_failed': "Couldn't copy the link",
  'share.more': 'More',
  'share.less': 'Less',
  'share.more_aria': 'More ways to share',
  'share.native': 'More apps',
  'share.sms': 'SMS',
  'share.email': 'Email',
  'share.qr': 'QR code',
  'share.qr_aria': 'Download a QR code of the link',
  'share.qr_saved': 'QR code downloaded',
  'share.qr_failed': "Couldn't make the QR code",
  'share.copy_url': 'Copy the link',
  'reels.play': 'Play',
  'reels.back_10': 'Back 10 seconds',
  'reels.fwd_10': 'Forward 10 seconds',
  'reels.ten_s': '10s',
  'reels.speed_2x': '2×',
  'reels.progress': 'Progress',
  'reels.counter': '{n}/{total}',
  'reels.business': 'Business',
  'reels.like': 'Like',
  'reels.views': '{n} views',
  'reels.views_one': '{n} view',
  'reels.liked_toast': 'Liked',
  'reels.unliked_toast': 'Like removed',
  'reels.like_signin': 'Sign in to like reels',
  'reels.count_k': '{n}k',
  'reels.count_m': '{n}M',
  'reels.more_apps': 'More',
  'reels.email': 'Email',
  'reels.tap_sound': 'Tap for sound',
  'reels.link_title': 'Opening the reel…',
  'reels.link_gone': 'This reel is no longer available.',
  'reels.link_home': 'Back to home',
  'videos.title': 'Videos',
  'videos.sub': 'Reels and full reviews from businesses — every reel links to its offer or listing.',
  'videos.all': 'All',
  'videos.chips_label': 'Filter videos by category',
  'videos.up_next': 'Up next',
  'videos.save': 'Save',
  'videos.saved': 'Saved',
  'videos.view_business': 'View business',
  'videos.play': 'Play {name}',
  'videos.player_title': '{name} — video',
  'videos.top10': 'Top 10 Reels',
  'videos.hot': 'HOT',
  'videos.offer_reels': 'Offer Reels',
  'videos.live_offers': 'LIVE OFFERS',
  'videos.more_reels': 'More Reels',
  'videos.trending': 'Trending Reels',
  'videos.reviews': 'Full Reviews',
  'videos.fresh': 'Fresh Today',
  'videos.new': 'NEW',
  'videos.prev': 'Scroll back',
  'videos.next': 'Scroll forward',
  'videos.rank': '#{n}',
  'videos.empty': 'No videos here yet.',
  'videos.empty_all': 'Show all categories',
  'biz.closed': 'Closed',
  'biz.open_until': 'Open until',
  'biz.closed_today': 'Closed today',
  'biz.policies': 'Policies',
  'biz.nearby': 'Nearby & similar',
  'biz.payments': 'Payments accepted',
  'biz.location': 'Location',
  'biz.response_from': 'Response from the business',
  'biz.pay_disclaimer': '{brand} does not process, hold, or guarantee direct payments.',

  // Payment-method display labels (business.PaymentMethodKind).
  'payment.bkash': 'bKash',
  'payment.nagad': 'Nagad',
  'payment.rocket': 'Rocket',
  'payment.bank': 'Bank transfer',
  'payment.pay_at_venue': 'Pay at venue',

  // Weekday labels (0 = Sunday, DB business_hours.weekday).
  'weekday.0': 'Sun',
  'weekday.1': 'Mon',
  'weekday.2': 'Tue',
  'weekday.3': 'Wed',
  'weekday.4': 'Thu',
  'weekday.5': 'Fri',
  'weekday.6': 'Sat',

  // Not-found screen.
  'notfound.title': 'Page not found',
  'notfound.body': 'The page you’re looking for doesn’t exist or has moved.',
  'notfound.home': 'Back to home',

  // ---- Merchant dashboard (surface: dash-merchant, T30) ----
  // Navigation (side-nav + mobile tab bar: Today · Calendar · Verify · Clients · More).
  'merchant.nav.primary': 'Dashboard navigation',
  'merchant.nav.calendar': 'Calendar',
  'merchant.nav.verify': 'Verify',
  'merchant.nav.bookings': 'Bookings',
  'merchant.nav.more': 'More',
  'merchant.switch_business': 'Switch business',

  // Shared booking fields + actions.
  'merchant.field.code': 'Code',
  'merchant.field.total': 'Total',
  'merchant.field.advance': 'Advance',
  'merchant.field.phone': 'Phone',
  'merchant.action.complete': 'Complete',

  // Today.
  'merchant.today.stat.bookings': 'Bookings today',
  'merchant.today.awaiting': 'Awaiting verification',
  'merchant.today.this_week': 'this week',
  'merchant.today.last': 'last',
  'merchant.today.link_copied': 'Booking link copied',
  'merchant.today.schedule': "Today's schedule",

  // Rail chrome + business switcher (design merchant shell).
  'merchant.add_business': 'Add a business',
  'merchant.pause_online': 'Pause online bookings',
  'merchant.sign_out': 'Sign out',
  'merchant.sub_status.trial': 'Trial',
  'merchant.sub_status.active': 'Active',
  'merchant.sub_status.grace_period': 'Grace',
  'merchant.sub_status.expired': 'Expired',
  'merchant.sub_status.cancelled': 'Cancelled',

  // Walk-in / add-booking sheet.
  'merchant.walkin.service': 'Service',
  'merchant.walkin.time': 'Time',

  // Pause-online confirm dialog.
  'merchant.pause_confirm.title': 'Pause online bookings?',
  'merchant.pause_confirm.body':
    "Customers won't be able to book online until you resume. Existing bookings are unaffected.",
  'merchant.pause_confirm.keep': 'Keep it on',

  // Booking detail sheet.
  'merchant.detail.payment': 'Payment',
  'merchant.detail.proof': 'Payment proof',
  'merchant.reject.duplicate': 'Duplicate',
  'merchant.reject.other': 'Other',

  // Verify queue.
  'merchant.verify.empty': 'Queue clear',
  'merchant.verify.confirm_payment': 'Verify & confirm',
  'merchant.verify.confirm_reject': 'Reject payment',
  'merchant.verify.in_queue': 'in queue',
  'merchant.proof.method': 'Method',
  'merchant.proof.trx': 'Transaction ID',
  'merchant.proof.amount': 'Amount',

  // Bookings.
  'merchant.bookings.filter.all': 'All',
  'merchant.bookings.source.all': 'All',
  'merchant.bookings.source.marketplace': 'Marketplace',
  'merchant.bookings.source.qr': 'QR code',
  'merchant.bookings.source.share_link': 'Shared link',
  'merchant.bookings.source.facebook': 'Facebook',
  'merchant.bookings.source.walk_in': 'Walk-in',

  // Calendar.
  'merchant.calendar.prev': 'Previous day',
  'merchant.calendar.next': 'Next day',
  'merchant.calendar.day': 'Day',
  'merchant.calendar.week': 'Week',
  'merchant.calendar.booked': 'booked',
  'merchant.calendar.block_to': 'To',

  // Clients.
  'merchant.clients.visits': 'visits',

  // ---- Merchant dashboard T31: nav groups ----
  'merchant.nav.business': 'Business',
  'merchant.nav.account': 'Account',
  'merchant.nav.checkin': 'Check-in',
  'merchant.nav.team': 'Team',
  'merchant.nav.reports': 'Reports',
  'merchant.nav.inbox': 'Inbox',
  'merchant.nav.settings': 'Settings',

  // Sales screen.
  'merchant.sales.tab.recorded': 'Revenue',
  'merchant.sales.tab.payout': 'Payouts',
  'merchant.sales.stat.this_month': 'This month',
  'merchant.sales.settled': 'Settled',
  'merchant.sales.export': 'Export CSV',
  'merchant.sales.payout_title': 'Coming soon',
  'merchant.sales.payout.gross': 'Gross',
  'merchant.sales.payout.fees': 'Fees',
  'merchant.sales.payout.net': 'Net',

  // Catalog screen.
  'merchant.catalog.add': 'Add service',
  'merchant.catalog.new_item': 'New service',
  'merchant.catalog.name': 'Service name',
  'merchant.catalog.group': 'Group',
  'merchant.catalog.price': 'Price',
  'merchant.catalog.duration': 'Duration (min)',
  'merchant.catalog.inactive': 'Hidden',
  'merchant.catalog.empty': 'No services yet',
  'merchant.catalog.save_changes': 'Save changes',
  'merchant.catalog.default_group': 'Services',
  'merchant.catalog.move_up': 'Move up',
  'merchant.catalog.move_down': 'Move down',

  // Team screen.
  'merchant.team.search': 'Search name or specialty',
  'merchant.team.pros_head': 'Professionals · {n}',
  'merchant.team.new_pro': 'New professional',
  'merchant.team.cap_word': 'professionals',
  'merchant.team.cap_upgrade': 'Upgrade',
  'merchant.team.cap_reached': 'Professional limit reached — upgrade to add more',
  'merchant.team.sub_years': '{spec} · {n} yrs',
  'merchant.team.years_only': '{n} yrs',
  'merchant.team.att_count': '{n, plural, one{# service} other{# services}}',
  'merchant.team.linked_empty': 'Not attached to any service',
  'merchant.team.linked_empty_long': 'Not attached to any service — pick one above so customers see who serves it',
  'merchant.team.row_actions': 'Actions for {name}',
  'merchant.team.archive': 'Archive',
  'merchant.team.archive_blocked': 'Remove from all services before archiving',
  'merchant.team.empty_pro': 'No professionals yet',
  'merchant.team.empty_pro_hint': 'Adding professionals shows customers who will serve them — that lifts bookings.',
  'merchant.team.no_results': 'Nothing found',
  'merchant.team.no_results_hint': 'Try another name or specialty.',
  'merchant.team.toast_saved': 'Professional saved',
  'merchant.team.toast_archived': 'Professional archived',
  'merchant.team.toast_added': '{name} added to the team',
  'merchant.team.save_failed': 'Could not save — try again.',
  'merchant.team.photo_failed': 'Could not upload the photo.',
  'merchant.team.change_photo': 'Change photo',
  'merchant.team.name_bn': 'Name (Bangla)',
  'merchant.team.name_en': 'Name (English)',
  'merchant.team.spec': 'Title / specialty',
  'merchant.team.years': 'Experience (years)',
  'merchant.team.more': 'More details',
  'merchant.team.qual': 'Qualification / degree',
  'merchant.team.langs': 'Languages',
  'merchant.team.bio_bn': 'Short bio (Bangla)',
  'merchant.team.bio_en': 'Short bio (English)',
  'merchant.team.linked': 'Attached services',
  'merchant.team.select_services': 'Select services',
  'merchant.team.n_services': '{n, plural, one{# service} other{# services}}',
  'merchant.team.guard_title': 'Unsaved changes',
  'merchant.team.guard_body': 'What do you want to do before closing?',
  'merchant.team.guard_save': 'Save changes',
  'merchant.team.guard_discard': 'Discard changes',
  'merchant.team.guard_back': 'Go back',
  'merchant.team.qa_title': 'New professional',
  'merchant.team.qa_photo': 'Photo',
  'merchant.team.qa_photo_hint': 'Optional — initials show if skipped',
  'merchant.team.qa_more': 'More details',
  'merchant.team.qa_more_help': 'Can also be added later',
  'merchant.team.qa_qual': 'Qualifications',
  'merchant.team.qa_bio_bn': 'Bio (Bangla)',
  'merchant.team.qa_bio_en': 'Bio (English)',
  'merchant.team.qa_submit': 'Add to team',
  'merchant.team.qa_dupe': 'Someone with this name is already on your team — open their profile instead?',
  'merchant.team.qa_dupe_open': 'Open profile',
  'merchant.team.qa_dupe_new': 'Create new anyway',
  'merchant.team.qa_dupe_toast': '{name} is already in your team',
  'merchant.team.decrease': 'Decrease',
  'merchant.team.increase': 'Increase',
  'merchant.team.logins_head': 'Staff logins · {n}',
  'merchant.team.new_login': 'New login',
  'merchant.team.login_active': 'Active',
  'merchant.team.login_invited': 'Invited',
  'merchant.team.logins_empty': 'No staff logins yet',
  'merchant.team.logins_hint': 'Staff sign in with their phone and handle today’s bookings, check-ins and settlements.',
  'merchant.team.resend': 'Resend invite',
  'merchant.team.revoke': 'Revoke login',
  'merchant.team.toast_resend': 'Invite re-sent',
  'merchant.team.toast_revoke': 'Login revoked',
  'merchant.team.toast_invite': 'Invite sent',
  'merchant.team.sheet_title': 'New staff login',
  'merchant.team.f_name': 'Name',
  'merchant.team.f_phone': 'Phone',
  'merchant.team.f_role': 'Role name',
  'merchant.team.f_role_hint': 'e.g. Reception, Manager',
  'merchant.team.invite': 'Send invite',

  // Marketing / QR studio.
  'merchant.marketing.qr_studio': 'QR booking codes',
  'merchant.marketing.qr_studio_sub':
    'Branded QR codes for your counter, cards and posters. Scans land on your booking page and are tracked as “QR code” in Reports.',
  'merchant.marketing.whole_business': 'Whole business',
  'merchant.marketing.scan_to_book': 'Scan to book on',
  'merchant.marketing.preview_scan': 'Preview the scan',
  'merchant.marketing.canonical': 'Canonical booking link',
  'merchant.marketing.share': 'Share',
  'merchant.marketing.download_png': 'Download QR (PNG)',
  'merchant.marketing.print_poster': 'Print poster (A5)',
  'merchant.marketing.copy': 'Copy',
  'merchant.marketing.copy_link': 'Copy link',
  'merchant.marketing.copied': 'Copied',
  'merchant.marketing.regenerate': 'Regenerate link',
  'merchant.marketing.regen_note': 'Old links keep redirecting — nothing breaks.',
  'merchant.marketing.scans': 'Scans',
  'merchant.marketing.qr_bookings': 'Bookings from QR',
  'merchant.nobiz.title': 'Set up your business first',
  'merchant.nobiz.body': 'Your QR codes and insights appear once your business profile is created.',
  'merchant.nobiz.cta': 'Go to My Business',
  'merchant.nobiz.grid_title': 'Add your first business',
  'merchant.nobiz.grid_body': 'Create your business profile to start taking bookings, share your QR code and see insights.',
  'merchant.nobiz.grid_cta': 'Create business',
  'merchant.marketing.stats_locked': 'Scan and booking counts come with a paid plan.',
  'merchant.marketing.stats_upgrade': 'See plans',
  'merchant.marketing.share_link_title': 'Shareable booking link',
  'merchant.marketing.share_link_sub': 'Bookings from this link show as “Shared link” in Reports.',
  'merchant.marketing.embed_title': '“Book Now” embed snippet',
  'merchant.marketing.embed_sub': 'Paste into your website — opens your booking page.',
  'merchant.marketing.fb_title': 'Facebook page button',
  'merchant.marketing.fb_action': 'Edit action button → Book now → Link to website',
  'merchant.marketing.fb_body_pre': 'On your Facebook page:',
  'merchant.marketing.fb_body_post':
    '— paste your booking link. Bookings arriving via Facebook are tagged in Reports.',
  'merchant.marketing.promos': 'Platform promos',
  'merchant.marketing.participating': 'Participating',
  'merchant.marketing.not_eligible': 'Not eligible',
  'merchant.marketing.opt_in': 'Opt in',
  'merchant.marketing.opt_out': 'Opt out',
  'merchant.marketing.all_categories': 'all categories',
  'merchant.marketing.percent_off': '% off',
  'merchant.marketing.off': 'off',
  'merchant.marketing.no_promos': 'No campaigns available right now.',

  // Reports screen.
  'merchant.reports.no_shows': 'No-shows',
  'merchant.reports.no_show_rate': 'No-show rate',
  'merchant.reports.period_7': '7 days',
  'merchant.reports.period_30': '30 days',
  'merchant.reports.weeks': 'weeks',

  // Services › Reviews tab + quick-add professional (DC75; design M:1711-1800, M:5245-5262).
  'merchant.services.review_count': '{n, plural, one{# review} other{# reviews}}',
  'merchant.services.awaiting': '{n} awaiting reply',
  'merchant.services.all_short': 'All',
  'merchant.services.unreplied': 'Not replied',
  'merchant.services.replied': 'Replied',
  'merchant.services.reply': 'Reply',
  'merchant.services.review_filters': 'Filter reviews',
  'merchant.services.rating_filter': 'Filter by rating',
  'merchant.services.anonymous': 'Anonymous',
  'merchant.services.verified': 'Verified booking',
  'merchant.services.view_booking': 'View booking',
  'merchant.services.all_services': 'All services',
  'merchant.services.service_filter': 'Filter by service',
  'dc72.review.title': 'Customer review',
  'dc72.review.reply': 'Reply',
  'dc72.review.open': 'Open review',
  'merchant.services.your_reply': 'Your reply',
  'merchant.services.editable_hours': '{n, plural, one{editable for # hour} other{editable for # hours}}',
  'merchant.services.reply_ph': 'Thank the customer or explain the fix…',
  'merchant.services.send_reply_btn': 'Send reply',
  'merchant.services.reply_sent': 'Reply posted',
  'merchant.services.reply_updated': 'Reply updated',
  'merchant.services.reply_failed': 'Could not send the reply — try again',
  'merchant.services.reply_expired': 'The edit window for this reply has closed',
  'merchant.services.reply_removed': 'Your reply was removed by moderation — a new one cannot be posted.',
  'merchant.services.report': 'Report',
  'merchant.services.report_reasons.fake': 'Fake review',
  'merchant.services.report_reasons.abusive': 'Abusive language',
  'merchant.services.report_reasons.other_business': 'Wrong business',
  'merchant.services.report_reasons.other': 'Other',
  'merchant.services.reported': 'Report sent',
  'merchant.services.report_failed': 'Could not send the report — try again',
  'merchant.services.reviews_empty': 'No reviews yet',
  'merchant.services.reviews_empty_hint': 'Reviews from customers who booked will show here.',
  'merchant.services.no_results': 'Nothing found',
  'merchant.services.no_results_hint': 'Try another rating or filter.',
  'merchant.services.qa_submit': 'Add & attach',
  'merchant.services.qa_dupe': 'Someone with this name already exists — attach them instead?',
  'merchant.services.qa_dupe_attach': 'Attach them',
  'merchant.services.qa_added': '{name} added and attached to this service',
  'merchant.services.qa_attached': '{name} attached to this service',
  'merchant.services.qa_more_help': 'Can also be added later from Team',

  // Inbox screen.
  'merchant.inbox.title': 'Inbox',
  'merchant.inbox.sub': 'Messages from customers and platform support.',
  'merchant.inbox.support': 'Support',
  'merchant.inbox.type_message': 'Type a message',
  'merchant.inbox.send': 'Send',
  'merchant.inbox.empty': 'No conversations yet',
  'merchant.inbox.select': 'Select a conversation',

  // Settings — three-tab shell + Business profile redesign.
  'merchant.settings.unsaved': 'Unsaved changes',
  'merchant.settings.discard': 'Discard',
  // My businesses tab.
  'merchant.biz.status.active': 'Active',
  'merchant.biz.status.pending_approval': 'Pending approval',
  'merchant.biz.status.suspended': 'Suspended',
  'merchant.biz.status.draft': 'Draft',
  'merchant.biz.status.rejected': 'Rejected',
  'merchant.biz.status.banned': 'Banned',
  'merchant.biz.status.departed': 'Departed',
  // Account tab.

  // Subscription status labels.
  'merchant.sub.status.trial': 'Trial',
  'merchant.sub.status.active': 'Active',
  'merchant.sub.status.grace_period': 'Grace period',
  'merchant.sub.status.expired': 'Expired',
  'merchant.sub.status.cancelled': 'Cancelled',

  // Shared T31 actions.
  'action.add': 'Add',
  'action.edit': 'Edit',
  'action.delete': 'Delete',
  'action.close': 'Close',
  'action.done': 'Done',

  // ================= Customer dashboard (surface: dash-user, T32) =================
  'user.nav.primary': 'Dashboard navigation',
  'user.nav.home': 'Overview',
  'user.nav.bookings': 'My bookings',
  'user.nav.saved': 'Favourites',
  'user.nav.messages': 'Messages',
  'user.nav.profile': 'Profile & settings',
  'user.nav.notifications': 'Notifications',
  'user.nav.reviews': 'My reviews',
  // Top-bar chrome (imported design c0996bce): the "For business" link back to the
  // marketing site, the "View as …" role switcher, and the account-chip sign-out.
  'user.nav.view_as': 'View as',
  'user.nav.sign_out': 'Sign out',

  // Account switcher — "Continue as…" role picker (M32). Shown only when one login
  // holds more than one role.
  'user.account.switch_heading': 'Switch account',
  'user.account.continue_as': 'Continue as {role}',
  'user.account.switching': 'Switching…',
  'user.account.switch_failed': 'Could not switch. Please try again.',
  'user.role.customer': 'Customer',
  'user.role.merchant': 'Merchant',
  'user.role.staff': 'Staff',
  'user.role.admin': 'Admin',

  // Bookings.
  'user.bookings.filter.upcoming': 'Upcoming',
  'user.bookings.filter.all': 'All',
  // Per-segment empty copy — the message tracks the active filter.
  'user.bookings.empty.completed': 'No completed bookings yet.',
  'user.bookings.empty.cancelled': 'No cancelled bookings.',

  // Booking detail.
  'user.detail.payment': 'Payment',
  'user.detail.submit_proof': 'Submit proof',
  'user.detail.method': 'Method',
  'user.detail.amount': 'Amount',
  'user.detail.cancel_booking': 'Cancel booking',
  'user.detail.add_calendar': 'Add to calendar',
  'user.detail.status_note': 'Note',
  // Payment proof trail (one card per submitted proof).
  'user.detail.screenshot_attached': 'Screenshot attached',
  'user.detail.proof_status.submitted': 'Under review',
  'user.detail.proof_status.verified': 'Verified',
  'user.detail.proof_status.rejected': 'Rejected',
  'user.detail.proof_status.superseded': 'Superseded',

  // Saved (DC64 — Saved.dc.html; favourites only, DR-42). Keys per round2-i18n-strings.json.
  'user.saved.lead': 'Everything you saved in one place — your favourite businesses.',
  'user.saved.tabs_label': 'Saved views',
  'user.saved.tabs.all': 'All',
  'user.saved.tabs.favs': 'Favourites',
  'user.saved.empty_tab': 'Nothing in this list yet',
  'user.saved.groups.favs': 'Favourite businesses',
  'user.saved.help.favs': 'Places you keep going back to — rebook in one tap.',
  'user.saved.visits': '{n, plural, one{# visit} other{# visits}}',
  'user.saved.remove': 'Remove {name} from favourites',
  'user.saved.removed': 'Removed from favourites',
  'user.saved.undo': 'Undo',
  'user.saved.remove_failed': 'Could not remove it — please try again',
  'user.saved.empty.t': 'Nothing saved yet',
  'user.saved.empty.s': 'Your favourite businesses collect here',
  'user.saved.empty.cta': 'Find a business',

  // Messages.
  'user.messages.title': 'Messages',
  'user.messages.reply_hint': 'Usually replies within an hour',
  'user.messages.type': 'Type a message',
  'user.messages.send': 'Send',
  'user.messages.support': 'Support',
  'user.messages.empty_title': 'No conversations yet',
  'user.messages.empty_body': 'Message any business from a booking to start a thread.',
  // Chat (FR-C15 / FR-B16, Infra SRS D13) — shared by the customer Messages and merchant Inbox screens.
  'chat.list_label': 'Conversations',
  'chat.search': 'Search conversations',
  'chat.filter.all': 'All',
  'chat.filter.unread': 'Unread',
  'chat.no_results': 'No conversations match.',
  'chat.unread_count': '{n} unread',
  'chat.you': 'You',
  'chat.today': 'Today',
  'chat.yesterday': 'Yesterday',
  'chat.select': 'Pick a conversation to read and reply.',
  'chat.empty_thread': 'No messages yet. Say hello!',
  'chat.state.sending': 'Sending…',
  'chat.state.sent': 'Sent',
  'chat.state.seen': 'Seen',
  'chat.state.failed': 'Not sent',
  'chat.retry': 'Retry',
  'chat.typing': 'Typing…',
  'chat.live': 'Live',
  'chat.polling': 'Refreshing automatically',
  'chat.enter_hint': 'Enter to send · Shift+Enter for a new line',
  'chat.booking': 'Booking',
  'chat.view_booking': 'View booking',
  'chat.new_messages': 'New messages',
  'chat.message_list': 'Messages',

  // Notifications.
  'user.notifs.mark_read': 'Mark all read',
  'user.notifs.empty': 'All caught up',

  // ================= Admin console (surface: dash-admin, T33) =================
  'admin.console': 'Admin',
  'admin.nav.primary': 'Admin navigation',
  'admin.nav.overview': 'Overview',
  'admin.nav.applications': 'Approvals',
  'admin.nav.reviews': 'Reviews',
  'admin.nav.disputes': 'Disputes',
  'admin.nav.bookings': 'Bookings',
  'admin.nav.subscriptions': 'Subscriptions',
  'admin.nav.users': 'Customers',
  'admin.nav.categories': 'Categories',
  'admin.nav.templates': 'Booking templates',
  'admin.nav.locations': 'Locations',
  'admin.nav.cms': 'CMS & growth',
  'admin.grant.super_admin': 'Super admin',
  'admin.grant.operations': 'Operations',
  'admin.grant.finance': 'Finance',
  'admin.grant.support': 'Support',
  'admin.grant.marketing': 'Marketing',
  'admin.grant.none': 'No grants',
  'admin.signout': 'Sign out',
  'auth.redirecting': 'Taking you to sign in…',
  'auth.gate_error_title': 'Could not sign you in',
  'auth.gate_error_sub': 'Your session could not be established. Please sign in again.',
  'auth.go_to_login': 'Go to sign in',

  // Admin sign-in (DC111, A §A.5) — phone + OTP only (DR-34), console palette (DR-45).
  'admin.signin.console_tag': 'Admin console',
  'admin.signin.theme': 'Appearance',
  'admin.signin.theme_light': 'Light',
  'admin.signin.theme_dark': 'Dark',
  'admin.signin.mode_light': 'light',
  'admin.signin.mode_dark': 'dark',
  'admin.signin.theme_note_default': 'Console default',
  'admin.signin.title': 'Sign in',
  'admin.signin.sub': 'Enter the mobile number your invite was sent to.',
  'admin.signin.phone_label': 'Mobile number',
  'admin.signin.continue': 'Continue',
  'admin.signin.wait': 'Wait {n}s',
  'admin.signin.cool': 'Too many attempts — try again in {n}s',
  'admin.signin.no_access': 'This number doesn’t have console access. Contact a Super Admin.',
  'admin.signin.otp_title': 'Verify your number',
  'admin.signin.otp_sub': 'Enter the 6-digit code sent to {phone}',
  'admin.signin.code_label': 'One-time code',
  'admin.signin.wrong': 'Wrong code — {n} attempts left',
  'admin.signin.resent': 'Code re-sent to {phone}',
  'admin.signin.resend': 'Resend code',
  'admin.signin.resend_in': 'Resend in {n}s',
  'admin.signin.resend_max': 'Resend limit reached',
  'admin.signin.back': '← Back',
  'admin.signin.welcome': 'Welcome back, {name}',
  'admin.signin.welcome_anon': 'Welcome back',
  'admin.signin.done_sub': 'Session stays active until you sign out.',
  'admin.signin.ready': 'Session ready · appearance: {mode}',
  'admin.signin.open': 'Open console →',
  'admin.signin.sign_out': 'Sign out — back to sign-in',
  'admin.signin.foot': 'Invite-only console — admin actions are recorded in the immutable audit log.',
  'admin.signin.send_failed': 'Could not send the code. Please try again.',
  'admin.signin.verify_failed': 'Could not sign you in. Please try again.',

  // Status chips (every admin domain).
  'admin.status.pending_approval': 'Pending',
  'admin.status.info_requested': 'Info requested',
  'admin.status.approved': 'Approved',
  'admin.status.rejected': 'Rejected',
  'admin.status.draft': 'Draft',
  'admin.status.active': 'Active',
  'admin.status.suspended': 'Suspended',
  'admin.status.banned': 'Banned',
  'admin.status.departed': 'Departed',
  'admin.status.verified': 'Verified',
  'admin.status.pending': 'Pending',
  'admin.status.open': 'Open',
  'admin.status.resolved': 'Resolved',
  'admin.status.blocked': 'Blocked',
  'admin.status.merged': 'Merged',
  'admin.status.deleted': 'Deleted',
  'admin.status.payment_verification': 'Verifying payment',
  'admin.status.confirmed': 'Confirmed',
  'admin.status.in_progress': 'In progress',
  'admin.status.completed': 'Completed',
  'admin.status.cancelled': 'Cancelled',
  'admin.status.no_show': 'No-show',
  'admin.status.expired': 'Expired',
  'admin.status.refunded': 'Refunded',
  'admin.status.paid': 'Paid',
  'admin.status.due': 'Due',
  'admin.status.overdue': 'Overdue',
  'admin.status.void': 'Void',
  'admin.status.available': 'Available',
  'admin.status.filled': 'Filled',
  'admin.status.disabled': 'Disabled',

  // Filters / common.
  'admin.filter.all': 'All',
  'admin.nav.group.overview': 'Overview',
  'admin.nav.group.operations': 'Operations',
  'admin.nav.group.people': 'People',
  'admin.nav.group.revenue': 'Revenue',
  'admin.nav.group.platform': 'Platform',
  'admin.nav.group.system': 'System',
  'admin.search_everything': 'Search everything…',
  'admin.audit_note': 'Actions logged to the audit trail.',
  'admin.search': 'Search',
  'admin.action.approve': 'Approve',
  'admin.action.reject_reason': 'Reject with reason',
  'admin.action.cancel': 'Cancel',
  'admin.reason.label': 'Reason',

  // Overview.
  'admin.overview.title': 'Platform overview',
  'admin.overview.sub': 'Live marketplace health across merchants, revenue, bookings and delivery.',
  'admin.overview.trend': 'Trend',
  'admin.overview.rate.strong': 'Strong',
  'admin.overview.rate.steady': 'Steady',
  'admin.overview.rate.down': 'Down',
  'admin.overview.rate.flat': 'Flat',
  'admin.overview.vs_last_month': 'vs last month',
  'admin.overview.vs_last_month_pct': '{pct} vs last month',
  'admin.overview.ytd_range': '{from} – {to} {year}',
  'admin.overview.kpi.subs.label': 'Active subscriptions',
  'admin.overview.kpi.subs.side_v': 'Paid subscriptions',
  'admin.overview.kpi.subs.side_s': '{a} + {b} active',
  'admin.overview.kpi.subs.new': '+{n} new',
  'admin.overview.kpi.subs.note': 'this month · vs {n} last month',
  'admin.overview.kpi.subs.active_paid': 'active paid',
  'admin.overview.kpi.subs.renewals': 'Renewals within 30 days: {n}',
  'admin.overview.kpi.revenue.label': 'Revenue',
  'admin.overview.kpi.revenue.subs': 'Subscriptions / mo',
  'admin.overview.kpi.revenue.promo': 'Promotion / mo',
  'admin.overview.kpi.revenue.sub': 'Subscriptions + promotion earnings — SMS excluded',
  'admin.overview.kpi.bookings.label': 'Bookings',
  'admin.overview.kpi.bookings.side_s': 'bookings this month',
  'admin.overview.kpi.bookings.note': 'today · vs last month',
  'admin.overview.kpi.bookings.booked': 'Booked value / mo',
  'admin.overview.kpi.bookings.ytd': 'Booked value · YTD',
  'admin.overview.kpi.biz.label': 'Active businesses',
  'admin.overview.kpi.biz.side_s': 'using booking services',
  'admin.overview.kpi.biz.top_city': 'Top city',
  'admin.overview.kpi.biz.top_city_n': '{n} businesses',
  'admin.overview.kpi.biz.categories': 'Categories live',
  'admin.overview.kpi.biz.categories_s': '{a}, {b} +{n}',
  'admin.overview.kpi.biz.pending': 'Pending approval',
  'admin.overview.kpi.biz.pending_s': 'in KYC review',
  'admin.overview.kpi.offers.label': 'Active offers',
  'admin.overview.kpi.offers.side_s': 'live offers today',
  'admin.overview.kpi.offers.note': 'this month · vs last month',
  'admin.overview.kpi.offers.served_month': 'Served this month',
  'admin.overview.kpi.offers.served_month_s': '{n} offers vs last month',
  'admin.overview.kpi.offers.served_year': 'Served this year',
  'admin.overview.kpi.gmv.label': 'GMV (booked value)',
  'admin.overview.kpi.gmv.side_s': 'completed this month',
  'admin.overview.kpi.gmv.note': 'confirmed + completed · vs last month',
  'admin.overview.kpi.gmv.repeat': 'Repeat-booking rate',
  'admin.overview.kpi.gmv.repeat_s': 'with 2+ bookings',
  'admin.overview.kpi.gmv.on_platform': 'Paid on-platform',
  'admin.overview.kpi.gmv.on_platform_s': 'of booked value',
  'admin.overview.kpi.gmv.directory': 'Directory coverage',
  'admin.overview.kpi.gmv.directory_s': 'areas live',
  'admin.overview.breakdown.loading': 'Loading the monthly breakdown…',
  'admin.overview.breakdown.error': 'The breakdown could not be loaded.',
  'admin.overview.breakdown.empty': 'No monthly figures yet.',
  'admin.overview.rev.title': 'Revenue mix — this month',
  'admin.overview.rev.sub': 'Two launch lines: subscriptions and admin-scheduled sponsored placement — app push and SMS credit are not sold at launch.',
  'admin.overview.rev.subscriptions': 'Subscriptions',
  'admin.overview.rev.placement': 'Sponsored placement',
  'admin.overview.bk.title': 'Bookings over time',
  'admin.overview.bk.sub': 'Monthly confirmed bookings and active subscriptions, January to December — later months fill in as they close.',
  'admin.overview.bk.bookings': 'Bookings',
  'admin.overview.bk.subscriptions': 'Subscriptions',
  'admin.overview.category_split': 'Category split',
  'admin.overview.city_split': 'City split',
  'admin.overview.sources': 'Booking sources',
  'admin.overview.sources_sub': 'Where platform bookings originate — QR codes tracked distinctly.',
  'admin.overview.src.marketplace': 'Marketplace',
  'admin.overview.src.qr': 'QR code',
  'admin.overview.src.link': 'Shared link',
  'admin.overview.src.facebook': 'Facebook',
  'admin.overview.health': 'System health',
  'admin.overview.health.api': 'API error rate',
  'admin.overview.health.sms': 'SMS delivery',
  'admin.overview.health.uptime': 'Uptime 30d',
  'admin.overview.health.p50': 'Search p50',
  'admin.overview.health.ms': '{n} ms',
  'admin.overview.health.healthy': 'Healthy',
  'admin.overview.health.degraded': 'Degraded',
  'admin.overview.health.fast': 'Fast',
  'admin.overview.health.slow': 'Slow',
  'admin.overview.funnel': 'Funnel per booking type',
  'admin.overview.funnel_sub': 'Profile views → bookings → completions, this month.',
  'admin.overview.funnel_nums': '{views} → {bookings} → {done}',
  'admin.overview.legend.views': 'Views',
  'admin.overview.legend.bookings': 'Bookings',
  'admin.overview.legend.completions': 'Completions',
  'admin.overview.queues': 'Work queues',
  'admin.overview.recent': 'Recent activity',
  'admin.overview.recent_empty': 'No admin activity recorded yet.',
  'admin.overview.when': '{date}, {time}',
  'admin.overview.q.hours': '{n} h',
  'admin.overview.q.within_sla': 'Within SLA',
  'admin.overview.q.overdue_n': '{n} overdue',
  'admin.overview.q.breaching_n': '{n} breaching',
  'admin.overview.q.pending_n': '{n} pending',
  'admin.overview.q.clear': 'Clear',
  'admin.overview.q.next_sla': 'Next SLA',
  'admin.overview.q.nearest_sla': 'Nearest SLA',
  'admin.overview.q.overdue_by': 'Overdue by',
  'admin.overview.q.overdue': 'Overdue',
  'admin.overview.q.value': 'Value',
  'admin.overview.q.methods': 'Methods',
  'admin.overview.q.approvals': 'Approvals waiting',
  'admin.overview.q.approvals_unit': 'businesses + services',
  'admin.overview.q.businesses': 'Businesses',
  'admin.overview.q.services': 'Services',
  'admin.overview.q.approvals_merchant': '{n} waiting on merchant reply',
  'admin.overview.q.approvals_team': 'All items are with the review team',
  'admin.overview.q.approvals_link': 'Review queue →',
  'admin.overview.q.payments': 'Payments to verify',
  'admin.overview.q.payments_unit': 'bookings',
  'admin.overview.q.manual_check': 'Manual check',
  'admin.overview.q.value_held': 'Value held',
  'admin.overview.q.avg_ticket': 'Avg ticket',
  'admin.overview.q.payments_sub': 'TRX IDs and screenshots need an operator decision',
  'admin.overview.q.payments_link': 'Open bookings →',
  'admin.overview.q.disputes': 'Open disputes',
  'admin.overview.q.disputes_unit': 'cases',
  'admin.overview.q.escalated': 'Escalated',
  'admin.overview.q.refunds': 'Refunds',
  'admin.overview.q.disputes_sub': 'Paid-but-rejected and no-show escalations',
  'admin.overview.q.disputes_link': 'Open disputes →',
  'admin.overview.q.flags': 'Review flags',
  'admin.overview.q.flags_unit': 'flagged reviews',
  'admin.overview.q.fake_signal': 'Fake-review signal',
  'admin.overview.q.manual_only': 'Manual only',
  'admin.overview.q.auto_flag': 'Auto-flag',
  'admin.overview.q.merchant_flag': 'Merchant flag',
  'admin.overview.q.reply_flag': 'Reply flags',
  'admin.overview.q.flags_sub': 'Text and rating stay immutable — hide or keep only',
  'admin.overview.q.flags_link': 'Moderate reviews →',
  'admin.overview.q.refund_queue': 'Refunds to approve',
  'admin.overview.q.refund_unit': 'refunds',
  'admin.overview.q.finance_signoff': 'Finance sign-off',
  'admin.overview.q.refund_sub': 'Payouts wait for Finance approval before they are sent',
  'admin.overview.q.refund_link': 'Open refunds →',
  'admin.overview.q.subs': 'Plan payments to verify',
  'admin.overview.q.subs_unit': 'plan payments',
  'admin.overview.q.billing_signoff': 'Billing sign-off',
  'admin.overview.q.merchants': 'Merchants',
  'admin.overview.q.subs_sub': 'Subscription TRX IDs waiting for verification',
  'admin.overview.q.subs_link': 'Verify payments →',

  // Applications.
  'admin.applications.selected': 'selected',

  // Merchants / businesses.
  'admin.businesses.suspend': 'Suspend',
  'admin.businesses.feature': 'Feature',
  'admin.businesses.featured': 'Featured',

  // Subscriptions.
  'admin.subs.col.invoice': 'Invoice',
  'admin.subs.col.plan': 'Plan',
  'admin.subs.col.amount': 'Amount',
  'admin.subs.col.method': 'Method',
  'admin.subs.per_month': '/month',
  'admin.subs.days': 'days',
  'admin.subs.mrr': 'MRR',

  // Disputes.
  'admin.disputes.title': 'Disputes',
  'admin.disputes.col.booking': 'Booking',
  'admin.disputes.resolve': 'Resolve',
  'admin.disputes.res.override_confirmed': 'Override → confirmed',
  'admin.disputes.res.refund_recorded': 'Refund recorded',
  'admin.disputes.res.dismissed': 'Dismissed',

  // Users.
  'admin.users.col.role': 'Role',
  'admin.users.col.contact': 'Contact',
  'admin.users.block': 'Block',
  'admin.users.unblock': 'Unblock',
  'admin.users.export': 'Export data',

  // Reviews.
  'admin.reviews.hide': 'Hide review',
  'admin.reviews.hidden': 'Hidden',

  // Categories (DC106).
  'admin.categories.on': 'On',
  'admin.cat.title': 'Categories',
  'admin.cat.sub':
    'Marketplace browse verticals — each maps to a booking template. Order here = homepage chip-row order.',
  'admin.cat.stat.live': 'Live',
  'admin.cat.stat.live_sub': 'on the marketplace',
  'admin.cat.stat.hidden': 'Hidden',
  'admin.cat.stat.draft': 'Draft',
  'admin.cat.stat.services': 'Total services',
  'admin.cat.stat.services_sub': 'across categories',
  'admin.cat.add': '＋ Add category',
  'admin.cat.loading': 'Loading categories',
  'admin.cat.list_title': 'Marketplace categories',
  'admin.cat.order_note':
    'Drag ⠿ to reorder — the homepage chip row and search filter order follow this list. Order autosaves.',
  'admin.cat.hidden_note':
    'Hidden categories disappear from browse & search immediately — existing bookings and services are unaffected.',
  'admin.cat.empty': 'No categories yet — add one to appear on the homepage chip row.',
  'admin.cat.chip.hidden': 'Hidden',
  'admin.cat.chip.draft': 'Draft',
  'admin.cat.tpl_title': '{model} model — opens the booking templates',
  'admin.cat.count_sub': 'services',
  'admin.cat.drag': 'Drag to reorder',
  'admin.cat.handle_label': 'Reorder {name} — drag, or use the arrow keys',
  'admin.cat.switch_label': '{name} on the marketplace',
  'admin.cat.menu_label': 'Actions for {name}',
  'admin.cat.menu.edit': 'Edit',
  'admin.cat.menu.archive': 'Archive',
  'admin.cat.toggle.live': 'Live — click to hide from browse & search',
  'admin.cat.toggle.hidden': 'Hidden — click to make live',
  'admin.cat.toggle.draft': 'Draft — save it with its template to take it out of draft',
  'admin.cat.hide.title': 'Hide “{name}” from the marketplace?',
  'admin.cat.hide.body':
    'The category disappears from browse and search immediately. Existing bookings and its live services are unaffected. A reason is required and audit-logged.',
  'admin.cat.hide.fact': 'Live services',
  'admin.cat.hide.action': 'Hide category',
  'admin.cat.toast.hidden_logged': '{name} hidden from marketplace — audit-logged',
  'admin.cat.toast.hidden': '{name} hidden from marketplace',
  'admin.cat.toast.live': '{name} live on marketplace',
  'admin.cat.toast.draft_toggle': 'Draft categories can’t go live until saved with their template',
  'admin.cat.toast.order': 'Order updated — homepage chip row & search filters follow this order',
  'admin.cat.archive.blocked': 'Only categories with 0 services can be archived — hide it instead',
  'admin.cat.archive.title': 'Archive “{name}”?',
  'admin.cat.archive.body':
    'Archived categories leave this list but stay restorable below — there is no hard delete. The change is audit-logged.',
  'admin.cat.archive.action': 'Archive category',
  'admin.cat.toast.archived': '{name} archived — restorable from the archived list',
  'admin.cat.arch.label': 'Archived categories ({n})',
  'admin.cat.arch.note':
    'Archived categories are removed from ordering and browse but never hard-deleted. Restoring returns them live.',
  'admin.cat.arch.meta': '{n, plural, one{# service} other{# services}}',
  'admin.cat.arch.restore': 'Restore',
  'admin.cat.toast.restored': '{name} restored — live again',
  'admin.cat.err.generic': 'That didn’t go through — please try again.',
  'admin.cat.err.hide_conflict': 'This category still has active services — hiding it needs a reason.',
  'admin.cat.err.archive_conflict':
    'Active merchants are still listed under this category — remap them before archiving.',
  'admin.cat.err.slug': 'Slug is required — lowercase letters, numbers and dashes only.',
  'admin.cat.err.slug_taken': 'That slug is already in use.',
  'admin.cat.err.labels': 'Both the EN and BN labels are required.',
  'admin.cat.err.tpl': 'Pick the booking template this category maps to.',
  'admin.cat.form.add_title': 'Add category',
  'admin.cat.form.edit_title': 'Edit category',
  'admin.cat.form.close': 'Close',
  'admin.cat.form.slug': 'Slug',
  'admin.cat.form.slug_ph': 'lowercase-with-dashes',
  'admin.cat.form.slug_locked': 'Slug is immutable after creation — it anchors public URLs and analytics.',
  'admin.cat.form.slug_note_add': 'Lowercase, dashes only. Cannot be changed after creation.',
  'admin.cat.form.slug_note_edit': 'Immutable after creation — anchors public URLs and analytics.',
  'admin.cat.form.en': 'EN label *',
  'admin.cat.form.en_ph': 'e.g. Photo Studios',
  'admin.cat.form.bn': 'BN label *',
  'admin.cat.form.bn_ph': 'যেমন: ফটো স্টুডিও',
  'admin.cat.form.icon': 'Icon',
  'admin.cat.form.colour': 'Tile color',
  'admin.cat.form.tpl': 'Template mapping *',
  'admin.cat.form.tpl_pick': '— Pick a booking template —',
  'admin.cat.form.remap_warn':
    'Remap warning: existing services keep their current booking template; only NEW services will use the new one. Confirmed on save.',
  'admin.cat.form.draft_note':
    'Draft category — saving it with its template takes it out of draft as hidden; switch it live when ready.',
  'admin.cat.form.desc_en': 'Short description — EN',
  'admin.cat.form.desc_en_ph': 'Search / SEO — optional',
  'admin.cat.form.desc_bn': 'Short description — BN',
  'admin.cat.form.desc_bn_ph': 'ঐচ্ছিক',
  'admin.cat.form.save': 'Save category',
  'admin.cat.pv.heading': 'Live preview',
  'admin.cat.pv.chip': 'Homepage chip row',
  'admin.cat.pv.tile': 'Category tile',
  'admin.cat.pv.en': 'EN',
  'admin.cat.pv.bn': 'বাং',
  'admin.cat.pv.label_en_ph': 'Category name…',
  'admin.cat.pv.label_bn_ph': 'বাংলা লেবেল…',
  'admin.cat.pv.desc_en_ph': 'Short description…',
  'admin.cat.pv.desc_bn_ph': 'সংক্ষিপ্ত বিবরণ…',
  'admin.cat.remap.title': 'Remap “{name}” to a new template?',
  'admin.cat.remap.body':
    'Existing services keep their current booking template; only NEW services in this category will use the new template. The change is audit-logged from → to.',
  'admin.cat.remap.from': 'From',
  'admin.cat.remap.to': 'To',
  'admin.cat.remap.action': 'Remap template',
  'admin.cat.toast.created': '{name} created — hidden until you switch it live',
  'admin.cat.toast.updated': '{name} updated',

  // Locations (DC106).
  'admin.loc.title': 'Locations',
  'admin.loc.sub':
    'Division → City → Area registry. A city with no live merchants stays hidden until one is approved, unless force-shown.',
  'admin.loc.stat.divisions': 'Divisions',
  'admin.loc.stat.cities_live': 'Cities live',
  'admin.loc.add': 'Add location',
  'admin.loc.edit': 'Edit location',
  'admin.loc.publish': 'Publish',
  'admin.loc.publish_n': 'Publish ({n})',
  'admin.loc.pending.label':
    '{n, plural, one{# staged change} other{# staged changes}} — draft until you publish:',
  'admin.loc.pending.add': '＋ {division} → {city} · {vis}',
  'admin.loc.pending.edit': '✎ {division} → {city} · {vis}',
  'admin.loc.pending.live': 'live',
  'admin.loc.pending.hidden': 'hidden',
  'admin.loc.pending.remove': 'Remove staged change',
  'admin.loc.pending.note': 'Publish applies & audit-logs these',
  'admin.loc.tabs': 'Divisions',
  'admin.loc.tabs.all': 'All divisions',
  'admin.loc.search': 'Search city or area…',
  'admin.loc.card.divisions': 'Divisions',
  'admin.loc.card.divisions_sub': 'Administrative regions',
  'admin.loc.card.cities_live': 'Cities live',
  'admin.loc.card.cities_live_sub': 'Visible on the marketplace',
  'admin.loc.card.hidden': 'Hidden cities',
  'admin.loc.card.hidden_sub': 'Await an approved merchant',
  'admin.loc.card.areas': 'Areas',
  'admin.loc.card.areas_sub': 'Across all cities',
  'admin.loc.group.cities': '{n, plural, one{# city} other{# cities}}',
  'admin.loc.group.live': '{live} live · {hidden} hidden',
  'admin.loc.city.areas': '{n, plural, one{# area} other{# areas}}',
  'admin.loc.chip.live': 'Live',
  'admin.loc.chip.hidden': 'Hidden',
  'admin.loc.switch_label': '{city} on the marketplace',
  'admin.loc.toggle.to_hide': 'Live — click to force-hide',
  'admin.loc.toggle.to_show': 'Hidden — click to force-show',
  'admin.loc.area_ph': 'Add area…',
  'admin.loc.area_label': 'New area in {city}',
  'admin.loc.area_add': 'Add',
  'admin.loc.empty.q': 'No city or area matches “{q}”',
  'admin.loc.empty.div': 'No locations in this division yet — use Add location',
  'admin.loc.toast.hidden': '{city} hidden',
  'admin.loc.toast.shown': '{city} force-shown',
  'admin.loc.toast.area_added': 'Area added to {city}',
  'admin.loc.toast.nothing': 'No staged location changes — add or edit a location first',
  'admin.loc.toast.published':
    '{n, plural, one{# location change published} other{# location changes published}} — audit-logged',
  'admin.loc.toast.staged': 'Staged — click Publish to apply & audit-log',
  'admin.loc.toast.removed': 'Staged change removed',
  'admin.loc.sug.title': 'Merchant area suggestions',
  'admin.loc.sug.count': '{n} waiting',
  'admin.loc.sug.note': 'Adding an area moves the merchant’s business into it.',
  'admin.loc.sug.no_city': 'No city given',
  'admin.loc.sug.meta': '{city} · for {business} · {date}',
  'admin.loc.sug.review': 'Review',
  'admin.loc.sug.city': 'City',
  'admin.loc.sug.link': 'Area',
  'admin.loc.sug.create_new': 'Create a new area',
  'admin.loc.sug.name_en': 'Name (English)',
  'admin.loc.sug.name_bn': 'Name (Bangla)',
  'admin.loc.sug.reject_note': 'Note to the merchant (optional, for Reject)',
  'admin.loc.sug.add_btn': 'Add area',
  'admin.loc.sug.link_btn': 'Use this area',
  'admin.loc.sug.reject_btn': 'Reject',
  'admin.loc.sug.cancel': 'Cancel',
  'admin.loc.sug.added': '“{name}” added — the business moved into it.',
  'admin.loc.sug.rejected': '“{name}” rejected.',
  'admin.loc.err.generic': 'That didn’t go through — please try again.',
  'admin.loc.err.taken': 'A location with that name already exists there.',
  'admin.loc.err.slug': 'Use Latin letters or digits in the name — its URL slug is made from it.',
  'admin.loc.err.publish':
    'Publishing failed — a location with the same name went live meanwhile, or the batch is gone. Remove the change and stage it again.',
  'admin.loc.form.kicker': 'Location registry',
  'admin.loc.form.add_title': 'Add location',
  'admin.loc.form.edit_title': 'Edit location',
  'admin.loc.form.close': 'Close',
  'admin.loc.form.add_sub':
    'New cities stage as a draft and appear in the registry on Publish. Hidden cities stay off the marketplace until a merchant is approved, unless force-shown.',
  'admin.loc.form.edit_sub':
    'Rename the city, add areas or change visibility. Changes stage as a draft and go live on Publish, audit-logged from → to.',
  'admin.loc.form.division': 'Division',
  'admin.loc.form.new_division': '+ New division',
  'admin.loc.form.new_division_name': 'New division name',
  'admin.loc.form.new_division_ph': 'Division name',
  'admin.loc.form.city_pick': 'City to edit',
  'admin.loc.form.city_select': 'Select a city',
  'admin.loc.form.city': 'City name',
  'admin.loc.form.city_ph': 'City name',
  'admin.loc.form.areas': 'Areas — comma-separated',
  'admin.loc.form.areas_ph': 'Area, another area',
  'admin.loc.form.visibility': 'Visibility',
  'admin.loc.form.vis_hidden': 'Hidden',
  'admin.loc.form.vis_hidden_desc': 'Stays off the marketplace until a merchant is approved.',
  'admin.loc.form.vis_live': 'Live',
  'admin.loc.form.vis_live_desc': 'Force-shown on the marketplace right away.',
  'admin.loc.form.stage_add': 'Stage new location',
  'admin.loc.form.stage_edit': 'Stage changes',
  'admin.loc.err.division': 'A division name is required.',
  'admin.loc.err.city': 'A city name is required.',
  'admin.loc.err.exists': '{city} already exists under {division} — use Edit location instead.',
  'admin.loc.err.area_removed': 'Existing areas can’t be removed here — the registry only merges locations.',
  'admin.loc.err.nothing': 'Nothing changed.',

  // Audit log.
  'admin.audit.col.action': 'Action',
  'admin.audit.col.entity': 'Entity',
  'admin.audit.col.when': 'When',

  // Website content & marketing pages (T29, FR-A11).
  'nav.pricing': 'Pricing',
  'nav.blog': 'Blog',
  'common.sending': 'Sending…',

  // For business.
  'fb.cta_primary': 'List your business',
  'fb.cta_secondary': 'See pricing',
  'fb.stat4_value': '0%',

  // Pricing.
  'pricing.title': 'Simple, transparent pricing',
  'pricing.subtitle': 'Start free for 14 days. No card required.',
  'pricing.billing': 'Billing period',
  'pricing.monthly': 'Monthly',
  'pricing.annual': 'Annual',
  'pricing.save': 'Save 17%',
  'pricing.popular': 'Most popular',
  'pricing.per_month': 'mo',
  'pricing.billed_annually': 'billed annually',
  'pricing.unlimited_bookings': 'Unlimited bookings',
  'pricing.bookings_per_month': 'bookings / month',
  'pricing.feature_staff': 'Staff management',
  'pricing.feature_insights': 'Insights & reports',
  'pricing.start_trial': 'Start free trial',
  'pricing.trial_note': 'Every plan starts with a 14-day free trial. Cancel anytime.',
  'pricing.eyebrow': 'Pricing',
  'pricing.headline': 'One flat subscription. Zero commission.',
  'pricing.sub':
    'Customers pay you directly — bKash, Nagad, bank or cash. {brand} never takes a cut of your bookings.',
  'pricing.join_now': 'Join now',
  'pricing.compare_feature': 'Feature',
  'pricing.unlimited': 'Unlimited',
  'pricing.cmp_bookings': 'Monthly bookings',
  'pricing.cmp_staff': 'Staff accounts',
  'pricing.cmp_payments': 'Online payment methods',
  'pricing.cmp_insights': 'Insights & reports',
  'pricing.cmp_support': 'Priority support',
  'pricing.cmp_featured': 'Featured placement',
  'pricing.faq_title': 'Pricing FAQ',
  'pricing.faq1_q': 'Is there really no commission?',
  'pricing.faq1_a':
    'Correct — you pay a flat monthly subscription and keep 100% of every booking. Customers pay you directly.',
  'pricing.faq2_q': 'Can I change plans later?',
  'pricing.faq2_a':
    'Yes — upgrade or downgrade anytime; changes apply from your next billing cycle.',
  'pricing.faq3_q': 'What happens after the free trial?',
  'pricing.faq3_a':
    'Your plan begins only if you choose to continue. We’ll remind you before the 14-day trial ends.',

  // Apps.
  'apps.fine_print': 'No spam — one SMS at launch, that’s it.',

  // Status page (design §5.8).
  'status.title': 'Platform status',
  'status.all_operational': 'All systems operational',
  'status.operational': 'Operational',
  'status.svc_notifications': 'Notifications',

  // Contact page (design §5.7).
  'contact.title': 'Contact us',
  'contact.intro': 'We reply within one business day — Bangla or English, your choice.',
  'contact.name': 'Your name',
  'contact.message': 'How can we help?',
  'contact.send': 'Send message',
  'contact.support_label': 'Support',
  'contact.topic_label': 'Topic',
  'contact.topic.booking': 'Booking help',
  'contact.topic.listing': 'Business listing',
  'contact.topic.payments': 'Payments & proof',
  'contact.topic.other': 'Something else',
  'contact.whatsapp_online': 'WhatsApp online {hours}',
  'contact.wa_label': 'WhatsApp messages only · no calls',
  'contact.wa_hours': '{hours}, every day',
  'contact.wa_cta': 'Message us on WhatsApp',
  'contact.wa_prefill': 'Hi {brand}, I need help with ',
  'contact.copy_email': 'Copy email address',
  'contact.copy_whatsapp': 'Copy WhatsApp number',
  'contact.product_of': 'A product of {company_short}',
  'contact.powered_by': 'Powered by {company}',
  'contact.chat_label': 'Example WhatsApp conversation',
  'contact.chat_title': '{brand} support',
  'contact.chat_example': 'Example chat',
  'contact.chat_user': 'Hi, I want to list my turf.',
  'contact.chat_desk': 'Welcome! Send us your business name and area to get started.',
  'contact.sent_title_named': 'Thanks, {name}. Message sent',
  'contact.topic.offers': 'Offers & deals',
  'contact.topic.account': 'Account & sign-in',

  // Help / FAQ page (design §5.6).
  'help.title': 'Help Center',
  'help.intro': 'Answers to the most common questions from customers and merchants.',

  // Auth — the central sign-in (DR-35): customer face (DC67, C X1) and merchant face
  // (DC83, P A.5). OTP-only (DR-34); no copy says whether an account exists (§4.1).
  'auth.phone_label': 'Phone number',
  'auth.code_label': 'Verification code',
  'auth.choose_title': 'Choose where to continue',
  'auth.choose_subtitle': 'Your number has access to more than one workspace.',
  'auth.send_failed': 'Could not send the code. Please try again.',
  'auth.verify_failed': 'Could not sign you in. Please try again.',
  'auth.cust.tagline': 'Your bookings, in one place',
  'auth.cust.phone_title': 'Enter your phone number',
  'auth.cust.phone_help': 'Use the number you booked with',
  'auth.cust.send_otp': 'Send OTP',
  'auth.cust.cooldown': 'Too many tries — try again in {n} seconds',
  'auth.cust.otp_title': 'Enter the code',
  'auth.cust.otp_sent': 'We sent a 6-digit code to {phone}',
  'auth.cust.wrong': 'Code didn’t match — {n} tries left',
  'auth.cust.locked': 'Too many wrong codes — request a new one',
  'auth.cust.expiry': 'The code works for {n} minutes',
  'auth.cust.resend': 'Resend code',
  'auth.cust.resend_in': 'Resend ({n}s)',
  'auth.cust.resend_max': 'No more codes can be sent',
  'auth.cust.change_number': 'Change number',
  'auth.cust.welcome': 'Welcome, {name}',
  'auth.cust.welcome_anon': 'Welcome',
  'auth.cust.opening': 'Opening your bookings…',
  'auth.cust.privacy': 'Your number is only used to verify bookings and send you updates.',
  'auth.cust.business_q': 'Registering a business?',
  'auth.cust.business_cta': 'Create a merchant account',
  'auth.merchant.tabs': 'Sign in or create an account',
  'auth.merchant.tab_in': 'Sign in',
  'auth.merchant.tab_up': 'New account',
  'auth.merchant.theme': 'Theme',
  'auth.merchant.phone_label': 'Phone number',
  'auth.merchant.continue': 'Continue',
  'auth.merchant.up_help': 'We will text a verification code (OTP) to this number',
  'auth.merchant.send_code': 'Send code',
  'auth.merchant.rate': 'Too many attempts — wait {n}s',
  'auth.merchant.step_of': 'Step {n} of {total}',
  'auth.merchant.otp_title': 'Enter the code',
  'auth.merchant.otp_sent': 'A 6-digit code was sent to {phone}',
  'auth.merchant.wrong': 'Wrong code — {n} attempts left',
  'auth.merchant.no_tries': 'No attempts left — get a new code',
  'auth.merchant.resend': 'Resend',
  'auth.merchant.resend_in': 'Resend in {n}s',
  'auth.merchant.resend_max': 'Max {n} resends — try again later',
  'auth.merchant.back': 'Back',
  'auth.merchant.welcome_back': 'Welcome back, {name}',
  'auth.merchant.welcome_new': 'Welcome, {name}',
  'auth.merchant.welcome_anon': 'Welcome',
  'auth.merchant.redirecting': 'Taking you to the dashboard…',
  'auth.merchant.terms_line': 'Signing up means you agree to the',
  'auth.merchant.terms': 'Terms',
  'auth.merchant.privacy': 'Privacy',

  // Footer.
  'footer.about': 'About',
  'footer.blog': 'Blog',
  'footer.contact': 'Contact',
  'footer.help': 'Help Center',
  'footer.legal': 'Legal',
  'footer.resources': 'Resources',
  'footer.terms': 'Terms of service',
  'footer.privacy': 'Privacy policy',
  'footer.payment_disclaimer': 'does not process, hold, or guarantee direct payments.',

  // ---- Design-fidelity rework (screens 1-9, design c0996bce verbatim copy) ----
  'admin.badge.bookings': 'bookings awaiting payment-proof verification',
  'admin.badge.disputes': 'open disputes',
  'admin.tbl.of': 'of',
  'admin.tbl.prev': 'Prev',
  'admin.tbl.next': 'Next',
  'admin.disputes.kind.paid_but_rejected': 'Paid but rejected',
  'admin.disputes.kind.complaint': 'Complaint',
  'admin.disputes.kind.refund_claim': 'Refund claim',
  'admin.disputes.kind.other': 'Other',
  'admin.disputes.sla_breached': 'SLA breached',
  'admin.disputes.notes': 'Internal notes',
  'admin.disputes.add_note': 'Add note',
  'admin.disputes.detail_title': 'Dispute',
  'admin.disputes.add_note_ph': 'Add internal note…',
  'admin.disputes.note_you': 'You',
  'admin.disputes.trail.screenshot': 'screenshot attached',
  'admin.disputes.ev.rescheduled': 'Rescheduled',
  // DC96 Disputes (design 4c · disputes)
  'admin.dsp.sub': 'Dispute queue with SLA timers. Resolution writes to the booking and the audit log.',
  'admin.dsp.stat.open': 'Open',
  'admin.dsp.stat.overdue': 'Overdue',
  'admin.dsp.stat.resolved30': 'Resolved (30 d)',
  'admin.dsp.new': '＋ New dispute',
  'admin.dsp.tab.open': 'Open ({n})',
  'admin.dsp.tab.resolved': 'Resolved',
  'admin.dsp.search_ph': 'Search booking code / TRX / customer / merchant…',
  'admin.dsp.filter.types': 'All types',
  'admin.dsp.filter.cities': 'All cities',
  'admin.dsp.filter.merchants': 'All merchants',
  'admin.dsp.filter.resolutions': 'All resolutions',
  'admin.dsp.inv.title': 'Bookings matching your search — no dispute yet',
  'admin.dsp.inv.open': 'Open booking →',
  'admin.dsp.list.open': 'Work queue',
  'admin.dsp.list.record': 'The record',
  'admin.dsp.list.count': '{n} of {total}',
  'admin.dsp.list.open_sub': 'Overdue first, then shortest SLA. Open a card for the full proof trail, internal notes and typed resolution.',
  'admin.dsp.list.record_sub': 'Read-only — the full trail and notes are preserved. Reopen is Super Admin only.',
  'admin.dsp.empty.open': 'Queue clear ✓ — no open disputes match',
  'admin.dsp.empty.record': 'No resolved disputes match',
  'admin.dsp.vs': '{merchant} vs {customer}',
  'admin.dsp.manual': 'MANUAL',
  'admin.dsp.prior': '↺ {n} prior — {name}',
  'admin.dsp.prior_title': 'Open party record',
  'admin.dsp.resolved_badge': '✓ Resolved: {label}',
  'admin.dsp.escalated_badge': '↑ Escalated',
  'admin.dsp.resolved_at': 'Resolved · {when}',
  'admin.dsp.refund_suffix': '· ● Refund: {status}',
  'admin.dsp.refund.pending': 'Pending',
  'admin.dsp.refund.paid': 'Paid',
  'admin.dsp.refund.declined': 'Declined',
  'admin.dsp.sla.left': '{h} h left',
  'admin.dsp.sla.overdue': 'Overdue {h} h',
  'admin.dsp.sheet.opened': 'opened {when}',
  'admin.dsp.sheet.open_booking': '{code} — open booking →',
  'admin.dsp.sheet.close': 'Close',
  'admin.dsp.sheet.refund': 'Refund',
  'admin.dsp.sheet.refund_pending': '{ref} · Pending Finance approval',
  'admin.dsp.sheet.refund_paid': '{ref} · Paid — {when}',
  'admin.dsp.sheet.refund_paid_via': '{ref} · Paid — {when} via {method}',
  'admin.dsp.sheet.refund_declined': '{ref} · Declined — {reason}',
  'admin.dsp.sheet.refund_declined_default': 'payout not approved',
  'admin.dsp.sheet.refund_title': 'Open the refund queue',
  'admin.dsp.sheet.refund_note': 'Payout decisions are made in Payments by Finance.',
  'admin.dsp.sheet.reopened_title': 'Refund declined — needs re-resolution',
  'admin.dsp.sheet.reopened_body': 'Finance declined {ref} — {reason}. Pick a new resolution for this case.',
  'admin.dsp.sheet.statement': 'Statement — {party}',
  'admin.dsp.party.customer': 'customer',
  'admin.dsp.party.merchant': 'merchant',
  'admin.dsp.party.admin': 'platform',
  'admin.dsp.trail': 'Proof trail',
  'admin.dsp.trail.opened': 'Dispute opened — {ref}',
  'admin.dsp.trail.opened_by': '{party} · {kind}',
  'admin.dsp.trail.escalated': 'Escalated',
  'admin.dsp.trail.reopened': 'Reopened',
  'admin.dsp.trail.resolved': 'Resolved — {label}',
  'admin.dsp.trail.refund_paid': 'Refund {ref} approved & paid',
  'admin.dsp.trail.refund_declined': 'Refund {ref} declined by Finance — {reason}',
  'admin.dsp.trail.finance': 'Finance',
  'admin.dsp.actor.customer': 'Customer',
  'admin.dsp.actor.merchant': 'Merchant',
  'admin.dsp.actor.staff': 'Merchant staff',
  'admin.dsp.actor.admin': 'Admin',
  'admin.dsp.actor.system': 'System',
  'admin.dsp.ev.created': 'Booking created',
  'admin.dsp.ev.proof_submitted': 'Payment proof uploaded',
  'admin.dsp.ev.reuploaded_proof': 'New payment proof uploaded',
  'admin.dsp.ev.verified': 'Proof accepted',
  'admin.dsp.ev.rejected': 'Proof rejected',
  'admin.dsp.ev.confirmed': 'Confirmed',
  'admin.dsp.ev.admin_override_confirmed': 'Admin override — Confirmed',
  'admin.dsp.ev.admin_override_refunded': 'Admin override — Refunded',
  'admin.dsp.ev.cancelled': 'Cancelled',
  'admin.dsp.ev.rescheduled': 'Rescheduled',
  'admin.dsp.ev.checked_in': 'Checked in',
  'admin.dsp.ev.completed': 'Completed',
  'admin.dsp.ev.no_show': 'Marked no-show',
  'admin.dsp.ev.refunded': 'Refunded',
  'admin.dsp.ev.expired': 'Expired',
  'admin.dsp.ev_customer': 'Customer\'s evidence',
  'admin.dsp.ev_merchant': 'Merchant\'s response',
  'admin.dsp.ev_admin': 'Platform evidence',
  'admin.dsp.ev.none_customer': 'No evidence attached',
  'admin.dsp.ev.none_merchant': 'No response yet',
  'admin.dsp.ev.shot': 'payment screenshot',
  'admin.dsp.ev.file': 'attached file',
  'admin.dsp.ev.file_error': 'The file could not be opened.',
  'admin.dsp.notes': 'Internal notes',
  'admin.dsp.notes.private': 'never shown to the parties',
  'admin.dsp.notes.empty': 'No notes yet.',
  'admin.dsp.notes.ph': 'Add an internal note…',
  'admin.dsp.notes.add': 'Add note',
  'admin.dsp.notes.first': 'Write the note first',
  'admin.dsp.notes.added': 'Note added — internal only, audit-logged',
  'admin.dsp.resolve': 'Resolve',
  'admin.dsp.res.refund': 'Refund customer',
  'admin.dsp.res.uphold': 'Uphold merchant',
  'admin.dsp.res.penalty': 'Penalty on merchant',
  'admin.dsp.res.warn': 'Warning to customer',
  'admin.dsp.res.block': 'Block customer',
  'admin.dsp.res.override': 'Confirm booking',
  'admin.dsp.res.dismiss': 'Dismiss',
  'admin.dsp.res.escalate': 'Escalate',
  'admin.dsp.hint.refund': 'Queues a {amount} refund in the Payments refund queue — Finance approves. One refund pipeline, no duplicates.',
  'admin.dsp.hint.uphold': 'Closes in the merchant\'s favour; the customer is notified with your note.',
  'admin.dsp.hint.penalty': 'Records a penalty against the merchant account; the merchant is notified.',
  'admin.dsp.hint.warn': 'Issues a formal warning to the customer account.',
  'admin.dsp.hint.block': 'For repeat abuse — blocks the customer account (visible in Customers) and closes the dispute.',
  'admin.dsp.hint.override': 'Moves the booking to Confirmed and closes the dispute.',
  'admin.dsp.hint.dismiss': 'Closes the case with no action against either party.',
  'admin.dsp.hint.escalate': 'Keeps the dispute open and records the escalation.',
  'admin.dsp.res.method': 'Refund via',
  'admin.dsp.res.note_ph': 'Resolution note — required, attaches to the resolution and the audit log',
  'admin.dsp.res.footer': 'The resolution writes to the booking and the audit log; the relevant party is notified. Refunds route to the Payments refund queue — Finance approves.',
  'admin.dsp.res.confirm': 'Confirm resolution',
  'admin.dsp.res.confirm_x': 'Confirm — {label}',
  'admin.dsp.res.pick_first': 'Pick a resolution first',
  'admin.dsp.res.note_required': 'A resolution note is required — it attaches to the resolution',
  'admin.dsp.res.done': 'Resolved — {ref} · {label}',
  'admin.dsp.res.escalated': '{ref} escalated — audit-logged',
  'admin.dsp.reopen': 'Reopen',
  'admin.dsp.reopen.title': 'Reopen {ref}?',
  'admin.dsp.reopen.body': 'The dispute returns to the open queue. The prior resolution stays in the audit trail. Super Admin only — a reason is required.',
  'admin.dsp.reopen.action': 'Reopen dispute',
  'admin.dsp.reopen.reason': 'Reason — written to the audit log',
  'admin.dsp.reopen.reason_ph': 'Add a short reason…',
  'admin.dsp.reopen.reason_required': 'A reason is required to reopen',
  'admin.dsp.reopen.done': '{ref} reopened — audit-logged',
  'admin.dsp.new.title': 'New dispute — manual',
  'admin.dsp.new.sub': 'For disputes arriving by phone, WhatsApp or walk-in. Creation is audit-logged on behalf of the reporting party.',
  'admin.dsp.new.booking': 'Booking',
  'admin.dsp.new.booking_ph': 'Search by code / customer…',
  'admin.dsp.new.ctx': '{merchant} vs {customer} · {amount} · {service} · status: {status}',
  'admin.dsp.new.type': 'Dispute type',
  'admin.dsp.new.sla': 'SLA',
  'admin.dsp.new.sla_value': '{h} h',
  'admin.dsp.new.sla_auto': '· auto by type',
  'admin.dsp.new.raised_by': 'Raised by',
  'admin.dsp.new.raised.customer': 'Customer',
  'admin.dsp.new.raised.merchant': 'Merchant',
  'admin.dsp.new.raised.admin': 'Platform',
  'admin.dsp.new.via': 'Came in by',
  'admin.dsp.new.via.phone': 'Phone',
  'admin.dsp.new.via.whatsapp': 'WhatsApp',
  'admin.dsp.new.via.walk_in': 'Walk-in',
  'admin.dsp.new.statement': 'Statement — as reported',
  'admin.dsp.new.statement_ph': 'The complaint, in the reporter\'s words…',
  'admin.dsp.new.evidence': 'Evidence (optional)',
  'admin.dsp.new.trx_ph': 'TRX id — e.g. BKH1A2B3C4',
  'admin.dsp.new.trx_note': 'TRX {trx} — reported at manual creation',
  'admin.dsp.new.attach': '＋ Attach screenshot',
  'admin.dsp.new.attached': '✓ Screenshot attached',
  'admin.dsp.new.uploading': 'Uploading…',
  'admin.dsp.new.create': 'Create dispute',
  'admin.dsp.new.pick_booking': 'Pick the booking first — it pulls merchant, customer and amount',
  'admin.dsp.new.statement_required': 'The statement is required — as reported by the party',
  'admin.dsp.new.created': '{ref} created on behalf of the {party} — audit-logged',
  'admin.dsp.method.bkash': 'bKash',
  'admin.dsp.method.nagad': 'Nagad',
  'admin.dsp.method.rocket': 'Rocket',
  'admin.dsp.method.bank': 'Bank',
  'admin.reviews.hide_short': 'Hide\u2026',
  'admin.reviews.restore': 'Restore',
  'admin.businesses.search_ph': 'Search name, phone, city\u2026',
  'admin.businesses.col.type': 'Type',
  'admin.businesses.slug': 'Slug',
  'admin.businesses.slug_title': 'Booking slug',
  'admin.businesses.slug_current': 'Current',
  'admin.businesses.slug_new': 'New slug',
  'admin.businesses.slug_save': 'Save slug',
  // Maps (admin Settings → Maps: the listing-map provider waterfall; screens/maps).
  'admin.nav.maps': 'Maps',
  'admin.maps.title': 'Maps',
  'admin.maps.sub': 'Listing maps: Mapbox → Google → OpenStreetMap within the free tiers. Saves apply at once — no redeploy.',
  'admin.maps.save': 'Save',
  'admin.maps.save_n': 'Save {n} section(s)',
  'admin.maps.saved': 'Maps settings saved — live now, audit-logged',
  'admin.maps.reset_section': 'Reset section to defaults',
  'admin.maps.read_only': 'Read-only: your role can view the usage dashboard and settings. Only a super admin can edit them or see credentials.',
  'admin.maps.errors_n': '{n} field(s) need attention before saving.',
  'admin.maps.yes': 'Yes',
  'admin.maps.no': 'No',
  'admin.maps.tab.general': 'General',
  'admin.maps.tab.credentials': 'Credentials',
  'admin.maps.tab.limits': 'Limits & usage',
  'admin.maps.tab.view': 'Zoom & view',
  'admin.maps.tab.cluster': 'Clustering',
  'admin.maps.tab.marker': 'Markers & popup',
  'admin.maps.tab.placement': 'Placement',
  'admin.maps.tab.performance': 'Performance & diagnostics',
  'admin.maps.provider.mapbox': 'Mapbox',
  'admin.maps.provider.google': 'Google Maps',
  'admin.maps.provider.osm': 'OpenStreetMap',
  'admin.maps.order.up': 'Move up',
  'admin.maps.order.down': 'Move down',
  'admin.maps.order.enabled': 'enabled',
  // General
  'admin.maps.f.general.enabled': 'Maps enabled',
  'admin.maps.h.general.enabled': 'Master switch for every map on the public site.',
  'admin.maps.f.general.mode': 'Provider mode',
  'admin.maps.h.general.mode': 'Auto walks the order below within the limits; a forced provider ignores limits and order (the MAP_FORCE_PROVIDER environment variable still overrides everything).',
  'admin.maps.f.general.order': 'Provider order',
  'admin.maps.h.general.order': 'The waterfall, top first. A disabled provider is skipped.',
  'admin.maps.f.general.when_exhausted': 'When every enabled provider is exhausted',
  'admin.maps.h.general.when_exhausted': 'OSM has no free-tier cap, so it is the safe default.',
  'admin.maps.opt.mode.auto': 'Auto (waterfall)',
  'admin.maps.opt.mode.mapbox': 'Force Mapbox',
  'admin.maps.opt.mode.google': 'Force Google',
  'admin.maps.opt.mode.osm': 'Force OpenStreetMap',
  'admin.maps.opt.when_exhausted.osm': 'Fall back to OpenStreetMap',
  'admin.maps.opt.when_exhausted.hide': 'Hide the map',
  'admin.maps.opt.when_exhausted.note': 'Show a "map unavailable" note',
  // Credentials
  'admin.maps.cred.intro': 'Keys are encrypted at rest and shown masked after saving. Leave a masked value as it is to keep the stored key; clear it to remove the key. The environment variables MAPBOX_PUBLIC_TOKEN / GOOGLE_MAPS_API_KEY act as fallbacks while a field is empty. Staging and production keep separate settings.',
  'admin.maps.cred.stored': 'Key stored',
  'admin.maps.cred.env': 'Using environment fallback',
  'admin.maps.cred.none': 'No key',
  'admin.maps.cred.test': 'Test connection',
  'admin.maps.cred.env_force': 'MAP_FORCE_PROVIDER={provider} is set in this environment — it overrides every setting here until removed.',
  'admin.maps.f.credentials.mapbox_token': 'Mapbox public access token',
  'admin.maps.h.credentials.mapbox_token': 'Starts with pk. — a secret token (sk.) is refused. Restrict it by URL in the Mapbox dashboard.',
  'admin.maps.f.credentials.mapbox_style': 'Mapbox style URL',
  'admin.maps.h.credentials.mapbox_style': 'mapbox://styles/… or an https style JSON URL.',
  'admin.maps.f.credentials.google_key': 'Google Maps API key (browser key)',
  'admin.maps.h.credentials.google_key': 'Restrict it by HTTP referrer and to the Maps JavaScript API only. Only map loads are used — never Places, Geocoding or Directions.',
  'admin.maps.f.credentials.google_map_id': 'Google Map ID (optional)',
  'admin.maps.h.credentials.google_map_id': 'For a cloud-styled map; leave empty for the default style.',
  'admin.maps.f.credentials.osm_tile_url': 'Tile URL template',
  'admin.maps.h.credentials.osm_tile_url': 'Must contain {z}, {x} and {y}. Heavy production traffic should use a tile provider rather than the public OSM servers.',
  'admin.maps.f.credentials.osm_attribution': 'Attribution text',
  'admin.maps.f.credentials.osm_max_zoom': 'Max native zoom',
  'admin.maps.h.credentials.osm_max_zoom': 'The deepest zoom the tile server offers (19 for public OSM).',
  // Limits
  'admin.maps.f.limits.mapbox_limit': 'Mapbox monthly free limit',
  'admin.maps.h.limits.mapbox_limit': 'Map loads per month in the free tier (50,000 at the time of writing).',
  'admin.maps.f.limits.google_limit': 'Google monthly free limit',
  'admin.maps.h.limits.google_limit': 'Dynamic map loads per month in the free tier (10,000). Also set a quota cap in Google Cloud.',
  'admin.maps.f.limits.safety_margin_pct': 'Safety margin %',
  'admin.maps.h.limits.safety_margin_pct': 'The switch to the next provider happens this much below the limit.',
  'admin.maps.f.limits.mapbox_hard_stop': 'Mapbox: hard stop at the limit',
  'admin.maps.h.limits.mapbox_hard_stop': 'On: never exceed the free tier — skip to the next provider. Off: paid overage allowed.',
  'admin.maps.f.limits.google_hard_stop': 'Google: hard stop at the limit',
  'admin.maps.h.limits.google_hard_stop': 'On: never exceed the free tier — skip to the next provider. Off: paid overage allowed.',
  'admin.maps.f.limits.reset_day': 'Counter reset day of month',
  'admin.maps.h.limits.reset_day': '1–28. A new period starts a fresh counter.',
  'admin.maps.f.limits.timezone': 'Counter timezone',
  'admin.maps.h.limits.timezone': 'IANA zone name for the period boundary, e.g. UTC.',
  'admin.maps.f.limits.alert_pcts': 'Alert at % of limit',
  'admin.maps.h.limits.alert_pcts': 'Comma-separated percentages. Each alert is sent once per month per provider as an admin notice (and e-mail when set).',
  'admin.maps.f.limits.alert_email': 'Alert e-mail (optional)',
  'admin.maps.h.limits.alert_email': 'Leave empty for admin notices only.',
  'admin.maps.limits.switch_at': 'switches at {n}',
  // Usage dashboard
  'admin.maps.usage.period': 'This period: {period}',
  'admin.maps.usage.active': 'Active provider',
  'admin.maps.usage.none': 'none (map hidden)',
  'admin.maps.usage.export': 'Export usage CSV',
  'admin.maps.usage.disabled': 'disabled',
  'admin.maps.usage.exhausted': 'exhausted',
  'admin.maps.usage.of_limit': '{pct}% of {limit}',
  'admin.maps.usage.hard_stop': 'hard stop',
  'admin.maps.usage.overage': 'overage allowed',
  'admin.maps.usage.uncapped': 'No free-tier cap — counted for the dashboard only.',
  'admin.maps.usage.reset': 'Reset counter',
  'admin.maps.usage.set': 'Set counter manually',
  'admin.maps.usage.reset_body': 'Sets this period’s counter for the provider back to zero. Audit-logged.',
  'admin.maps.usage.set_body': 'Set the counter to the figure the provider’s own dashboard shows, to correct drift. Audit-logged.',
  'admin.maps.usage.set_value': 'New counter value',
  'admin.maps.usage.reason': 'Reason',
  'admin.maps.usage.confirm': 'Confirm',
  'admin.maps.usage.adjusted': 'Counter updated — audit-logged',
  'admin.maps.usage.alerts_sent': 'Alerts sent this period',
  'admin.maps.usage.history': 'Last 6 periods',
  'admin.maps.usage.col_period': 'Period',
  // View
  'admin.maps.f.view.center_lat': 'Default centre latitude',
  'admin.maps.f.view.center_lng': 'Default centre longitude',
  'admin.maps.f.view.default_zoom': 'Default zoom (listings page)',
  'admin.maps.f.view.min_zoom': 'Min zoom',
  'admin.maps.f.view.max_zoom': 'Max zoom',
  'admin.maps.f.view.single_zoom': 'Single listing page zoom',
  'admin.maps.h.view.single_zoom': 'Zoom around the one pin on a business page.',
  'admin.maps.f.view.zoom_controls': 'Show zoom controls',
  'admin.maps.f.view.scroll_wheel': 'Scroll-wheel zoom',
  'admin.maps.h.view.scroll_wheel': 'On touch screens one-finger scrolling is never captured — pinch (two fingers) zooms.',
  'admin.maps.f.view.restrict_bounds': 'Restrict panning to bounds',
  'admin.maps.h.view.restrict_bounds': 'Keeps the map inside the box below.',
  'admin.maps.f.view.bounds': 'Bounds box',
  'admin.maps.h.view.bounds': 'Decimal degrees; north above south, east above west.',
  'admin.maps.f.view.fit_to_markers': 'Fit map to markers on load',
  'admin.maps.f.view.cluster_click_zoom': 'Zoom into a cluster on click',
  'admin.maps.f.view.double_click_zoom': 'Double-click zoom',
  'admin.maps.opt.scroll_wheel.disabled': 'Disabled',
  'admin.maps.opt.scroll_wheel.ctrl': 'Ctrl + scroll (⌘ on Mac)',
  'admin.maps.opt.scroll_wheel.always': 'Always enabled',
  'admin.maps.bounds.north': 'North',
  'admin.maps.bounds.south': 'South',
  'admin.maps.bounds.east': 'East',
  'admin.maps.bounds.west': 'West',
  // Cluster
  'admin.maps.f.cluster.enabled': 'Clustering enabled',
  'admin.maps.f.cluster.radius': 'Cluster radius (px)',
  'admin.maps.h.cluster.radius': 'Mapbox clusterRadius · Google SuperCluster radius · Leaflet maxClusterRadius.',
  'admin.maps.f.cluster.max_zoom': 'Max zoom for clustering',
  'admin.maps.h.cluster.max_zoom': 'Above this zoom pins are shown individually.',
  'admin.maps.f.cluster.min_points': 'Minimum points to form a cluster',
  'admin.maps.h.cluster.min_points': 'Mapbox clusterMinPoints · Google SuperCluster minPoints.',
  'admin.maps.f.cluster.disable_below': 'Disable clustering below N markers',
  'admin.maps.h.cluster.disable_below': 'With fewer pins than this on the page, every pin is shown.',
  'admin.maps.f.cluster.colors': 'Cluster colours by size',
  'admin.maps.h.cluster.colors': 'Small (<10), medium (<50), large.',
  'admin.maps.f.cluster.show_count': 'Show the count on clusters',
  'admin.maps.f.cluster.spiderfy': 'Spread overlapping markers at max zoom',
  'admin.maps.h.cluster.spiderfy': 'Leaflet spiderfies; on Google overlapping pins are spread on a small ring.',
  'admin.maps.h.unsupported': 'No equivalent on: {providers}.',
  'admin.maps.tier.small': 'Small',
  'admin.maps.tier.medium': 'Medium',
  'admin.maps.tier.large': 'Large',
  // Marker
  'admin.maps.f.marker.color': 'Marker colour',
  'admin.maps.h.marker.color': 'Hex colour; the default is the brand accent.',
  'admin.maps.f.marker.icon_url': 'Custom marker icon URL (optional)',
  'admin.maps.h.marker.icon_url': 'An https image (SVG/PNG, about 28×28). Empty = the default pin.',
  'admin.maps.f.marker.popup': 'Popup fields',
  'admin.maps.f.marker.hover_highlight': 'Hover-highlight between card and marker',
  'admin.maps.h.marker.hover_highlight': 'Hovering a result card enlarges its marker.',
  'admin.maps.popup.image': 'Image',
  'admin.maps.popup.title': 'Title',
  'admin.maps.popup.price': 'Price',
  'admin.maps.popup.rating': 'Rating',
  'admin.maps.popup.category': 'Category',
  'admin.maps.popup.view': '“View” button',
  // Placement
  'admin.maps.f.placement.listings_enabled': 'Map on the All listings page',
  'admin.maps.f.placement.single_enabled': 'Map on the Single listing page',
  'admin.maps.f.placement.listings_layout': 'All listings layout',
  'admin.maps.h.placement.listings_layout': 'Split = list beside the map; Map on top; Toggle = a “Show map” button only.',
  'admin.maps.f.placement.mobile_default': 'Mobile default view',
  'admin.maps.f.placement.mobile_toggle': 'Mobile Map / List toggle',
  'admin.maps.f.placement.height_desktop': 'Map height (desktop)',
  'admin.maps.h.placement.height_desktop': 'px or vh, e.g. 560px or 70vh.',
  'admin.maps.f.placement.height_mobile': 'Map height (mobile)',
  'admin.maps.h.placement.height_mobile': 'px or vh. The full-screen mobile map ignores it.',
  'admin.maps.f.placement.single_position': 'Single listing: map position',
  'admin.maps.f.placement.directions_link': 'Show “Get directions” link',
  'admin.maps.h.placement.directions_link': 'A plain Google Maps URL in a new tab — no API usage.',
  'admin.maps.opt.listings_layout.split': 'Split (list + map)',
  'admin.maps.opt.listings_layout.top': 'Map on top',
  'admin.maps.opt.listings_layout.toggle': 'Map toggle only',
  'admin.maps.opt.mobile_default.list': 'List',
  'admin.maps.opt.mobile_default.map': 'Map',
  'admin.maps.opt.single_position.below_gallery': 'Below the gallery',
  'admin.maps.opt.single_position.sidebar': 'Sidebar',
  'admin.maps.opt.single_position.below_description': 'Below the description',
  // Performance
  'admin.maps.f.performance.lazy_load': 'Lazy-load when the map enters the viewport',
  'admin.maps.h.performance.lazy_load': 'The SDK is fetched only when the map scrolls into view.',
  'admin.maps.f.performance.exclude_bots': 'Exclude bots from the counter',
  'admin.maps.h.performance.exclude_bots': 'Crawlers and prefetch requests get OpenStreetMap, uncounted.',
  'admin.maps.f.performance.decision_cache_minutes': 'Provider decision cache (minutes)',
  'admin.maps.h.performance.decision_cache_minutes': 'How long a visitor keeps the chosen provider in sessionStorage; 0 = for the visit.',
  'admin.maps.f.performance.consent_required': 'Load the map only after cookie consent',
  'admin.maps.h.performance.consent_required': 'Waits for a cookie_consent cookie before loading any provider.',
  // Diagnostics
  'admin.maps.diag.effective': 'Effective configuration',
  'admin.maps.diag.force_env': 'Environment override',
  'admin.maps.diag.version': 'Settings version',
  'admin.maps.diag.counts': 'Loads this period',
  'admin.maps.diag.events': 'Last 20 fallback / error events',
  'admin.maps.diag.no_events': 'No events reported yet.',
  'admin.maps.diag.col_time': 'Time',
  'admin.maps.diag.col_provider': 'Provider',
  'admin.maps.diag.col_kind': 'Kind',
  'admin.maps.diag.col_page': 'Page',
  'admin.maps.diag.col_next': 'Next',
  'admin.maps.diag.col_message': 'Message',
  'admin.maps.diag.audit': 'Maps audit log',
  'admin.maps.diag.no_audit': 'No Maps changes recorded yet.',
  // Preview + test
  'admin.maps.preview.title': 'Live preview (OpenStreetMap, sample markers)',
  'admin.maps.preview.note': 'Reflects the centre, zoom, clustering and marker colour of the unsaved draft. Uses OpenStreetMap, so it spends no paid quota.',
  'admin.maps.test.title': 'Test connection — {provider}',
  'admin.maps.test.running': 'Loading a test map…',
  'admin.maps.test.ok': 'Success — the map loaded in {ms} ms.',
  'admin.maps.test.fail': 'Failed: {error}',
  'admin.maps.test.close': 'Close',
  // Validation
  'admin.maps.err.order': 'Each provider must appear exactly once.',
  'admin.maps.err.secret_token': 'That is a Mapbox SECRET token (sk.). Use the public token (pk.).',
  'admin.maps.err.public_token': 'A Mapbox public token starts with pk.',
  'admin.maps.err.tile': 'The tile URL must start with http(s) and contain {z}, {x} and {y}.',
  'admin.maps.err.style': 'Use a mapbox://styles/… URL or an https URL.',
  'admin.maps.err.range': 'Out of range.',
  'admin.maps.err.min0': 'Must be 0 or more.',
  'admin.maps.err.pct': 'Must be between 0 and 100.',
  'admin.maps.err.reset_day': 'Must be a day from 1 to 28.',
  'admin.maps.err.zone': 'Unknown timezone name.',
  'admin.maps.err.email': 'Enter a valid e-mail address.',
  'admin.maps.err.lat': 'Latitude must be between -90 and 90.',
  'admin.maps.err.lng': 'Longitude must be between -180 and 180.',
  'admin.maps.err.zoom_range': 'Zoom must be between 0 and 22.',
  'admin.maps.err.min_default': 'Min zoom must not exceed the default zoom.',
  'admin.maps.err.default_max': 'Default zoom must not exceed the max zoom.',
  'admin.maps.err.single_zoom': 'Single listing zoom must sit between min and max zoom.',
  'admin.maps.err.bounds_ns': 'North must be above south, within ±90.',
  'admin.maps.err.bounds_we': 'East must be above west, within ±180.',
  'admin.maps.err.min_points': 'At least 2.',
  'admin.maps.err.cluster_zoom': 'Must be between 0 and the max zoom.',
  'admin.maps.err.hex': 'Use a hex colour like #0E7490.',
  'admin.maps.err.https': 'Must be an https URL.',
  'admin.maps.err.css_length': 'Use px or vh, e.g. 560px or 70vh.',
  // Roles & security (DC109).
  'admin.sec.title': 'Security',
  'admin.sec.sub': 'Team accounts, role permissions, feature flags and the immutable audit log.',
  'admin.sec.stat.team': 'Team',
  'admin.sec.stat.all_active': 'All accounts active',
  'admin.sec.stat.deactivated_chip': '{n} deactivated',
  'admin.sec.stat.supers': 'Super admins',
  'admin.sec.stat.owner': 'Owner · {name}',
  'admin.sec.stat.mods': 'Moderators',
  'admin.sec.stat.deactivated': 'Deactivated',
  'admin.sec.stat.flags_on': 'Flags on',
  'admin.sec.stat.entries': 'Log entries',
  'admin.sec.tab.team': 'Team',
  'admin.sec.tab.perm': 'Permissions',
  'admin.sec.tab.flags': 'Flags',
  'admin.sec.tab.audit': 'Audit',
  'admin.sec.tab.settings': 'Settings',
  'admin.sec.tab.count': '{label} · {n}',
  'admin.sec.tab.on': '{n} on',
  'admin.sec.team.supers': 'Super admins',
  'admin.sec.team.supers_sub': 'Full platform control, including this screen. Peers by design: super admins cannot deactivate or downgrade each other, and each signs in with their own phone and a one-time code. One seat is the Owner — immune to deactivation and downgrade, transferable only by its holder, and the only seat that can deactivate another super admin.',
  'admin.sec.team.mods': 'Moderators',
  'admin.sec.team.mods_sub': 'Scoped by role — what they see and do is fixed per role and shown in the Permissions tab. Role changes and deactivation apply here only; deactivation needs a reason and is audit-logged. Deactivation keeps all history; nothing is ever hard-deleted.',
  'admin.sec.team.add_super': '＋ Add super admin',
  'admin.sec.team.add_mod': '＋ Add moderator',
  'admin.sec.team.you': 'you',
  'admin.sec.team.owner': 'OWNER',
  'admin.sec.team.owner_title': 'The Owner seat cannot be deactivated or downgraded',
  'admin.sec.team.active': 'Active',
  'admin.sec.team.deactivated': 'Deactivated',
  'admin.sec.team.actions': 'Actions for {name}',
  'admin.sec.team.foot': 'Members sign in with their phone and a one-time code. Deactivation and an ownership handover need a reason and write an audit entry; accounts are never deleted, so audit history survives.',
  'admin.sec.team.m.view': 'View details',
  'admin.sec.team.m.edit': 'Edit role',
  'admin.sec.team.m.transfer': 'Transfer ownership…',
  'admin.sec.team.m.deactivate': 'Deactivate…',
  'admin.sec.team.m.deactivate_sa': 'Deactivate · Owner only…',
  'admin.sec.team.m.reactivate': 'Reactivate',
  'admin.sec.team.d.phone': 'Phone',
  'admin.sec.team.d.status': 'Status',
  'admin.sec.team.d.role': 'Role',
  'admin.sec.team.d.added': 'Added',
  'admin.sec.team.d.last': 'Last action',
  'admin.sec.team.d.recent': 'Recent actions',
  'admin.sec.team.d.you': 'this is you',
  'admin.sec.team.d.owner': 'Owner seat — cannot be deactivated or downgraded',
  'admin.sec.team.d.readonly': 'read-only detail view.',
  'admin.sec.team.deact.title': 'Deactivate {name}?',
  'admin.sec.team.deact.body': 'Sign-in is blocked immediately. Their history and audit entries are kept, and you can reactivate later. No hard delete.',
  'admin.sec.team.deact.title_sa': 'Deactivate super admin {name}?',
  'admin.sec.team.deact.body_sa': 'Owner-only action. Sign-in is blocked immediately; history and audit entries are kept and the seat can be reactivated.',
  'admin.sec.team.deact.action': 'Deactivate',
  'admin.sec.team.deact.done': '{name} deactivated — history kept',
  'admin.sec.team.react.done': '{name} reactivated',
  'admin.sec.team.tr.pick_title': 'Transfer ownership',
  'admin.sec.team.tr.pick_body': 'Exactly one super admin holds the Owner seat, and it moves in a single action. Only an active super admin can receive it.',
  'admin.sec.team.tr.eligible': 'Eligible · active super admin',
  'admin.sec.team.tr.to': 'Transfer to {name}',
  'admin.sec.team.tr.title': 'Hand the Owner seat to {name}?',
  'admin.sec.team.tr.body': 'You lose the Owner seat — from then on {name} is the only seat that cannot be deactivated or downgraded, and only they can hand it over again.',
  'admin.sec.team.tr.confirm': 'Type {word} to confirm',
  'admin.sec.team.tr.confirm_err': 'Type {word} exactly to confirm',
  'admin.sec.team.tr.action': 'Transfer ownership',
  'admin.sec.team.tr.done': 'Owner seat transferred to {name}',
  'admin.sec.team.tr.none': 'No other active super admin can receive the Owner seat',
  'admin.sec.form.add_super': 'Add super admin',
  'admin.sec.form.add_mod': 'Add moderator',
  'admin.sec.form.edit': 'Edit — {name}',
  'admin.sec.form.sub_add': 'Creates the account at once. They sign in with this phone and a one-time code.',
  'admin.sec.form.sub_edit': 'Role only — the name and the phone, which is the sign-in identity, do not change here.',
  'admin.sec.form.warn_super': '⚠ Super admins have full platform control — including this screen.',
  'admin.sec.form.name': 'Full name — required',
  'admin.sec.form.name_ph': 'e.g. Nusrat Jahan',
  'admin.sec.form.phone': 'Phone — required, mobile number',
  'admin.sec.form.role': 'Role',
  'admin.sec.form.err_name': 'Full name is required',
  'admin.sec.form.err_dupe': 'That phone already has a team account',
  'admin.sec.form.foot_add': 'Duplicate phones are rejected — one account per number.',
  'admin.sec.form.foot_edit': 'Changes are audit-logged from → to.',
  'admin.sec.form.add': 'Add member',
  'admin.sec.form.save': 'Save changes',
  'admin.sec.form.confirm_super_title': 'Add a super admin?',
  'admin.sec.form.confirm_super_body': 'Super admins have full platform control — including this screen. {name} can sign in with their phone at once.',
  'admin.sec.form.added': '{name} added — audit-logged',
  'admin.sec.form.updated': 'Member updated — audit-logged',
  'admin.sec.perm.title': 'Permission matrix',
  'admin.sec.perm.sub': 'Screen → sub-tab → action, exactly as the API enforces them for each role. A screen the API closes for a role greys its sub-tabs and actions. Read-only — the five roles are fixed in application configuration and change only with a release, never here.',
  'admin.sec.perm.col': 'Permission',
  'admin.sec.perm.immutable': '⚿ immutable',
  'admin.sec.perm.members': '{n, plural, one{# member} other{# members}}',
  'admin.sec.perm.screen': 'screen · can open',
  'admin.sec.perm.sa_only': 'SA only',
  'admin.sec.perm.cell_on': '{role}: allowed',
  'admin.sec.perm.cell_off': '{role}: not allowed',
  'admin.sec.perm.cell_nav': '{role}: in the sidebar, but the API refuses this role',
  'admin.sec.perm.foot': 'Every cell is what the API allows that role today; Super admin always holds everything. “!” marks a screen the sidebar shows but whose data the API refuses for that role.',
  'admin.sec.perm.p.kyc_decide': 'Approve / reject KYC',
  'admin.sec.perm.p.services_review': 'Review queue',
  'admin.sec.perm.p.services_add': 'Add new',
  'admin.sec.perm.p.services_all': 'All services',
  'admin.sec.perm.p.offer_takedown': 'Take down offers & services',
  'admin.sec.perm.p.merchant_suspend': 'Suspend merchants',
  'admin.sec.perm.p.merchant_ban': 'Ban merchants (permanent)',
  'admin.sec.perm.p.pricing_subs': 'Subscriptions',
  'admin.sec.perm.p.pricing_fees': 'Booking fees',
  'admin.sec.perm.p.pricing_edit': 'Edit platform pricing',
  'admin.sec.perm.p.refund_approve': 'Approve refunds',
  'admin.sec.perm.p.audit_export': 'Export audit log',
  'admin.sec.perm.p.notifs_inbox': 'Inbox',
  'admin.sec.perm.p.notifs_alerts': 'My alerts',
  'admin.sec.perm.p.roles_manage': 'Manage the admin team',
  'admin.sec.flags.title': 'Feature flags',
  'admin.sec.flags.sub': 'Platform-wide and immediate. Rollout flags ship to a percentage of customers.',
  'admin.sec.flags.on': 'ON',
  'admin.sec.flags.off': 'OFF',
  'admin.sec.flags.rollout': 'Rollout {pct}%',
  'admin.sec.flags.down': 'Rollout −10%',
  'admin.sec.flags.up': 'Rollout +10%',
  'admin.sec.flags.turn_on_title': 'Turn ON — {label}?',
  'admin.sec.flags.turn_off_title': 'Turn OFF — {label}?',
  'admin.sec.flags.body': 'Platform-wide and immediate for every customer and merchant.',
  'admin.sec.flags.turn_on': 'Turn ON',
  'admin.sec.flags.turn_off': 'Turn OFF',
  'admin.sec.flags.done_on': '{label} is ON — audit-logged',
  'admin.sec.flags.done_off': '{label} is OFF — audit-logged',
  'admin.sec.flags.rollout_done': 'Rollout → {pct}% — audit-logged',
  'admin.sec.flags.foot': 'Every change is audit-logged. Super admin only.',
  'admin.sec.flags.empty': 'No feature flags.',
  'admin.sec.set.title': 'Platform constants',
  'admin.sec.set.sub': 'The runtime constants the platform reads live. Super admin only — each change confirms and is audit-logged.',
  'admin.sec.set.edit': 'Edit',
  'admin.sec.set.sms_sender': 'SMS sender ID',
  'admin.sec.set.sms_sender_desc': 'Name shown on every outbound SMS — one-time codes and reminders.',
  'admin.sec.set.support_email': 'Support email',
  'admin.sec.set.support_email_desc': 'Reply-to on system emails and the address shown in the app footer.',
  'admin.sec.set.hold_minutes': 'Booking stepper hold',
  'admin.sec.set.hold_minutes_desc': 'Minutes a slot stays reserved while a customer finishes the booking stepper.',
  'admin.sec.set.invite_expiry_hours': 'Invite-link expiry',
  'admin.sec.set.invite_expiry_hours_desc': 'Hours before a team invite link stops working.',
  'admin.sec.set.session_timeout_minutes': 'Session timeout',
  'admin.sec.set.session_timeout_minutes_desc': 'Idle minutes before an admin session is signed out.',
  'admin.sec.set.unit_min': '{n} min',
  'admin.sec.set.unit_h': '{n} h',
  'admin.sec.set.new_value': 'New value',
  'admin.sec.set.save': 'Save — audit-logged',
  'admin.sec.set.body': '{desc} Effective immediately; the change is audit-logged.',
  'admin.sec.set.err_number': 'Enter a whole number',
  'admin.sec.set.err_value': 'A value is required',
  'admin.sec.set.done': '{label} updated — audit-logged',
  'admin.sec.set.foot': 'These apply immediately — no deploy. Every edit writes an audit entry.',
  'admin.sec.audit.title': 'Immutable audit log',
  'admin.sec.audit.search': 'Search action, target, person…',
  'admin.sec.audit.all_roles': 'All roles',
  'admin.sec.audit.all_types': 'All action types',
  'admin.sec.audit.any_date': 'Any date',
  'admin.sec.audit.type.security': 'Security',
  'admin.sec.audit.type.pricing': 'Pricing',
  'admin.sec.audit.type.flags': 'Flags',
  'admin.sec.audit.type.moderation': 'Moderation',
  'admin.sec.audit.type.automated': 'Automated',
  'admin.sec.audit.day.today': 'Today',
  'admin.sec.audit.day.yesterday': 'Yesterday',
  'admin.sec.audit.day.earlier': 'Earlier',
  'admin.sec.audit.col.when': 'When',
  'admin.sec.audit.col.who': 'Who',
  'admin.sec.audit.col.action': 'Action',
  'admin.sec.audit.col.target': 'Target',
  'admin.sec.audit.col.summary': 'Summary',
  'admin.sec.audit.col.type': 'Type',
  'admin.sec.audit.system': 'System',
  'admin.sec.audit.export': 'Export CSV',
  'admin.sec.audit.export_done': 'Audit log exported — the export is itself audit-logged',
  'admin.sec.audit.export_failed': 'The export could not be prepared — try again',
  'admin.sec.audit.empty': 'No rows match',
  'admin.sec.audit.clear': 'Clear filters',
  'admin.sec.audit.page': '{from}–{to} of {n}',
  'admin.sec.audit.prev': '← Prev',
  'admin.sec.audit.next': 'Next →',
  'admin.sec.audit.note': '⊘ Append-only — entries can never be edited or deleted. Exports are themselves audit-logged.',
  'admin.sec.audit.record_body': 'Append-only entry {id} — the payload exactly as stored. Entries can never be edited or deleted.',
  'admin.sec.dlg.reason': 'Reason — required, audit-logged',
  'admin.sec.dlg.reason_ph': 'Add a short reason…',
  'admin.sec.dlg.reason_required': 'A reason is required — it is recorded in the audit log',
  'admin.sec.dlg.close': 'Close',
  'admin.sec.error': 'Something went wrong — try again',

  // Booking stepper (FR-C7) + confirmation. Direct-payment strings use {brand} so
  // no tenant literal ships in the dictionary (INF-1).
  'booking.title': 'Book',
  'booking.step.details': 'Details',
  'booking.step.review': 'Review & confirm',
  'booking.choose_service': 'Choose a service',
  'booking.pick_time': 'Pick a time',
  'booking.slot_full': 'Full',
  'booking.slot_taken': 'That slot was just taken — pick another.',
  'booking.rate_limited': 'Too many attempts. Please wait a few minutes and try again.',
  'booking.contact': 'Contact details',
  'booking.your_name': 'Your name',
  'booking.your_phone': 'Mobile number',
  'booking.your_email': 'Email (optional)',
  'booking.phone_verified': 'Phone verified',
  'booking.verify_first': 'We verify your number before confirming.',
  'booking.payment': 'Payment',
  'booking.trx_ph': 'e.g. BKA7X92MT4',
  'booking.total': 'Total',
  'booking.offer_unavailable': 'That offer is no longer available — the regular price now applies.',
  'booking.due_venue': 'Due at venue',
  'booking.start_again': 'Start again',
  'booking.confirm': 'Confirm booking',
  'confirm.code_label': 'Booking code',
  'confirm.view_business': 'View business',

  // ---- Offers destination (FR-C19) ----
  'offers.title': 'Offers',
  'offers.search_placeholder': 'Search offers',
  'offers.ending_soon': 'Ending soon',
  'offers.bookable_only': 'Bookable now',
  'offers.results_count': '{count} offers',
  'offers.load_more': 'Load more',
  'offers.sort.discount': 'Biggest discount',
  'offers.sort.newest': 'Just added',
  'offers.rail.top_discount': 'Biggest discounts',
  'offers.see_all': 'See all offers ›',
  'offers.empty_line': 'No live deals in this category right now — check back soon.',
  'offers.chip_all': 'All',
  'offers.chip_food': 'Food',
  'offers.chip_beauty': 'Beauty & Wellness',
  'offers.chip_bookable': 'Bookable only',
  'offers.ends_today': 'Ends today',
  'offers.ends_tomorrow': 'Ends tomorrow',
  'offers.deal_alerts.title': 'Never miss a deal',
  // ---- Trending (FR-C20) ----
  'trending.rising': 'Rising fast',
  'trending.new': 'New',
  // Business CTA banner at the foot of the page — v3's own copy (tpCta*).
  'trending.cta_title': 'Want your business trending here?',
  'trending.cta_sub': 'Businesses with fresh offers, quick replies and verified reviews rise fastest. List free and start climbing.',
  // ---- Activity ticker (FR-C21) ----

  // ---- Business claim flow (FR-B17) ----
  'claim.unclaimed_badge': 'Unclaimed',
  'claim.under_review_badge': 'Claim under review',
  'claim.claimed_badge': 'Claimed',
  'claim.verified_badge': 'Verified',
  'claim.step2_title': 'Verify ownership',
  'claim.doc_trade_license': 'Trade licence',
  'claim.doc_nid': 'National ID',
  'claim.already_open': 'Someone has already claimed this listing and it is under review.',
  'claim.already_claimed': 'This listing already has an owner. Contact support if that is wrong.',
  'claim.rejected_notice': 'Your claim was not approved: {reason}',
  'claim.back': 'Back',
  'claim.next': 'Continue',
  'claim.close': 'Close',
  'claim.done': 'Done',

  // ---- Theme (§12) ----
  'theme.switch_to_light': 'Switch to light theme',
  'theme.switch_to_dark': 'Switch to dark theme',
  'theme.current_light': 'Light theme',
  'theme.current_dark': 'Dark theme',
  // ---- Mobile tab bar (§7.3) ----
  'tabs.home': 'Home',
  'tabs.offers': 'Offers',
  'tabs.categories': 'Categories',
  'tabs.bookings': 'Bookings',
  'tabs.more': 'More',
  'nav.skip_to_content': 'Skip to content',
  // ---- App page (FR-C22, §7.3 route 11) ----
  'common.selected': 'selected',
  'pricing.per_month_annual': 'mo, billed yearly',
  'pricing.get_started': 'Get started',
  // ---- Feature-your-offer band (FR-A18, DR-27) ----
  'offers.feature.cta': 'Talk to us',
  // ---- v3 header / nav ----
  'nav.primary': 'Main',
  'nav.menu': 'Menu',
  'nav.offers': 'Offers',
  'nav.booking': 'Booking',
  'nav.videos': 'Videos',
  'nav.new_badge': 'NEW',
  'nav.resources': 'Resources',
  'nav.categories': 'Categories',
  'nav.popular': 'Popular',
  'nav.popular_offers': 'Live offers',
  'nav.popular_trending': 'Trending now',
  'nav.popular_offer_reels': 'Offer reels',
  'nav.popular_near_you': 'Near you',
  'nav.popular_top_rated': 'Top rated',
  'nav.popular_new_week': 'New this week',
  'nav.res_for_business': 'For business',
  'nav.res_blog': 'Blog',
  'nav.res_help': 'Help centre',
  'nav.sign_in': 'Sign in',
  'nav.add_business': 'Add your business',
  'nav.free': 'FREE',
  // ---- v3 footer (copy transcribed from the design's own dictionary) ----
  'footer.tagline': '{region}\u2019s multi-vertical booking marketplace. A {company_short} product.',
  'footer.for_business': 'For business',
  'footer.list_free': 'List your business free',
  'footer.post_offer': 'Post an offer',
  'footer.get_featured': 'Get featured',
  'footer.copyright': '\u00a9 2026 {brand}. Powered by {company}. All rights reserved.',
  'footer.social': 'Social',
  'footer.facebook': 'Facebook',
  'footer.instagram': 'Instagram',
  'footer.youtube': 'YouTube',
  // ---- v3 home (copy transcribed from the design's own dictionary) ----
  'v3home.search_service': 'Service or business',
  'v3home.offers_near_you': 'Offers near you.',
  'v3home.popular_near_you': 'Popular near you.',
  'v3home.next_plan_title': 'You will find your next plan here.',
  'v3home.next_plan_sub': 'Dive into thousands of local businesses — doctors, dining, stays and more — all bookable in one place.',
  'v3home.trend_title': 'Trending right now.',
  'v3home.trend_sub': 'The 10 most-booked businesses — updated today',
  'v3home.trend_rank': '#{n} Trending',
  'v3home.weekend_title': 'Things to do this weekend.',
  'v3home.weekend_sub': 'Plans near you — updated every Thursday',
  'v3home.see_all_listings': 'All listings \u203a',
  'v3home.see_all_trending': 'See all trending nearby \u2192',
  'v3home.note_rankings': 'Rankings are organic except cards marked Sponsored.',
  'v3home.note_recs': 'Recommendations are organic except cards marked Sponsored.',
  'v3home.stat_bookings': 'bookings facilitated',
  // ---- v3 search additions ----
  // ---- v3 business page sticky tabs ----
  // ---- v3 promotion products band (Pricing + For Business) ----

  // Merchant offers (FR-B18) — the merchant-authored discount surface.
  'merchant.nav.offers': 'Offers',
  'merchant.offers.new': 'New offer',
  'merchant.offers.edit_title': 'Edit offer',
  'merchant.offers.pause': 'Pause',
  'merchant.offers.resume': 'Resume',
  'merchant.offers.insights': 'Insights',
  'merchant.offers.views': 'Views',
  'merchant.offers.status.active': 'Live',
  'merchant.offers.status.paused': 'Paused',
  'merchant.offers.status.expired': 'Ended',
  'merchant.offers.status.taken_down': 'Taken down',
  'merchant.offers.f_starts': 'Starts',
  'merchant.offers.f_ends': 'Ends',
  'merchant.offers.f_cap': 'Redemption cap',
  'merchant.offers.err_limit': 'Your plan’s offer limit is full. Pause or end a live offer, or upgrade your plan.',
  'merchant.offers.err_generic': 'The offer could not be saved. Please try again.',

  // Admin claims, offer moderation and leads (FR-A17 / FR-A18 / FR-C22).
  'admin.nav.claims': 'Claims',
  'admin.nav.offers': 'Offers',
  'admin.claims.status.pending': 'Awaiting review',
  'admin.claims.status.approved': 'Approved',
  'admin.claims.status.rejected': 'Rejected',
  'admin.claims.status.withdrawn': 'Withdrawn',
  'admin.offers.title': 'Offer moderation',
  'admin.offers.sponsored': 'Sponsored',
  'admin.offers.take_down': 'Take down',
  'admin.offers.confirm_promote': 'Schedule',
  'admin.offers.unplace': 'Remove placement',
  'admin.offers.slot_label': 'Slot',
  'admin.offers.from': 'From',
  'admin.offers.to': 'To',
  'admin.offers.window_invalid': 'Choose a placement window that ends after it starts.',
  'admin.offers.slot.offers_featured': 'Offers page — featured',
  'admin.offers.slot.home_offers_rail': 'Home — offers rail',
  'admin.offers.slot.offers_top_discount': 'Offers page — biggest discounts',

  // ── DC90 admin shell (rail, header, ⌘K, tray, account menu, chip row, restricted) ──
  'admin.nav.approvals': 'Approvals',
  'admin.nav.services': 'Services',
  'admin.nav.merchants': 'Merchants',
  'admin.nav.customers': 'Customers',
  'admin.nav.pricing': 'Pricing control',
  'admin.nav.payments': 'Payments',
  'admin.nav.reports': 'Reports',
  'admin.nav.notifications': 'Notifications',
  'admin.nav.roles': 'Roles & security',
  'admin.shell.collapse_all': 'Collapse all groups',
  'admin.shell.expand_all': 'Expand all groups',
  'admin.shell.version': 'Version {version}',
  'admin.shell.group_badge': '{n} waiting in this group',
  'admin.shell.badge.approvals': 'items waiting in Approvals',
  'admin.shell.badge.offers': 'offers awaiting review',
  'admin.shell.badge.payments': 'refunds and subscription payments awaiting a decision',
  'admin.shell.signout_hold': 'Hold to sign out',
  'admin.shell.signout_title': 'Sign out?',
  'admin.shell.signout_body': 'You will return to the console sign-in. Drafts saved on the server stay there.',
  'admin.shell.signout_stay': 'Stay signed in',
  'admin.shell.search_placeholder': 'Search merchants, bookings, customers…',
  'admin.shell.open_search': 'Open search',
  'admin.shell.env.development': 'Development',
  'admin.shell.env.staging': 'Staging',
  'admin.shell.env.title': 'Environment: {env}',
  'admin.shell.notifications': 'Notifications',
  'admin.shell.notifications_unread': '{n} unread notifications',
  'admin.shell.account': 'Account menu',
  'admin.shell.palette.title': 'Global search',
  'admin.shell.palette.all': 'All',
  'admin.shell.palette.category': '{label} ({n})',
  'admin.shell.palette.go_to': 'Go to',
  'admin.shell.palette.empty': 'No matches — try a name, booking code, phone or TRX code',
  'admin.shell.palette.error': 'Search is unavailable right now. Try again in a moment.',
  'admin.shell.palette.open': 'Open',
  'admin.shell.palette.close': 'Close',
  'admin.shell.palette.esc': 'Esc',
  'admin.shell.tray.mark_all': 'Mark all read',
  'admin.shell.tray.all': 'All ({n})',
  'admin.shell.tray.unread': 'Unread ({n})',
  'admin.shell.tray.empty_all': 'Nothing new',
  'admin.shell.tray.empty_unread': 'All caught up ✓',
  'admin.shell.tray.dismiss': 'Dismiss',
  'admin.shell.tray.see_all': 'See all →',
  'admin.shell.tray.settings': 'Notification settings',
  'admin.shell.acct.you': 'You',
  'admin.shell.acct.team': 'Team & roles',
  'admin.shell.acct.team_sub': 'Team, permissions and flags',
  'admin.shell.acct.activity': 'My activity log',
  'admin.shell.acct.activity_sub': 'Every action recorded against you',
  'admin.shell.acct.notif': 'Notification settings',
  'admin.shell.acct.notif_sub': 'Alerts and platform messages',
  'admin.shell.acct.prefs': 'Preferences',
  'admin.shell.acct.appearance': 'Appearance',
  'admin.shell.acct.light': 'Light',
  'admin.shell.acct.dark': 'Dark',
  'admin.shell.acct.environment': 'Environment',
  'admin.shell.acct.roles': 'Roles',
  'admin.shell.restricted.title': 'Restricted',
  'admin.shell.restricted.body': 'Your role does not include {section}. Ask a super admin if you need access.',
  'admin.shell.restricted.back': 'Back to Overview',
  // ── end DC90 admin shell ──
  // ── DC08 canonical status labels ─────────────────────────────────────────────
  // Display labels for the canonical enumerations (docs/design-gap/
  // round2-entity-reconciliation.md, appendix). Keyed `vocab.<enum>.<stored value>`.
  // Only values NOT already covered by an existing key live here — the full
  // value → key table (incl. reused keys such as booking.status.*) is
  // status-label.ts; tones are status-tone.ts. Values marked "pending" await the
  // GAP §5/§7.3 decision named in status-label.ts PENDING_DECISION.
  // Derived booking state (never stored; customer design STATUS.running).
  'vocab.booking_status.running': 'Happening today',
  // Proof reject reason codes (customer REJECT_REASONS = merchant rejectReasons) — pending #27.
  'vocab.proof_reject_reason.not_received': 'Money not received',
  'vocab.proof_reject_reason.trx_mismatch': 'TRX does not match',
  'vocab.proof_reject_reason.amount_short': 'Amount is short',
  'vocab.proof_reject_reason.other': 'Other',
  // Offer lifecycle — draft/pending_review/rejected pending #28; scheduled is derived.
  'vocab.offer_status.draft': 'Draft',
  'vocab.offer_status.pending_review': 'In review',
  'vocab.offer_status.rejected': 'Rejected',
  'vocab.offer_status.scheduled': 'Scheduled',
  // Review status (replaces reviews.hidden) — pending #19; flagged is derived.
  'vocab.review_status.visible': 'Visible',
  'vocab.review_status.hidden': 'Hidden',
  'vocab.review_status.removed': 'Removed',
  'vocab.review_status.flagged': 'Flagged',
  // Dispute additions — pending #19.
  'vocab.dispute_kind.no_service': 'Service not delivered',
  'vocab.dispute_kind.no_show': 'No-show refund',
  'vocab.dispute_status.escalated': 'Escalated',
  'vocab.dispute_resolution.uphold_merchant': 'Upheld for merchant',
  'vocab.dispute_resolution.warn_customer': 'Customer warned',
  'vocab.dispute_resolution.penalty_merchant': 'Penalty on merchant',
  'vocab.dispute_resolution.block_customer': 'Customer blocked',
  // Per-business payment-KYC state (merchant + admin designs agree; PAY_STATE_LABEL).
  'vocab.payment_kyc_state.not_started': 'Not started',
  'vocab.payment_kyc_state.under_review': 'Under review',
  'vocab.payment_kyc_state.approved': 'Approved',
  'vocab.payment_kyc_state.more_info': 'More info needed',
  'vocab.payment_kyc_state.rejected': 'Rejected',
  'vocab.payment_kyc_state.blocked_individual': 'Individual — blocked',
  // Catalog item status + visibility — pending #11 (service moderation).
  'vocab.catalog_item_status.draft': 'Draft',
  'vocab.catalog_item_status.pending_review': 'Under review',
  'vocab.catalog_item_status.changes_requested': 'Changes needed',
  'vocab.catalog_item_status.active': 'Live',
  'vocab.catalog_item_status.rejected': 'Rejected',
  'vocab.catalog_item_status.paused': 'Paused',
  'vocab.catalog_item_status.suspended': 'Suspended',
  'vocab.catalog_item_status.archived': 'Archived',
  'vocab.catalog_visibility.marketplace': 'Marketplace',
  'vocab.catalog_visibility.private': 'Private',
  // Plan tiers + price kinds — pending #1 (supersedes DR-26).
  'vocab.plan_tier.free': 'Free',
  'vocab.plan_tier.premium': 'Premium',
  'vocab.plan_tier.business_plus': 'Business Plus',
  'vocab.plan_tier.corporate': 'Corporate',
  'vocab.price_kind.regular': 'Regular',
  'vocab.price_kind.launch': 'Launch special',
  'vocab.price_kind.loyalty': 'Loyalty price',
  // Subscription payment (TRX) status + invoice `refunded` (pending #1).
  'vocab.subscription_payment_status.pending': 'Verifying',
  'vocab.subscription_payment_status.verified': 'Verified',
  'vocab.subscription_payment_status.rejected': 'Rejected',
  'vocab.invoice_status.refunded': 'Refunded',
  // Notification preference events (customer + merchant rows) and channels — pending #12.
  'vocab.notification_event.booking_confirmed': 'Booking confirmed',
  'vocab.notification_event.payment_update': 'Payment updates',
  'vocab.notification_event.reminder': 'Reminders',
  'vocab.notification_event.offers': 'Offers and deals',
  'vocab.notification_event.booking_new': 'New booking',
  'vocab.notification_event.proof_submitted': 'Payment proof',
  'vocab.notification_event.booking_cancelled': 'Cancellation',
  'vocab.notification_event.review_new': 'New review',
  'vocab.notification_channel.sms': 'SMS',
  'vocab.notification_channel.email': 'Email',
  'vocab.notification_channel.push': 'Push',
  'vocab.notification_channel.inapp': 'In-app',
  'vocab.notification_channel.whatsapp': 'WhatsApp',
  // ── end DC08 canonical status labels ─────────────────────────────────────────

  // Claim document upload (FR-B17 / DR-17).
  'claim.doc_credential': 'Professional registration certificate',
  'claim.doc_other': 'Supporting document',
  'claim.upload_failed': 'That file could not be uploaded. Please try again.',
  'claim.file_too_large': 'That file is too large. Please upload a file under 8 MB.',

  // The claimant's own claim list (FR-B17).
  'user.claims.title': 'My claims',
  'user.claims.empty_title': 'No claims yet',
  'user.claims.empty_body': 'If your business is already listed here, you can claim its page and take it over.',
  'user.claims.opened': 'opened',
  'user.claims.path_instant': 'Verified by phone',
  'user.claims.path_documents': 'Verified by documents',
  'user.claims.status.pending': 'Under review',
  'user.claims.status.approved': 'Approved',
  'user.claims.status.rejected': 'Rejected',
  'user.claims.status.withdrawn': 'Withdrawn',
  'user.claims.outstanding': '{n} document(s) still needed. Open the listing to upload them.',
  'user.claims.view_listing': 'Open listing',
  'user.claims.withdraw': 'Withdraw claim',
  'user.claims.footnote': 'While a claim is under review the listing is held for you, so nobody else can claim it. Withdrawing releases it.',

  // DC07 formatter words — used by format-number/-money/-date/-duration and
  // interpolate.ts (round2-i18n.md §6). Plural syntax: {n, plural, one{…} other{…}};
  // bn has no plural inflection, so bn values are plain {n} templates.
  'format.day.sun': 'Sun',
  'format.day.mon': 'Mon',
  'format.day.tue': 'Tue',
  'format.day.wed': 'Wed',
  'format.day.thu': 'Thu',
  'format.day.fri': 'Fri',
  'format.day.sat': 'Sat',
  'format.day_long.sun': 'Sunday',
  'format.day_long.mon': 'Monday',
  'format.day_long.tue': 'Tuesday',
  'format.day_long.wed': 'Wednesday',
  'format.day_long.thu': 'Thursday',
  'format.day_long.fri': 'Friday',
  'format.day_long.sat': 'Saturday',
  'format.month.jan': 'Jan',
  'format.month.feb': 'Feb',
  'format.month.mar': 'Mar',
  'format.month.apr': 'Apr',
  'format.month.may': 'May',
  'format.month.jun': 'Jun',
  'format.month.jul': 'Jul',
  'format.month.aug': 'Aug',
  'format.month.sep': 'Sep',
  'format.month.oct': 'Oct',
  'format.month.nov': 'Nov',
  'format.month.dec': 'Dec',
  'format.month_long.jan': 'January',
  'format.month_long.feb': 'February',
  'format.month_long.mar': 'March',
  'format.month_long.apr': 'April',
  'format.month_long.may': 'May',
  'format.month_long.jun': 'June',
  'format.month_long.jul': 'July',
  'format.month_long.aug': 'August',
  'format.month_long.sep': 'September',
  'format.month_long.oct': 'October',
  'format.month_long.nov': 'November',
  'format.month_long.dec': 'December',
  'format.date.day_month': '{d} {month}',
  'format.date.short': '{d} {month} {year}',
  'format.date.long': '{day} {d} {month}, {year}',
  'format.date.month_year': '{month} {year}',
  'format.date.range_same_month': '{d1}–{d2} {month}',
  'format.date.range': '{from} – {to}',
  'format.date.with_time': '{date}, {time}',
  'format.relative.today': 'Today',
  'format.relative.tomorrow': 'Tomorrow',
  'format.relative.yesterday': 'Yesterday',
  'format.relative.in_days': '{n, plural, one{In # day} other{In # days}}',
  'format.relative.days_ago': '{n, plural, one{# day ago} other{# days ago}}',
  'format.relative.last_week': 'Last week',
  'format.relative.weeks_ago': '{n, plural, one{# week ago} other{# weeks ago}}',
  'format.relative.hours_ago': '{n} h ago',
  'format.relative.minutes_ago': '{n} min ago',
  'format.relative.just_now': 'Just now',
  'format.clock.pattern': '{time} {period}',
  'format.clock.hour': '{h}',
  'format.clock.hour_minute': '{h}:{m}',
  'format.clock.period.late_night': 'AM',
  'format.clock.period.dawn': 'AM',
  'format.clock.period.morning': 'AM',
  'format.clock.period.midday': 'PM',
  'format.clock.period.afternoon': 'PM',
  'format.clock.period.evening': 'PM',
  'format.clock.period.night': 'PM',
  'format.duration.minutes': '{n} min',
  'format.duration.hours': '{n} h',
  'format.duration.hours_minutes': '{h} h {m} min',
  'format.duration.days': '{n, plural, one{# day} other{# days}}',
  'format.duration.weeks': '{n, plural, one{# week} other{# weeks}}',
  'format.duration.months': '{n, plural, one{# month} other{# months}}',
  'format.money.compact': '{amount} {unit}',
  'format.money.lakh_short': 'L',
  'format.money.crore_short': 'Cr',
  'format.money.lakh': 'lakh',
  'format.money.crore': 'crore',
  'format.count.of': '{n} of {total}',
  // ---- DC44 website chrome: toast, nav drawer, header pill, inner app bar,
  //      account menu, add-to-home bar, tab-bar sheets and badges ----
  'shell.close': 'Close',
  'account.menu': 'Account',
  'account.menu_for': 'Account menu for {name}',
  'account.overview': 'Overview',
  'account.my_bookings': 'My bookings',
  'account.favourites': 'Favourites',
  'account.my_reviews': 'My reviews',
  'account.messages': 'Messages',
  'account.profile_settings': 'Profile & settings',
  'account.switch_business': 'Switch to Business',
  'merchant.gate.no_biz_title': 'No business on this account yet',
  'merchant.gate.no_biz_sub': 'Add your business to take bookings here. Your customer account stays as it is.',
  'merchant.gate.to_customer': 'Go to my customer dashboard',
  'account.switch_customer': 'Switch to Customer',
  'account.switch_admin': 'Switch to Admin',
  'account.sign_out': 'Sign out',
  'account.switch_failed': 'Couldn\u2019t switch accounts. Please try again.',
  'account.signed_out': 'You\u2019re signed out',
  'nav.add_business_free': 'Add your business \u2014 free',
  'a2hs.label': 'Install the app',
  'a2hs.text': 'Add {brand} to your Home Screen for the full app experience',
  'a2hs.dismiss': 'Dismiss',
  'sheet.all_categories': 'All categories',
  'sheet.bookings_title': 'Your bookings will live here',
  'sheet.bookings_sub': 'Sign in to see all your bookings, payment status and reminders in one place.',
  'sheet.more_sub': 'Sign in for saved places, notifications and profile settings.',
  'sheet.saved_places': 'Saved places',
  'sheet.light_mode': 'Light mode',
  'sheet.dark_mode': 'Dark mode',
  'sheet.about': 'About {brand}',
  'tabs.label': 'Quick navigation',
  'tabs.offers_live': 'live offers',
  'tabs.needs_attention': 'needs your attention',
  'tabs.unread': '{n} unread',
  // ---- end DC44 ----
  // ---- DC70 merchant shell: glass rail (Work / Grow / Account), top bar, ⌘K
  // palette, notification centre, user menu, context bar, 4-slot mobile bar + More
  // sheet, help panel + contact sheet, plan-lock / coming-later states and the
  // folded-route hosts (P §A.1–A.2; PRD v2.5 §7.3.3). ----
  'dc70.nav.home': 'Home',
  'dc70.nav.bookings': 'Bookings',
  'dc70.nav.services': 'Services',
  'dc70.nav.business': 'My Business',
  'dc70.nav.offers': 'Offers',
  'dc70.nav.promote': 'Promote',
  'dc70.nav.insights': 'Insights',
  'dc70.nav.team': 'Team',
  'dc70.nav.plan': 'My Plan',
  'dc70.nav.account': 'Account',
  'dc70.nav.inbox': 'Inbox',
  'dc70.nav.qr': 'QR studio',
  'dc70.nav.more': 'More',
  'dc70.group.work': 'Work',
  'dc70.group.grow': 'Grow',
  'dc70.group.account': 'Account',
  'dc70.group.more': 'More',
  'dc70.group.dashboard': 'Dashboard',
  'dc70.nav.locked': 'Locked on your plan',
  'dc70.nav.soon': 'Soon',
  'dc70.nav.pending_badge': '{n, plural, one {# payment to check} other {# payments to check}}',
  'dc70.rail.collapse': 'Collapse menu',
  'dc70.rail.expand': 'Expand menu',
  'dc70.role.owner': 'Owner',
  'dc70.role.team': 'Team',
  'dc70.help.card_title': 'Need a hand?',
  'dc70.help.card_body': 'Guides, tips and support',
  'dc70.help.open': 'Help',
  'dc70.help.contact': 'Contact',
  'dc70.help.logout': 'Log out',
  'dc70.help.title': 'Help centre',
  'dc70.help.sub': 'The questions merchants ask most',
  'dc70.help.q1': 'How do I take a manual booking?',
  'dc70.help.a1': 'On Home, press “New booking”, enter the customer’s name and phone, then pick the service and time.',
  'dc70.help.q2': 'How do I verify a payment?',
  'dc70.help.a2': 'Open Bookings › Payment check, match the receipt against your account, then confirm or reject.',
  'dc70.help.q3': 'Where is my booking link?',
  'dc70.help.a3': 'Open QR studio from the menu — your booking link and QR code are both there.',
  'dc70.help.q4': 'How do I pause online bookings?',
  'dc70.help.a4': 'Open the menu under your name and switch on “Pause online bookings”. Switch it off to take bookings again.',
  'dc70.contact.title': 'Contact support',
  'dc70.contact.whatsapp': 'Message on WhatsApp',
  'dc70.contact.email': 'Email us',
  'dc70.contact.call': 'Call the hotline',
  'dc70.contact.none': 'Support contacts are not available right now. Please try again later.',
  'dc70.top.search': 'Search services, bookings, customers, codes',
  'dc70.top.search_open': 'Open search',
  'dc70.lang.switch': 'Switch language',
  'dc70.rail.version': 'Version {version}',
  'dc70.top.notifications': 'Notifications',
  'dc70.top.help': 'Help',
  'dc70.top.user_menu': 'Account menu',
  'dc70.notif.all': 'All ({n})',
  'dc70.notif.unread': 'Unread ({n})',
  'dc70.notif.unread_count': '{n, plural, one {# unread notification} other {# unread notifications}}',
  'dc70.notif.mark_all': 'Mark all read',
  'dc70.notif.dismiss': 'Dismiss',
  'dc70.notif.open': 'Open',
  'dc70.notif.empty_caught_up': 'You are all caught up',
  'dc70.notif.empty_none': 'Nothing new',
  'dc70.notif.empty_body': 'New activity shows up here.',
  'dc70.notif.settings': 'Notification settings',
  'dc70.notif.error': 'Couldn’t load notifications.',
  'dc70.search.label': 'Search the dashboard',
  'dc70.search.scopes': 'Search scope',
  'dc70.search.scope_all': 'All',
  'dc70.search.scope_bookings': 'Bookings',
  'dc70.search.scope_services': 'Services',
  'dc70.search.scope_customers': 'Customers',
  'dc70.search.scope_count': '{label} ({n})',
  'dc70.search.advanced': 'Advanced filters',
  'dc70.search.status': 'Status',
  'dc70.search.when': 'When',
  'dc70.search.business': 'Business',
  'dc70.search.any': 'Any',
  'dc70.search.when_today': 'Today',
  'dc70.search.when_upcoming': 'Upcoming',
  'dc70.search.when_past': 'Past',
  'dc70.search.reset': 'Reset filters',
  'dc70.search.start': 'Type a name, phone number, service or booking code.',
  'dc70.search.searching': 'Searching…',
  'dc70.search.no_results': 'No results',
  'dc70.search.no_results_hint': 'Try a name, phone number or booking code.',
  'dc70.search.error': 'Search is unavailable right now.',
  'dc70.search.bookings_count': '{n, plural, one {# booking} other {# bookings}}',
  'dc70.search.hint_open': 'Open',
  'dc70.search.hint_close': 'Close',
  'dc70.search.key_esc': 'Esc',
  'dc70.menu.language': 'Language',
  'dc70.menu.theme': 'Theme',
  'dc70.ctx.scope': 'Business scope',
  'dc70.ctx.quick_actions': 'Quick actions',
  'dc70.ctx.all_businesses': 'All businesses',
  'dc70.ctx.businesses_n': '{n} businesses',
  'dc70.qa.booking': 'New booking',
  'dc70.qa.service': 'Service',
  'dc70.qa.offer': 'Offer',
  'dc70.qa.link': 'Share link',
  'dc70.lock.title': '{page} is not on your plan',
  'dc70.soon.title': 'Coming later',
  'dc70.soon.body': 'Self-serve promotion is not available yet. We will let you know when it opens.',
  // ---- end DC70 ----
  // ---- DC72 merchant Bookings: stat cards, Payment check / Upcoming / All / Hidden
  // tabs, list + 7-day week view, filters with suggestions, bulk verify, verify window
  // + done, receipt zoom, reject sheet (DR-46 codes), booking drawer (incl. mark
  // settled, DR-37), reschedule on availability, QR check-in (P §A.3–A.4, FR-B25/B26). ----
  'dc72.page.sub': 'Who’s coming today and who has paid.',
  'dc72.page.export': 'Export CSV',
  'dc72.page.new_booking': 'New booking',
  'dc72.stat.today': 'Today',
  'dc72.stat.today_sub': 'excluding cancelled',
  'dc72.stat.today_kicker': 'Next slot',
  'dc72.stat.none': 'Nothing left today',
  'dc72.stat.payment': 'Payment check',
  'dc72.stat.payment_sub': 'waiting on you',
  'dc72.stat.payment_kicker': 'Oldest',
  'dc72.stat.hours': '{n}h',
  'dc72.stat.confirmed': 'Confirmed',
  'dc72.stat.confirmed_sub': 'on today’s list',
  'dc72.stat.confirmed_kicker': 'Of today',
  'dc72.stat.confirmed_line': '{pct}% of today · {n} to go',
  'dc72.stat.value': 'Today’s value',
  'dc72.stat.value_sub': 'collected {amount}',
  'dc72.stat.value_kicker': 'At the venue',
  'dc72.stat.value_line': 'due {amount} at venue',
  'dc72.stat.vs_last_month': 'vs last month',
  'dc72.stat.next_slot_line': 'next slot {time}',
  'dc72.stat.trend_up': '+{n}%',
  'dc72.stat.trend_down': '−{n}%',
  'dc72.status.payment_verification': 'Payment check',
  'dc72.filter.search_prefixed': 'Name, phone, {prefix}- code or TRX',
  'dc72.method.bank': 'Bank',
  'dc72.tabs.label': 'Booking lists',
  'dc72.tabs.payment': 'Payment check',
  'dc72.tabs.upcoming': 'Upcoming',
  'dc72.tabs.all': 'All',
  'dc72.tabs.hidden': 'Hidden',
  'dc72.view.label': 'View',
  'dc72.view.list': 'List',
  'dc72.view.calendar': 'Calendar',
  'dc72.filter.search': 'Name, phone, booking code or TRX',
  'dc72.filter.search_label': 'Search bookings',
  'dc72.filter.clear_search': 'Clear search',
  'dc72.filter.suggestions': 'Matching bookings',
  'dc72.filter.service': 'Service',
  'dc72.filter.all_services': 'All services',
  'dc72.filter.status': 'Status',
  'dc72.filter.all_statuses': 'All statuses',
  'dc72.filter.dates': 'Dates',
  'dc72.filter.this_week': 'This week',
  'dc72.filter.today': 'Today',
  'dc72.filter.next_30': 'Next 30 days',
  'dc72.filter.any_date': 'Any date',
  'dc72.hidden.banner': '{n, plural, one{# booking is hidden this month — upgrade to see customer details} other{# bookings are hidden this month — upgrade to see customer details}}',
  'dc72.hidden.help': 'Customers can still book normally — the time is held.',
  'dc72.hidden.upgrade': 'Upgrade',
  'dc72.hidden.masked': 'Hidden customer',
  'dc72.pay.title': 'Payment check',
  'dc72.pay.select_all': 'Select all',
  'dc72.pay.select': 'Select {name}',
  'dc72.pay.verify': 'Verify & confirm',
  'dc72.pay.more': 'More actions',
  'dc72.pay.reject': 'Reject',
  'dc72.pay.open': 'Open booking',
  'dc72.pay.receipt': 'Payment receipt',
  'dc72.pay.disputed': 'Disputed — review on its own',
  'dc72.pay.sla_ok': 'Verify within {n} hours',
  'dc72.pay.sla_late': 'Over {n} hours old · {age}',
  'dc72.pay.empty': 'No payments waiting',
  'dc72.pay.empty_hint': 'Share your booking link and bookings start coming in.',
  'dc72.bulk.selected': '{n} selected',
  'dc72.bulk.verify': 'Verify selected',
  'dc72.bulk.clear': 'Clear',
  'dc72.bulk.done': '{n, plural, one{# payment verified} other{# payments verified}}',
  'dc72.bulk.refused': '{n, plural, one{# needs a look on its own — open it to verify} other{# need a look on their own — open them to verify}}',
  'dc72.list.count': '{n, plural, one{# booking} other{# bookings}}',
  'dc72.list.open': 'Open booking {code}',
  'dc72.list.empty': 'No bookings yet',
  'dc72.list.empty_filtered': 'No bookings match these filters',
  'dc72.list.share': 'Share link',
  'dc72.pay_state.paid': 'Paid',
  'dc72.pay_state.advance': 'Advance',
  'dc72.pay_state.venue': 'Pay at venue',
  'dc72.pay_state.due': 'Due',
  'dc72.cal.free': 'Free',
  'dc72.cal.legend': 'Status colours',
  'dc72.drawer.title': 'Booking {code}',
  'dc72.drawer.customer': 'Customer',
  'dc72.drawer.no_email': 'No email',
  'dc72.drawer.call': 'Call',
  'dc72.drawer.whatsapp': 'WhatsApp',
  'dc72.drawer.details': 'Booking details',
  'dc72.drawer.service': 'Service',
  'dc72.drawer.serial': 'Serial',
  'dc72.drawer.source': 'Booked via',
  'dc72.drawer.note': 'Customer note',
  'dc72.drawer.notify': 'Notify',
  'dc72.drawer.notify_sms': 'SMS',
  'dc72.drawer.notify_email': 'Email',
  'dc72.drawer.notify_send': 'Send update',
  'dc72.drawer.notify_sent': 'Update sent to the customer',
  'dc72.drawer.payment': 'Payment',
  'dc72.drawer.advance': 'Advance',
  'dc72.drawer.due': 'Due',
  'dc72.drawer.receipt': 'Payment receipt',
  'dc72.drawer.decision': 'Decision',
  'dc72.drawer.decision_verified': 'Verified',
  'dc72.drawer.decision_rejected': 'Rejected · {reason}',
  'dc72.drawer.decision_pending': 'Not verified yet',
  'dc72.drawer.decision_none': 'No payment proof',
  'dc72.drawer.settled': 'Settled {when}',
  'dc72.drawer.timeline': 'What happened',
  'dc72.drawer.masked': 'Customer details are hidden on your plan',
  'dc72.event.created': 'Booking created',
  'dc72.event.receipt': 'Receipt submitted',
  'dc72.event.waiting': 'Waiting for verification',
  'dc72.event.verified': 'Payment verified',
  'dc72.event.rejected': 'Payment rejected',
  'dc72.event.confirmed': 'Confirmed',
  'dc72.event.declined': 'Declined',
  'dc72.event.checked_in': 'Checked in',
  'dc72.event.completed': 'Completed',
  'dc72.event.no_show': 'Marked no-show',
  'dc72.event.cancelled': 'Cancelled',
  'dc72.event.rescheduled': 'Rescheduled',
  'dc72.event.settled': 'Marked settled',
  'dc72.event.other': 'Updated',
  'dc72.event.by.customer': 'Customer',
  'dc72.event.by.merchant': 'You',
  'dc72.event.by.staff': 'Team',
  'dc72.event.by.admin': 'Support',
  'dc72.event.by.system': 'Automatic',
  'dc72.action.confirm': 'Confirm',
  'dc72.action.complete': 'Complete',
  'dc72.action.qr': 'QR check-in',
  'dc72.action.reschedule': 'Reschedule',
  'dc72.action.no_show': 'No-show',
  'dc72.action.settle': 'Mark settled',
  'dc72.action.cancel': 'Cancel',
  'dc72.action.done_confirmed': 'Booking confirmed',
  'dc72.action.done_completed': 'Booking completed',
  'dc72.action.done_no_show': 'Marked as no-show',
  'dc72.action.done_settled': 'Marked settled',
  'dc72.action.done_cancelled': 'Booking cancelled — the customer is told',
  'dc72.action.failed': 'That didn’t go through. Please try again.',
  'dc72.cancel.title': 'Cancel this booking?',
  'dc72.cancel.body': 'The customer is told. Tell them why.',
  'dc72.cancel.reason': 'Reason for the customer',
  'dc72.cancel.confirm': 'Cancel booking',
  'dc72.cancel.keep': 'Keep booking',
  'dc72.verify.title': 'Check the payment',
  'dc72.verify.position': '{n} / {total}',
  'dc72.verify.prev': 'Previous payment',
  'dc72.verify.next': 'Next payment',
  'dc72.verify.close': 'Close',
  'dc72.verify.all_ok': '{n, plural, one{The check passed} other{All # checks passed}}',
  'dc72.verify.ok_sub': 'Amount, TRX ID and receiving account all match — safe to confirm.',
  'dc72.verify.some_bad': '{n, plural, one{# check needs your eye} other{# checks need your eye}}',
  'dc72.verify.flag_warn': 'Match this payment in your own statement before you confirm.',
  'dc72.verify.checks_loading': 'Running the automatic checks…',
  'dc72.verify.checks_error': 'The automatic checks are unavailable — check this one by hand.',
  'dc72.verify.receipt': 'Customer’s receipt',
  'dc72.verify.receipt_tag': 'Screenshot',
  'dc72.verify.receipt_line': 'Transaction successful',
  'dc72.verify.sent_to': 'Sent to',
  'dc72.verify.trx': 'TRX ID',
  'dc72.verify.time': 'Time',
  'dc72.verify.sent_by': 'Sent by customer · {ago}',
  'dc72.verify.zoom_hint': 'tap to enlarge',
  'dc72.verify.checks': 'Automatic checks',
  'dc72.verify.amount_ok': 'Amount matches',
  'dc72.verify.amount_bad': 'Amount is short',
  'dc72.verify.trx_ok': 'TRX ID is valid and never used before',
  'dc72.verify.trx_dup': 'This TRX ID was already used on {code}',
  'dc72.verify.trx_dup_other': 'This TRX ID was already used on another booking',
  'dc72.verify.account_ok': 'Money landed in your own account',
  'dc72.verify.account_bad': 'The receiving account is not one of yours',
  'dc72.verify.dispute': 'The customer has an open dispute on this booking',
  'dc72.verify.payment': 'Payment',
  'dc72.verify.expected': 'You should receive · {amount}',
  'dc72.verify.booking': 'Booking',
  'dc72.verify.f_customer': 'Customer',
  'dc72.verify.f_phone': 'Phone',
  'dc72.verify.f_service': 'Service',
  'dc72.verify.f_slot': 'Slot',
  'dc72.verify.f_total': 'Total bill',
  'dc72.verify.f_code': 'Booking code',
  'dc72.verify.copy_trx': 'Copy TRX ID',
  'dc72.verify.copied': 'Copied · {value}',
  'dc72.verify.ack': 'I checked this in my own statement',
  'dc72.verify.confirm': 'Confirm payment',
  'dc72.verify.confirm_sub': 'Confirming books the slot and tells the customer.',
  'dc72.verify.reject': 'Reject',
  'dc72.verify.later': 'Decide later',
  'dc72.verify.no_proof': 'This booking has no receipt to check yet.',
  'dc72.done.title': 'Payment confirmed',
  'dc72.done.body': 'The customer is told their booking is confirmed.',
  'dc72.done.next': 'Next payment · {n}',
  'dc72.done.clear': 'Queue is clear',
  'dc72.done.close': 'Close',
  'dc72.zoom.title': '{method} · Receipt',
  'dc72.zoom.loading': 'Loading the screenshot…',
  'dc72.zoom.none': 'The customer sent a TRX ID only — no screenshot.',
  'dc72.zoom.error': 'Couldn’t load the screenshot.',
  'dc72.zoom.alt': 'Payment screenshot',
  'dc72.zoom.close': 'Close',
  'dc72.reject.title': 'Why are you rejecting?',
  'dc72.reject.note': 'Note (optional)',
  'dc72.reject.note_required': 'Note (required for Other)',
  'dc72.reject.hint': 'The customer is told, and can submit a new receipt.',
  'dc72.reject.do': 'Reject payment',
  'dc72.reject.cancel': 'Cancel',
  'dc72.reject.done': 'Payment rejected — the customer can send a new receipt',
  'dc72.resched.title': 'Pick a new time',
  'dc72.resched.date': 'Date',
  'dc72.resched.time': 'Time',
  'dc72.resched.closed': 'Closed',
  'dc72.resched.no_times': 'No open times on this day',
  'dc72.resched.loading': 'Loading open times…',
  'dc72.resched.error': 'Couldn’t load open times.',
  'dc72.resched.taken': 'That time was just taken — pick another.',
  'dc72.resched.save': 'Confirm reschedule',
  'dc72.resched.cancel': 'Cancel',
  'dc72.resched.done': 'Rescheduled — the customer is told',
  'dc72.qr.title': 'QR check-in',
  'dc72.qr.help': 'The customer’s QR carries this code — scan it or match it on arrival.',
  'dc72.qr.unavailable': 'QR image unavailable — match the code instead.',
  'dc72.qr.alt': 'Booking QR code',
  'dc72.qr.done': 'Mark checked in',
  'dc72.qr.close': 'Close',
  'dc72.qr.checked_in': 'Checked in — the visit has started',
  // ---- end DC72 ----
  // ---- DC60 customer dashboard shell: rail, top bar, command palette,
  //      notification tray (FR-C28), account menu, tab bar + More sheet ----
  'user.shell.role': 'Customer',
  'user.shell.group.bookings': 'Bookings',
  'user.shell.group.mine': 'Mine',
  'user.shell.group.account': 'Account',
  'user.shell.nav.home': 'Home',
  'user.shell.nav.bookings': 'My bookings',
  'user.shell.nav.saved': 'Saved',
  'user.shell.nav.reviews': 'Reviews',
  'user.shell.nav.account': 'Account',
  'user.shell.nav.help': 'Help',
  'user.shell.nav.messages': 'Messages',
  'user.shell.nav.claims': 'My claims',
  'user.shell.help_card.title': 'Need help?',
  'user.shell.help_card.sub': 'Guides and support',
  'user.shell.help_card.help': 'Help',
  'user.shell.help_card.contact': 'Contact',
  'user.shell.sign_out': 'Sign out',
  'user.shell.tab.home': 'Home',
  'user.shell.tab.bookings': 'Bookings',
  'user.shell.tab.saved': 'Saved',
  'user.shell.tab.reviews': 'Reviews',
  'user.shell.tab.more': 'More',
  'user.shell.more.title': 'More',
  'user.shell.more.sub': 'Account, help and messages',
  'user.shell.search.placeholder': 'Search bookings, businesses or a booking code',
  'user.shell.search.placeholder_prefixed': 'Search bookings, businesses or {prefix}- code',
  'user.shell.search.placeholder_short': 'Search',
  'user.shell.version': 'Version {version}',
  'user.shell.search.open': 'Search',
  'user.shell.search.shortcut': '⌘K',
  'user.shell.lang.switch': 'Switch language',
  'user.shell.account.menu': 'Account menu',
  'user.shell.offline': 'Offline',
  'user.shell.offline_note': 'No internet connection',
  'user.shell.palette.results': 'Search results',
  'user.shell.palette.esc': 'ESC',
  'user.shell.palette.kind.booking': 'Booking',
  'user.shell.palette.kind.business': 'Business',
  'user.shell.palette.kind.page': 'Page',
  'user.shell.palette.go_to_page': 'Go to page',
  'user.shell.palette.business_page': 'Business page',
  'user.shell.palette.empty_title': 'Nothing found',
  'user.shell.palette.empty_sub': 'Try another name or booking code',
  'user.shell.palette.hint_move': '↑↓ to move',
  'user.shell.palette.hint_open': 'Enter to open',
  'user.shell.notif.title': 'Notifications',
  'user.shell.notif.mark_all': 'Mark all read',
  'user.shell.notif.empty_title': 'All caught up',
  'user.shell.notif.empty_sub': 'Nothing needs you right now',
  'user.shell.notif.error': 'Couldn\u2019t load notifications.',
  'user.shell.notif.unread': '{n, plural, one{# unread notification} other{# unread notifications}}',
  // ---- end DC60 ----
  // ---- DC62 customer My bookings + booking sheet + action layers (FR-C11,
  //      FR-C13, DR-42, DR-46, DR-50) ----
  'user.bk.tabs_label': 'Booking lists',
  'user.bk.tab.upcoming': 'Upcoming',
  'user.bk.tab.verify': 'Being checked',
  'user.bk.tab.past': 'Past',
  'user.bk.tab.cancelled': 'Cancelled',
  'user.bk.search': 'Search service, business or booking code',
  'user.bk.search_short': 'Search bookings',
  'user.bk.empty.upcoming.title': 'No upcoming bookings',
  'user.bk.empty.upcoming.sub': 'New bookings will show up here',
  'user.bk.empty.upcoming.cta': 'Find a business',
  'user.bk.empty.verify.title': 'No payments being checked',
  'user.bk.empty.verify.sub': 'Send a proof and its status shows here',
  'user.bk.empty.past.title': 'No past bookings',
  'user.bk.empty.past.sub': 'Completed bookings collect here',
  'user.bk.empty.cancelled.title': 'No cancelled bookings',
  'user.bk.empty.cancelled.sub': 'Anything you cancel shows here',
  'user.bk.empty.search.title': 'Nothing found',
  'user.bk.empty.search.sub': 'Try another name or booking code',
  'user.bk.row.serial': 'Serial number {n}',
  'user.bk.row.open': 'Open booking {code}',
  'user.bk.not_found': 'We couldn\'t find that booking',
  'user.bk.sheet.label': 'Booking {code}',
  'user.bk.add_calendar': 'Add to calendar',
  'user.bk.calendar_done': 'Calendar file downloaded',
  'user.bk.pass_title': 'Your booking code',
  'user.bk.pass_hint': 'Show this code or QR at the counter',
  'user.bk.qr_tap': 'Tap to enlarge the QR code',
  'user.bk.copy_code': 'Copy the booking code',
  'user.bk.code_copied': 'Code copied',
  'user.bk.trx_copied': 'TRX copied',
  'user.bk.sec.details': 'Details',
  'user.bk.sec.pay': 'Payment',
  'user.bk.sec.biz': 'Business',
  'user.bk.fact.serial': 'Serial number',
  'user.bk.fact.slot': 'Slot',
  'user.bk.fact.session': 'Session',
  'user.bk.fact.show': 'Show',
  'user.bk.fact.checkin': 'Check-in',
  'user.bk.fact.checkout': 'Check-out',
  'user.bk.fact.starts': 'Starts',
  'user.bk.fact.ends': 'Ends',
  'user.bk.fact.seats': 'Seats',
  'user.bk.fact.services': 'Services',
  'user.bk.fact.notes': 'Notes',
  'user.bk.fact.rescheduled': 'Rescheduled',
  'user.bk.fact.moved': '{from} → {to}',
  'user.bk.fact.yes': 'Yes',
  'user.bk.fact.no': 'No',
  'user.bk.next.payment_verification.title': 'What happens now',
  'user.bk.next.payment_verification.body': 'The business is checking your payment proof. It is usually confirmed within a few hours — nothing to do from your side.',
  'user.bk.next.pending.title': 'Next step',
  'user.bk.next.pending.body': 'The business has not confirmed this slot yet. You will get a notification as soon as it does.',
  'user.bk.next.proof_rejected.title': 'Action needed from you',
  'user.bk.next.proof_rejected.body': 'Your payment proof was not accepted. Send it again from the payment section below to keep this booking.',
  'user.bk.next.confirmed.title': 'You are all set',
  'user.bk.next.confirmed.body': 'Arrive about 10 minutes early and show your booking code at the counter.',
  'user.bk.next.running.title': 'Happening today',
  'user.bk.next.running.body': 'This is today\'s booking — show your code at the counter, and call the business if you are running late.',
  'user.bk.next.completed.title': 'All done',
  'user.bk.next.completed.body': 'This visit is complete. You can leave a rating or book it again.',
  'user.bk.next.cancelled.title': 'This booking is cancelled',
  'user.bk.next.cancelled.body': 'The slot is no longer held for you. Book again whenever you like.',
  'user.bk.next.refunded.title': 'Refund on the way',
  'user.bk.next.refunded.body': 'The money goes back to the account you paid from, usually within 3 working days.',
  'user.bk.next.no_show.title': 'Marked as missed',
  'user.bk.next.no_show.body': 'No arrival was recorded for this slot. Raise an issue below if that looks wrong.',
  'user.bk.next.expired.title': 'This booking lapsed',
  'user.bk.next.expired.body': 'It was not completed in time, so the slot was released. Book again whenever you like.',
  'user.bk.pay.headline.unaccepted': '{amount} sent — not accepted yet',
  'user.bk.pay.headline.venue': 'You will pay {amount} at the venue',
  'user.bk.pay.headline.advance': '{paid} advance paid · {due} due at the venue',
  'user.bk.pay.headline.paid': '{amount} paid',
  'user.bk.pay.headline.due': '{amount} advance to send',
  'user.bk.pay.reject_banner': 'Payment not accepted — this booking is still live, send a new proof',
  'user.bk.pay.reason': 'Business\'s reason',
  'user.bk.pay.details': 'Payment details',
  'user.bk.pay.less': 'Show less',
  'user.bk.pay.row.total': 'Total',
  'user.bk.pay.row.discount': 'Discount',
  'user.bk.pay.row.paid': 'Paid',
  'user.bk.pay.row.counter': 'Pay at the counter',
  'user.bk.pay.row.awaiting': 'Awaiting verification',
  'user.bk.pay.trx': 'TRX ID',
  'user.bk.pay.zoom': 'Tap to zoom',
  'user.bk.pay.proof': 'Payment proof',
  'user.bk.pay.step.sent': 'Sent',
  'user.bk.pay.step.checking': 'Being checked',
  'user.bk.pay.step.accepted': 'Accepted',
  'user.bk.pay.step.rejected': 'Rejected',
  'user.bk.pay.receipt': 'Download receipt',
  'user.bk.pay.receipt_done': 'Receipt ready',
  'user.bk.pay.disclaimer': 'We never take or hold this money — you are paying the business directly.',
  'user.bk.refund.pending': '{amount} refund requested · expected within {days} days',
  'user.bk.refund.paid': '{amount} refunded',
  'user.bk.refund.declined': 'Refund request declined',
  'user.bk.case.open': 'Open case',
  'user.bk.case.closed': 'Closed case',
  'user.bk.case.step.filed': 'Filed',
  'user.bk.case.step.review': 'Under review',
  'user.bk.case.step.decision': 'Decision',
  'user.bk.case.kind.paid_but_rejected': 'I paid but it wasn\'t accepted',
  'user.bk.case.kind.complaint': 'Complaint',
  'user.bk.case.kind.refund_claim': 'I want a refund',
  'user.bk.case.kind.no_service': 'I didn\'t get the service',
  'user.bk.biz.directions': 'Directions',
  'user.bk.biz.call': 'Call',
  'user.bk.biz.whatsapp': 'WhatsApp',
  'user.bk.act.reschedule': 'Reschedule',
  'user.bk.act.cancel': 'Cancel',
  'user.bk.act.rebook': 'Book again',
  'user.bk.act.dispute': 'Report a problem',
  'user.bk.act.resubmit': 'Resubmit proof',
  'user.bk.act.send_proof': 'Send proof',
  'user.bk.resched.title': 'Reschedule',
  'user.bk.resched.current': 'Current slot',
  'user.bk.resched.days': 'Pick a day',
  'user.bk.resched.pick': 'Pick a new slot',
  'user.bk.resched.none': 'No free slots on this day',
  'user.bk.resched.next_serial': 'Next serial {n}',
  'user.bk.resched.left': '{n} left',
  'user.bk.resched.approval': 'The business confirms the new time before it is final.',
  'user.bk.resched.confirm': 'Confirm reschedule',
  'user.bk.resched.done': 'Rescheduled — the business has been told',
  'user.bk.resched.taken': 'That slot was just taken — pick another',
  'user.bk.cancel.title': 'Cancel this booking?',
  'user.bk.cancel.pick': 'Pick a reason',
  'user.bk.cancel.reason.plans_changed': 'Plans changed',
  'user.bk.cancel.reason.mistake': 'Booked by mistake',
  'user.bk.cancel.reason.unreachable': 'Couldn\'t reach the business',
  'user.bk.cancel.reason.other': 'Other',
  'user.bk.cancel.note': 'Note (optional)',
  'user.bk.cancel.no_advance': 'Nothing was paid in advance, so there is nothing to refund.',
  'user.bk.cancel.paid_line': 'You paid a {amount} advance.',
  'user.bk.cancel.refund_note': 'The business refunds you directly. We record the request and mediate if needed.',
  'user.bk.cancel.request_refund': 'Request a refund',
  'user.bk.cancel.refund_on': 'Refund will be requested',
  'user.bk.cancel.confirm': 'Cancel booking',
  'user.bk.cancel.done': 'Booking cancelled',
  'user.bk.cancel.refund_done': 'Booking cancelled — refund request recorded',
  'user.bk.dispute.title': 'Report a problem',
  'user.bk.dispute.kinds': 'What went wrong?',
  'user.bk.dispute.statement': 'Tell us what happened',
  'user.bk.dispute.attach': 'Attach my payment proof',
  'user.bk.dispute.attached': 'Your earlier proof has been attached',
  'user.bk.dispute.platform_note': 'We record and review your report, and mediate with the business if needed. You will be told the decision.',
  'user.bk.dispute.submit': 'Submit',
  'user.bk.dispute.done': 'Your report was submitted',
  'user.bk.dispute.conflict': 'A report is already open for this booking',
  'user.bk.proof.title_resubmit': 'Send the proof again',
  'user.bk.proof.title_first': 'Send your payment proof',
  'user.bk.proof.sub': 'Add a new screenshot or the correct TRX number',
  'user.bk.proof.method': 'Paid with',
  'user.bk.proof.shot': 'Attach screenshot',
  'user.bk.proof.shot_added': 'Screenshot attached',
  'user.bk.proof.trx_ph': 'TRX number',
  'user.bk.proof.send': 'Send',
  'user.bk.proof.sent': 'Proof sent — the business will check it',
  'user.bk.image.qr': 'Booking QR code',
  'user.bk.image.proof': 'Payment screenshot',
  // ---- end DC62 ----
  // ---- DC54 website booking stepper + confirmation: template-driven steps, the
  // seven step-3 widgets, guest OTP (guest_booking), hold timer, payment + proof,
  // confirmation with QR / calendar / opt-in account (W §D.7–D.8, FR-C4..C9, C27) ----
  'bkflow.step.services': 'Services',
  'bkflow.step.details': 'Details',
  'bkflow.step.professional': 'Choose professional',
  'bkflow.step.time': 'Time',
  'bkflow.step.review': 'Review & confirm',
  'bkflow.step_of': '{n} / {total}',
  'bkflow.select_services': 'Select services',
  'bkflow.choose_option': 'Choose an option',
  'bkflow.select_many_sub': 'Select one or more services',
  'bkflow.select_one_sub': 'Select one option',
  'bkflow.left': '{n} left',
  'bkflow.free': 'Free',
  'bkflow.on_request': 'Price on request',
  'bkflow.any_pro': 'Any professional',
  'bkflow.any_pro_sub': 'Maximum availability',
  'bkflow.select_placeholder': 'Select…',
  'bkflow.yes': 'Yes',
  'bkflow.no': 'No',
  'bkflow.when.slot': 'Date & time',
  'bkflow.when.hour': 'Date & hour',
  'bkflow.when.range': 'Check-in & check-out',
  'bkflow.when.serial': 'Choose a session',
  'bkflow.when.show': 'Show date & time',
  'bkflow.when.sessions': 'Session date(s)',
  'bkflow.when.block': 'Date & block',
  'bkflow.when.duration': 'Start date & duration',
  'bkflow.when.pickup': 'Pickup & return',
  'bkflow.peak': 'PEAK',
  'bkflow.day_full': 'Fully booked',
  'bkflow.day_full_body': 'Every time on this day is taken — try another day.',
  'bkflow.no_times': 'No times are open right now — contact the business to book.',
  'bkflow.check_in': 'Check-in',
  'bkflow.check_out': 'Check-out',
  'bkflow.pickup': 'Pickup',
  'bkflow.return': 'Return',
  'bkflow.start_date': 'Start date',
  'bkflow.duration': 'Duration',
  'bkflow.block': 'Block',
  'bkflow.select_date': 'Select a date',
  'bkflow.unit.day': 'Day(s)',
  'bkflow.unit.week': 'Week(s)',
  'bkflow.unit.month': 'Month(s)',
  'bkflow.nights_line': '{n} night(s)',
  'bkflow.days_line': '{n} day(s)',
  'bkflow.booked_of': '{booked}/{capacity} booked',
  'bkflow.your_serial': 'Your serial',
  'bkflow.serial_auto': 'Your serial number is assigned automatically when you pick a session.',
  'bkflow.no_blocks': 'No blocks are free on this date — pick another date.',
  'bkflow.contact': 'Contact details',
  'bkflow.your_name': 'Your name',
  'bkflow.your_phone': 'Phone',
  'bkflow.your_email': 'Email (optional)',
  'bkflow.send_otp': 'Send OTP',
  'bkflow.otp_ph': '6-digit code',
  'bkflow.otp_hint': 'Enter the code we sent by SMS.',
  'bkflow.verify_first': 'We verify your number before confirming.',
  'bkflow.phone_verified': 'Phone verified',
  'bkflow.otp_sent': 'If this number can receive SMS, a code is on its way.',
  'bkflow.otp_bad': 'That code didn’t work — check it and try again.',
  'bkflow.payment': 'Payment',
  'bkflow.pay_direct': 'Pay the business directly',
  'bkflow.pay_direct_sub': 'bKash / Nagad / bank transfer to the business, then upload your proof',
  'bkflow.pay_venue': 'Pay at venue',
  'bkflow.pay_venue_full': 'Pay the full amount when you arrive',
  'bkflow.pay_venue_free': 'No advance needed for this reservation',
  'bkflow.pay_gateway': 'Pay securely via {brand}',
  'bkflow.pay_gateway_sub': 'Cards, bKash & Nagad — held safely until your visit',
  'bkflow.pay_gateway_tip': '{brand} Pay is coming soon — payments will be protected by {brand}.',
  'bkflow.coming_soon': 'Coming soon',
  'bkflow.venue_only_title': 'This business takes payment at the venue',
  'bkflow.venue_only_sub': 'Nothing to pay now — settle the full amount when you arrive.',
  'bkflow.pay_to_business': 'Send this to the business — {amount}:',
  'bkflow.trx_id': 'Transaction (TRX) ID',
  'bkflow.trx_ph': 'e.g. 8N7A6XK2PQ',
  'bkflow.upload_proof': 'Upload payment screenshot',
  'bkflow.choose_image': '⇪ Choose image…',
  'bkflow.replace_image': 'Replace screenshot',
  'bkflow.proof_alt': 'Payment proof',
  'bkflow.proof_note': 'Add a TRX ID or a screenshot — the business verifies it within 2 hours and your booking is confirmed.',
  'bkflow.disclaimer': '{brand} does not process, hold, or guarantee direct payments.',
  'bkflow.policy': 'Cancellation policy',
  'bkflow.hold_note': 'We’re holding your booking for',
  'bkflow.hold_expired': 'Your hold expired — times may have changed.',
  'bkflow.start_again': 'Start again',
  'bkflow.total': 'Total',
  'bkflow.advance': 'Advance (pay now)',
  'bkflow.due_venue': 'Due at venue',
  'bkflow.discount': 'Offer discount',
  'bkflow.quantity': 'Quantity',
  'bkflow.qty_units': '{units} × {qty}',
  'bkflow.professional': 'Professional',
  'bkflow.when': 'When',
  'bkflow.no_selection': 'No selection yet',
  'bkflow.bar_sub': '{n, plural, one{# service} other{# services}} · hold {time}',
  'bkflow.bar_sub_nohold': '{n, plural, one{# service} other{# services}}',
  'bkflow.summary': 'Order summary',
  'bkflow.continue': 'Continue',
  'bkflow.back': 'Back',
  'bkflow.confirm': 'Confirm booking',
  'bkflow.confirming': 'Confirming…',
  'bkflow.holding': 'Holding…',
  'bkflow.hint.step1': 'Select an option to continue',
  'bkflow.hint.step2': 'Complete the details to continue',
  'bkflow.hint.step3': 'Pick a date and time to continue',
  'bkflow.hint.contact': 'Complete your contact details',
  'bkflow.hint.otp': 'Verify your phone with the OTP',
  'bkflow.hint.pay': 'Choose how you’ll pay',
  'bkflow.hint.proof': 'Add a TRX ID or upload the payment screenshot',
  'bkflow.err.required': 'This is required.',
  'bkflow.err.invalid_option': 'Pick one of the options.',
  'bkflow.err.below_min': 'This is below the minimum.',
  'bkflow.err.max_exceeded': 'This is above the maximum.',
  'bkflow.err.too_long': 'This is too long.',
  'bkflow.err.invalid': 'This value isn’t valid.',
  'bkflow.err.fields': 'Check the highlighted details and try again.',
  'bkflow.paused': 'This business has paused online bookings — try again later.',
  'bkflow.no_services': 'This business has no services open for online booking yet — check back soon.',
  'bkflow.proof_failed': 'Your booking is placed, but the payment proof didn’t upload — add it below.',
  'bkconf.title': 'Booking placed!',
  'bkconf.code': 'Booking code',
  'bkconf.qr_alt': 'Booking QR code',
  'bkconf.row.business': 'Business',
  'bkconf.row.when': 'When',
  'bkconf.row.serial': 'Your serial',
  'bkconf.row.total': 'Total',
  'bkconf.row.trx': 'TRX ID',
  'bkconf.row.contact': 'Contact',
  'bkconf.note_verifying': 'The business is verifying your payment — usually within 2 hours. You will get an SMS the moment it is confirmed.',
  'bkconf.note_pending': 'The business will confirm your booking shortly.',
  'bkconf.note_pending_venue': 'The business will confirm your booking shortly. Pay at the venue on arrival.',
  'bkconf.guest_title': 'Keep this booking in an account?',
  'bkconf.guest_body': 'You booked as a guest — no account was created. Create one with this number to see this booking, get reminders and rebook faster.',
  'bkconf.create_account': 'Create account',
  'bkconf.account_code_hint': 'We’ll text a sign-in code to {phone}.',
  'bkconf.send_code': 'Send code',
  'bkconf.not_now': 'Not now',
  'bkconf.ready_title': 'Your account is ready',
  'bkconf.ready_body': 'This booking and earlier bookings on this number are now in your account.',
  'bkconf.saved_title': 'Your booking is saved to your account',
  'bkconf.saved_body': 'Follow its status, reminders and receipt from your account.',
  'bkconf.see_booking': 'See your booking',
  'bkconf.add_calendar': 'Add to calendar',
  'bkconf.view_business': 'View business',
  'bkconf.home': 'Home',
  'bkconf.sms_sent': 'SMS confirmation sent to your phone',
  'bkconf.pay_title': 'Send your payment proof',
  'bkconf.pay_body': 'Pay the business, then add the TRX ID or a screenshot so they can verify it.',
  'bkconf.submit_proof': 'Submit proof',
  'bkconf.proof_sent': 'Proof sent — the business will verify it.',
  // ---- end DC54 ----
  // ---- DC92 admin Approvals (business verification, services, payment verification, claims) ----
  'admin.approvals.title': 'Approvals',
  'admin.approvals.sub': 'Business verification and service moderation — every decision is audit-logged.',
  'admin.approvals.hero.waiting': 'Waiting',
  'admin.approvals.hero.businesses': 'Businesses',
  'admin.approvals.hero.services': 'Services',
  'admin.approvals.hero.payments': 'Payment KYC',
  'admin.approvals.hero.overdue': 'Overdue',
  'admin.approvals.hero.flagged': 'Flagged',
  'admin.approvals.hero.median': 'Median decision',
  'admin.approvals.hours': '{n} h',
  'admin.approvals.tabs': 'Approval queues',
  'admin.approvals.tab.merchants': 'Business verification ({n})',
  'admin.approvals.tab.listings': 'Services ({n})',
  'admin.approvals.tab.payments': 'Payment verification ({n})',
  'admin.approvals.tab.claims': 'Claims ({n})',
  'admin.approvals.decided.label': 'Decision filter',
  'admin.approvals.decided.pending': 'Pending',
  'admin.approvals.decided.approved': 'Approved',
  'admin.approvals.decided.rejected': 'Rejected',
  'admin.approvals.decided.all': 'All',
  'admin.approvals.search.merchants': 'Search business, area, city…',
  'admin.approvals.search.other': 'Search service, branch, city…',
  'admin.approvals.filter.sla': 'SLA filter',
  'admin.approvals.filter.sla_all': 'SLA: All',
  'admin.approvals.filter.sla_due': 'Due soon (≤{h} h)',
  'admin.approvals.filter.sla_overdue': 'Overdue',
  'admin.approvals.filter.city': 'City',
  'admin.approvals.filter.city_all': 'All cities',
  'admin.approvals.filter.category': 'Category',
  'admin.approvals.filter.category_all': 'All categories',
  'admin.approvals.filter.flagged': '⚑ Flagged only',
  'admin.approvals.legend.waiting': 'Waiting — SLA live',
  'admin.approvals.legend.paused': 'Waiting on merchant — SLA paused',
  'admin.approvals.legend.overdue': 'Overdue',
  'admin.approvals.legend.flagged': 'Flagged — excluded from bulk',
  'admin.approvals.empty.pending': 'Queue clear — nothing waiting for a decision',
  'admin.approvals.empty.filtered': 'No records match these filters',
  'admin.approvals.sla.left': '{n} h left',
  'admin.approvals.sla.overdue': 'Overdue {n} h',
  'admin.approvals.sla.paused': 'Paused',
  'admin.approvals.state.approved': 'Approved',
  'admin.approvals.state.rejected': 'Rejected',
  'admin.approvals.state.none': 'Not applied',
  'admin.approvals.warn.overdue': '⚠ Past the {h} h review SLA — prioritise',
  'admin.approvals.warn.flag': '⚠ {flag}',
  'admin.approvals.chip.waiting_merchant': 'Waiting on merchant',
  'admin.approvals.toast.biz_approved': '{name} approved — it is live now, audit-logged',
  'admin.approvals.toast.biz_rejected': '{name} rejected — the merchant can fix it and resubmit, audit-logged',
  'admin.approvals.biz.note_required': 'Write the reason — the merchant is shown it word for word.',
  'admin.approvals.biz.area': 'Area',
  'admin.approvals.biz.address': 'Address',
  'admin.approvals.biz.about': 'The merchant added this listing from their dashboard. Check the details; the person’s NID and the business documents are under Merchants and Payment verification.',
  'admin.approvals.biz.approve': 'Approve business',
  'admin.approvals.biz.reject': 'Reject',
  'admin.approvals.biz.reject_title': 'Why is it rejected?',
  'admin.approvals.biz.reject_hint': 'The merchant sees this reason and can fix the business and resubmit.',
  'admin.approvals.biz.reject_send': 'Reject business',
  'admin.approvals.biz.approve_title': 'Approve {name}?',
  'admin.approvals.biz.approve_body': 'The business goes live on the marketplace. One audit entry is written.',
  'admin.approvals.chip.individual': 'Individual',
  'admin.approvals.chip.claim': 'Claim — unclaimed service',
  'admin.approvals.chip.branch_pending': '{name} · Branch pending',
  'admin.approvals.chip.branch_rejected': '{name} · Branch rejected',
  'admin.approvals.chip.path_instant': 'Instant — phone matched',
  'admin.approvals.chip.path_documents': 'Documents',
  'admin.approvals.meta.service': '{price} · {category} · submitted {date}',
  'admin.approvals.meta.payment': '{category} · {area}',
  'admin.approvals.meta.claim': 'Filed by {name} · {phone} · submitted {date}',
  'admin.approvals.meta.service_panel': 'As customers would see it — {price} · {category} · {city} · submitted {date}',
  'admin.approvals.flag.nid': 'Individual business — no trade licence or TIN',
  'admin.approvals.flag.more': 'Waiting on the merchant',
  'admin.approvals.panel.close': 'Close',
  'admin.approvals.panel.submission': 'Submission',
  'admin.approvals.panel.context': 'Context',
  'admin.approvals.panel.decision': 'Decision',
  'admin.approvals.panel.history': 'History',
  'admin.approvals.panel.footnote': 'Every decision writes an immutable audit entry — who, what, when, reason, from → to. No hard deletes; decided records stay under the Decided filter.',
  'admin.approvals.panel.photos': '{n, plural, one{# photo — click to view} other{# photos — click to view}}',
  'admin.approvals.panel.no_photos': 'No photos uploaded',
  'admin.approvals.doc.view': 'view file ↗',
  'admin.approvals.doc.open': 'Open the uploaded file',
  'admin.approvals.doc.hint': 'Per-document status is set by the reviewer — click a chip to cycle Clear / Unreadable / Mismatch.',
  'admin.approvals.doc.cycle': 'Click to set: Clear → Unreadable → Mismatch',
  'admin.approvals.doc.clear': 'Clear',
  'admin.approvals.doc.unreadable': 'Unreadable',
  'admin.approvals.doc.mismatch': 'Mismatch',
  'admin.approvals.doc.unchecked': 'Check',
  'admin.approvals.doc.kind.nid': 'Owner NID',
  'admin.approvals.doc.kind.trade_license': 'Trade licence',
  'admin.approvals.doc.kind.tin': 'TIN certificate',
  'admin.approvals.doc.kind.credential': 'Professional credential',
  'admin.approvals.doc.kind.other': 'Other document',
  'admin.approvals.doc.submitted': 'uploaded',
  'admin.approvals.doc.verified': 'uploaded · verified',
  'admin.approvals.doc.rejected': 'uploaded · rejected',
  'admin.approvals.doc.none': 'No documents uploaded',
  'admin.approvals.doc.alt': '{doc} — uploaded document',
  'admin.approvals.doc.unavailable': 'Preview unavailable — this document cannot be shown here yet.',
  'admin.approvals.doc.zoom_in': 'Zoom in',
  'admin.approvals.doc.zoom_out': 'Zoom out',
  'admin.approvals.fact.category': 'Category',
  'admin.approvals.fact.price': 'Price',
  'admin.approvals.fact.template': 'Template',
  'admin.approvals.fact.city': 'City',
  'admin.approvals.fact.pay_state': 'Payment state',
  'admin.approvals.fact.methods': 'Methods requested',
  'admin.approvals.fact.biz_type': 'Business type',
  'admin.approvals.fact.listing': 'Listing',
  'admin.approvals.fact.claimant': 'Claimant',
  'admin.approvals.fact.claim_phone': 'Phone verified',
  'admin.approvals.fact.phone_match': 'Phone match',
  'admin.approvals.fact.path': 'Path',
  'admin.approvals.fact.waiting': 'Waiting',
  'admin.approvals.value.none': '—',
  'admin.approvals.value.none_yet': 'None yet',
  'admin.approvals.value.individual': 'Individual',
  'admin.approvals.value.company': 'Registered company',
  'admin.approvals.value.match_yes': 'Matches listing number',
  'admin.approvals.value.match_no': 'Does not match',
  'admin.approvals.claim.phone_matched': 'The claimant verified the number already recorded on this listing.',
  'admin.approvals.value.on_request': 'Price on request',
  'admin.approvals.ctx.parent': 'Parent business',
  'admin.approvals.ctx.parent_ok': 'Approved · live',
  'admin.approvals.ctx.parent_pending': 'Business approval pending — booking blocked',
  'admin.approvals.ctx.parent_rejected': 'Rejected — services frozen',
  'admin.approvals.ctx.chip_ok': 'Approved',
  'admin.approvals.ctx.chip_pending': 'Business pending',
  'admin.approvals.ctx.chip_rejected': 'Rejected',
  'admin.approvals.ctx.other_services': 'Other services by this business',
  'admin.approvals.ctx.no_other_services': 'No other services in the queue',
  'admin.approvals.ctx.in_queue': 'In queue',
  'admin.approvals.ctx.payout': 'Payout accounts',
  'admin.approvals.ctx.no_payout': 'No payout accounts submitted',
  'admin.approvals.ctx.claim_history': 'Claims on this listing',
  'admin.approvals.decision.approved': '✓ Approved — {when}',
  'admin.approvals.decision.rejected': '✕ Rejected — {when}',
  'admin.approvals.decision.reason': 'Reason: {reason}',
  'admin.approvals.decision.logged': 'Decision recorded in the immutable audit log.',
  'admin.approvals.history.service_submitted': 'Submitted by merchant',
  'admin.approvals.history.claim_filed': 'Claim filed',
  'admin.approvals.history.changes_requested': 'Changes requested — {reason}',
  'admin.approvals.history.approved': 'Approved',
  'admin.approvals.history.rejected': 'Rejected — {reason}',
  'admin.approvals.history.by': '{when} · {by}',
  'admin.approvals.act.note_ph': 'Note to the applicant — sent verbatim',
  'admin.approvals.act.send_info': 'Send — SLA pauses',
  'admin.approvals.act.info_required': 'Pick at least one document or write a note',
  'admin.approvals.act.reject': '✕ Reject with reason',
  'admin.approvals.act.reason_pick': 'Pick a reason — required',
  'admin.approvals.act.reason_other': 'Other (note required)',
  'admin.approvals.act.reason_note_ph': 'Reason note — the applicant sees this verbatim; recorded in the audit log',
  'admin.approvals.act.reject_send': 'Reject — audit-logged',
  'admin.approvals.act.reason_required': 'A reason is required — it is recorded in the audit log',
  'admin.approvals.act.reason_label': 'Rejection reason',
  'admin.approvals.act.approve_service': '✓ Approve — publish service',
  'admin.approvals.act.approve_blocked': 'Approve — blocked by business status',
  'admin.approvals.act.approve_service_help': 'The service publishes immediately; its booking toggle stays as the merchant set it.',
  'admin.approvals.act.blocked_pending': 'Blocked — business approval pending. Approve the parent business first; Request changes and Reject stay available.',
  'admin.approvals.act.blocked_rejected': 'Blocked — business rejected. This service is frozen; it cannot be approved.',
  'admin.approvals.act.request_changes': 'Request changes — pick what to fix',
  'admin.approvals.act.approve_claim': '✓ Approve claim — transfer ownership',
  'admin.approvals.act.approve_claim_help': 'Ownership transfers to the claimant; the listing keeps its history. Audit-logged.',
  'admin.approvals.act.request_proof': 'Request more proof',
  'admin.approvals.act.claim_info_note': 'The claim stays pending and keeps the listing blocked while you wait.',
  'admin.approvals.reason.prohibited_service': 'Prohibited or illegal service',
  'admin.approvals.reason.policy_violation': 'Policy violation',
  'admin.approvals.reason.misleading': 'Misleading content',
  'admin.approvals.reason.composed': '{code} — {note}',
  'admin.approvals.fix.photos': 'Photos',
  'admin.approvals.fix.documents': 'Documents',
  'admin.approvals.fix.price_proof': 'Price proof',
  'admin.approvals.fix.description': 'Description',
  'admin.approvals.fix.ownership_docs': 'Ownership documents',
  'admin.approvals.fix.utility_bill': 'Utility bill',
  'admin.approvals.fix.trade_licence': 'Trade licence',
  'admin.approvals.pay.approve': '✓ Approve payments',
  'admin.approvals.pay.more': '◷ Ask for more information',
  'admin.approvals.pay.reject': '✕ Reject',
  'admin.approvals.pay.nid': '⚑ Identity required — block application',
  'admin.approvals.pay.current': '{label} — current state',
  'admin.approvals.pay.already': 'This business is already {state}',
  'admin.approvals.pay.approve_title': 'Approve payments for {name}?',
  'admin.approvals.pay.approve_body': 'Direct payment methods go live on this business profile only. Other businesses on the same account are unaffected.',
  'admin.approvals.pay.approve_action': 'Approve payments',
  'admin.approvals.pay.nid_title': 'Block {name} as identity-required?',
  'admin.approvals.pay.nid_body': 'An Individual business without a trade licence or TIN cannot take direct payments. This is a blocked application, not a rejection — the merchant can come back with documents.',
  'admin.approvals.pay.nid_action': 'Mark identity required',
  'admin.approvals.pay.shown': 'Shown to the merchant',
  'admin.approvals.pay.form_more': 'What do you need from the merchant?',
  'admin.approvals.pay.form_reject': 'Why are payments rejected?',
  'admin.approvals.pay.form_hint': 'This text is shown to the merchant word for word.',
  'admin.approvals.pay.ph_more': 'e.g. Upload a clearer photo of the full trade licence page.',
  'admin.approvals.pay.ph_reject': 'The merchant sees this exactly as written',
  'admin.approvals.pay.note_required': 'The note is required — the merchant is shown it word for word.',
  'admin.approvals.pay.send_more': 'Send request — SLA pauses',
  'admin.approvals.pay.send_reject': 'Reject — audit-logged',
  'admin.approvals.pay.foot': 'Approving affects this business profile only. Every outcome writes an audit entry with the text the merchant was shown.',
  'admin.approvals.pay.sub.approved': 'Direct payment methods are live on this business profile.',
  'admin.approvals.pay.sub.blocked_individual': 'Blocked application — not a rejection. Individual businesses need identity documents before payments.',
  'admin.approvals.pay.sub.not_started': 'The merchant has not applied for payments on this business yet.',
  'admin.approvals.pay.sub.under_review': 'Documents submitted — waiting on your decision.',
  'admin.approvals.pay.sub.more_info': 'Waiting on the merchant to send what you asked for.',
  'admin.approvals.pay.sub.rejected': 'Payments are off on this business profile.',
  'admin.approvals.toast.service_approved': '{name} approved — published, audit-logged',
  'admin.approvals.toast.service_changes': 'Merchant notified — SLA paused, audit-logged',
  'admin.approvals.toast.service_rejected': '{name} rejected — merchant sees the reason verbatim, audit-logged',
  'admin.approvals.toast.service_blocked_pending': 'Blocked — approve the parent business first',
  'admin.approvals.toast.service_blocked_rejected': 'Blocked — the parent business was rejected',
  'admin.approvals.toast.pay_approved': 'Payments approved for {name} — audit-logged',
  'admin.approvals.toast.pay_more': 'Information requested — sent to the merchant verbatim',
  'admin.approvals.toast.pay_rejected': 'Payments rejected for {name} — reason sent verbatim',
  'admin.approvals.toast.pay_nid': '{name} blocked pending identity documents — audit-logged',
  'admin.approvals.toast.claim_approved': 'Claim approved — ownership transferred, audit-logged',
  'admin.approvals.toast.claim_info': 'Claimant notified — the claim stays pending, audit-logged',
  'admin.approvals.toast.claim_rejected': 'Claim rejected — the claimant sees the reason verbatim, audit-logged',
  'admin.approvals.toast.failed': 'That did not go through — nothing changed. Try again.',
  'admin.approvals.toast.doc_failed': 'The document status was not saved — try again',
  // ---- end DC92 ----
  // ---- DC50 website home: hero search panels, live counter, offers rail,
  //      popular + browse, trending, carousels, app band, trust stats,
  //      for-business showcase, reviews, browse by area ----
  'dc50.hero.title': 'Book any service in {region}',
  'dc50.hero.sub': 'Hotels, doctors, salons, turfs, venues and more — one search away.',
  'dc50.hero.all_services': 'All services',
  'dc50.hero.current_location': 'Current location',
  'dc50.hero.any_time': 'Any time',
  'dc50.hero.search': 'Search',
  'dc50.hero.bookings_today': 'bookings made today',
  'dc50.hero.get_app': 'Get the app',
  'dc50.hero.scan_download': 'Scan to download',
  'dc50.hero.qr_label': 'QR code — get the {brand} app',
  'dc50.hero.open_search': 'Open search — service, location and date',
  'dc50.hero.service_label': 'Service or business',
  'dc50.hero.location_label': 'Location',
  'dc50.hero.when_label': 'Date & time',
  'dc50.svc.tab_all': 'All',
  'dc50.svc.cats': 'Categories',
  'dc50.svc.biz': 'Businesses',
  'dc50.svc.pros': 'Professionals',
  'dc50.svc.tab_count': '{label} ({n})',
  'dc50.svc.tabs_label': 'Result type',
  'dc50.svc.results_label': 'Suggestions',
  'dc50.svc.no_matches': 'No matches for “{q}”',
  'dc50.svc.try_category': 'Try a category, or check the spelling.',
  'dc50.loc.current': 'Current location',
  'dc50.loc.current_sub': 'Search around where you are',
  'dc50.loc.filter_ph': 'Search city or area…',
  'dc50.loc.recent': 'Recent',
  'dc50.loc.all': 'All of {region}',
  'dc50.loc.city': 'City',
  'dc50.when.today': 'Today',
  'dc50.when.tomorrow': 'Tomorrow',
  'dc50.when.date_label': '{day}, {month} {d}',
  'dc50.when.prev_month': 'Previous month',
  'dc50.when.next_month': 'Next month',
  'dc50.when.select_time': 'Select a time',
  'dc50.when.any': 'Any time',
  'dc50.when.morning': 'Morning 9am–12pm',
  'dc50.when.afternoon': 'Afternoon 12pm–5pm',
  'dc50.when.evening': 'Evening 5pm–12am',
  'dc50.when.custom': 'Custom',
  'dc50.when.short_morning': 'Morning',
  'dc50.when.short_afternoon': 'Afternoon',
  'dc50.when.short_evening': 'Evening',
  'dc50.when.from': 'From',
  'dc50.when.to': 'to',
  'dc50.when.apply': 'Apply',
  'dc50.mq.playing': 'Playing',
  'dc50.mq.paused': 'Paused',
  'dc50.offers.title': "Today's offers.",
  'dc50.offers.context': 'Live deals from across the country — updated today',
  'dc50.offers.see_all': 'See all offers ›',
  'dc50.offers.filter_label': 'Filter offers',
  'dc50.offers.chip_all': 'All',
  'dc50.offers.chip_food': 'Food',
  'dc50.offers.chip_beauty': 'Beauty & Wellness',
  'dc50.offers.chip_stays': 'Hotels & Resorts',
  'dc50.offers.chip_ending': 'Ending soon',
  'dc50.offers.chip_bookable': 'Bookable only',
  'dc50.offers.autoplay': 'Auto-play',
  'dc50.offers.go_to': 'Go to offer {n}',
  'dc50.offers.empty': 'No live deals in this category right now — check back soon.',
  'dc50.offers.alerts': 'Deal alerts for your favorite categories — free in the app.',
  'dc50.card.sponsored': 'Sponsored',
  'dc50.card.verified': 'Verified',
  'dc50.card.price_from': 'From {price}',
  'dc50.card.free_reservation': 'Free reservation',
  'dc50.card.save': 'Save {name}',
  'dc50.card.unsave': 'Remove {name} from saved',
  'dc50.next.title': 'You will find your next plan here.',
  'dc50.next.sub':
    'Dive into thousands of local businesses — doctors, dining, stays and more — all bookable in one place.',
  'dc50.popular.title': 'Popular near you.',
  'dc50.popular.places_in': '{n}+ places in {region}',
  'dc50.popular.match_count': '{n} places match your filters',
  'dc50.popular.all_listings': 'All listings ›',
  'dc50.popular.collapse': 'Collapse ‹',
  'dc50.popular.category_label': 'Category',
  'dc50.popular.location_label': 'Location',
  'dc50.popular.sort_label': 'Sort',
  'dc50.popular.all_categories': 'All categories',
  'dc50.popular.all_locations': 'All locations',
  'dc50.popular.sort_relevance': 'Recommended',
  'dc50.popular.sort_rating': 'Top rated',
  'dc50.popular.sort_price': 'Price',
  'dc50.popular.verified_only': 'Verified only',
  'dc50.popular.result_one': '{n} result',
  'dc50.popular.results': '{n} results',
  'dc50.popular.rankings_note': 'Rankings are organic except cards marked Sponsored.',
  'dc50.popular.empty': 'No places match those filters — try widening them.',
  'dc50.popular.load_more': 'Load more',
  'dc50.trend.title': 'Trending right now.',
  'dc50.trend.sub': 'The {n} most-booked businesses — updated today',
  'dc50.trend.cta': 'See all trending nearby →',
  'dc50.trend.rank': '#{n} trending',
  'dc50.trend.play_pause': 'Play / pause',
  'dc50.trend.go_to': 'Go to trending #{n}',
  'dc50.car.recommended': 'Recommended',
  'dc50.car.new': 'New to {brand}',
  'dc50.car.weekend': 'Things to do this weekend.',
  'dc50.car.weekend_sub': 'Plans near you — updated every Thursday',
  'dc50.car.see_all': 'See all ›',
  'dc50.car.reco_note': 'Recommendations are organic except cards marked Sponsored.',
  'dc50.car.prev': 'Scroll back',
  'dc50.car.next': 'Scroll forward',
  'dc50.app.coming_soon': 'Coming soon',
  'dc50.app.title': 'Download the {brand} app',
  'dc50.app.sub': 'Book any service across {region} from your phone',
  'dc50.app.app_store': 'App Store',
  'dc50.app.google_play': 'Google Play',
  'dc50.app.notify': 'Notify me',
  'dc50.app.link_note': '{url} — we’ll SMS you the link at launch',
  'dc50.app.available_on': 'Available on',
  'dc50.app.scan_notify': 'Scan to get notified',
  'dc50.app.leave_number': '{url} — leave your number, we’ll SMS you at launch',
  'dc50.app.preview_page': 'Preview the page',
  'dc50.app.qr_label': 'QR code for the {brand} app page',
  'dc50.app.reviews': '{n} reviews',
  'dc50.app.open_now': 'Open now · {hours}',
  'dc50.app.instant': 'Instant booking',
  'dc50.app.pay_accepted': '{method} accepted',
  'dc50.app.tab_services': 'Services',
  'dc50.app.tab_team': 'Team',
  'dc50.app.tab_reviews': 'Reviews',
  'dc50.app.tab_details': 'Details',
  'dc50.app.book': 'Book',
  'dc50.app.book_now': 'Book now',
  'dc50.app.services_available': '{n} services available',
  'dc50.app.tablet_query': '{cat} · {where}',
  'dc50.app.sort': 'Sort',
  'dc50.app.type': 'Type',
  'dc50.app.nearby_count': '{n} businesses nearby',
  'dc50.trust.title': '{region}’s top destination for bookings',
  'dc50.trust.sub': 'One platform for every service. Trusted across the country.',
  'dc50.trust.lakh': '{n} lakh+',
  'dc50.trust.bookings_made': 'bookings made on {brand}',
  'dc50.trust.merchants': 'merchant businesses',
  'dc50.trust.districts': 'districts covered',
  'dc50.trust.professionals': 'professionals',
  'dc50.fb.title': '{brand} for business',
  'dc50.fb.para':
    'Grow your business with {region}’s booking platform. Calendar, bookings, payments to your own bKash or Nagad, and clients — all in one place.',
  'dc50.fb.cta': 'Find out more',
  'dc50.fb.excellent': 'Excellent {score}',
  'dc50.fb.reviews_line': 'Over {n} reviews from merchants on {brand}',
  'dc50.fb.working_staff': 'Working staff',
  'dc50.fb.add_new': 'Add new',
  'dc50.fb.hour_am': '{time} am',
  'dc50.fb.hour_pm': '{time} pm',
  'dc50.reviews.title': 'What customers say',
  'dc50.reviews.on_biz': 'on {biz}',
  'dc50.reviews.stars': '{n} out of {max} stars',
  'dc50.area.title': 'Browse by area',
  'dc50.area.link': '{cat} in {area}',
  'dc50.area.cat_salon': 'Salons',
  'dc50.area.cat_doctor': 'Doctors',
  'dc50.area.cat_turf': 'Turfs',
  'dc50.area.cat_restaurant': 'Restaurants',
  'dc50.area.cat_hotel': 'Hotels',
  'dc50.area.cat_coworking': 'Co-working',
  'dc50.unit.night': '/night',
  'dc50.unit.hr': '/hr',
  'dc50.unit.day': '/day',
  'dc50.unit.visit': '/visit',
  'dc50.unit.session': '/session',
  'dc50.unit.class': '/class',
  'dc50.unit.table': '/table',
  'dc50.unit.person': '/person',
  'dc50.unit.month': '/mo',
  // DC122 — Bangla sweep: strings the bn leak sweep found hardcoded or unlocalised.
  'dc122.unit.month': '/month',
  'dc122.unit.week': '/week',
  'dc122.unit.ticket': '/ticket',
  'dc122.unit.booking': '/booking',
  'dc122.unit.child': '/child',
  'dc122.unit.sitting': '/sitting',
  'dc122.unit.scan': '/scan',
  // /DC122
  // ---- end DC50 ----
  // ---- DC52 (website offers) ----
  'dc52.search_ph': 'Search offers — biryani, spa day, sea view…',
  'dc52.search': 'Search',
  'dc52.filters': 'Filters',
  'dc52.discount': 'Discount',
  'dc52.any': 'Any',
  'dc52.pct_plus': '{n}%+',
  'dc52.ending_chip': 'Ending soon',
  'dc52.bookable_chip': 'Bookable now',
  'dc52.sort_label': 'Sort',
  'dc52.sort.relevance': 'Sort: Recommended',
  'dc52.sort.discount': 'Sort: Biggest discount',
  'dc52.sort.price': 'Sort: Lowest price',
  'dc52.sort.rating': 'Sort: Top rated',
  'dc52.sort.newest': 'Sort: Newest',
  'dc52.clear_all': 'Clear all',
  'dc52.done': 'Done',
  'dc52.count': '{n} offers',
  'dc52.count_one': '{n} offer',
  'dc52.summary.discount': '{n}%+ off',
  'dc52.summary.ending': 'ending soon',
  'dc52.summary.bookable': 'bookable now',
  'dc52.summary.query': '“{q}”',
  'dc52.date_label': 'Day of the visit',
  'dc52.any_date': 'Any day',
  'dc52.summary.date': 'on {date}',
  'dc52.hero.title': 'Today\'s best deals near you.',
  'dc52.hero.sub': '{n} live offers in {area}',
  'dc52.tiles_label': 'Offer categories',
  'dc52.tile.all': 'All',
  'dc52.featured_label': 'Featured offers',
  'dc52.play_pause': 'Play / pause',
  'dc52.go_to': 'Go to offer {n}',
  'dc52.off': '{n}% OFF',
  'dc52.off_amount': '{amount} OFF',
  'dc52.deal': 'Deal',
  'dc52.more.title': 'More deals around town.',
  'dc52.more.sub': 'Shared by the businesses themselves. Contact them directly to get the deal.',
  'dc52.cta.listing': 'See the place',
  'dc52.cta.call': 'Call',
  'dc52.cta.whatsapp': 'WhatsApp',
  'dc52.cta.website': 'Visit website',
  'dc52.cta.facebook': 'Open Facebook page',
  'dc52.cta.link': 'Learn more',
  'dc52.sponsored': 'Sponsored',
  'dc52.verified': 'Verified',
  'dc52.applies.business': 'Every service here',
  'dc52.applies.account': 'All of this merchant’s listings',
  'dc52.book_now': 'Book now',
  'dc52.view_details': 'View details',
  'dc52.ends_today': 'Ends today',
  'dc52.ends_tomorrow': 'Ends tomorrow',
  'dc52.days_left': '{n} days left',
  'dc52.see_all': 'See all ›',
  'dc52.big.title': 'Biggest discounts.',
  'dc52.big.sub': 'Deepest cuts near you today.',
  'dc52.ending.title': 'Ending soon.',
  'dc52.new.title': 'Just added.',
  'dc52.new.sub': 'Fresh deals — newest first.',
  'dc52.new.tag': 'New',
  'dc52.posted_now': 'Just now',
  'dc52.posted_hours': '{n}h ago',
  'dc52.posted_days': '{n}d ago',
  'dc52.spot.title': 'A place to put your offer in the spotlight.',
  'dc52.spot.sub': 'Feature your offer in the slider and in promoted positions across the Offers feed — always clearly labelled.',
  'dc52.spot.cta': 'Feature from {price}/day',
  'dc52.spot.cta_no_price': 'Feature your offer',
  'dc52.spot.pricing': 'See pricing',
  'dc52.spot.tile_alt': 'Offer tile',
  'dc52.grid.title': 'All offers near you.',
  'dc52.grid.empty': 'No offers match — try clearing a filter.',
  'dc52.grid.clear_filters': 'Clear filters',
  'dc52.grid.load_more': 'Load more',
  'dc52.alerts.title': 'Never miss a deal.',
  'dc52.alerts.sub': 'Get free alerts when businesses near you post new offers — in every category, or just the one you pick.',
  'dc52.alerts.sub_category': 'Get free alerts when businesses near you post new {category} offers.',
  'dc52.alerts.cta': 'Turn on deal alerts',
  'dc52.alerts.note': 'No account needed — just your phone number.',
  'dc52.alerts.phone': 'Your phone number',
  'dc52.alerts.consent': 'I agree to receive offer messages.',
  'dc52.alerts.submit': 'Notify me',
  'dc52.alerts.done': 'Deal alerts on ✓ — we will message you when new offers go live.',
  'dc52.popup.open': 'Open offer: {offer}',
  'dc52.popup.view_biz': 'View business',
  'dc52.popup.full_image': 'Full image',
  'dc52.popup.validity': 'Validity',
  'dc52.popup.valid_till': 'Valid till {date}',
  'dc52.popup.valid_range': '{from} – {to}',
  'dc52.popup.location': 'Location',
  'dc52.popup.phone': 'Phone',
  'dc52.popup.copy': 'Copy',
  'dc52.popup.number_copied': 'Number copied',
  'dc52.popup.link_copied': 'Offer link copied',
  'dc52.popup.copy_failed': 'Select and copy the text',
  'dc52.popup.not_bookable': 'Not bookable online yet — redeem at the venue',
  'dc52.popup.share': 'Share offer',
  'dc52.popup.disclaimer': 'Redeem and pay directly with the business — the platform does not process, hold, or guarantee this payment.',
  // ---- end DC52 ----
  // ---- DC71 merchant Home ----
  'dc71.greeting.morning': 'Good morning, {name}',
  'dc71.greeting.afternoon': 'Good afternoon, {name}',
  'dc71.greeting.evening': 'Good evening, {name}',
  'dc71.subtitle': 'Today: {n, plural, one{# booking} other{# bookings}} · {p, plural, one{# payment} other{# payments}} to verify',
  'dc71.checklist.title': 'Get set up',
  'dc71.checklist.progress': '{n}/{total} steps done',
  'dc71.checklist.dismiss': 'Dismiss',
  'dc71.checklist.dismiss_title': 'Dismiss the checklist?',
  'dc71.checklist.dismiss_body': 'The card will stop showing on Home.',
  'dc71.checklist.start': 'Start',
  'dc71.checklist.done': 'Done',
  'dc71.checklist.step.create_business': 'Create your business',
  'dc71.checklist.step.add_service': 'Add your first service',
  'dc71.checklist.step.share_link': 'Share your booking link',
  'dc71.checklist.step.turn_on_payments': 'Turn on payments',
  'dc71.overview': 'At a glance',
  'dc71.period.label': 'Period',
  'dc71.period.1d': 'Yesterday',
  'dc71.period.7d': '7 days',
  'dc71.period.30d': '30 days',
  'dc71.period.12m': '12 months',
  'dc71.vs.1d': 'vs day before',
  'dc71.vs.7d': 'vs previous 7 days',
  'dc71.vs.30d': 'vs previous 30 days',
  'dc71.vs.12m': 'vs last year',
  'dc71.kpi.bookings': 'Bookings',
  'dc71.kpi.cancelled': 'cancelled {n}',
  'dc71.kpi.completed': 'Completed',
  'dc71.kpi.completion': '{pct}% completion rate',
  'dc71.kpi.revenue': 'Revenue',
  'dc71.kpi.avg': 'avg {amount} per booking',
  'dc71.kpi.rating': 'Rating',
  'dc71.kpi.reviews': 'from {n, plural, one{# review} other{# reviews}}',
  'dc71.kpi.no_reviews': 'No reviews yet',
  'dc71.kpi.pct': '{n}%',
  'dc71.attention.title': 'Needs your attention',
  'dc71.attention.all_good': 'All clear',
  'dc71.attention.range': '{from}–{to} of {total}',
  'dc71.attention.prev': 'Previous',
  'dc71.attention.next': 'Next',
  'dc71.att.proofs.title': '{n, plural, one{# payment proof to verify} other{# payment proofs to verify}}',
  'dc71.att.proofs.detail': 'Oldest is {n, plural, =0{under an hour} one{# hour} other{# hours}} old',
  'dc71.att.proofs.action': 'Verify',
  'dc71.att.fix.title': '{n, plural, one{Fix # service} other{Fix # services}}',
  'dc71.att.fix.detail': 'Changes requested',
  'dc71.att.fix.action': 'Open',
  'dc71.att.offer.title': '{n, plural, one{# offer is ending} other{# offers are ending}}',
  'dc71.att.offer.detail': '{n, plural, =0{Ends today} one{# day left} other{# days left}}',
  'dc71.att.offer.action': 'Offers',
  'dc71.att.kyc.title': '{name} application submitted',
  'dc71.att.kyc.detail': 'Under review, nothing to do',
  'dc71.att.kyc.chip': 'In review',
  'dc71.schedule.tabs': 'Businesses',
  'dc71.schedule.all': 'All',
  'dc71.schedule.title': 'Today\'s schedule',
  'dc71.schedule.prev_day': 'Previous day',
  'dc71.schedule.next_day': 'Next day',
  'dc71.schedule.today': 'Today',
  'dc71.schedule.tomorrow': 'Tomorrow',
  'dc71.schedule.meta': '{n, plural, one{# booking} other{# bookings}}',
  'dc71.schedule.meta_need': '{count} · {n} need you',
  'dc71.schedule.see_all': 'See all',
  'dc71.schedule.review': 'Review',
  'dc71.schedule.empty': 'Nothing left today',
  'dc71.schedule.coming_up': 'Coming up',
  'dc71.schedule.empty_day': 'No bookings on this day',
  'dc71.schedule.new_booking': 'New booking',
  'dc71.schedule.walk_in': 'Walk-in',
  'dc71.schedule.open': 'Open booking {code}',
  'dc71.schedule.status.payment_verification': 'Payment check',
  'dc71.schedule.status.pending': 'Pending',
  'dc71.biz.chip': '{n, plural, one{# business} other{# businesses}} · Total today {total}',
  'dc71.biz.see_all': 'See all',
  'dc71.biz.see_all_n': 'See all {n}',
  'dc71.biz.count_cap': 'bookings today',
  'dc71.biz.revenue_cap': 'expected today',
  'dc71.biz.to_review': '{n} to review',
  'dc71.biz.all_clear': 'All clear',
  'dc71.biz.next': 'Next {time} · {service}',
  'dc71.biz.nothing_left': 'Nothing left today',
  'dc71.biz.add': 'Add business',
  'dc71.biz.add_help': 'Run another branch from this dashboard',
  'dc71.biz.at_cap': 'Upgrade',
  'dc71.chart.title': 'Bookings · last 30 days',
  'dc71.chart.details': 'Details',
  'dc71.chart.series': 'Chart series',
  'dc71.chart.all': 'All',
  'dc71.chart.single': 'This month',
  'dc71.chart.avg': 'Avg {n}/day',
  'dc71.chart.best': 'Best {n} · {date}',
  'dc71.chart.point': '{n} · {label}',
  'dc71.chart.empty': 'Bookings will show here after the first one',
  'dc71.free.kicker': 'Free plan limit',
  'dc71.free.promo_title': '{months} months of unlimited bookings · ends {date}',
  'dc71.free.promo_help': 'After the promo it is {cap} bookings a month; the rest arrive blurred.',
  'dc71.free.cap_title': '{used}/{cap} bookings this month · {hidden} hidden',
  'dc71.free.cap_help': 'Customers can still book normally — the time is held.',
  'dc71.free.blurred': 'Free plans get {cap} bookings a month. {n, plural, one{# booking is} other{# bookings are}} blurred — name and phone hidden, the time slot still held.',
  'dc71.free.hidden_row': 'Hidden booking',
  'dc71.free.upgrade': 'Upgrade',
  'dc71.share.title': 'Your booking link',
  'dc71.share.hint': 'Put this link on your Facebook page or send it to a customer.',
  'dc71.share.scope': 'Which link to send',
  'dc71.share.send': 'Copy and send',
  'dc71.share.service': 'Service',
  'dc71.share.whole': 'Whole business · all services',
  'dc71.share.only': 'Only {name}',
  'dc71.share.copy': 'Copy',
  'dc71.share.copied': 'Link copied',
  'dc71.share.facebook': 'Facebook',
  'dc71.share.whatsapp': 'WhatsApp',
  'dc71.share.instagram': 'Instagram',
  'dc71.share.telegram': 'Telegram',
  'dc71.share.sms': 'SMS',
  'dc71.share.email': 'Email',
  'dc71.share.qr_title': 'QR check-in',
  'dc71.share.qr_all': 'All services',
  'dc71.share.qr_help': 'The customer scans this code on arrival',
  'dc71.share.qr_download': 'Download PNG',
  // ---- end DC71 ----
  // ---- DC74 merchant Services (Merchant Dashboard M:1131-1821, M:4906-5229; L.*.s) ----
  'dc74.tabs_label': 'Services view',
  'dc74.tab.list': 'Services',
  'dc74.tab.reviews': 'Reviews',
  'dc74.new': 'New service',
  'dc74.cap.near': 'services — near the limit',
  'dc74.cap.upgrade': 'Upgrade',
  'dc74.cap.reached': 'Limit reached — upgrade',
  'dc74.zero.title': 'Businesses like yours usually offer these…',
  'dc74.customer_fills': 'What the customer fills in',
  'dc74.blank': 'Start from blank',
  'dc74.blank_help': 'Set every field yourself',
  'dc74.attn.one': '{n} service needs changes — {name}',
  'dc74.attn.many': '{n} services need attention',
  'dc74.attn.view': 'View',
  'dc74.group': '{name} · {n}',
  'dc74.row.edit': 'Edit',
  'dc74.row.more': 'More actions for {name}',
  'dc74.kebab.edit': 'Edit',
  'dc74.kebab.copy': 'Copy to another business',
  'dc74.copy.title': 'Copy “{name}” to…',
  'dc74.copy.body': 'A service belongs to one business. The copy starts as a draft in the business you pick — open it there and submit it for review.',
  'dc74.copy.done': 'Copied to {business} as a draft — switch to it to submit for review.',
  'dc74.kebab.duplicate': 'Duplicate',
  'dc74.kebab.share': 'Share',
  'dc74.kebab.pause': 'Pause',
  'dc74.kebab.resume': 'Resume',
  'dc74.kebab.archive': 'Archive',
  'dc74.kebab.availability': 'Where it\'s offered',
  'dc74.avail.title': 'Where “{name}” is offered',
  'dc74.avail.body': 'Your services are yours — offer each one at any of your listings. Pausing stops new bookings at that listing only.',
  'dc74.avail.at': 'Available at',
  'dc74.avail.none': 'Not offered anywhere yet',
  'dc74.avail.on': 'Offered here',
  'dc74.avail.off': 'Not offered here',
  'dc74.avail.paused': 'Paused here',
  'dc74.avail.home': 'created here',
  'dc74.avail.offer_at': 'Offer at {name}',
  'dc74.avail.pause': 'Pause here',
  'dc74.avail.resume': 'Resume here',
  'dc74.avail.pause_at': 'Pause at {name}',
  'dc74.avail.resume_at': 'Resume at {name}',
  'dc74.toast.avail_saved': 'Availability updated',
  'dc74.done': 'Done',
  'dc74.price_on_request': 'Price on request',
  'dc74.untitled': 'Untitled service',
  'dc74.load_error': 'Couldn\'t load your services.',
  'dc74.retry': 'Try again',
  'dc74.toast.duplicated': 'Duplicated as a draft',
  'dc74.toast.paused': 'Service paused',
  'dc74.toast.resumed': 'Service resumed',
  'dc74.toast.archived': 'Service archived',
  'dc74.toast.saved': 'Saved',
  'dc74.toast.saved_review': 'Saved — back under review',
  'dc74.toast.resubmitted': 'Resubmitted',
  'dc74.toast.draft_saved': 'Draft saved',
  'dc74.toast.copied': 'Link copied',
  'dc74.toast.failed': 'Something went wrong — try again',
  'dc74.toast.cap': 'Your plan\'s service limit is reached',
  'dc74.toast.upcoming': 'This service has upcoming bookings — it can\'t be archived yet',
  'dc74.toast.pending': 'An earlier change is still under review',
  'dc74.toast.not_found': 'This service isn\'t in your list',
  'dc74.archive.title': 'Archive this service?',
  'dc74.archive.body': 'Nothing is deleted — it leaves your list and its booking history stays.',
  'dc74.archive.yes': 'Yes, archive',
  'dc74.cancel': 'Cancel',
  'dc74.ed.live': 'Live on {brand}',
  'dc74.ed.close': 'Close',
  'dc74.ed.prev': 'Previous service',
  'dc74.ed.next': 'Next service',
  'dc74.ed.sections': 'Sections',
  'dc74.sec.photos': 'Photos or icon',
  'dc74.sec.details': 'Details',
  'dc74.sec.booking': 'Booking',
  'dc74.sec.pro': 'Professional',
  'dc74.sec.visibility': 'Visibility',
  'dc74.sec.share': 'Share',
  'dc74.ed.fix': 'Fix it',
  'dc74.ed.resubmit': 'Resubmit',
  'dc74.ph.photos': 'Photos',
  'dc74.ph.icon': 'Icon',
  'dc74.ph.cover': 'Cover',
  'dc74.ph.add': 'Add photo',
  'dc74.ph.guard': 'Only upload photos you took or own — photos carrying another organisation’s logo get rejected',
  'dc74.ph.helper': 'Services with photos get 3× more bookings',
  'dc74.ph.replace': 'New photos replace the current ones when you save',
  'dc74.ph.search': 'Search: blood, tooth, hair…',
  'dc74.ph.tint': 'Tile colour',
  'dc74.ph.tint_n': 'Colour {n}',
  'dc74.ph.no_match': 'No match — try another word',
  'dc74.ph.photo_n': 'Photo {n}',
  'dc74.f.name_bn': 'Name (Bangla)',
  'dc74.f.name_en': 'Name (English)',
  'dc74.f.desc_bn': 'Description (Bangla)',
  'dc74.f.desc_en': 'Description (English)',
  'dc74.f.chars': '{n} characters',
  'dc74.f.price': 'Price ({symbol})',
  'dc74.f.unit': 'Unit',
  'dc74.bk.help': 'When on, customers pick a time and book; when off only the info shows.',
  'dc74.bk.on': 'Booking on',
  'dc74.bk.fields': 'Scheduling fields',
  'dc74.bk.fields_hint': 'what customers fill in',
  'dc74.bk.template_note': 'Set by your business category',
  'dc74.bk.duration': 'Duration (min)',
  'dc74.bk.duration_hint': 'Length of each booking',
  'dc74.bk.capacity': 'Capacity',
  'dc74.bk.capacity_hint': 'Max at the same time',
  'dc74.bk.buffer': 'Buffer (min)',
  'dc74.bk.buffer_hint': 'Gap between bookings',
  'dc74.bk.decrease': 'Decrease',
  'dc74.bk.increase': 'Increase',
  'dc74.bk.advance': 'Advance',
  'dc74.bk.advance_hint': 'Collected when booking',
  'dc74.bk.advance_hint_default': 'Collected when booking · your business default',
  'dc74.bk.adv_full': 'Full',
  'dc74.pro.new': 'New professional',
  'dc74.pro.attached': 'Attached',
  'dc74.pro.attach': 'Attach',
  'dc74.pro.remove': 'Remove',
  'dc74.pro.none': 'No professional attached — customers just see the service info',
  'dc74.pro.show': 'Show profile',
  'dc74.pro.show_help': 'Turn this off when you cannot say in advance who will serve.',
  'dc74.pro.team_link': 'Full roster & editing → Team',
  'dc74.vis.market': 'Marketplace',
  'dc74.vis.market_help': 'Anyone can find it on {brand}',
  'dc74.vis.private': 'Private',
  'dc74.vis.private_help': 'Only people with your link',
  'dc74.vis.private_later': 'Coming later',
  'dc74.sh.link': 'Booking link',
  'dc74.sh.qr': 'Download QR',
  'dc74.sh.embed_show': 'Show embed code',
  'dc74.sh.embed_hide': 'Hide embed code',
  'dc74.sh.embed_label': 'Embed code for your website',
  'dc74.sh.embed_cta': 'Book Now',
  'dc74.ed.archive_hint': 'Nothing is deleted — archived services keep their booking history',
  'dc74.ed.archive': 'Archive',
  'dc74.ed.re_review': 'After this change the service goes back under review (a few minutes)',
  'dc74.ed.dirty': '{n, plural, one{# unsaved change} other{# unsaved changes}}',
  'dc74.ed.save': 'Save changes',
  'dc74.guard.title': 'Unsaved changes',
  'dc74.guard.body': 'What do you want to do before switching?',
  'dc74.guard.discard': 'Discard changes',
  'dc74.guard.back': 'Go back',
  'dc74.wz.step1': 'What kind of service?',
  'dc74.wz.step2': 'Name and price',
  'dc74.wz.step3': 'Photos or icon',
  'dc74.wz.step4': 'Take bookings?',
  'dc74.wz.progress': 'Step {n} of {total}',
  'dc74.wz.pick_biz': 'Which business?',
  'dc74.wz.no_template': 'No booking template is set for this category yet',
  'dc74.wz.one_line': 'One-line description (optional)',
  'dc74.wz.price_later': 'I’ll set the price later',
  'dc74.wz.price_later_hint': 'The public card shows “Price on request”',
  'dc74.wz.photos': 'Add photos',
  'dc74.wz.photos_help': 'Your own photos work best',
  'dc74.wz.icon': 'Pick an icon',
  'dc74.wz.icon_help': 'No photo? Choose a fitting icon instead',
  'dc74.wz.skip': 'You can skip this — add one later from the service page’s “Photos or icon” section.',
  'dc74.wz.preview': 'How it will look',
  'dc74.wz.in_list': 'In your list',
  'dc74.wz.on_card': 'On the customer card',
  'dc74.wz.booking_help': 'Customers pick a time and book',
  'dc74.wz.days': 'Which days',
  'dc74.wz.hours': 'Hours',
  'dc74.wz.to': 'to',
  'dc74.wz.closed_day': '{day} — closed',
  'dc74.wz.slot': 'Minutes per slot',
  'dc74.wz.serials': 'Serials per day',
  'dc74.wz.hours_note': 'Days and hours follow your business hours',
  'dc74.wz.deep': 'Detailed settings live on the service page',
  'dc74.wz.booking_off': 'Booking off',
  'dc74.wz.back': 'Back',
  'dc74.wz.next': 'Next step',
  'dc74.wz.submit': 'Submit',
  'dc74.wz.close_title': 'Unfinished service',
  'dc74.wz.close_body': 'You can keep what you typed as a draft and finish it later from the list.',
  'dc74.wz.save_draft': 'Save as draft',
  'dc74.wz.discard': 'Discard',
  'dc74.wz.done_title': 'Sent for review',
  'dc74.wz.done_body': 'Usually live within minutes — it’s already in your list',
  'dc74.wz.view': 'View the service',
  'dc74.wz.again': 'Add another',
  'dc74.icon_cat.all': 'All',
  'dc74.icon_cat.med': 'Medical',
  'dc74.icon_cat.beauty': 'Beauty',
  'dc74.icon_cat.fit': 'Fitness',
  'dc74.icon_cat.food': 'Food',
  'dc74.icon_cat.edu': 'Education',
  'dc74.icon_cat.travel': 'Travel',
  'dc74.icon_cat.repair': 'Repair',
  'dc74.icon_cat.other': 'Other',
  // ---- end DC74 ----
  // ---- DC61 customer Home (Home.dc.html, bd-core.js L.home) ----
  'user.home.greet': 'Assalamu alaikum, {name}',
  'user.home.today_count': '{n, plural, one{# booking today} other{# bookings today}}',
  'user.home.today_none': 'Nothing on today',
  'user.home.new_booking': 'New booking',
  'user.home.hero.eyebrow': 'Next booking',
  'user.home.hero.payment_verification': 'Payment being checked — the business is reviewing your proof',
  'user.home.hero.confirmed': 'Confirmed — just arrive on time',
  'user.home.hero.pending': 'Waiting — the business has to confirm',
  'user.home.hero.rejected': 'Payment not accepted — send the proof again',
  'user.home.hero.running': 'Happening today — arrive on time',
  'user.home.hero.resubmit': 'Resubmit proof',
  'user.home.hero.code': 'Booking code',
  'user.home.hero.copy_hint': 'Tap to copy the code',
  'user.home.hero.copied': 'Code copied',
  'user.home.hero.qr': 'Check-in code',
  'user.home.hero.qr_tap': 'Tap to enlarge',
  'user.home.hero.details': 'Details',
  'user.home.hero.directions': 'Directions',
  'user.home.hero.call': 'Call',
  'user.home.hero.none': 'No upcoming booking',
  'user.home.hero.empty_line': 'Once you book, everything shows up here.',
  'user.home.hero.find': 'Find a service',
  'user.home.att.title': 'Something needs you',
  'user.home.att.proof': 'Payment not accepted',
  'user.home.att.rate_yesterday': 'Rate yesterday\'s service',
  'user.home.att.rate': 'Rate your service',
  'user.home.att.rate_cta': 'Rate',
  'user.home.att.more': '{n} more →',
  'user.home.next_up': 'Next up',
  'user.home.all_bookings': 'All bookings →',
  'user.home.offers.title': 'New offers near you',
  'user.home.offers.all': 'All offers →',
  'user.home.offers.off': 'off',
  'user.home.offers.view': 'View offer',
  'user.home.offers.ends_today': 'Ends today',
  'user.home.offers.days_left': '{n, plural, one{# day left} other{# days left}}',
  'user.home.offers.prev': 'Previous offers',
  'user.home.offers.next': 'More offers',
  // ---- end DC61 ----
  // ---- DC53 (website business page + claim flow) ----
  'dc53.save_signin': 'Sign in to save businesses',
  'dc53.claim.unclaimed': 'Unclaimed',
  'dc53.claim.under_review': 'Claim under review',
  'dc53.claim.tip_title': 'Why does this say Unclaimed?',
  'dc53.claim.tip_body': 'This listing was added by {brand} from public information. Are you the owner? Claim it free.',
  'dc53.claim.banner_title': 'Do you own {name}?',
  'dc53.claim.banner_body': 'Claim this listing free — update info, reply to reviews, and get bookings.',
  'dc53.claim.banner_cta': 'Claim this business',
  'dc53.claim.pending_line': 'A claim for this listing is being reviewed.',
  'dc53.claim.dispute': 'This is my business — dispute',
  'dc53.claim.rej_title': 'Your claim was not approved',
  'dc53.claim.rej_why': 'Reason from our review team',
  'dc53.claim.retry': 'Try again',
  'dc53.claim.s1': 'Confirm it’s yours',
  'dc53.claim.s2': 'Verify ownership',
  'dc53.claim.s3': 'Done',
  'dc53.claim.step_of': 'Step {n} of 3',
  'dc53.claim.confirm': 'I own or manage this business',
  'dc53.claim.confirm_sub': 'Claiming gives you control of this listing once ownership is checked.',
  'dc53.claim.free': 'Claiming is always free.',
  'dc53.claim.signin': 'Sign in or create a free account',
  'dc53.claim.signin_sub': 'We’ll send a code to your mobile number.',
  'dc53.claim.phone': 'Mobile number',
  'dc53.claim.send_code': 'Send code',
  'dc53.claim.sent_to': 'Sent to {masked}',
  'dc53.claim.phone_verified': 'Phone verified — you are signed in',
  'dc53.claim.signed_in_as': 'Signed in as {name}',
  'dc53.claim.code_wrong': 'That code is not right — check the SMS and try again',
  'dc53.claim.instant_title': 'Verify by business phone',
  'dc53.claim.instant_body': 'Your number matches the one on this listing. We sent a code to {masked}.',
  'dc53.claim.instant_cap': 'Fastest — the business is yours as soon as the code checks out.',
  'dc53.claim.instant_tag': 'Instant',
  'dc53.claim.docs_title': 'Verify with documents',
  'dc53.claim.docs_body': 'Your number does not match the one on this listing, so our team checks ownership documents instead.',
  'dc53.claim.docs_cap': 'Checked within one business day. Payment features are reviewed separately, per business.',
  'dc53.claim.docs_tag': 'Manual',
  'dc53.claim.code_title': 'Enter the 6-digit code',
  'dc53.claim.code_aria': '6-digit code',
  'dc53.claim.pick_file': 'Choose file — {doc}',
  'dc53.claim.file_ready': '{doc} attached',
  'dc53.claim.file_hint': 'JPG, PNG or PDF',
  'dc53.claim.submit_docs': 'Submit for review',
  'dc53.claim.verify': 'Verify & claim',
  'dc53.claim.success_title': 'This business is now yours.',
  'dc53.claim.next1': 'It now sits under your merchant account',
  'dc53.claim.next2': 'Edit photos, services and hours — live instantly',
  'dc53.claim.next3': 'Turn booking on per service, whenever you’re ready',
  'dc53.claim.go_dash': 'Go to my dashboard',
  'dc53.claim.see_plans': 'See plans',
  'dc53.claim.review_title': 'We’re checking your documents',
  'dc53.claim.review_pill': 'Review in progress',
  'dc53.claim.review_body': 'The business profile is already live — we’ll SMS you the moment the claim is confirmed.',
  'dc53.claim.eta': 'Usually within one business day',
  'dc53.claim.footnote': 'False claims are removed and may lead to account bans. Business data is verified per our guidelines.',
  'dc53.claim.free_after': 'Claiming puts the business on the Free tier. Paid features come with a paid plan — see plans for what each includes.',
  // ---- end DC53 ----
  // ---- DC63 customer new-booking wizard (My-Bookings.dc.html L176-674, bd-core.js `nb`) ----
  'user.nb.title.0': 'What to book',
  'user.nb.title.1': 'Results',
  'user.nb.title.2': 'Service details',
  'user.nb.title.3': 'Pick a time',
  'user.nb.title.4': 'Your details',
  'user.nb.title.5': 'Payment',
  'user.nb.title.6': 'Check it over',
  'user.nb.title.7': 'Done',
  'user.nb.dialog': 'New booking',
  'user.nb.progress': 'Step {n} of {total}',
  'user.nb.next': 'Next',
  'user.nb.back': 'Back',
  'user.nb.close': 'Close',
  'user.nb.discard.title': 'Close this booking?',
  'user.nb.discard.body': 'What you picked and typed will be lost.',
  'user.nb.discard.yes': 'Yes, close it',
  'user.nb.discard.no': 'No, keep going',
  'user.nb.area.pick': 'Pick an area',
  'user.nb.area.label': '{city} · {area}',
  'user.nb.rebook': 'Book again',
  'user.nb.search.title': 'Search',
  'user.nb.search.ph': 'Type a service or business name',
  'user.nb.cats': 'Categories',
  'user.nb.cat_count': '{n, plural, one{# service} other{# services}}',
  'user.nb.all_areas': 'all areas',
  'user.nb.all_cats': 'All',
  'user.nb.any_price': 'Any price',
  'user.nb.band.upto': '{sym}{a}–{b}',
  'user.nb.band.over': '{sym}{a}+',
  'user.nb.today_free': 'Free today',
  'user.nb.none.title': 'Nothing found',
  'user.nb.none.sub': 'No match in this area — try widening it.',
  'user.nb.none.cta': 'Search every area',
  'user.nb.verified': 'Verified',
  'user.nb.reviews_n': '{n, plural, one{# review} other{# reviews}}',
  'user.nb.about': 'About this service',
  'user.nb.policy': 'Cancellation',
  'user.nb.recent': 'Recent reviews',
  'user.nb.address': 'Address',
  'user.nb.map': 'See on map',
  'user.nb.pro_line': '{spec} · {n} years of experience',
  'user.nb.pick_time': 'Pick a time',
  'user.nb.offer_ends': 'Offer ends · {n, plural, =0{today} one{# day} other{# days}}',
  'user.nb.off': '{n}% off',
  'user.nb.days_ago': '{n}d ago',
  'user.nb.date': 'Date',
  'user.nb.checkin': 'Check-in',
  'user.nb.checkout_line': 'Check-out: {date}',
  'user.nb.nights': 'Nights',
  'user.nb.nights_n': '{n, plural, one{# night} other{# nights}}',
  'user.nb.slots': 'Free times',
  'user.nb.booked_note': 'Greyed times are already booked.',
  'user.nb.sessions.serial': 'Chamber session',
  'user.nb.sessions.capacity': 'Pick a batch',
  'user.nb.left': '{n, plural, one{# place left} other{# places left}}',
  'user.nb.booked': 'Booked',
  'user.nb.serial.title': 'Your serial will be',
  'user.nb.serial.note': 'The system assigns the serial — it cannot be changed.',
  'user.nb.options.date_range': 'Room type',
  'user.nb.options.seat_inventory': 'Pick a tier',
  'user.nb.options.block_booking': 'How long',
  'user.nb.seats': 'Pick your seats',
  'user.nb.seat_note': 'Grey seats are sold',
  'user.nb.seat_picked': 'Chosen seats: {seats}',
  'user.nb.stepper.dec': 'Fewer',
  'user.nb.stepper.inc': 'More',
  'user.nb.hold': '{time} held for you',
  'user.nb.full.title': 'No times free right now',
  'user.nb.full.sub': 'Every time in the next few days is booked — try another service.',
  'user.nb.full.cta': 'See other services',
  'user.nb.closed.title': 'Online booking is not open for this listing yet',
  'user.nb.closed.sub': 'Contact the business directly to book.',
  'user.nb.none_day': 'Nothing free on this day — pick another date.',
  'user.nb.you.name': 'Your name',
  'user.nb.you.phone': 'Phone number',
  'user.nb.you.phone_note': 'This number is your identity',
  'user.nb.you.verified': 'Verified ✓',
  'user.nb.you.email': 'Email (optional)',
  'user.nb.you.email_ph': 'For receipts by email',
  'user.nb.you.notes': 'Anything the business should know? (optional)',
  'user.nb.you.notes_ph': 'e.g. I need wheelchair access',
  'user.nb.you.consent': 'I accept the terms and policies',
  'user.nb.you.err': 'Fill these in',
  'user.nb.you.consent_err': 'Accept the terms to continue',
  'user.nb.you.yes': 'Yes',
  'user.nb.you.no': 'No',
  'user.nb.pay.total': 'Total',
  'user.nb.pay.split': 'Due now {now} · Due at the venue {venue}',
  'user.nb.pay.venue': 'I will pay at the venue',
  'user.nb.pay.venue_sub': 'Pay when you get there',
  'user.nb.pay.now': 'I will pay now',
  'user.nb.pay.now_sub': 'Send to the business\'s number and add proof',
  'user.nb.pay.gateway': 'Pay with {brand}',
  'user.nb.pay.gateway_sub': 'Not switched on yet',
  'user.nb.pay.soon': 'Coming soon',
  'user.nb.pay.due_now': 'Due now',
  'user.nb.pay.due_venue': 'Due at the venue',
  'user.nb.pay.send_to': 'Send to this number',
  'user.nb.pay.how': 'Send the money, then add a screenshot or the TRX ID — either one is enough.',
  'user.nb.pay.due_now_line': 'Due now {amount}',
  'user.nb.pay.shot': 'Attach screenshot',
  'user.nb.pay.shot_done': 'Screenshot attached',
  'user.nb.pay.trx': 'TRX ID',
  'user.nb.pay.proof_err': 'Add a screenshot or TRX ID',
  'user.nb.pay.pick_err': 'Pick one way to pay',
  'user.nb.pay.free_plan': 'This business does not take online payments yet — you will pay at the venue.',
  'user.nb.pay.note': '{brand} never takes or holds this money — you are paying the business directly.',
  'user.nb.pay.method.bkash': 'bKash',
  'user.nb.pay.method.nagad': 'Nagad',
  'user.nb.pay.method.rocket': 'Rocket',
  'user.nb.pay.method.bank': 'Bank',
  'user.nb.pick_err': 'Pick a time to continue',
  'user.nb.rv.service': 'Service',
  'user.nb.rv.biz': 'Business',
  'user.nb.rv.when': 'When',
  'user.nb.rv.detail': 'Details',
  'user.nb.rv.price': 'Price',
  'user.nb.rv.price_line': '{total} ({n} × {unit})',
  'user.nb.rv.discount': 'Offer discount',
  'user.nb.rv.total': 'Total',
  'user.nb.rv.method': 'Payment',
  'user.nb.rv.confirm': 'Confirm booking',
  'user.nb.rv.serial': 'Serial {n}',
  'user.nb.ok.confirmed': 'Booking confirmed ✓',
  'user.nb.ok.confirmed_sub': 'Just arrive on time',
  'user.nb.ok.pending': 'Waiting — the business will confirm',
  'user.nb.ok.pending_sub': 'You get an SMS the moment it is confirmed',
  'user.nb.ok.verifying': 'Payment being checked',
  'user.nb.ok.verifying_sub': 'Usually done within 2 hours',
  'user.nb.ok.sms': 'SMS sent',
  'user.nb.ok.code': 'Booking code',
  'user.nb.ok.qr': 'Check-in code',
  'user.nb.ok.view': 'See the booking',
  'user.nb.ok.cal': 'Add to calendar',
  'user.nb.ok.close': 'Close',
  'user.nb.ok.copied': 'Code copied',
  'user.nb.ok.proof_failed': 'Booked — the proof did not go through. Send it again from the booking.',
  'user.nb.err.taken': 'That time was just taken — pick another.',
  'user.nb.err.expired': 'Time\'s up — pick again',
  'user.nb.err.generic': 'Something went wrong — try again.',
  'user.nb.err.offer': 'That offer is no longer available — the regular price now applies.',
  'user.nb.busy.hold': 'Holding your time…',
  'user.nb.busy.confirm': 'Booking…',
  'user.nb.err.load': 'Could not load this — check your connection and try again.',
  'user.nb.err.slots': 'Could not load the free times.',
  'user.nb.you.email_err': 'Check the email address',
  'user.nb.unit./visit': 'per visit',
  'user.nb.unit./session': 'per session',
  'user.nb.unit./scan': 'per scan',
  'user.nb.unit./sitting': 'per sitting',
  'user.nb.unit./booking': 'per booking',
  'user.nb.unit./child': 'per child',
  'user.nb.unit./person': 'per person',
  'user.nb.unit./night': 'per night',
  'user.nb.unit./ticket': 'per ticket',
  'user.nb.unit./hr': 'per hour',
  'user.nb.next.today': 'Free today {time}',
  'user.nb.next.tomorrow': 'Free tomorrow {time}',
  'user.nb.next.date': 'Free {date}, {time}',
  'user.nb.next.none': 'Fully booked this week',
  // ---- end DC63 ----
  // ---- DC51 (website search + trending) ----
  'dc51.sort.relevance': 'Recommended',
  'dc51.sort.rating': 'Top-rated',
  'dc51.sort.price': 'Price: low to high',
  'dc51.sort.price_desc': 'Price: high to low',
  'dc51.sort.newest': 'Newest',
  'dc51.sort.trending': 'Trending',
  'dc51.sort.walk': 'Walkable first',
  'dc51.sort.name': 'Name A–Z',
  'dc51.filt.category': 'Category',
  'dc51.amenity.pool': 'Pool',
  'dc51.amenity.sea_view': 'Sea view',
  'dc51.amenity.wifi': 'Wi-Fi',
  'dc51.amenity.parking': 'Parking',
  'dc51.amenity.breakfast': 'Breakfast',
  'dc51.remove_filter': 'Remove {label}',
  'dc51.verified': 'Verified',
  'dc51.load_more': 'Load more',
  'dc51.tp.search_ph': 'Search trending — turf, doctor, salon, resort…',
  'dc51.tp.search': 'Search',
  'dc51.tp.all': 'All',
  'dc51.tp.title': 'All-time hits.',
  'dc51.tp.context': 'Top 10 businesses holding the chart for more than a week · updated today',
  'dc51.tp.all_time_hit': '#{n} all-time hit',
  'dc51.tp.view_book': 'View & Book →',
  'dc51.tp.play_pause': 'Play / pause',
  'dc51.tp.go_to_slide': 'Go to slide {n}',
  'dc51.tp.chart_kicker': 'This week\'s chart',
  'dc51.tp.top10_of': 'Top 10 — {cat}',
  'dc51.tp.all_of_region': 'all of {region}',
  'dc51.tp.ranked_by': 'Ranked by bookings & reviews · updated today',
  'dc51.tp.rising': 'Rising fast.',
  'dc51.tp.spots': '▲ +{n} spots this week',
  'dc51.tp.new_entry': '▲ New this week',
  'dc51.tp.month': 'Trending this month.',
  'dc51.tp.biz_count': '{n} businesses',
  'dc51.tp.rank': '#{n} trending',
  'dc51.tp.price_from': 'From {price}',
  'dc51.tp.free_reservation': 'Free reservation',
  'dc51.tp.save': 'Save {name}',
  'dc51.tp.empty': 'No trending businesses match.',
  'dc51.tp.empty_sub': 'Try a different search or category.',
  'dc51.tp.clear_filters': 'Clear filters',
  'dc51.tp.cta_title': 'Want your business trending here?',
  'dc51.tp.cta_sub': 'Businesses with fresh offers, quick replies and verified reviews rise fastest. List free and start climbing.',
  'dc51.tp.cta_btn': 'List your business →',
  // ---- end DC51 ----
  // ---- DC93 admin Services directory (design listingVals / lsTableVals) ----
  'admin.services.sub': 'The full marketplace directory — browse and manage services. Moderation and ownership claims live in Approvals.',
  'admin.services.stat.queue': 'Service queue',
  'admin.services.stat.claims': 'Claims',
  'admin.services.stat.total': 'Total services',
  'admin.services.tab.all': 'All services',
  'admin.services.search': 'Search name, business, area…',
  'admin.services.filter.category': 'All categories',
  'admin.services.filter.city': 'All cities',
  'admin.services.filter.status': 'All statuses',
  'admin.services.filter.claim': 'Claimed & unclaimed',
  'admin.services.filter.tier': 'All tiers',
  'admin.services.claim.claimed': 'Claimed',
  'admin.services.claim.unclaimed': 'Unclaimed',
  'admin.services.col.service': 'Service',
  'admin.services.col.business': 'Business',
  'admin.services.col.tier': 'Tier',
  'admin.services.col.status': 'Status',
  'admin.services.col.claim': 'Claim state',
  'admin.services.col.submitted': 'Submitted',
  'admin.services.col.actions': 'Actions',
  'admin.services.csv.id': 'ID',
  'admin.services.csv.category': 'Category',
  'admin.services.csv.city': 'City',
  'admin.services.featured': 'Featured business',
  'admin.services.select_page': 'Select every service on this page',
  'admin.services.action.view': 'View',
  'admin.services.action.suspend': 'Suspend',
  'admin.services.action.reinstate': 'Reinstate',
  'admin.services.action.review': 'Review',
  'admin.services.bulk.selected': '{n} selected',
  'admin.services.bulk.suspend': 'Suspend selected',
  'admin.services.bulk.approve': 'Approve selected',
  'admin.services.bulk.export': 'Export selected',
  'admin.services.export': 'Export CSV',
  'admin.services.range': '{from}–{to} of {total}',
  'admin.services.empty': 'No rows match those filters.',
  'admin.services.empty.clear': 'Clear filters',
  'admin.services.confirm.reason': 'Reason — written to the audit log',
  'admin.services.confirm.suspend.title': 'Suspend {name}?',
  'admin.services.confirm.suspend.body': 'The service leaves the marketplace immediately and the merchant is notified. Existing bookings stay.',
  'admin.services.confirm.reinstate.title': 'Reinstate {name}?',
  'admin.services.confirm.reinstate.body': 'The service returns to the marketplace immediately and the merchant is notified.',
  'admin.services.confirm.bulk_suspend.title': 'Suspend {n} services?',
  'admin.services.confirm.bulk_suspend.body': 'Live and paused services leave the marketplace immediately; each merchant is notified. Others are skipped.',
  'admin.services.confirm.bulk_approve.title': 'Approve {n} services?',
  'admin.services.confirm.bulk_approve.body': 'Only services waiting for review go live; the rest are skipped. Each merchant is notified.',
  'admin.services.confirm.bulk_approve.cta': 'Approve',
  'admin.services.toast.suspended': '{name} suspended — audit-logged',
  'admin.services.toast.reinstated': '{name} reinstated — audit-logged',
  'admin.services.toast.bulk': '{ok} of {n} services updated — audit-logged',
  'admin.services.fact.price': 'Price',
  'admin.services.fact.on_request': 'On request',
  'admin.services.detail.public': 'View public service',
  'admin.services.detail.approvals': 'Open in Approvals',
  'admin.services.confirm.cta': 'Confirm',
  'admin.services.confirm.reason_ph': 'Add a short reason…',
  'admin.services.reason_required': 'A reason is required — it is recorded in the audit log',
  'admin.services.fact.template': 'Template',
  'admin.services.fact.phone': 'Phone',
  'admin.services.fact.photos': 'Photos',
  'admin.services.detail.by': 'by {business}',
  // ---- end DC93 ----
  // ---- DC73 merchant new-booking wizard ----
  'dc73.nb.title': 'New booking',
  'dc73.nb.step_label': 'Step {n}/{total} · {name}',
  'dc73.nb.step.customer': 'Customer',
  'dc73.nb.step.service': 'Service',
  'dc73.nb.step.when': 'Time & payment',
  'dc73.nb.close': 'Close',
  'dc73.nb.phone': 'Phone number',
  'dc73.nb.returning': 'Returning customer',
  'dc73.nb.returning_line': '{n, plural, one{# past booking} other{# past bookings}} · last on {day}',
  'dc73.nb.name': 'Name',
  'dc73.nb.email_opt': 'Email (optional)',
  'dc73.nb.business': 'Business',
  'dc73.nb.service': 'Service',
  'dc73.nb.search_ph': 'Search services…',
  'dc73.nb.recent': 'Recent & most-booked',
  'dc73.nb.matches': 'Matches',
  'dc73.nb.no_match': 'No services match',
  'dc73.nb.clear_service': 'Change service',
  'dc73.nb.price': 'Price',
  'dc73.nb.price_line': 'Price {amount}',
  'dc73.nb.change_price': 'Change',
  'dc73.nb.price_warn': 'A changed price carries a "(price changed)" tag on the booking',
  'dc73.nb.price_changed': 'Price changed',
  'dc73.nb.date': 'Date',
  'dc73.nb.date_today': 'Today',
  'dc73.nb.date_tmrw': 'Tmrw',
  'dc73.nb.prev_week': 'Previous week',
  'dc73.nb.next_week': 'Next week',
  'dc73.nb.full_month': 'Full month',
  'dc73.nb.time': 'Time',
  'dc73.nb.times_loading': 'Loading open times…',
  'dc73.nb.times_error': 'Couldn’t load open times.',
  'dc73.nb.no_times': 'No open times on this day',
  'dc73.nb.next_serial': 'Next serial',
  'dc73.nb.serial_help': 'Serial number is assigned automatically',
  'dc73.nb.check_in': 'Check-in',
  'dc73.nb.check_out': 'Check-out',
  'dc73.nb.pay_status': 'Payment status',
  'dc73.nb.pay.venue': 'Will pay at venue',
  'dc73.nb.pay.venue_help': 'Customer pays when they arrive',
  'dc73.nb.pay.paid': 'Already paid',
  'dc73.nb.pay.paid_help': 'Paid already — attach proof if you have it',
  'dc73.nb.pay.due': 'Due',
  'dc73.nb.pay.due_help': 'Collect later',
  'dc73.nb.trx': 'TRX ID (optional)',
  'dc73.nb.attach': 'Attach screenshot',
  'dc73.nb.attached': 'Attached',
  'dc73.nb.pay_info_only': 'Saved as booking information',
  'dc73.nb.notify': 'Notifications',
  'dc73.nb.sms': 'SMS',
  'dc73.nb.email': 'Email',
  'dc73.nb.email_free': 'always free',
  'dc73.nb.summary': 'Summary',
  'dc73.nb.sum.customer': 'Customer',
  'dc73.nb.sum.service': 'Service',
  'dc73.nb.sum.when': 'Date · Time',
  'dc73.nb.sum.price': 'Price',
  'dc73.nb.sum.pay': 'Payment status',
  'dc73.nb.back': 'Previous step',
  'dc73.nb.next': 'Next step',
  'dc73.nb.confirm': 'Confirm booking',
  'dc73.nb.create_failed': 'Couldn’t create the booking — try again.',
  'dc73.nb.slot_taken': 'That time was just taken — pick another.',
  'dc73.nb.upload_failed': 'Couldn’t attach the screenshot — the booking was not created.',
  'dc73.nb.discard_title': 'Discard this booking?',
  'dc73.nb.discard_body': 'What you typed will be lost.',
  'dc73.nb.keep_editing': 'Keep editing',
  'dc73.nb.discard': 'Discard',
  'dc73.nb.created': 'Booking created',
  'dc73.nb.copy': 'Copy',
  'dc73.nb.copied': 'Copied',
  'dc73.nb.sms_sent': 'SMS sent to the customer',
  'dc73.nb.another': 'Another booking',
  'dc73.nb.view': 'View booking',
  'dc73.nb.done_close': 'Close',
  // ---- end DC73 ----
  // ---- DC65 customer Reviews (Reviews.dc.html; bd-core.js T.rv) ----
  'user.rv.tabs_label': 'Review views',
  'user.rv.tab.all': 'All',
  'user.rv.tab.pending': 'To rate',
  'user.rv.tab.replied': 'Replied',
  'user.rv.avg_aria': 'Average rating {avg}',
  'user.rv.count': '{n, plural, one{# review} other{# reviews}}',
  'user.rv.pending': 'To rate',
  'user.rv.help_pending': 'Tell people how it went — it takes under a minute.',
  'user.rv.mine': 'My reviews',
  'user.rv.help_mine': 'Posted reviews are visible to the business and other customers.',
  'user.rv.tap_star': 'Tap a star to rate',
  'user.rv.rate_label': 'Rate {service}',
  'user.rv.star': '{n, plural, one{# star} other{# stars}}',
  'user.rv.word.1': 'Poor',
  'user.rv.word.2': 'Not great',
  'user.rv.word.3': 'Okay',
  'user.rv.word.4': 'Good',
  'user.rv.word.5': 'Excellent',
  'user.rv.placeholder': 'Write about your experience (optional)',
  'user.rv.photos': 'Add photos (up to {n})',
  'user.rv.photos_added': '{n, plural, one{# photo attached} other{# photos attached}}',
  'user.rv.photo_remove': 'Remove photo',
  'user.rv.photo_failed': 'That photo could not be added. Try another one.',
  'user.rv.post': 'Post',
  'user.rv.posted': 'Review posted',
  'user.rv.post_failed': 'Your review could not be posted. Try again.',
  'user.rv.verified': 'Verified booking',
  'user.rv.edited': 'Edited · {when}',
  'user.rv.hidden': 'Not shown publicly',
  'user.rv.hidden_note': 'This review is hidden from the business page while it is checked.',
  'user.rv.removed': 'Removed',
  'user.rv.removed_note': 'This review no longer shows on the business page.',
  'user.rv.reply': 'Business reply',
  'user.rv.reply_edited': 'edited',
  'user.rv.no_words': '(no words)',
  'user.rv.menu_label': 'Review options',
  'user.rv.menu.final': 'You can edit or delete a review for {n} days after posting it.',
  'user.rv.menu.edit': 'Edit review',
  'user.rv.menu.del': 'Delete review',
  'user.rv.menu.report': 'Report the reply',
  'user.rv.menu.closed': 'The {n}-day window to change this review has closed.',
  'user.rv.edit_t': 'Edit your review',
  'user.rv.current': 'Current review',
  'user.rv.your_version': 'Your new version',
  'user.rv.save': 'Save changes',
  'user.rv.saved': 'Review updated',
  'user.rv.del_t': 'Delete this review?',
  'user.rv.del_sub': 'It comes off the business page, and this visit cannot be reviewed again.',
  'user.rv.del_cta': 'Delete',
  'user.rv.deleted': 'Review deleted',
  'user.rv.report_t': 'Report this reply',
  'user.rv.report_sub': 'Tell us why you are reporting it.',
  'user.rv.report_reason.abusive': 'Abusive language',
  'user.rv.report_reason.false_info': 'False information',
  'user.rv.report_reason.personal_info': 'Personal information',
  'user.rv.report_reason.other': 'Other',
  'user.rv.report_cta': 'Send report',
  'user.rv.report_done': 'Report sent',
  'user.rv.failed': 'That did not go through. Try again.',
  'user.rv.window_closed': 'The {n}-day window to change this review has closed.',
  'user.rv.none_pending': 'Everything is rated',
  'user.rv.none_replied': 'No replies yet',
  'user.rv.none_all': 'No reviews yet',
  'user.rv.trust': 'Only completed bookings can be reviewed, so these are trustworthy.',
  // ---- end DC65 ----
  // ---- DC66 customer Account + Help (Account.dc.html, Help.dc.html; bd-core.js T.ac / T.hp) ----
  'user.ac.verified': 'Verified',
  'user.ac.edit': 'Edit',
  'user.ac.cancel': 'Cancel',
  'user.ac.name': 'Name',
  'user.ac.email': 'Email (optional)',
  'user.ac.email_none': 'Not added',
  'user.ac.email_help': 'Add an email and receipts and confirmations come free',
  'user.ac.area': 'Default area',
  'user.ac.area_none': 'Not set',
  'user.ac.phone_help': 'This number is your account',
  'user.ac.save_profile': 'Save profile',
  'user.ac.profile_saved': 'Profile saved',
  'user.ac.name_required': 'Add your name',
  'user.ac.email_invalid': 'That email does not look right',
  'user.ac.save_failed': 'Could not save. Please try again.',
  'user.ac.change_photo': 'Change photo',
  'user.ac.photo': 'Profile picture',
  'user.ac.photo_pick': 'Choose a picture from your phone or computer.',
  'user.ac.photo_upload': 'Upload a picture',
  'user.ac.photo_current': 'Current picture',
  'user.ac.photo_saved': 'Profile picture updated',
  'user.ac.photo_failed': 'Could not upload the picture. Please try again.',
  'user.ac.photo_another': 'Pick another',
  'user.ac.photo_preview': 'New picture',
  'user.ac.photo_uploading': 'Uploading your picture…',
  'user.ac.photo_saving': 'Saving it to your profile…',
  'user.ac.photo_done': 'Done — your new picture is set',
  'user.ac.photo_step': '{n}/{total}',
  'user.ac.photo_too_big': 'That picture is over {mb} MB — pick a smaller one.',
  'user.ac.photo_not_image': 'That file is not a picture — pick a JPG, PNG or WebP.',
  'user.ac.look': 'Language and theme',
  'user.ac.look_sub': 'How the app looks and which language it speaks.',
  'user.ac.lang_label': 'Language',
  'user.ac.theme_label': 'Theme',
  'user.ac.light': 'Light',
  'user.ac.dark': 'Dark',
  'user.ac.notif': 'Notifications',
  'user.ac.notif_sub': 'Which news reaches you, and how.',
  'user.ac.row.booking_confirmed': 'Booking confirmed',
  'user.ac.row.payment_update': 'Payment updates',
  'user.ac.row.reminder': 'Reminders (24 hours and 2 hours before)',
  'user.ac.row.offers': 'Offers and deals',
  'user.ac.col.sms': 'SMS',
  'user.ac.col.email': 'Email',
  'user.ac.col.push': 'Push',
  'user.ac.notif_help':
    'Booking and payment news cannot be switched off entirely — at least one channel stays on so urgent news reaches you.',
  'user.ac.notif_saved': 'Notifications updated',
  'user.ac.last_on': 'At least one channel must stay on',
  'user.ac.receipts': 'Receipts',
  'user.ac.receipts_sub': 'Download receipts for your bookings.',
  'user.ac.receipts_row': 'Every booking from the year you pick, as a PDF.',
  'user.ac.download': 'Download',
  'user.ac.receipts_done': 'Preparing receipts — we will email them',
  'user.ac.receipts_need_email': 'Add an email first — the receipts are emailed to you',
  'user.ac.privacy': 'Privacy',
  'user.ac.privacy_sub': 'Close your account or sign out of this device.',
  'user.ac.del': 'Delete my account',
  'user.ac.del_action': 'Delete',
  'user.ac.del_warn': 'Your name and contact details are erased; booking records stay anonymously for accounting.',
  'user.ac.del_type': 'Type "মুছুন" to confirm',
  'user.ac.del_word': 'মুছুন',
  'user.ac.del_cta': 'Request deletion',
  'user.ac.del_done': 'Deletion request received',
  'user.ac.requested': 'Request received',
  'user.ac.signout': 'Sign out',
  'user.ac.signout_q': 'Sign out?',
  'user.ac.signout_s': 'To get back in you only need your phone number and an OTP.',
  'user.hp.search_ph': 'Type your question',
  'user.hp.clear': 'Clear search',
  'user.hp.topic_all': 'All',
  'user.hp.cases': 'My problems',
  'user.hp.case_sub': 'Problems you reported and where they stand.',
  'user.hp.faq_title': 'Common questions',
  'user.hp.faq_sub': 'The questions people ask us most.',
  'user.hp.answers': '{n, plural, one{# answer} other{# answers}}',
  'user.hp.none_t': 'No answer for that',
  'user.hp.none_s': 'Try other words, or just ask us directly.',
  'user.hp.ask_us': 'Ask us',
  'user.hp.contact': 'Contact us',
  'user.hp.contact_sub': 'We answer in Bangla or English, usually within 5 minutes.',
  'user.hp.call': 'Talk to us',
  'user.hp.wa': 'Message on WhatsApp',
  'user.hp.mail': 'Send an email',
  'user.hp.write': 'Write to us',
  'user.hp.write_sub': 'We answer here, under My problems',
  'user.hp.ticket.meta': 'Support request · {ago}',
  'user.hp.ticket.status.open': 'Waiting for us',
  'user.hp.ticket.status.answered': 'Answered',
  'user.hp.ticket.status.closed': 'Closed',
  'user.hp.ticket.you': 'You',
  'user.hp.ticket.team': 'Support team',
  'user.hp.ticket.reply_ph': 'Write a reply',
  'user.hp.ticket.send': 'Send',
  'user.hp.ticket.sent': 'Reply sent',
  'user.hp.ticket.failed': 'Could not send. Please try again.',
  'user.hp.ask.title': 'Ask us',
  'user.hp.ask.sub': 'Tell us what you need — we answer here, under My problems.',
  'user.hp.ask.message': 'Your question',
  'user.hp.ask.send': 'Send',
  'user.hp.ask.sent': 'Sent — we will answer under My problems',
  // ---- end DC66 ----
  // ---- DC95 admin Offers (design "6b · OFFERS") ----
  'admin.ofr.sub': 'Merchant-created deal moderation and the full offer directory.',
  'admin.ofr.dash.title': 'Offers dashboard',
  'admin.ofr.dash.sub': 'Marketplace state today · performance of every offer that reached customers.',
  'admin.ofr.tile.live': 'Live offers',
  'admin.ofr.tile.live_sub': 'On the marketplace today',
  'admin.ofr.tile.pending': 'Pending review',
  'admin.ofr.tile.pending_sub': 'New submissions in the queue',
  'admin.ofr.tile.flagged': 'Flagged',
  'admin.ofr.tile.flagged_sub': 'Price-claim flags to resolve',
  'admin.ofr.tile.ending': 'Ending ≤48 h',
  'admin.ofr.tile.ending_sub': 'Live offers closing soon',
  'admin.ofr.tile.clicks': 'Offer clicks',
  'admin.ofr.tile.clicks_sub': 'Card opens from Home & Offers',
  'admin.ofr.tile.bookings': 'Bookings via offers',
  'admin.ofr.tile.bookings_sub': 'Bookings that redeemed an offer',
  'admin.ofr.tile.revenue': 'Offer revenue',
  'admin.ofr.tile.revenue_sub': 'Value of those bookings',
  'admin.ofr.tile.takedowns': 'Take-downs (30 d)',
  'admin.ofr.tile.takedowns_sub': 'Policy or quality violations',
  'admin.ofr.tab.review': 'Review ({n})',
  'admin.ofr.tab.review_label': 'Review queue',
  'admin.ofr.tab.all': 'All offers',

  // Offers › Reels queue (FR-A27)
  'admin.reels.tab': 'Reels ({n})',
  'admin.reels.title': 'Reels for review',
  'admin.reels.seg.pending_review': 'Waiting',
  'admin.reels.seg.approved': 'Approved',
  'admin.reels.seg.rejected': 'Rejected',
  'admin.reels.empty': 'Nothing here',
  'admin.reels.approve': 'Approve',
  'admin.reels.reject': 'Reject',
  'admin.reels.reject_title': 'Reject this reel?',
  'admin.reels.reject_body': 'The merchant sees your reason and can submit a different clip.',
  'admin.reels.reason': 'Reason',
  'admin.reels.reason_ph': 'Why it cannot show',
  'admin.reels.approved': 'Reel approved',
  'admin.reels.rejected': 'Reel rejected',
  'admin.reels.failed': 'The decision was not saved',
  'admin.reels.open': 'Open on YouTube',
  'admin.reels.creator': 'Creator: {creator}',
  'admin.reels.promotes': 'Promotes: {offer}',
  'admin.reels.submitted': 'Submitted {date}',
  'admin.reels.cancel': 'Cancel',
  'admin.ofr.seg.new': 'New submissions ({n})',
  'admin.ofr.seg.flagged': 'Flagged ({n})',
  'admin.ofr.filter.issue': 'All issue types',
  'admin.ofr.sorted': 'sorted by severity, then oldest first',
  'admin.ofr.queue_clear': 'Queue clear ✓ — no offers waiting for moderation',
  'admin.ofr.sev.low': 'low',
  'admin.ofr.sev.medium': 'medium',
  'admin.ofr.sev.high': 'high',
  'admin.ofr.issue.new': 'New submission',
  'admin.ofr.issue.price_mismatch': 'Pricing claim',
  'admin.ofr.issue.unverifiable': 'Unverifiable price',
  'admin.ofr.issue.prohibited': 'Prohibited',
  'admin.ofr.issue_text.price_mismatch': 'Price claim doesn’t match — the discount the offer copy claims is not what the catalog price delivers',
  'admin.ofr.issue_text.unverifiable': 'Discount claim can’t be verified — there is no catalog price to check it against',
  'admin.ofr.issue_text.prohibited': 'Flagged as prohibited content — needs an admin decision',
  'admin.ofr.verify.ok': '✓ Verify price — catalog {price} · matches the claimed regular price',
  'admin.ofr.verify.ok_svc': '✓ Verify price — catalog: {service} {price} · matches the claimed regular price',
  'admin.ofr.verify.mismatch': '⚠ Verify price — catalog {price} does not support the claimed discount',
  'admin.ofr.verify.mismatch_svc': '⚠ Verify price — catalog: {service} {price} does not support the claimed discount',
  'admin.ofr.verify.none': '⚠ Verify price — no catalog price to check the claim against',
  'admin.ofr.win_left': '{window} · {n, plural, one{# day} other{# days}} left',
  'admin.ofr.win_ended': '{window} · ended',
  'admin.ofr.submitted': 'submitted {date}',
  'admin.ofr.high_first': 'high severity — handle first',
  'admin.ofr.type.percent': 'Percentage discount',
  'admin.ofr.type.fixed': 'Amount off',
  'admin.ofr.free': 'Free',
  'admin.ofr.gated': 'Moderator approval required',
  'admin.ofr.act.approve': 'Approve',
  'admin.ofr.act.approve_anyway': 'Approve anyway',
  'admin.ofr.act.approve_live': 'Approve — go live',
  'admin.ofr.act.reject': 'Reject',
  'admin.ofr.act.take_down': 'Take down',
  'admin.ofr.act.feature': 'Feature — Sponsored slot',
  'admin.ofr.search': 'Search offer or merchant…',
  'admin.ofr.filter.status': 'All statuses',
  'admin.ofr.filter.category': 'All categories',
  'admin.ofr.filter.city': 'All cities',
  'admin.ofr.filter.ending': 'Any end date',
  'admin.ofr.filter.ending_soon': 'Ends ≤48 h',
  'admin.ofr.filter.ending_later': 'Later',
  'admin.ofr.filter.promo': 'Promoted & organic',
  'admin.ofr.filter.organic': 'Organic',
  'admin.ofr.col.offer': 'Offer',
  'admin.ofr.col.merchant': 'Merchant',
  'admin.ofr.col.category': 'Category',
  'admin.ofr.col.deal': 'Deal',
  'admin.ofr.col.window': 'Window',
  'admin.ofr.col.status': 'Status',
  'admin.ofr.col.clicks': 'Clicks',
  'admin.ofr.col.bookings': 'Bookings',
  'admin.ofr.col.revenue': 'Revenue',
  'admin.ofr.col.promo': 'Promo',
  'admin.ofr.csv.ref': 'Ref',
  'admin.ofr.csv.city': 'City',
  'admin.ofr.csv.regular': 'Regular price',
  'admin.ofr.csv.deal': 'Offer price',
  'admin.ofr.pct_off': '−{n}% off',
  'admin.ofr.ends_in': 'Ends in {n} d',
  'admin.ofr.d_left': '{n} d left',
  'admin.ofr.select_page': 'Select every offer on this page',
  'admin.ofr.bulk.selected': '{n} selected',
  'admin.ofr.bulk.approve': 'Approve selected',
  'admin.ofr.bulk.take_down': 'Take down selected',
  'admin.ofr.bulk.export': 'Export selected',
  'admin.ofr.empty': 'No offers match — clear filters',
  'admin.ofr.empty_clear': 'Clear filters',
  'admin.ofr.export': 'Export CSV',
  'admin.ofr.range': '{from}–{to} of {total}',
  'admin.ofr.back': '← Back to all offers',
  'admin.ofr.as_customers': 'As customers see it',
  'admin.ofr.pv.cover': 'service cover photo',
  'admin.ofr.pv.ended': 'Ended',
  'admin.ofr.pv.ends_in': 'Ends in {n} d',
  'admin.ofr.pv.starts': 'Starts {date}',
  'admin.ofr.economics': 'Economics & validity',
  'admin.ofr.fact.type': 'Type',
  'admin.ofr.fact.service': 'Service',
  'admin.ofr.fact.whole_business': 'Whole business',
  'admin.ofr.fact.catalog': 'Catalog price',
  'admin.ofr.fact.matches': '✓ matches',
  'admin.ofr.fact.flagged': '⚠ flagged',
  'admin.ofr.fact.no_catalog': '⚠ not in catalog',
  'admin.ofr.fact.offer_price': 'Offer price',
  'admin.ofr.fact.discount': 'Discount',
  'admin.ofr.fact.window': 'Window',
  'admin.ofr.fact.ends': 'Ends',
  'admin.ofr.fact.ends_left': '{date} · {n} d left',
  'admin.ofr.fact.cap': 'Redemption cap',
  'admin.ofr.fact.cap_used': '{used} of {cap} used',
  'admin.ofr.fact.no_cap': 'No cap',
  'admin.ofr.fact.created_by': 'Created by',
  'admin.ofr.performance': 'Performance — all time',
  'admin.ofr.perf.clicks': 'Clicks',
  'admin.ofr.perf.bookings': 'Bookings via offer',
  'admin.ofr.perf.revenue': 'Revenue',
  'admin.ofr.perf.rate': 'Click → booking',
  'admin.ofr.history': 'Moderation history',
  'admin.ofr.history_empty': 'No moderation decisions yet',
  'admin.ofr.hist.engine': 'Policy engine',
  'admin.ofr.hist.system': 'System',
  'admin.ofr.hist.admin': 'Admin',
  'admin.ofr.hist.submitted': 'Submitted for review',
  'admin.ofr.hist.check_passed': 'Price claim verified — catalog {price} matches the struck-through price',
  'admin.ofr.hist.check_passed_bare': 'Price claim checked — no flag',
  'admin.ofr.hist.check_flagged': 'Auto-flagged ({severity}) — {issue}',
  'admin.ofr.hist.approved': 'Approved — live',
  'admin.ofr.hist.approved_flag': 'Approved despite the flag — live',
  'admin.ofr.hist.rejected': 'Rejected',
  'admin.ofr.hist.rejected_reason': 'Rejected — {reason}',
  'admin.ofr.hist.taken_down': 'Taken down',
  'admin.ofr.hist.taken_down_reason': 'Taken down — {reason}',
  'admin.ofr.hist.placed': 'Sponsored placement scheduled — {slot}',
  'admin.ofr.hist.unplaced': 'Sponsored placement cancelled — {slot}',
  'admin.ofr.hist.ended': 'Window ended — removed from customer surfaces automatically',
  'admin.ofr.placements': 'Sponsored placements',
  'admin.ofr.placements_sub': 'Paid slots this offer holds — always labelled “Sponsored” to customers.',
  'admin.ofr.actions': 'Actions',
  'admin.ofr.note.live': 'Live on Home and the Offers page.',
  'admin.ofr.note.live_sponsored': 'Live on Home and the Offers page · Sponsored: {slot}.',
  'admin.ofr.note.paused': 'Paused by the merchant — hidden from customers but not ended. Only the merchant can resume it.',
  'admin.ofr.note.pending': 'Waiting for moderation — also in the Review queue.',
  'admin.ofr.note.flagged': '⚠ {issue} — also in the Review queue.',
  'admin.ofr.note.draft': 'Draft — not visible to customers. The merchant submits it through moderation.',
  'admin.ofr.note.ended': 'This offer’s window has ended — read-only, kept for the ledger.',
  'admin.ofr.note.rejected': 'This offer was rejected — read-only, kept for the ledger. The merchant can edit and resubmit it.',
  'admin.ofr.note.taken_down': 'This offer was taken down — read-only, kept for the ledger. See the moderation history for the reason.',
  'admin.ofr.never_delete':
    'Offers never hard-delete — they end, pause or are taken down, and every decision is written to the audit log. Expired offers leave customer surfaces automatically.',
  'admin.ofr.reason_required': 'A reason is required — the merchant is shown it and it is recorded in the audit log.',
  'admin.ofr.dlg.reason': 'Reason (shown to the merchant · audit log)',
  'admin.ofr.dlg.reason_ph': 'Why? The merchant sees this.',
  'admin.ofr.dlg.reject_title': 'Reject {ref}?',
  'admin.ofr.dlg.reject_body': 'The merchant is notified with your reason and can resubmit a corrected offer.',
  'admin.ofr.dlg.take_down_title': 'Take down {ref}?',
  'admin.ofr.dlg.bulk_take_down_title': 'Take down {n} offer(s)?',
  'admin.ofr.dlg.take_down_body': 'The offer leaves every customer surface immediately and its paid placements are released; the merchant is notified.',
  'admin.ofr.dlg.place_title': 'Feature “{title}”?',
  'admin.ofr.dlg.place_body': 'Featuring is a paid Sponsored placement and is always labelled “Sponsored” to customers. There is no silent free featuring.',
  'admin.ofr.dlg.unplace_title': 'Cancel this placement?',
  'admin.ofr.toast.approved': '{ref} approved — live on the marketplace',
  'admin.ofr.toast.approved_flag': '{ref} live — flag overridden, audit-logged',
  'admin.ofr.toast.rejected': 'Offer rejected — audit-logged',
  'admin.ofr.toast.taken_down': 'Offer taken down — audit-logged',
  'admin.ofr.toast.bulk_taken_down': '{ok} of {n} offer(s) taken down — audit-logged',
  'admin.ofr.toast.bulk_approved': '{ok} offer(s) approved · {skipped} skipped (flagged or not pending) — audit-logged',
  'admin.ofr.toast.bulk_none': 'Nothing approved — flagged offers are excluded from bulk approve and need individual review',
  'admin.ofr.toast.placed': 'Sponsored placement scheduled — audit-logged',
  'admin.ofr.toast.unplaced': 'Placement cancelled — audit-logged',
  // ---- end DC95 ----
  // ---- DC94 bookings + support booking ----
  'admin.bk.sub': 'Platform-wide feed — open any booking for its full proof trail.',
  'admin.bk.stat.today': 'Today',
  'admin.bk.stat.verification': 'Verification',
  'admin.bk.feed': 'Booking feed',
  'admin.bk.search': 'Search code, customer, TRX id…',
  'admin.bk.filter.type': 'All types',
  'admin.bk.filter.city': 'All cities',
  'admin.bk.filter.status': 'All statuses',
  'admin.bk.filter.source': 'All sources',
  'admin.bk.support.cta': '＋ Support booking',
  'admin.bk.support.cta_title': 'Create a booking on a customer’s behalf — recorded with your name and reason.',
  'admin.bk.col.code': 'Code',
  'admin.bk.col.merchant': 'Merchant',
  'admin.bk.col.customer': 'Customer',
  'admin.bk.col.when': 'When',
  'admin.bk.col.amount': 'Amount',
  'admin.bk.col.status': 'Status',
  'admin.bk.col.source': 'Source',
  'admin.bk.col.open': 'Open',
  'admin.bk.row_open': 'Open booking {code}',
  'admin.bk.csv.phone': 'Phone',
  'admin.bk.csv.service': 'Service',
  'admin.bk.csv.type': 'Type',
  'admin.bk.csv.city': 'City',
  'admin.bk.csv.trx': 'TRX',
  'admin.bk.csv.decision': 'Proof decision',
  'admin.bk.source.admin': 'Admin · support',
  'admin.bk.source.walk_in': 'Walk-in',
  'admin.bk.source.phone': 'Phone',
  'admin.bk.source.marketplace': 'Online',
  'admin.bk.source.share_link': 'Link',
  'admin.bk.source.embed': 'Embed',
  'admin.bk.source.facebook': 'Facebook',
  'admin.bk.source.qr': 'QR',
  'admin.bk.source.other': 'Other',
  'admin.bk.empty': 'No bookings match — clear filters',
  'admin.bk.empty.clear': 'Clear filters',
  'admin.bk.export': 'Export CSV',
  'admin.bk.range': '{from}–{to} of {total}',
  'admin.bk.prev': '← Prev',
  'admin.bk.next': 'Next →',
  'admin.bk.error.title': 'Something went wrong',
  'admin.bk.error.body': 'The booking feed could not load. Your filters are kept — try again.',
  'admin.bk.error.retry': 'Retry',
  'admin.bk.sheet.label': 'Booking proof trail',
  'admin.bk.sheet.support_title': 'Created by {brand} support',
  'admin.bk.sheet.support_by': 'Support · {when}',
  'admin.bk.sheet.support_foot': 'The merchant sees this booking in their normal flow flagged the same way, with the reason visible. The customer sees it in their account like any other booking.',
  'admin.bk.fact.customer': 'Customer',
  'admin.bk.fact.merchant': 'Merchant',
  'admin.bk.fact.service': 'Service',
  'admin.bk.fact.when': 'When',
  'admin.bk.fact.amount': 'Amount',
  'admin.bk.fact.source': 'Source',
  'admin.bk.fact.source_support': 'Created by support on the customer’s behalf',
  'admin.bk.fact.source_merchant': 'Added by the merchant',
  'admin.bk.fact.source_customer': 'Customer-placed',
  'admin.bk.sheet.details': 'Booking details',
  'admin.bk.sheet.serial': 'Serial',
  'admin.bk.sheet.proof': 'Payment proof',
  'admin.bk.sheet.proof_shot': 'screenshot + TRX id',
  'admin.bk.sheet.proof_trx': 'TRX id',
  'admin.bk.sheet.screenshot': 'payment screenshot',
  'admin.bk.decision.accepted': 'Accepted',
  'admin.bk.decision.rejected': 'Rejected',
  'admin.bk.decision.awaiting': 'Awaiting verification',
  'admin.bk.decision.none': 'No proof',
  'admin.bk.sheet.decided_at': 'decided {when}',
  'admin.bk.sheet.no_decision': 'no decision yet',
  'admin.bk.sheet.no_proof_pending': 'No payment proof yet — the booking is awaiting an advance or venue payment.',
  'admin.bk.sheet.no_proof_venue': 'No proof on file — pay-at-venue booking.',
  'admin.bk.sheet.history': 'Status history',
  'admin.bk.sheet.no_history': 'No events recorded yet.',
  'admin.bk.sheet.dispute': 'Related dispute',
  'admin.bk.sheet.open_dispute': 'Open in Disputes →',
  'admin.bk.sheet.export': '↓ Export record',
  'admin.bk.sheet.reject': 'Reject proof',
  'admin.bk.sheet.accept': 'Accept proof → Confirmed',
  'admin.bk.toast.accepted': 'Proof accepted — {code} confirmed',
  'admin.bk.toast.rejected': 'Proof rejected — audit-logged',
  'admin.bk.toast.created': '{code} created on the customer’s behalf — audit-logged',
  'admin.bk.toast.copied': '{code} copied',
  'admin.bk.reject.title': 'Reject payment proof for {code}?',
  'admin.bk.reject.body': 'The customer is notified with your reason and can re-upload a correct proof or open a dispute.',
  'admin.bk.reject.key': 'Reason',
  'admin.bk.reject.reason': 'Note — written to the audit log',
  'admin.bk.reject.reason_ph': 'What did not match?',
  'admin.bk.reject.key_required': 'Pick a reason — it is required',
  'admin.bk.reject.note_required': 'A note is required — it is recorded in the audit log',
  'admin.bk.reason.phoned': 'Customer phoned support — could not complete it themselves',
  'admin.bk.reason.recovery': 'Recovering a failed or lost booking',
  'admin.bk.reason.recovery.hint': 'Attach the old booking code — the new booking links back to it',
  'admin.bk.reason.dispute': 'Dispute resolution — re-booking agreed in a case',
  'admin.bk.reason.dispute.hint': 'The case code is required for this reason',
  'admin.bk.reason.merchant': 'Merchant requested on the customer’s behalf',
  'admin.bk.reason.other': 'Other',
  'admin.bk.reason.other.hint': 'Free text, minimum 10 characters',
  'admin.bk.sb.label': 'Support booking',
  'admin.bk.sb.title.why': 'Why are you creating this booking?',
  'admin.bk.sb.title.who': 'Who is it for?',
  'admin.bk.sb.title.what': 'What are they booking?',
  'admin.bk.sb.title.when': 'When?',
  'admin.bk.sb.title.money': 'How is it being paid?',
  'admin.bk.sb.title.review': 'Review & create',
  'admin.bk.sb.sub.why': 'A support booking carries your name and your reason. Both sides see it.',
  'admin.bk.sb.sub.who': 'Phone first — if they already have an account, this lands in it.',
  'admin.bk.sb.sub.what': 'The merchant’s own catalogue, prices and payment state.',
  'admin.bk.sb.sub.when': 'Only what the merchant actually has left. A sold slot cannot be taken.',
  'admin.bk.sb.sub.money': 'Only what this business is set up to accept.',
  'admin.bk.sb.sub.review': 'Read it back the way the customer and the merchant will see it.',
  'admin.bk.sb.step.why': 'Why',
  'admin.bk.sb.step.who': 'Who',
  'admin.bk.sb.step.what': 'What',
  'admin.bk.sb.step.when': 'When',
  'admin.bk.sb.step.money': 'Money',
  'admin.bk.sb.step.review': 'Confirm',
  'admin.bk.sb.step_label': 'Step {n}/{total} — {name}',
  'admin.bk.sb.back': '← Back',
  'admin.bk.sb.continue': 'Continue',
  'admin.bk.sb.create': 'Create booking',
  'admin.bk.sb.change': 'Change',
  'admin.bk.sb.loading': 'Loading…',
  'admin.bk.sb.hint.reason': 'Pick a reason — it is required',
  'admin.bk.sb.hint.dispute': 'Attach the case code',
  'admin.bk.sb.hint.other': 'Write at least 10 characters',
  'admin.bk.sb.hint.phone': 'Enter the customer’s full number',
  'admin.bk.sb.hint.name': 'Enter the new customer’s name',
  'admin.bk.sb.hint.business': 'Pick the business',
  'admin.bk.sb.hint.service': 'Pick a bookable service',
  'admin.bk.sb.hint.when': 'Pick from what the merchant has open',
  'admin.bk.sb.hint.fields': 'Fill the required customer fields',
  'admin.bk.sb.hint.trx': 'Enter the TRX id the customer gave you',
  'admin.bk.sb.link_booking': 'Old booking code — the new booking links back to it',
  'admin.bk.sb.link_booking_ph': 'Booking code, customer name or phone…',
  'admin.bk.sb.link_booking_empty': 'No booking matches — leave it blank if the code is lost.',
  'admin.bk.sb.link_dispute': 'Dispute code — required for this reason',
  'admin.bk.sb.link_dispute_ph': 'Case code, customer or business…',
  'admin.bk.sb.link_dispute_empty': 'No case matches that code.',
  'admin.bk.sb.other_label': 'In your own words — minimum 10 characters',
  'admin.bk.sb.other_ph': 'What happened, in the customer’s terms',
  'admin.bk.sb.other_count': '{n}/{min} characters',
  'admin.bk.sb.verbatim': 'This appears in the booking’s history and the audit log, word for word.',
  'admin.bk.sb.note.recovers': 'Recovers {code}',
  'admin.bk.sb.note.case': 'Case {ref}',
  'admin.bk.sb.phone': 'Customer phone',
  'admin.bk.sb.phone_count': '{n}/{total} — the lookup runs at the full number.',
  'admin.bk.sb.phone_looking': 'Looking up this number…',
  'admin.bk.sb.phone_matched': 'Matched an account on this number.',
  'admin.bk.sb.phone_new': 'No account on this number yet.',
  'admin.bk.sb.existing': 'Existing customer',
  'admin.bk.sb.joined': 'Joined {when}',
  'admin.bk.sb.cust_bookings': '{n, plural, one{# booking} other{# bookings}}',
  'admin.bk.sb.cust_attach': 'Name and email attach automatically — nothing to retype.',
  'admin.bk.sb.new_customer': 'No account on this number — new customer',
  'admin.bk.sb.name_ph': 'Full name — required',
  'admin.bk.sb.email_ph': 'Email — optional',
  'admin.bk.sb.same_as_any': 'The customer gets the same SMS as any booking — they will see it in their own account under this number.',
  'admin.bk.sb.business': 'Business',
  'admin.bk.sb.business_ph': 'Search the merchant directory…',
  'admin.bk.sb.business_closed': '{names} — suspended and banned merchants cannot take bookings, so they are not offered here.',
  'admin.bk.sb.kyc.verified': 'Payment KYC verified',
  'admin.bk.sb.kyc.in_review': 'Payment KYC in review',
  'admin.bk.sb.kyc.not_enabled': 'Payment not enabled',
  'admin.bk.sb.service': 'Service',
  'admin.bk.sb.service_none': 'Pick the business first — only its live services are offered.',
  'admin.bk.sb.service_empty': 'This business has no live service to book.',
  'admin.bk.sb.bookable': 'Bookable',
  'admin.bk.sb.booking_off': 'Booking off',
  'admin.bk.sb.booking_off_title': 'Booking is switched off by the merchant.',
  'admin.bk.sb.live_count': '{n, plural, one{# live service} other{# live services}} — prices are the merchant’s own.',
  'admin.bk.sb.photo': 'service photo',
  'admin.bk.sb.change_service': 'Change service',
  'admin.bk.sb.model_note': 'Scheduling model: {model} — rendered exactly as the merchant configured it.',
  'admin.bk.sb.model.time_slot': 'Time slot',
  'admin.bk.sb.model.serial_session': 'Serial queue',
  'admin.bk.sb.model.capacity_session': 'Capacity session',
  'admin.bk.sb.model.seat_inventory': 'Seat inventory',
  'admin.bk.sb.model.date_range': 'Date range',
  'admin.bk.sb.model.block_booking': 'Block booking',
  'admin.bk.sb.date': 'Date',
  'admin.bk.sb.check_in': 'Check-in',
  'admin.bk.sb.today': 'Today',
  'admin.bk.sb.slots': 'Slots the merchant has open',
  'admin.bk.sb.slot_taken': 'Already booked with the merchant',
  'admin.bk.sb.no_override': 'A slot the merchant has already sold cannot be taken — there is no admin override.',
  'admin.bk.sb.none_on_date': 'The merchant has nothing open on this date — try another.',
  'admin.bk.sb.full_title': 'Every slot on this date is taken',
  'admin.bk.sb.full_block': 'This date is already booked out',
  'admin.bk.sb.full_body': 'There is no admin override for a merchant’s inventory. Pick another date.',
  'admin.bk.sb.chamber': 'Chamber session',
  'admin.bk.sb.session': 'Session',
  'admin.bk.sb.serials_issued': '{n}/{cap} serials issued',
  'admin.bk.sb.places_left': '{n}/{cap} places left',
  'admin.bk.sb.places_in_session': '{n} places left in this session',
  'admin.bk.sb.full': 'Full',
  'admin.bk.sb.selected': 'Selected',
  'admin.bk.sb.serial_no': 'Serial number',
  'admin.bk.sb.serial_value': 'No. {n}',
  'admin.bk.sb.serial_note': 'Assigned by the merchant’s queue and shown read-only — support does not choose serial numbers.',
  'admin.bk.sb.room_type': 'Room type',
  'admin.bk.sb.per_night': 'per night',
  'admin.bk.sb.left': '{n} left',
  'admin.bk.sb.none_left': 'none left',
  'admin.bk.sb.sold_out': 'Sold out',
  'admin.bk.sb.block': 'Block',
  'admin.bk.sb.total': 'total',
  'admin.bk.sb.fields_label': '{template} — the customer’s own fields',
  'admin.bk.sb.required': 'required',
  'admin.bk.sb.optional': 'optional',
  'admin.bk.sb.select': 'Select…',
  'admin.bk.sb.as_described': 'As the customer described it',
  'admin.bk.sb.pay.venue': 'Pay at venue',
  'admin.bk.sb.pay.venue_sub': 'The customer settles with the merchant when they arrive.',
  'admin.bk.sb.pay.paid': 'Customer already paid the merchant directly',
  'admin.bk.sb.pay.paid_sub': 'Goes into the payment check. Support cannot mark it verified.',
  'admin.bk.sb.pay.not_enabled': 'This business takes payment at the venue. It is not payment-enabled, so there is nothing else to record here.',
  'admin.bk.sb.proof_label': 'Proof the customer gave you',
  'admin.bk.sb.method': 'Payment method',
  'admin.bk.sb.trx_ph': 'TRX id — e.g. BKH1A2B3C4',
  'admin.bk.sb.attest': 'Verified by phone with the merchant',
  'admin.bk.sb.proof_note': 'The booking enters the payment check for verification, exactly as a customer-uploaded proof would. Support cannot mark money as verified.',
  'admin.bk.sb.review.reason': 'Reason — recorded verbatim',
  'admin.bk.sb.review.customer': 'Customer',
  'admin.bk.sb.review.existing': 'existing account',
  'admin.bk.sb.review.new': 'new customer',
  'admin.bk.sb.review.business': 'Business',
  'admin.bk.sb.review.service': 'Service',
  'admin.bk.sb.review.when': 'When',
  'admin.bk.sb.review.price': 'Price',
  'admin.bk.sb.review.price_sub': 'The merchant’s own price — confirmed by the server at create',
  'admin.bk.sb.review.payment': 'Payment',
  'admin.bk.sb.review.paid': 'Paid the merchant directly — to be verified',
  'admin.bk.sb.review.attested': 'verified by phone with the merchant',
  'admin.bk.sb.notify': 'Notify the customer',
  'admin.bk.sb.notify_sms': 'SMS',
  'admin.bk.sb.notify_sms_meta': 'Sent on create — the same booking SMS as any booking.',
  'admin.bk.sb.review_foot': 'The merchant sees it flagged “Created by {brand} support” with your reason, and the creation is written to the audit log under your name.',
  'admin.bk.sb.done.title': 'Support booking created',
  'admin.bk.sb.done.sub': 'The customer and the merchant both see who created it and why.',
  'admin.bk.sb.done.code': 'Booking code',
  'admin.bk.sb.done.copy': 'Copy code',
  'admin.bk.sb.done.copied': 'Copied',
  'admin.bk.sb.done.notified': 'The customer is notified by SMS.',
  'admin.bk.sb.done.trail': 'Source is recorded as Admin · support, with your reason on the booking’s history and the audit log.',
  'admin.bk.sb.done.trail_paid': 'Source is recorded as Admin · support. The direct payment now waits in the payment check — {brand} does not verify it on the customer’s word.',
  'admin.bk.sb.done.rate_warning': 'This customer has several support-created bookings. That is unusual enough to be worth a look.',
  'admin.bk.sb.done.open': 'Open booking',
  'admin.bk.sb.done.another': 'Create another',
  'admin.bk.sb.discard.title': 'Discard this support booking?',
  'admin.bk.sb.discard.body': 'Nothing is created and nothing is logged. The reason and the details entered here are lost.',
  'admin.bk.sb.discard.keep': 'Keep editing',
  'admin.bk.sb.discard.cta': 'Discard',
  // ---- end DC94 ----
  // ---- DC55 website For Business + Pricing (DR-31 plan ladder, W §D.10–D.11) ----
  'dc55.pr.eyebrow': 'Pricing',
  'dc55.pr.title': 'One flat subscription. Zero commission.',
  'dc55.pr.sub': 'Customers pay you directly — bKash, Nagad, bank or cash. {brand} never takes a cut of your bookings.',
  'dc55.term.label': 'Subscription term',
  'dc55.term.months': '{n} months',
  'dc55.launch_offer': 'Launch offer — first term',
  'dc55.plan.free_forever': 'Free forever',
  'dc55.plan.per_term': '/ {n} months',
  'dc55.plan.per_month_eq': '≈ {price} a month, equivalent',
  'dc55.plan.renews_at': 'Renews at {price} / {n} months',
  'dc55.plan.was': 'Regular price {price}',
  'dc55.plan.most_popular': 'Most popular',
  'dc55.plan.best_value': 'Best value',
  'dc55.plan.start_free': 'Start free',
  'dc55.plan.get': 'Get {plan}',
  'dc55.plan.unlimited_bookings': 'Unlimited bookings',
  'dc55.plan.free_gate': 'Unlimited for {months} months, then {cap} a month',
  'dc55.plan.cap': '{cap} bookings a month',
  'dc55.plan.ent_line': '{label} — {n}',
  'dc55.plan.ent_unlimited': '{label} — unlimited',
  'dc55.plan.booking_toggle': 'Booking is a toggle on every service',
  'dc55.plan.share_link': 'Shareable booking link',
  'dc55.plan.email_free': 'Email notifications are always free',
  'dc55.plan.included': 'Included',
  'dc55.plan.not_included': 'Not included',
  'dc55.ent.businesses': 'Business profiles',
  'dc55.ent.services': 'Services per business',
  'dc55.ent.professionals': 'Professionals',
  'dc55.ent.offers': 'Live offers',
  'dc55.ent.moderator_seats': 'Moderator seats',
  'dc55.ent.payments': 'Direct payments (bKash / Nagad / bank / cash)',
  'dc55.ent.private_page': 'Private booking page',
  'dc55.ent.promote': 'Promote',
  'dc55.ent.insights': 'Insights — sources, busy hours, no-shows',
  'dc55.plan.loyalty_line': 'Stay subscribed and you renew at your loyalty price — always below the regular rate.',
  'dc55.plan.terms_note': 'Launch price for new merchants only, once, on your first term. Prices exclude VAT. Payment features need your business documents reviewed — usually within 24 hours.',
  'dc55.plan.terms_note_regular': 'Prices exclude VAT. Payment features need your business documents reviewed — usually within 24 hours.',
  'dc55.contact.title': 'Need more than {plan}?',
  'dc55.contact.body': 'Running more than {n} businesses? Tell us what you need and we will talk it through.',
  'dc55.contact.cta': 'Contact us',
  'dc55.see_full_pricing': 'See full pricing details',
  'dc55.cmp.title': 'Compare plans',
  'dc55.cmp.feature': 'Feature',
  'dc55.cmp.bookings': 'Bookings a month',
  'dc55.cmp.unlimited': 'Unlimited',
  'dc55.cmp.gate': 'Unlimited {months} months, then {cap}',
  'dc55.cmp.price_free': '{price} · Free forever',
  'dc55.cmp.price_term': '{price} / {n} months',
  'dc55.promo.title': 'Promote when you’re ready.',
  'dc55.promo.sub': 'Featured placement is booked with our team: your offer in a labelled spot on the Home and Offers pages, on the dates you agree. Email notifications are always free.',
  'dc55.promo.kicker': 'Placement',
  'dc55.promo.card_title': 'Featured offer placements',
  'dc55.promo.card_sub': 'Put one of your offers in a sponsored position on the Home and Offers pages.',
  'dc55.promo.per_day': '{price} per slot per day',
  'dc55.promo.ask_rate': 'Ask us for the current rate',
  'dc55.promo.slots': 'Three slots: the Offers slider, the biggest-discounts rail and the home offers rail',
  'dc55.promo.labelled': 'Always labelled Sponsored',
  'dc55.steps.kicker': 'Get started',
  'dc55.steps.title': 'Your first customers are three steps away',
  'dc55.steps.sub': 'No contracts, no commission — start with one small deal run and watch the bookings arrive.',
  'dc55.steps.talk_to_sales': 'Talk to sales',
  'dc55.steps.free_consult': 'Free consultation · Bangla or English',
  'dc55.prfaq.title': 'Pricing FAQ',
  'dc55.prfaq.q1': 'Do you take commission on bookings?',
  'dc55.prfaq.a1': 'No. Customers pay you directly — bKash, Nagad, bank transfer or cash — and {brand} never touches the money. {brand} does not process, hold, or guarantee direct payments.',
  'dc55.prfaq.q2': 'What if I stop paying?',
  'dc55.prfaq.a2': 'You drop back to the Free tier. Your business profiles, services and reviews stay live; bookings past {cap} a month arrive blurred again until you subscribe.',
  'dc55.prfaq.q3': 'What is the loyalty price?',
  'dc55.prfaq.a3': 'Your renewal price. As long as the subscription runs without a break you renew below the regular rate — {prices} for the {terms}-month terms.',
  'dc55.prfaq.q4': 'How do I pay for the subscription?',
  'dc55.prfaq.a4': 'bKash or Nagad today; cards arrive with SSLCommerz.',
  'dc55.list.and': ' and ',
  'dc55.list.and_short': ' and ',
  // For Business — "List your business free" page (website /for-business).
  // Post an offer + Get featured pages (website /post-an-offer, /get-featured).
  'pao.kicker': 'For business',
  'pao.title_a': 'Post an offer.',
  'pao.title_b': 'It’s free.',
  'pao.lede': 'Every business with a claimed listing can post its own offers at no cost. Our team reviews each offer and approves it before it goes live on the Offers page.',
  'pao.who_label': 'Your situation',
  'pao.who_listed': 'My business is on {brand}',
  'pao.who_not_listed': 'My business isn’t listed yet',
  'pao.c_title': 'Post your first offer in four steps.',
  'pao.c_sub': 'Your listing is already claimed, so you can start right away.',
  'pao.c1_t': 'Sign in to your business',
  'pao.c1_b': 'Use the account that claimed your listing.',
  'pao.c2_t': 'Create your offer',
  'pao.c2_b': 'Add a title, the discount, the service it applies to and the dates it runs.',
  'pao.c3_t': 'Submit it for review',
  'pao.c3_b': 'Our team checks that the offer is accurate, halal and legal.',
  'pao.c4_t': 'It goes live once approved',
  'pao.c4_b': 'Approved offers appear on the Offers page and on your listing.',
  'pao.c_cta': 'Sign in to post an offer',
  'pao.n_title': 'List your business first. Then post offers.',
  'pao.n_sub': 'Offers belong to a listing. Create yours for free, and once it is approved you can add your own offers.',
  'pao.n1_t': 'List your business and services',
  'pao.n1_b': 'Add your name, category, area and the services you offer.',
  'pao.n2_t': 'We review your listing',
  'pao.n2_b': 'We approve businesses that meet our standard: halal and legal.',
  'pao.n3_t': 'Your listing goes live',
  'pao.n3_b': 'Once approved, customers can find and book you.',
  'pao.n4_t': 'Add your own offers',
  'pao.n4_b': 'Post offers for free. Each one is reviewed and approved before it goes live.',
  'pao.n_cta': 'List your business — free',
  'pao.tag_review': 'Reviewed by our team',
  'pao.tag_free': 'Free for claimed businesses',
  'pao.tag_forever': 'Free forever',
  'pao.mock_label': 'Example offer form',
  'pao.mock_title': 'What an offer looks like',
  'pao.mock_cap': 'Example. Change the fields to preview your offer.',
  'pao.mock_f_title': 'Offer title',
  'pao.mock_f_discount': 'Discount (%)',
  'pao.mock_f_applies': 'Applies to',
  'pao.mock_title_value': 'Weekday night slots',
  'pao.mock_svc_1': 'Night slot, 90 min',
  'pao.mock_svc_2': 'Day slot, 90 min',
  'pao.mock_svc_all': 'All services',
  'pao.mock_off': '{pct} OFF',
  'pao.mock_your_business': 'Your business',
  'pao.mock_submit': 'Submit for review',
  'pao.st_draft': 'Draft',
  'pao.st_pending': 'Pending review',
  'pao.st_approved': 'Approved',
  'pao.tips_kicker': 'Make it a good one',
  'pao.tips_title': 'Offers customers trust get booked.',
  'pao.tip1_t': 'Be clear about the deal',
  'pao.tip1_b': 'State the discount and what it applies to. Customers should know the price before they book.',
  'pao.tip2_t': 'Set honest dates',
  'pao.tip2_b': 'Give a start and end date, and keep to them. Expired or changed terms are removed.',
  'pao.tip3_t': 'Meet our standard',
  'pao.tip3_b': 'Offers must be halal and legal, like every listing on {brand}.',
  'pao.faq_kicker': 'Questions',
  'pao.faq_title': 'About posting offers',
  'pao.q1': 'Is posting an offer really free?',
  'pao.a1': 'Yes, for every business with a claimed listing. There is no fee to post an offer, and {brand} takes no commission on bookings.',
  'pao.q2': 'Who approves my offer?',
  'pao.a2': 'Our team reviews every offer before it goes live. We check that it is accurate and meets our halal and legal standard.',
  'pao.q3': 'My business isn’t on {brand} yet. Can I still post an offer?',
  'pao.a3': 'First list your business and services for free. Once your listing is approved, you can add your own offers.',
  'pao.q4': 'Can I get my offer more visibility?',
  'pao.a4': 'Featured placement is a paid service that is coming soon.',
  'pao.a4_link': 'See what’s coming',
  'gfe.soon': 'Coming soon',
  'gfe.title_a': 'Get',
  'gfe.title_b': 'featured.',
  'gfe.lede': 'Put your offer in a labelled spot on the Home and Offers pages. Featured placement is a paid service, launching soon.',
  'gfe.nb_title': 'Be first to know',
  'gfe.nb_body': 'Leave your email or WhatsApp number and we will message you when featured placements open.',
  'gfe.nb_label': 'Email or WhatsApp number',
  'gfe.nb_ph': 'you@mail.com / +880…',
  'gfe.nb_err': 'Enter a valid email or WhatsApp number.',
  'gfe.nb_failed': 'We could not save your request. Please try again.',
  'gfe.nb_cta': 'Notify me at launch',
  'gfe.nb_sending': 'Saving…',
  'gfe.ticket_message': 'Please notify me when featured placements open.',
  'gfe.done_title': 'You’re on the list',
  'gfe.done_body': 'We will message you when featured placements open.',
  'gfe.where_kicker': 'Where you’ll appear',
  'gfe.where_title': 'Three spots, always labelled Sponsored.',
  'gfe.where_sub': 'Featured offers sit in prominent places that customers already browse.',
  'gfe.sponsored': 'Sponsored',
  'gfe.slot1_t': 'Offers slider',
  'gfe.slot1_b': 'The first thing customers see on the Offers page.',
  'gfe.slot2_t': 'Biggest-discounts rail',
  'gfe.slot2_b': 'A highlighted spot among the best deals live right now.',
  'gfe.slot3_t': 'Home offers rail',
  'gfe.slot3_b': 'Visible to everyone who opens the {brand} home page.',
  'gfe.note_b': 'Always labelled.',
  'gfe.note': 'Every featured offer is clearly marked Sponsored, so customers know what is paid placement.',
  'gfe.how_kicker': 'How it will work',
  'gfe.how_title': 'Booked with our team.',
  'gfe.how1_t': 'Choose your spot and dates',
  'gfe.how1_b': 'Tell our team which placement you want and the dates that suit you. Details are announced at launch.',
  'gfe.how2_t': 'Pick an approved offer',
  'gfe.how2_b': 'Feature one of your own offers that has already been reviewed and approved.',
  'gfe.how3_t': 'Go live, labelled',
  'gfe.how3_b': 'Your offer appears in the spot on the agreed dates, marked Sponsored.',
  'gfe.cta_title': 'Don’t wait to get noticed.',
  'gfe.cta_body': 'Offers are free for claimed businesses today. Post one now and be ready when featured placements open.',
  'gfe.cta_btn': 'Post a free offer',
  'lbf.kicker': '{brand} for business',
  'lbf.title_a': 'List your business.',
  'lbf.title_b': 'Free, forever.',
  'lbf.lede': 'Submit your listing for free. We review every submission, and once it meets our business standard (halal and legal) it goes live. Customers across {region} then find you in search, see your services and prices, and book you directly.',
  'lbf.cta_list': 'List your business — free',
  'lbf.cta_how': 'See how it works',
  'lbf.assure_free': '{zero}, no card needed',
  'lbf.assure_commission': 'Zero commission',
  'lbf.assure_reviewed': 'Reviewed before going live',
  'lbf.form.title': 'Start your listing',
  'lbf.form.badge': 'FREE FOREVER',
  'lbf.form.sub': 'A few details now. Add services, photos and prices once you are in.',
  'lbf.form.name': 'Business name',
  'lbf.form.name_ph': 'e.g. {example}',
  'lbf.form.name_err': 'Enter your business name.',
  'lbf.form.category': 'Category',
  'lbf.form.category_ph': 'Choose a category',
  'lbf.form.category_err': 'Choose a category.',
  'lbf.form.area': 'Area',
  'lbf.form.area_ph': 'Choose an area',
  'lbf.form.area_err': 'Choose an area.',
  'lbf.form.other': 'Other',
  'lbf.form.phone': 'Mobile number',
  'lbf.form.submit': 'Submit my free listing',
  'lbf.form.note': 'Free to submit. We review every listing before it goes live.',
  'lbf.form.done_title': '{name} is ready to submit',
  'lbf.form.done_body': '{category} in {area}. Create your free account to send it for review. We approve listings that meet our halal and legal standard.',
  'lbf.form.done_cta': 'Continue to sign up',
  'lbf.form.edit': 'Edit details',
  'lbf.prom.free_t': 'Free forever',
  'lbf.prom.free_b': 'Listing your business costs nothing today or any day after.',
  'lbf.prom.comm_b': 'Customers pay you directly. {brand} never takes a cut of your bookings.',
  'lbf.prom.vet_big': 'Vetted',
  'lbf.prom.vet_t': 'Reviewed for trust',
  'lbf.prom.vet_b': 'Every listing is checked against our halal and legal standard before it goes live.',
  'lbf.prom.own_big': 'Yours',
  'lbf.prom.own_t': 'You stay in control',
  'lbf.prom.own_b': 'Edit prices, hours and services whenever you like, or take the listing down.',
  'lbf.how.kicker': 'How it works',
  'lbf.how.title': 'From submission to first booking in three steps.',
  'lbf.how.s1_t': 'Submit your listing',
  'lbf.how.s1_b': 'Tell us your business name, category, area and phone number. Add services, prices, hours and photos so the review has the full picture.',
  'lbf.how.s1_tags': 'Name|Category|Area|Services',
  'lbf.how.s2_t': 'We review it',
  'lbf.how.s2_b': 'Our team checks your listing against our business standard. We approve businesses that are halal and legal.',
  'lbf.how.s2_tags': 'Halal|Legal|Reviewed',
  'lbf.how.s3_t': 'Go live and get booked',
  'lbf.how.s3_b': 'Once approved, customers searching in your category and area see your listing. Turn booking on for any service and share your link anywhere.',
  'lbf.how.s3_tags': 'Search|Booking link|Email alerts',
  'lbf.found.kicker': 'Be found',
  'lbf.found.title': 'Customers are already searching. Show up where they look.',
  'lbf.found.lede': 'Once approved, your free listing is part of the whole {brand} ecosystem, not a page that sits alone waiting for visitors.',
  'lbf.found.search_t': 'Search',
  'lbf.found.search_b': 'Appear when people look for your kind of business in your area.',
  'lbf.found.pages_t': 'Category & area pages',
  'lbf.found.pages_b': 'Browsed by city and category, from turfs to salons to clinics.',
  'lbf.found.offers_t': 'Offers',
  'lbf.found.offers_b': 'Post an offer on your listing and it can appear on the Offers page.',
  'lbf.found.videos_t': 'Videos',
  'lbf.found.videos_b': 'Reels and reviews that link straight to your listing.',
  'lbf.found.query': '{category} in {area}',
  'lbf.found.free_badge': 'Free listing',
  'lbf.found.book': 'Book now',
  'lbf.found.call': 'Call',
  'lbf.found.caption': 'Example listing. It updates as you fill in the form above.',
  'lbf.found.preview_label': 'Example of how a listing looks in search',
  'lbf.get.kicker': 'What you get',
  'lbf.get.title': 'Everything you need to be bookable.',
  'lbf.get.profile_t': 'A public business profile',
  'lbf.get.profile_b': 'Your name, category, area, contact details and photos on one page.',
  'lbf.get.prices_t': 'Services with prices',
  'lbf.get.prices_b': 'Customers see what you charge before they get in touch.',
  'lbf.get.booking_t': 'Booking on any service',
  'lbf.get.booking_b': 'Switch it on per service and share a booking link on WhatsApp, Facebook or in your bio.',
  'lbf.get.email_t': 'Email notifications',
  'lbf.get.email_b': 'New booking alerts reach you by email, always free.',
  'lbf.get.reach_t': 'Offers and video reach',
  'lbf.get.reach_b': 'Post an offer or get featured in reels to bring in more customers.',
  'lbf.who.title': 'Built for every local business',
  'lbf.who.body': 'If customers book you, ask for a quote or walk in after finding you online, you belong here.',
  'lbf.who.more_chip': 'And more',
  'lbf.who.more': 'One business or several, a single chair or a whole resort. The listing is free for all of them.',
  'lbf.std.kicker': 'Our business standard',
  'lbf.std.title': 'Every listing is reviewed. We approve halal and legal businesses.',
  'lbf.std.lede': 'Customers trust {brand} to list businesses they can rely on. That is why we look at each submission before it goes live.',
  'lbf.std.halal_t': 'Halal',
  'lbf.std.halal_b': 'The products, services and activities you offer must be halal.',
  'lbf.std.legal_t': 'Legal',
  'lbf.std.legal_b': 'Your business must operate legally in {region} and offer what it says it offers.',
  'lbf.std.live_t': 'Approved, then live',
  'lbf.std.live_b': 'When your listing meets the standard, we approve it and it goes live. A listing that does not meet it is not approved.',
  'lbf.faq.kicker': 'Questions',
  'lbf.faq.title': 'Questions business owners ask us',
  'lbf.faq.q1': 'Is it really free forever?',
  'lbf.faq.a1': 'Yes. Submitting and keeping a listing on {brand} is free, and it stays free. There is no trial period and no card to enter.',
  'lbf.faq.q2': 'Does {brand} take a commission on bookings?',
  'lbf.faq.a2': 'No. Customers pay you directly, by bKash, Nagad, bank or cash. {brand} never takes a cut of your bookings.',
  'lbf.faq.q3': 'Will my listing go live straight away?',
  'lbf.faq.a3': 'No. Every listing is reviewed first. If it meets our business standard, we approve it and it goes live.',
  'lbf.faq.q4': 'What is your business standard?',
  'lbf.faq.a4': 'We list businesses that are halal and legal. We review each submission against that standard and approve only the listings that meet it.',
  'lbf.faq.q5': 'How will customers find me?',
  'lbf.faq.a5': 'Once your listing is approved, customers find it through search by category and area, on category pages, in offers and in videos across {brand}. Add clear services and prices so your listing is easy to choose.',
  'lbf.faq.q6': 'Can I change or remove my listing later?',
  'lbf.faq.a6': 'Yes. You can update services, prices and hours whenever you like, or take your listing down at any time.',
  'lbf.end.pill': 'Free forever · Reviewed listings',
  'lbf.end.title_a': 'Your customers are already searching.',
  'lbf.end.title_b': 'Be the one they find.',
  'lbf.end.body': 'Submit your business today. It takes a few details, costs nothing and goes live once we approve it.',
  'lbf.end.talk': 'Questions? Talk to our team',
  'lbf.end.trust_free': '{zero}, free forever',
  'lbf.end.trust_comm': '{pct} commission',
  'lbf.end.trust_std': 'Halal & legal standard',
  'lbf.end.float_searching': 'A customer searching now',
  'lbf.end.float_off': '{pct} OFF',
  'lbf.end.float_yours': 'Your business',
  'lbf.end.float_approved': 'Approved',
  'lbf.end.float_booking': 'New booking',
  // ---- end DC55 ----
  // ---- DC99 admin Merchants ----
  'admin.merchants.sub': 'Every business listing with its owner, tier, term, renewal and verification state. Row → full history and admin actions. The people behind them are under Merchants.',
  'admin.merchants.stat.active': 'Active — all tiers',
  'admin.merchants.stat.pending': 'Pending approval',
  'admin.merchants.stat.suspended': 'Suspended',
  'admin.merchants.stat.banned': 'Banned',
  'admin.merchants.stat.tier_count': '{tier} {n}',
  'admin.merchants.tab.all': 'All businesses ({n})',
  'admin.merchants.tab.free': 'Free ({n})',
  'admin.merchants.tab.paid': 'Paid ({n})',
  'admin.merchants.add': '＋ Add business',
  'admin.merchants.title.all': 'All businesses',
  'admin.merchants.title.free': 'Businesses on Free',
  'admin.merchants.title.paid': 'Businesses on a paid plan',
  'admin.merchants.search': 'Search name, phone, city…',
  'admin.merchants.filter.tier': 'All tiers',
  'admin.merchants.filter.status': 'All statuses',
  'admin.merchants.filter.kyc': 'All KYC',
  'admin.merchants.filter.city': 'All cities',
  'admin.merchants.filter.category': 'All categories',
  'admin.merchants.chip.tier': 'Tier',
  'admin.merchants.chip.status': 'Status',
  'admin.merchants.chip.kyc': 'KYC',
  'admin.merchants.chip.city': 'City',
  'admin.merchants.chip.category': 'Category',
  'admin.merchants.filter.select_all': 'Select all',
  'admin.merchants.filter.clear': 'Clear',
  'admin.merchants.filter.clear_all': 'Clear all filters',
  'admin.merchants.filter.more': '{first} +{n}',
  'admin.merchants.col.merchant': 'Business',
  'admin.merchants.col.merchant_title': 'Business name · phone · area, city',
  'admin.merchants.col.plan': 'Plan',
  'admin.merchants.col.plan_title': 'Tier, paid term and renewal date',
  'admin.merchants.col.usage': 'Usage',
  'admin.merchants.col.usage_title': 'Business profiles, services and professionals in use',
  'admin.merchants.col.status': 'Status',
  'admin.merchants.col.status_title': 'Account status and payment-KYC state',
  'admin.merchants.col.quality': 'Quality',
  'admin.merchants.col.quality_title': 'Customer rating and review count',
  'admin.merchants.col.value': 'Value',
  'admin.merchants.col.value_title': 'Monthly recurring revenue and lifetime bookings',
  'admin.merchants.col.actions': 'Actions',
  'admin.merchants.sort.tier': 'Sort by tier',
  'admin.merchants.sort.term': 'Sort by term',
  'admin.merchants.sort.renewal': 'Sort by renewal date',
  'admin.merchants.sort.rating': 'Sort by rating',
  'admin.merchants.sort.reviews': 'Sort by reviews',
  'admin.merchants.sort.mrr': 'Sort by MRR',
  'admin.merchants.sort.bookings': 'Sort by bookings',
  'admin.merchants.cell.term': '{n} mo',
  'admin.merchants.cell.never_expires': 'Never expires',
  'admin.merchants.cell.no_renewal': 'No renewal',
  'admin.merchants.cell.renews': 'Renews {date}',
  'admin.merchants.cell.month_cap': '{n} of {cap} bookings this month',
  'admin.merchants.cell.month': '{n} bookings this month',
  'admin.merchants.cell.usage1': '{b}/{bc} businesses · {s}/{sc} services',
  'admin.merchants.cell.usage2': '{p}/{pc} professionals',
  'admin.merchants.cell.rating': '{r} ★',
  'admin.merchants.cell.no_rating': 'No rating',
  'admin.merchants.cell.reviews': '{n} reviews',
  'admin.merchants.cell.rating_reviews': '{r} ★ · {n} reviews',
  'admin.merchants.cell.bookings': '{n} bookings',
  'admin.merchants.action.review': 'Review →',
  'admin.merchants.action.view': 'View merchant',
  'admin.merchants.action.slug': 'Booking slug',
  'admin.merchants.action.suspend': 'Suspend',
  'admin.merchants.action.ban': 'Ban merchant',
  'admin.merchants.action.ban_short': 'Ban',
  'admin.merchants.action.reinstate': 'Reinstate',
  'admin.merchants.action.reactivate': 'Reactivate',
  'admin.merchants.action.verify': 'Verify',
  'admin.merchants.action.unverify': 'Remove verification',
  'admin.merchants.action.feature': 'Feature',
  'admin.merchants.action.unfeature': 'Unfeature',
  'admin.merchants.toast.verify': '{name} verified — badge shown',
  'admin.merchants.toast.unverify': '{name} — verification removed',
  'admin.merchants.toast.feature': '{name} featured',
  'admin.merchants.toast.unfeature': '{name} no longer featured',
  'admin.merchants.action.more': 'More actions',
  'admin.merchants.bulk.selected': '{n} selected',
  'admin.merchants.bulk.suspend': 'Suspend selected',
  'admin.merchants.bulk.export': 'Export selected',
  'admin.merchants.select_page': 'Select this page',
  'admin.merchants.empty': 'No businesses match',
  'admin.merchants.empty.clear': 'Clear filters',
  'admin.merchants.export': 'Export CSV',
  'admin.merchants.range': '{from}–{to} of {total}',
  'admin.merchants.dlg.reason': 'Reason — written to the audit log',
  'admin.merchants.dlg.reason_ph': 'Add a short reason…',
  'admin.merchants.dlg.reason_required': 'A reason is required — it is recorded in the audit log',
  'admin.merchants.dlg.cancel': 'Cancel',
  'admin.merchants.dlg.suspend.title': 'Suspend {name}',
  'admin.merchants.dlg.suspend.body': 'Suspending hides all their services from the marketplace instantly. The owner is notified with your reason.',
  'admin.merchants.dlg.ban.title': 'Ban {name}?',
  'admin.merchants.dlg.ban.body': 'Permanent removal from the marketplace. Logged and reversible only by a Super Admin.',
  'admin.merchants.dlg.reactivate.title': 'Reactivate {name}?',
  'admin.merchants.dlg.reactivate.body': 'Restores the account and returns all their services to the marketplace immediately. A note is optional.',
  'admin.merchants.dlg.reactivate.ban_note': 'Reversing a ban is Super Admin only.',
  'admin.merchants.dlg.bulk.title': 'Suspend {n} merchant(s)?',
  'admin.merchants.dlg.bulk.body': 'All their services leave the marketplace immediately; owners are notified.',
  'admin.merchants.dlg.bulk.skipped': '{n} merchant(s) that are not active are left out.',
  'admin.merchants.dlg.slug.title': 'Booking slug — {name}',
  'admin.merchants.dlg.slug.body': 'Current public URL: {url}. Old slugs keep redirecting to the new one, and QR tracking keeps working.',
  'admin.merchants.dlg.slug.label': 'New slug',
  'admin.merchants.dlg.slug.regenerate': 'Regenerate',
  'admin.merchants.dlg.slug.save': 'Save slug',
  'admin.merchants.dlg.slug.invalid': 'Enter a valid slug',
  'admin.merchants.dlg.slug.taken': 'That slug is already taken — pick another',
  'admin.merchants.toast.suspended': '{name} suspended — services hidden, owner notified',
  'admin.merchants.toast.banned': '{name} banned — owner notified',
  'admin.merchants.toast.reactivated': '{name} reactivated — services restored, owner notified',
  'admin.merchants.toast.slug': 'Slug saved — /b/{slug} · old slugs redirect',
  'admin.merchants.toast.bulk': '{ok} of {n} business(es) suspended',
  'admin.merchants.toast.exported': '{n} merchant(s) exported',
  'admin.merchants.toast.draft': '{name} saved as a draft — owner notified',
  'admin.merchants.toast.sent': '{name} saved & sent to Approvals — owner notified',
  'admin.merchants.toast.failed': 'Could not save — please try again',
  'admin.merchants.pd.back': '← All businesses',
  // DR-78 merchant workspace: Merchants are people, Businesses are their listings.
  'admin.nav.businesses': 'Businesses',
  'admin.merchants.col.owner': 'Owner',
  'admin.merchants.col.owner_title': 'The merchant (person) who owns the listing — opens their record',
  'admin.merchants.pd.sec.owner': 'Owner',
  'admin.merchants.pd.f.owner_name': 'Name',
  'admin.merchants.pd.f.owner_phone': 'Phone',
  'admin.merchants.pd.link.owner': 'Open the merchant →',
  'admin.merchants.action.reassign': 'Reassign owner',
  'admin.merchants.dlg.reassign.title': 'Move {name} to another merchant',
  'admin.merchants.dlg.reassign.body': 'The listing, the services and offers created under it and its invoices move to the new owner. Each person keeps their own plan. Both are notified by SMS.',
  'admin.merchants.dlg.reassign.input': 'New owner — user id',
  'admin.merchants.dlg.reassign.confirm': 'Reassign — audit-logged',
  'admin.merchants.dlg.reassign.invalid': 'Enter the new owner’s user id',
  'admin.merchants.dlg.reassign.refused': 'That account cannot own the listing (not an active customer or merchant, or already the owner).',
  'admin.merchants.toast.reassigned': '{name} reassigned — both owners notified, audit-logged',
  'admin.people.title': 'Merchants',
  'admin.people.title_count': 'Merchants ({n})',
  'admin.people.sub': 'Every merchant account — the person — with their NID state, plan and what they own. A listing is a Business; open it from the row.',
  'admin.people.search': 'Search name, phone, email…',
  'admin.people.filter.tier': 'All tiers',
  'admin.people.filter.nid': 'All NID states',
  'admin.people.nid.none': 'No NID',
  'admin.people.nid.submitted': 'NID submitted',
  'admin.people.nid.verified': 'NID verified',
  'admin.people.nid.rejected': 'NID rejected',
  'admin.people.col.merchant': 'Merchant',
  'admin.people.col.nid': 'NID',
  'admin.people.col.plan': 'Plan',
  'admin.people.col.owns': 'Owns',
  'admin.people.col.bookings': 'Bookings',
  'admin.people.col.joined': 'Joined',
  'admin.people.owns': '{b} businesses · {s} services · {o} offers',
  'admin.people.owns_deals': '{n} imported deals',
  'admin.people.add': '＋ Add merchant',
  'admin.svcadd.add': '＋ Add service',
  'admin.svcadd.title': 'Add a service',
  'admin.svcadd.title_for': 'Add a service · {business}',
  'admin.svcadd.sub': 'It goes live at once — as the admin you are its reviewer. The service belongs to the business owner and is offered at this business.',
  'admin.svcadd.business': 'Business',
  'admin.svcadd.name_en': 'Service name (English)',
  'admin.svcadd.name_bn': 'Service name (Bangla)',
  'admin.svcadd.group': 'Menu group',
  'admin.svcadd.group_default': 'Services',
  'admin.svcadd.group_note': 'The heading it is listed under on the business page',
  'admin.svcadd.price': 'Price',
  'admin.svcadd.duration': 'Duration (minutes)',
  'admin.svcadd.unit': 'Price unit',
  'admin.svcadd.unit_note': 'Optional, e.g. /session, /night, /person',
  'admin.svcadd.save': 'Add and publish',
  'admin.svcadd.cancel': 'Cancel',
  'admin.svcadd.required': '{field} is required',
  'admin.svcadd.done': '“{name}” is live at {business}',
  'admin.svcadd.failed': 'The service could not be added — try again',
  'admin.people.system': 'System account',
  'admin.people.system_sub': 'Owns imported listings · no plan',
  'admin.people.per_month': 'month',
  'admin.people.empty': 'No merchants match',
  'admin.people.to_businesses': 'Businesses →',
  'admin.people.stat.total': 'Merchants',
  'admin.people.stat.paid': '{n} on a paid plan',
  'admin.people.stat.verified': 'NID verified',
  'admin.people.stat.no_business': 'Without a listing',
  'admin.people.pd.back': '← All merchants',
  'admin.people.pd.sec.account': 'Account',
  'admin.people.pd.f.phone': 'Phone',
  'admin.people.pd.f.email': 'Email',
  'admin.people.pd.f.nid': 'NID',
  'admin.people.pd.f.joined': 'Joined',
  'admin.people.pd.sec.plan': 'Plan',
  'admin.people.pd.businesses': 'Businesses',
  'admin.people.pd.businesses.empty': 'No listing yet',
  'admin.people.pd.open_business': 'Open →',
  'admin.people.pd.services': 'Services',
  'admin.people.pd.offered_at': '{n, plural, one{offered at # listing} other{offered at # listings}}',
  'admin.people.pd.offers': 'Offers',
  'admin.people.pd.offers.empty': 'No offers yet',
  'admin.people.pd.targets': '{n, plural, one{# target} other{# targets}}',
  'admin.people.pd.kyc.person': 'Person — identity',
  'admin.people.pd.kyc.business': 'Business document',
  'admin.people.pd.audit': 'Recent activity',
  'admin.people.pd.audit.empty': 'No activity yet',
  'admin.merchants.pd.kyc': 'KYC {state}',
  'admin.merchants.pd.review': 'Review in Approvals →',
  'admin.merchants.pd.division': '{name} Division',
  'admin.merchants.pd.sec.business': 'Business',
  'admin.merchants.pd.sec.contacts': 'Contacts',
  'admin.merchants.pd.sec.subscription': 'Subscription',
  'admin.merchants.pd.sec.performance': 'Performance',
  'admin.merchants.pd.f.category': 'Category',
  'admin.merchants.pd.f.address': 'Address',
  'admin.merchants.pd.f.division': 'Division',
  'admin.merchants.pd.f.url': 'Public URL',
  'admin.merchants.pd.f.joined': 'Joined',
  'admin.merchants.pd.f.phone': 'Phone',
  'admin.merchants.pd.f.email': 'Email',
  'admin.merchants.pd.f.tier': 'Tier',
  'admin.merchants.pd.f.term': 'Term',
  'admin.merchants.pd.f.renewal': 'Renewal',
  'admin.merchants.pd.f.mrr': 'MRR',
  'admin.merchants.pd.f.payments': 'Payment features',
  'admin.merchants.pd.f.gate': 'Booking gate',
  'admin.merchants.pd.f.bookings': 'Bookings (lifetime)',
  'admin.merchants.pd.f.month': 'Bookings this month',
  'admin.merchants.pd.f.rating': 'Rating',
  'admin.merchants.pd.v.term': '{n} months',
  'admin.merchants.pd.v.free_tier': 'Free — never expires',
  'admin.merchants.pd.v.no_renewal': 'None — no paid term',
  'admin.merchants.pd.v.no_payments': 'Not available on Free — booking info only',
  'admin.merchants.pd.v.no_reviews': 'No reviews yet',
  'admin.merchants.pd.usage': 'Plan usage',
  'admin.merchants.pd.usage.verified': 'Payments verified',
  'admin.merchants.pd.usage.kyc_pending': 'Payments available — KYC pending',
  'admin.merchants.pd.usage.info_only': 'Booking info only',
  'admin.merchants.pd.usage.summary': '{b} of {bc} business profiles · {s} of {sc} services',
  'admin.merchants.pd.usage.sub': '{p} of {pc} professionals · {m} bookings this month',
  'admin.merchants.pd.usage.note': 'Business profiles and services are managed by the merchant — read-only here. Payment features unlock per profile once payment KYC is verified in Approvals.',
  'admin.merchants.pd.pay': 'Business & payment verification',
  'admin.merchants.pd.pay.sub': 'Payment KYC belongs to the business profile; its decisions are made in Approvals.',
  'admin.merchants.pd.pay.individual': 'Individual',
  'admin.merchants.pd.pay.asked': 'What the merchant was asked for',
  'admin.merchants.pd.pay.rejected': 'Why the merchant was rejected',
  'admin.merchants.pd.services': 'Services',
  'admin.merchants.pd.services.count': '{n} services on file.',
  'admin.merchants.pd.services.empty': 'No services yet.',
  'admin.merchants.pd.docs': 'KYC documents',
  'admin.merchants.pd.docs.sub': 'Payment verification document',
  'admin.merchants.pd.docs.note': 'KYC decisions are made in Approvals — no approve/reject here.',
  'admin.merchants.pd.docs.not_sent': 'Uploaded but not sent for review yet — the merchant still has to press Submit on My Business › Payments.',
  'admin.merchants.pd.docs.review': 'Review in Approvals',
  'admin.merchants.pd.docs.empty': 'No documents on file.',
  'admin.merchants.pd.doc.submitted': 'uploaded',
  'admin.merchants.pd.doc.verified': 'verified',
  'admin.merchants.pd.doc.rejected': 'rejected',
  'admin.merchants.pd.history': 'Status history',
  'admin.merchants.pd.history.empty': 'No history yet.',
  'admin.merchants.pd.links': 'Elsewhere in the console',
  'admin.merchants.pd.link.bookings': 'Their bookings →',
  'admin.merchants.pd.link.offers': 'Their offers →',
  'admin.merchants.pd.link.disputes': 'Their disputes →',
  'admin.merchants.pd.link.reviews': 'Their reviews →',
  'admin.merchants.pf.title': 'Add merchant',
  'admin.merchants.pf.sub': 'Phone / walk-in onboarding. New merchants land as a draft, or pending approval when sent to Approvals.',
  'admin.merchants.pf.close': 'Close',
  'admin.merchants.pf.name': 'Business name',
  'admin.merchants.pf.category': 'Category',
  'admin.merchants.pf.city': 'City',
  'admin.merchants.pf.area': 'Area',
  'admin.merchants.pf.address': 'Street address',
  'admin.merchants.pf.owner': 'Owner name',
  'admin.merchants.pf.phone': 'Owner phone',
  'admin.merchants.pf.email': 'Email',
  'admin.merchants.pf.slug': 'Booking slug',
  'admin.merchants.pf.division': 'Division',
  'admin.merchants.pf.tier': 'Initial tier',
  'admin.merchants.pf.choose': 'Choose…',
  'admin.merchants.pf.phone_ok': '✓ Valid — the owner is linked or created by this number',
  'admin.merchants.pf.slug_note': '{url} — auto-generated, editable',
  'admin.merchants.pf.division_note': 'Set by the city',
  'admin.merchants.pf.tier_note': 'Starts on Free — upgrades later via subscription',
  'admin.merchants.pf.docs_note': 'KYC documents (trade licence + owner NID) are collected from the merchant: “Save as draft” keeps the business off the marketplace, “Save & send to Approvals” queues it for review.',
  'admin.merchants.pf.save_draft': 'Save as draft',
  'admin.merchants.pf.save_send': 'Save & send to Approvals',
  'admin.merchants.pf.required': 'Fill in {field}',
  'admin.merchants.kyc.glyph.ok': '✓ {label}',
  'admin.merchants.kyc.glyph.wait': '◷ {label}',
  'admin.merchants.kyc.glyph.bad': '✕ {label}',
  // ---- end DC99 ----
  // ---- DC97 admin Reviews ----
  'admin.rv.sub': 'Review moderation — fake-review flags, approve/remove decisions and public-reply oversight.',
  'admin.rv.stat.queue': 'In queue',
  'admin.rv.stat.fake': 'Fake flags',
  'admin.rv.stat.hidden': 'Hidden',
  'admin.rv.stat.avg': 'Avg rating',
  'admin.rv.stat.avg_v': '{n} ★',
  'admin.rv.tab.queue': 'Review queue ({n})',
  'admin.rv.tab.all': 'All reviews',
  'admin.rv.add': '＋ Add review',
  'admin.rv.seg.label': 'Flag type',
  'admin.rv.seg.all': 'All flags ({n})',
  'admin.rv.seg.fake': 'Fake-review ({n})',
  'admin.rv.seg.reply': 'Reply oversight ({n})',
  'admin.rv.seg.dispute': 'Disputes ({n})',
  'admin.rv.sorted': 'sorted by oldest flag first',
  'admin.rv.kind.fake': 'Fake-review flag',
  'admin.rv.kind.reply': 'Reply oversight',
  'admin.rv.kind.dispute': 'Merchant dispute',
  'admin.rv.verified': '✓ Verified booking',
  'admin.rv.collected': 'Collected by {brand}',
  'admin.rv.merchant_reason': 'Merchant’s reason: “{reason}”',
  'admin.rv.reason.fake': 'Fake review',
  'admin.rv.reason.abusive': 'Abusive language',
  'admin.rv.reason.other_business': 'Review of another business',
  'admin.rv.reason.other': 'Other',
  'admin.rv.flagged_on': 'flagged {date}',
  'admin.rv.by.merchant': 'Reported by merchant',
  'admin.rv.by.system': 'Automatic flag',
  'admin.rv.by.admin': 'Admin flag',
  'admin.rv.by.customer': 'Reported by customer',
  'admin.rv.no_booking_match': 'no matching booking',
  'admin.rv.verify_link': 'Verify booking {code} →',
  'admin.rv.verify_link_plain': 'Verify the linked booking →',
  'admin.rv.act.keep_review': 'Keep review',
  'admin.rv.act.keep': 'Keep',
  'admin.rv.act.remove': 'Remove',
  'admin.rv.act.message': 'Message merchant',
  'admin.rv.act.redact': 'Redact reply',
  'admin.rv.act.remove_reply': 'Remove reply',
  'admin.rv.act.hide': 'Hide review',
  'admin.rv.act.hide_short': 'Hide',
  'admin.rv.act.flag': 'Flag for queue',
  'admin.rv.act.flag_short': 'Flag',
  'admin.rv.act.restore': 'Restore review',
  'admin.rv.act.open_queue': 'Open in queue',
  'admin.rv.act.meta': 'Edit metadata',
  'admin.rv.act.remove_forever': 'Remove permanently',
  'admin.rv.act.send': 'Send message',
  'admin.rv.act.close': 'Close',
  'admin.rv.queue_clear': 'Queue clear ✓ — no flagged reviews waiting',
  'admin.rv.dlg.reason': 'Reason (audit log)',
  'admin.rv.dlg.reason_ph': 'Why? This is recorded in the audit log.',
  'admin.rv.dlg.message': 'Message',
  'admin.rv.dlg.message_ph': 'What should the merchant know?',
  'admin.rv.reason_required': 'A reason is required — it is recorded in the audit log',
  'admin.rv.message_required': 'Write the message first',
  'admin.rv.dlg.redact_title': 'Redact reply on {ref}?',
  'admin.rv.dlg.redact_body': 'The reply is withheld from the public profile; the customer review stays. The merchant is notified.',
  'admin.rv.dlg.remove_reply_title': 'Remove reply on {ref}?',
  'admin.rv.dlg.remove_reply_body': 'The merchant reply leaves the public profile; the customer review stays. The merchant is notified.',
  'admin.rv.dlg.remove_title': 'Remove review {ref}?',
  'admin.rv.dlg.remove_body': 'It leaves public view permanently but stays in the ledger and Reports. Author and merchant are notified.',
  'admin.rv.dlg.message_title': 'Message {name}',
  'admin.rv.dlg.message_body': 'Your note goes to the merchant’s inbox and is audit-logged. Write the message below.',
  'admin.rv.dlg.hide_title': 'Hide review {ref}?',
  'admin.rv.dlg.hide_body': 'It leaves the public profile until restored. The author is notified.',
  'admin.rv.dlg.flag_title': 'Flag {ref} for the review queue?',
  'admin.rv.dlg.flag_body': 'It joins the moderation queue as a manual flag with your reason.',
  'admin.rv.dlg.bulk_hide_title': 'Hide {n} review(s)?',
  'admin.rv.dlg.bulk_hide_body': 'They leave public profiles until restored; authors are notified. Flagged reviews are excluded — they resolve in the queue.',
  'admin.rv.dlg.verify_title': 'Booking {code} — proof trail',
  'admin.rv.dlg.verify_body_found': 'The dispute cites this booking. Verified-booking check: matching booking found in the platform ledger.',
  'admin.rv.dlg.verify_body_missing': 'The dispute cites this booking. Verified-booking check: the booking linked to the review could not be read.',
  'admin.rv.toast.kept_reply': 'Flag dismissed — review and reply stay public',
  'admin.rv.toast.kept': 'Review kept — flag dismissed',
  'admin.rv.toast.redacted': 'Reply redacted — merchant notified',
  'admin.rv.toast.reply_removed': 'Reply removed — merchant notified',
  'admin.rv.toast.removed': 'Review removed — audit-logged',
  'admin.rv.toast.messaged': 'Message sent to {name}',
  'admin.rv.toast.hidden': 'Review hidden — audit-logged',
  'admin.rv.toast.restored': 'Review restored to public view — audit-logged',
  'admin.rv.toast.flagged': 'Sent to the review queue',
  'admin.rv.toast.bulk_hidden': '{n} review(s) hidden · {skipped} skipped — audit-logged',
  'admin.rv.toast.bulk_hide_none': 'Nothing hidden — flagged, hidden and removed reviews are excluded from bulk hide',
  'admin.rv.toast.bulk_restored': '{n} review(s) restored — audit-logged',
  'admin.rv.toast.bulk_restore_none': 'Nothing restored — only hidden reviews can be restored',
  'admin.rv.toast.meta': 'Metadata saved — audit-logged',
  'admin.rv.toast.added': 'Review added with the “Collected by {brand}” tag — audit-logged',
  'admin.rv.tbl.search': 'Search author, business or review text…',
  'admin.rv.f.rating': 'All ratings',
  'admin.rv.f.status': 'All statuses',
  'admin.rv.f.booking': 'Verified & unverified',
  'admin.rv.f.verified': 'Verified',
  'admin.rv.f.unverified': 'Unverified',
  'admin.rv.f.reply': 'Reply — any',
  'admin.rv.f.business': 'All businesses',
  'admin.rv.f.city': 'All cities',
  'admin.rv.yes': 'Yes',
  'admin.rv.no': 'No',
  'admin.rv.col.review': 'Review',
  'admin.rv.col.business': 'Business',
  'admin.rv.col.rating': 'Rating',
  'admin.rv.col.booking': 'Booking',
  'admin.rv.col.status': 'Status',
  'admin.rv.col.reply': 'Reply',
  'admin.rv.col.date': 'Date',
  'admin.rv.cell.verified': '✓ Verified',
  'admin.rv.cell.no_booking': 'No booking',
  'admin.rv.select_page': 'Select every review on this page',
  'admin.rv.bulk.hide': 'Hide selected',
  'admin.rv.bulk.restore': 'Restore selected',
  'admin.rv.empty': 'No reviews match — clear filters',
  'admin.rv.csv.author': 'Author',
  'admin.rv.csv.city': 'City',
  'admin.rv.csv.text': 'Text',
  'admin.rv.csv.source': 'Source',
  'admin.rv.back_all': '← Back to all reviews',
  'admin.rv.back': '← Back to reviews',
  'admin.rv.d.reply': 'Merchant public reply',
  'admin.rv.d.reply_visible': 'visible',
  'admin.rv.d.reply_withheld': 'withheld',
  'admin.rv.d.reply_withheld_text': '(reply withheld from public view by moderation)',
  'admin.rv.d.booking': 'Linked booking',
  'admin.rv.d.f.booking': 'Booking',
  'admin.rv.d.f.service': 'Service',
  'admin.rv.d.f.when': 'When',
  'admin.rv.d.f.status': 'Status',
  'admin.rv.d.f.customer': 'Customer',
  'admin.rv.d.others': 'Author’s other reviews',
  'admin.rv.d.pattern': '{n} review(s) from {name}',
  'admin.rv.d.pattern_biz': '{n} review(s) from {name} — {m} for {biz}',
  'admin.rv.d.pattern_manual': 'Collected off-platform — no customer account to match',
  'admin.rv.d.no_others': 'No other reviews from this author.',
  'admin.rv.d.history': 'Flag & moderation history',
  'admin.rv.d.actions': 'Actions',
  'admin.rv.d.note': 'Hide, restore, removal and metadata edits need Operations. Reply moderation, flags and merchant messages need Support or Operations.',
  'admin.rv.d.note_removed': 'This review was removed from public view. It stays in the immutable ledger and in Reports.',
  'admin.rv.d.foot': 'Review text and rating are immutable — even for Super Admin. Metadata edits, hiding and removal are the only remedies, and every action is audit-logged. Removed reviews leave public view but stay in the ledger and Reports.',
  'admin.rv.d.organic': 'Organic — submitted in-app',
  'admin.rv.d.collected_via': 'Collected by {brand} — {source}',
  'admin.rv.d.edited': 'Edited {date}',
  'admin.rv.src.phone': 'Phone',
  'admin.rv.src.in_person': 'In-person',
  'admin.rv.src.imported': 'Imported',
  'admin.rv.src.organic': 'Organic',
  'admin.rv.h.submitted': 'Review submitted',
  'admin.rv.h.submitted_booking': 'Review submitted (booking {code})',
  'admin.rv.h.collected': 'Collected by {brand} — {source}',
  'admin.rv.h.reply_posted': 'Public reply posted',
  'admin.rv.h.edited': 'Edited by the author',
  'admin.rv.h.hide': 'Hidden — {reason}',
  'admin.rv.h.hide_plain': 'Hidden',
  'admin.rv.h.restore': 'Restored to public view',
  'admin.rv.h.remove': 'Removed — {reason}',
  'admin.rv.h.redact_reply': 'Merchant reply redacted — {reason}',
  'admin.rv.h.redact_reply_plain': 'Merchant reply redacted',
  'admin.rv.h.remove_reply': 'Merchant reply removed — {reason}',
  'admin.rv.h.remove_reply_plain': 'Merchant reply removed',
  'admin.rv.h.keep': 'Review kept — flag dismissed',
  'admin.rv.h.meta': 'Review metadata edited (text/rating immutable)',
  'admin.rv.h.message': 'Message sent to merchant — {reason}',
  'admin.rv.who.engine': 'Policy engine',
  'admin.rv.who.customer': 'Customer',
  'admin.rv.who.admin': 'Admin',
  'admin.rv.who.admin_role': '{name} ({role})',
  'admin.rv.fm.add_title': 'Add review — collected off-platform',
  'admin.rv.fm.add_sub': 'For phone follow-ups, walk-in feedback forms and imports. The review carries a public “Collected by {brand}” tag — never fake organic reviews.',
  'admin.rv.fm.meta_title': 'Edit metadata — {ref}',
  'admin.rv.fm.meta_sub': 'Business assignment and verified-booking link only. The customer’s text and rating are immutable — even for Super Admin.',
  'admin.rv.fm.business': 'Business',
  'admin.rv.fm.business_ph': 'Search businesses…',
  'admin.rv.fm.booking': 'Booking code',
  'admin.rv.fm.booking_ph': '{prefix}-00000 (optional)',
  'admin.rv.fm.booking_note': 'Attaching a matching completed booking grants the verified badge.',
  'admin.rv.fm.author': 'Author name',
  'admin.rv.fm.author_ph': 'Customer name',
  'admin.rv.fm.rating': 'Rating',
  'admin.rv.fm.source': 'Source',
  'admin.rv.fm.date': 'Date collected',
  'admin.rv.fm.text': 'Review text',
  'admin.rv.fm.text_ph': 'What the customer said…',
  'admin.rv.fm.text_note': 'Immutable once created — removal or hiding is the only remedy.',
  'admin.rv.fm.add_side': 'Create review',
  'admin.rv.fm.meta_side': 'Save changes',
  'admin.rv.fm.add_note': 'Created reviews go public immediately with the “Collected by {brand}” tag and are audit-logged with the creating admin.',
  'admin.rv.fm.meta_note': 'Metadata edits are audit-logged with your name. Review text and rating cannot be edited.',
  'admin.rv.fm.add_submit': '✓ Add review',
  'admin.rv.fm.meta_submit': '✓ Save metadata',
  'admin.rv.fm.err_required': 'Business, author and review text are required',
  'admin.rv.fm.err_business': 'Pick a business from the list',
  'admin.rv.fm.err_booking': 'No booking matches that code',
  'admin.rv.fm.err_date': 'The date cannot be in the future',
  // ---- end DC97 ----
  // ---- DC76 My Business (design Merchant Dashboard M:1823-2410, M:5265-5399) ----
  'dc76.new_business': 'New business',
  'dc76.individual': 'Individual',
  'dc76.booking_on': 'Bookings on',
  'dc76.booking_off': 'Bookings paused',
  'dc76.status_note.rejected': 'This business was not approved',
  'dc76.status_note.suspended': 'This business is suspended',
  'dc76.status_note.reason': 'Reason',
  'dc76.status_note.no_reason': 'No reason was given — contact support if you are unsure why.',
  'dc76.status_note.hint': 'Fix what is mentioned, then resubmit it for review.',
  'dc76.status_note.resubmit': 'Resubmit for review',
  'dc76.status_note.resubmitted': 'Sent for review again',
  'dc76.status_note.resubmit_failed': 'Could not resubmit — try again',
  'dc76.contact_only': 'Contact only',
  'dc76.contact_only_help': 'Online booking is switched off for this business by the platform.',
  'dc76.crumb_label': 'Breadcrumb',
  'dc76.tabs_label': 'Business sections',
  'dc76.saved': 'Saved',
  'dc76.err.save': 'Could not save — try again.',
  'dc76.err.invalid': 'Some details are not valid — check and try again.',
  'dc76.err.phone': 'Enter 11 digits starting with 01.',
  'dc76.grid.cap_line': '{used}/{cap} businesses',
  'dc76.grid.cap_upsell': 'Plan limit reached — upgrade',
  'dc76.grid.stat_line': '{services, plural, one{# service} other{# services}} · {bookings, plural, one{# booking} other{# bookings}} today',
  'dc76.grid.menu_for': 'Actions for {name}',
  'dc76.grid.view_profile': 'View profile',
  'dc76.grid.pause': 'Pause bookings',
  'dc76.grid.resume': 'Resume bookings',
  'dc76.grid.deactivate': 'Deactivate',
  'dc76.grid.deactivate_later': 'Deactivating a business is coming later — contact support meanwhile.',
  'dc76.tab.profile': 'Profile',
  'dc76.tab.hours': 'Hours',
  'dc76.tab.payments': 'Payments',
  'dc76.tab.services': 'Services',
  'dc76.pay_chip.approved': 'Payments on',
  'dc76.pay_chip.under_review': 'Application under review',
  'dc76.pay_chip.more_info': 'More info needed',
  'dc76.pay_chip.rejected': 'Rejected',
  'dc76.pay_chip.blocked_individual': 'ID required',
  'dc76.sec.identity': 'Identity',
  'dc76.sec.address': 'Address',
  'dc76.sec.photos': 'Photos',
  'dc76.sec.contact': 'Contact',
  'dc76.f.name_bn': 'Name (Bangla)',
  'dc76.f.name_en': 'Name (English)',
  'dc76.f.category': 'Category',
  'dc76.f.subcategory': 'Subcategory',
  'dc76.f.sub_pick': 'Select a subcategory',
  'dc76.f.other': 'Other',
  'dc76.f.other_add': 'Other — add your own',
  'dc76.f.sub_other_ph': 'Type your subcategory name',
  'dc76.f.about_bn': 'Description (Bangla)',
  'dc76.f.about_en': 'Description (English)',
  'dc76.f.area': 'Area',
  'dc76.f.pin': 'Map pin',
  'dc76.f.map_ph': 'Paste a Google Maps link',
  'dc76.f.map_hint': 'The pin is placed automatically from the link',
  'dc76.f.map_err': "Couldn't find a location in that link — paste a full Google Maps link or the coordinates (lat, lng)",
  'dc76.f.map_remove': 'Remove pin',
  'dc76.f.video': 'Showcase video',
  'dc76.f.video_ph': 'Paste a YouTube or Vimeo link',
  'dc76.f.video_hint': 'Shown on your public page. A 1–3 minute tour works best.',
  'dc76.f.video_err': "That isn't a YouTube or Vimeo link, or an https .mp4 file",

  // My Business › Reels (FR-B29): YouTube Shorts registered by link, approved by an admin.
  'dc76.reels.title': 'Reels',
  'dc76.reels.hint': 'Short vertical videos from YouTube — yours or a creator\u2019s. Each one is checked by our team before it shows.',
  'dc76.reels.url': 'YouTube Shorts link',
  'dc76.reels.url_ph': 'https://youtube.com/shorts/…',
  'dc76.reels.caption': 'Caption',
  'dc76.reels.caption_ph': 'One line about the clip',
  'dc76.reels.creator': 'Creator credit',
  'dc76.reels.creator_ph': '@handle — leave empty if it\u2019s yours',
  'dc76.reels.offer': 'Promotes an offer',
  'dc76.reels.offer_none': 'No offer',
  'dc76.reels.add': 'Add reel',
  'dc76.reels.added': 'Reel added — it shows once approved',
  'dc76.reels.remove': 'Remove',
  'dc76.reels.remove_confirm': 'Remove?',
  'dc76.reels.removed': 'Reel removed',
  'dc76.reels.count': '{n} of {max}',
  'dc76.reels.none': 'No reels yet',
  'dc76.reels.status.pending_review': 'Waiting for review',
  'dc76.reels.status.approved': 'Live',
  'dc76.reels.status.rejected': 'Rejected',
  'dc76.reels.err_link': 'That isn\u2019t a YouTube link',
  'dc76.reels.err_conflict': 'This video is already here, or the listing holds its limit of reels',
  'dc76.reels.err_failed': 'Couldn\u2019t save the reel',
  'dc76.reels.open': 'Open on YouTube',
  'dc76.f.phone': 'Phone',
  'dc76.f.email': 'Email (optional)',
  'dc76.photos.hint': 'The first photo is the cover customers see',
  'dc76.photos.make_cover': 'Make cover',
  'dc76.photos.remove': 'Remove photo',
  'dc76.photos.added': 'Photo added',
  'dc76.photos.removed': 'Photo removed',
  'dc76.photos.save_failed': 'The photos could not be saved — try again.',
  'dc76.photos.cover': 'Cover',
  'dc76.photos.add': 'Add photo',
  'dc76.hours.weekly': 'Weekly hours',
  'dc76.hours.copy_all': 'Copy to all days',
  'dc76.hours.open': 'Open',
  'dc76.hours.closed': 'Closed',
  'dc76.hours.from': 'opens',
  'dc76.hours.to': 'closes',
  'dc76.hours.help': 'Service-level hours must fit inside this schedule',
  'dc76.hours.holidays': 'Holiday closures',
  'dc76.hours.holidays_empty': 'Holidays you add here replace the saved list when you save.',
  'dc76.hours.holiday': 'Holiday',
  'dc76.hours.upcoming': 'Upcoming',
  'dc76.hours.remove': 'Remove holiday',
  'dc76.hours.date_label': 'Holiday date',
  'dc76.hours.date_ph': 'Date (e.g. 12 Jun)',
  'dc76.hours.occasion_label': 'Occasion',
  'dc76.hours.occasion_ph': 'Occasion (e.g. Eid)',
  'dc76.hours.add': 'Add holiday',
  'dc76.footer.unsaved': '{n, plural, one{# unsaved change} other{# unsaved changes}}',
  'dc76.footer.prev': 'Previous',
  'dc76.footer.next': 'Next',
  'dc76.footer.save': 'Save',
  'dc76.pause.title': 'Pause bookings at {name}?',
  'dc76.pause.body': 'New bookings stop; existing bookings stay.',
  'dc76.pause.yes': 'Pause',
  'dc76.method.bkash': 'bKash',
  'dc76.method.nagad': 'Nagad',
  'dc76.method.rocket': 'Rocket',
  'dc76.method.bank': 'Bank',
  'dc76.pay.locked_kicker': 'Upgrade to unlock',
  'dc76.pay.locked_line': 'Taking payments directly needs a paid plan · from {price}',
  'dc76.pay.locked_line_plain': 'Taking payments directly needs a paid plan',
  'dc76.pay.upgrade': 'Upgrade',
  'dc76.pay.load_failed': 'The payment application could not be loaded.',
  'dc76.pay.approved': 'Approved — these methods are on',
  'dc76.pay.edit': 'Edit',
  'dc76.pay.edit_reverify': 'Editing re-verifies the number',
  'dc76.pay.default_advance': 'Default advance',
  'dc76.pay.default_advance_help': 'Pre-applied to new orders',
  'dc76.pay.not_sent': 'Your documents are saved but not sent for review yet. Finish the steps below and press Submit.',
  'dc76.pay.missing.title': 'To submit, still needed:',
  'dc76.pay.missing.nid': 'Upload the owner NID',
  'dc76.pay.missing.trade_license': 'Upload the trade licence',
  'dc76.pay.missing.tin': 'Upload the TIN certificate',
  'dc76.pay.missing.credential': 'Upload the professional credential',
  'dc76.pay.missing.method': 'Turn on at least one payment method (bKash, Nagad, Rocket or bank)',
  'dc76.pay.missing.number': 'Add the account number for each method you turned on',
  'dc76.pay.missing.advance': 'Set the advance amount',
  'dc76.pay.review_help': 'Reviewed within 24 hours.',
  'dc76.pay.more_info': 'The platform asked for more information',
  'dc76.pay.respond': 'Respond',
  'dc76.pay.rejected': 'Application rejected',
  'dc76.pay.reapply': 'Apply again',
  'dc76.pay.step_docs': 'Documents',
  'dc76.pay.doc.trade_license': 'Trade licence',
  'dc76.pay.doc.nid': "Owner's NID",
  'dc76.pay.doc.tin': 'TIN',
  'dc76.pay.doc.credential': 'Professional licence',
  'dc76.pay.required': 'Required',
  'dc76.pay.file_hint': 'PDF, JPG or PNG · max 5 MB',
  'dc76.pay.file_received': 'File received',
  'dc76.pay.upload': 'Upload',
  'dc76.pay.uploaded': 'Uploaded',
  'dc76.pay.upload_failed': 'The file could not be uploaded — try again.',
  'dc76.pay.too_big': 'The file is larger than 5 MB.',
  'dc76.pay.remove_file': 'Remove the {doc} file',
  'dc76.pay.individual_block': 'A business ID is required for payment services.',
  'dc76.pay.methods': 'Payment methods',
  'dc76.pay.number': 'Number',
  'dc76.pay.instruction': 'What the customer is shown',
  'dc76.pay.fixed': 'Fixed {symbol}',
  'dc76.pay.percent': 'Percent %',
  'dc76.pay.submit': 'Submit',
  'dc76.pay.err_plan': 'Your plan does not include direct payments.',
  'dc76.pay.err_state': 'The application is already under review or approved.',
  'dc76.pay.err_failed': 'Could not submit — check the documents and try again.',
  'dc76.svc.total': 'Total',
  'dc76.svc.live': 'Live',
  'dc76.svc.attn': 'Needs attention',
  'dc76.svc.of_business': 'Services in this business',
  'dc76.svc.view_all': 'View all services →',
  'dc76.nbz.title_step': 'New business · Step {n}/{total}',
  'dc76.nbz.type': 'Type',
  'dc76.nbz.search_cat': 'Search categories',
  'dc76.nbz.individual': 'Individual',
  'dc76.nbz.individual_help': 'Start without a trade licence — tutors, chefs, trainers',
  'dc76.nbz.individual_what': 'What do you do?',
  'dc76.nbz.change': 'Change',
  'dc76.nbz.search_area': 'Search division, city or area',
  'dc76.nbz.area_pin': 'Adjust with the map pin on the profile later',
  'dc76.nbz.phone_help': 'Your account number — editable',
  'dc76.nbz.cover': 'Cover photo (optional)',
  'dc76.nbz.cover_added': 'Cover photo added',
  'dc76.nbz.footer_hint': 'You can change everything later',
  'dc76.nbz.back': '← Back',
  'dc76.nbz.create': 'Create business',
  'dc76.nbz.created': 'Created — live once approved',
  'dc76.nbz.next': 'What next?',
  'dc76.nbz.add_service': 'Add your first service',
  'dc76.nbz.add_service_hint': 'Customers cannot book without a service',
  'dc76.nbz.turn_on_payments': 'Turn on payments',
  'dc76.nbz.later': 'Later',
  'dc76.nbz.err_cap': 'Your plan has reached its business limit.',
  'dc76.nbz.err_failed': 'Could not create the business — try again.',
  'dc76.nbz.next_step': 'Next',
  'dc76.nbz.pick_type': 'Pick a category to continue',
  'dc76.nbz.pick_sub': 'Pick a subcategory, or tap Next to skip it',
  'dc76.nbz.cover_add': 'Tap to add a cover photo — or add it later',
  'dc76.nbz.cover_hint': 'JPG, PNG or WebP, up to {mb} MB',
  'dc76.nbz.cover_remove': 'Remove photo',
  'dc76.nbz.area_none': 'No area matches “{q}”. Suggest it as a new area, and pick the nearest one for now.',
  'dc76.nbz.suggest': 'Suggest “{q}” as a new area',
  'dc76.nbz.suggested_pick': 'We will ask the team to add “{q}”. Meanwhile, pick the nearest area above to start.',
  'dc76.nbz.suggested': '“{q}” will be sent for review — your business moves there once it is added.',
  'dc76.nbz.suggest_undo': 'Undo',
  'dc76.nbz.suggest_sent': 'Area “{q}” sent for review',
  'dc76.nbz.suggest_failed': 'The area suggestion could not be sent — you can contact support.',
  'dc76.nbz.creating': 'Creating…',
  'dc76.f.map_short': 'Short share link — the pin is looked up when you save. If it can\'t be found, open the link in Google Maps and paste the full link from the address bar, or the coordinates (lat, lng).',
  'dc76.hours.copied_from': 'Copied {day}\'s hours to every day',
  'dc76.hours.copy_none': 'Turn on at least one day first',
  'dc74.wz.submitting': 'Submitting…',
  'dc74.wz.saving': 'Saving…',
  'upload.uploading': 'Uploading…',
  'upload.err.with_file': '“{file}”: {reason}',
  'upload.err.type': 'Use a JPG, PNG or WebP photo — this file type is not supported.',
  'upload.err.too_big': 'The photo is larger than {mb} MB — choose a smaller one.',
  'upload.err.rate': 'Too many photo uploads in the last hour — try again later.',
  'upload.err.network': 'Upload failed — check your internet connection and try again.',
  'upload.err.session': 'Your session has expired — reload the page and try again.',
  'upload.err.generic': 'The photo could not be uploaded — try again.',
  // ---- end DC76 ----
  // ---- DC100 admin Customers ----
  'admin.customers.sub': 'Lookup, block/unblock, manual entry and PDPA data-subject requests.',
  'admin.customers.stat.total': 'Customers',
  'admin.customers.stat.growth': '↑ +{pct}% this month',
  'admin.customers.stat.active': 'Active',
  'admin.customers.stat.blocked': 'Blocked',
  'admin.customers.stat.new': 'New this month',
  'admin.customers.stat.dsr': 'DSR open',
  'admin.customers.tab.all': 'All customers',
  'admin.customers.tab.dsr': 'PDPA requests',
  'admin.customers.export': 'Export CSV',
  'admin.customers.add': '＋ Add customer',
  'admin.customers.directory': 'Customer directory',
  'admin.customers.search': 'Search name, phone, city…',
  'admin.customers.filter.status': 'All statuses',
  'admin.customers.filter.city': 'All cities',
  'admin.customers.filter.joined': 'Joined',
  'admin.customers.filter.more': '{first} +{n}',
  'admin.customers.filter.select_all': 'Select all',
  'admin.customers.filter.clear': 'Clear',
  'admin.customers.filter.clear_all': 'Clear all',
  'admin.customers.chip.status': 'Status',
  'admin.customers.chip.city': 'City',
  'admin.customers.chip.joined': 'Joined',
  'admin.customers.hint': 'Row click opens the 360° profile',
  'admin.customers.col.customer': 'Customer',
  'admin.customers.col.customer_title': 'Name and registered phone',
  'admin.customers.col.phone': 'Phone',
  'admin.customers.col.email': 'Email',
  'admin.customers.col.city': 'City',
  'admin.customers.col.division': 'Division',
  'admin.customers.col.activity': 'Activity',
  'admin.customers.col.activity_title': 'Lifetime bookings and last booking date',
  'admin.customers.col.value': 'Value',
  'admin.customers.col.value_title': 'Lifetime spend and average per booking',
  'admin.customers.col.status': 'Status',
  'admin.customers.col.actions': 'Actions',
  'admin.customers.sort.name': 'Sort by name',
  'admin.customers.sort.joined': 'Sort by joined',
  'admin.customers.sort.bookings': 'Sort by bookings',
  'admin.customers.sort.last': 'Sort by last booking',
  'admin.customers.sort.spend': 'Sort by lifetime spend',
  'admin.customers.sort.status': 'Sort by status',
  'admin.customers.cell.division': '{division} Division',
  'admin.customers.cell.bookings': '{n, plural, one{# booking} other{# bookings}}',
  'admin.customers.cell.last': 'Last {date}',
  'admin.customers.cell.no_bookings': 'No bookings yet',
  'admin.customers.cell.per_booking': '{amount} / booking',
  'admin.customers.cell.joined': 'Joined {date}',
  'admin.customers.status.active': 'Active',
  'admin.customers.status.blocked': 'Blocked',
  'admin.customers.status.deleted': 'Erased',
  'admin.customers.status.merged': 'Merged',
  'admin.customers.empty': 'No customer matches — clear the filters or search a phone number',
  'admin.customers.empty.clear': 'Clear filters',
  'admin.customers.range': '{from}–{to} of {total}',
  'admin.customers.audit_note': 'Block, merge and PDPA deletion all require a written reason and are written to the immutable audit log with your name and timestamp. Customers and their bookings are never hard-deleted — deletion requests anonymize the record and retain the booking ledger.',
  'admin.customers.action.view': 'View 360°',
  'admin.customers.action.edit': 'Edit',
  'admin.customers.action.block': 'Block',
  'admin.customers.action.unblock': 'Unblock',
  'admin.customers.action.merge': 'Merge duplicates',
  'admin.customers.action.more': 'More actions',
  'admin.customers.dlg.block.title': 'Block {name}?',
  'admin.customers.dlg.block.body': 'Blocked customers cannot place bookings. Existing bookings stay in the ledger untouched. The reason is recorded in the immutable audit log.',
  'admin.customers.dlg.block.confirm': 'Block customer',
  'admin.customers.dlg.unblock.title': 'Unblock {name}?',
  'admin.customers.dlg.unblock.body': 'The customer can place bookings again immediately. The change is kept on the status history.',
  'admin.customers.dlg.reason': 'Reason — recorded in the audit log',
  'admin.customers.dlg.reason_ph': 'Why? Other admins will see this.',
  'admin.customers.dlg.reason_required': 'A reason is required — it is recorded in the audit log',
  'admin.customers.dlg.cancel': 'Cancel',
  'admin.customers.toast.blocked': '{name} blocked — cannot place bookings, audit-logged',
  'admin.customers.toast.unblocked': '{name} unblocked — audit-logged',
  'admin.customers.toast.failed': 'That did not go through — try again',
  'admin.customers.toast.exported': 'Exported {file}',
  'admin.customers.toast.created': '{name} added — tagged “Added by admin”, audit-logged',
  'admin.customers.toast.saved': 'Changes saved — audit-logged from → to',
  'admin.customers.toast.note': 'Note added — attributed to you, audit-logged',
  'admin.customers.toast.merged': 'Records merged — duplicate kept as redirect, audit-logged',
  'admin.customers.toast.dsr_export': 'Export generated and sent — audit-logged',
  'admin.customers.toast.dsr_delete': '{name} anonymized — bookings retained, audit-logged',
  'admin.customers.toast.dsr_clarify': 'Clarification sent — audit-logged',
  'admin.customers.toast.dsr_decline': 'Request declined — audit-logged',
  'admin.customers.dsr.title': 'PDPA data-subject requests',
  'admin.customers.dsr.sub': 'The data-protection law gives {n} days from submission to fulfil an export or deletion request. Deletion anonymizes the customer record — booking rows are retained as anonymized ledger entries.',
  'admin.customers.dsr.sub_plain': 'Every export or deletion request must be fulfilled before its statutory deadline. Deletion anonymizes the customer record — booking rows are retained as anonymized ledger entries.',
  'admin.customers.dsr.filter.open': 'Open ({n})',
  'admin.customers.dsr.filter.all': 'All',
  'admin.customers.dsr.empty.open': 'No open requests',
  'admin.customers.dsr.empty.open_sub': 'Every data-subject request has been fulfilled inside the statutory window.',
  'admin.customers.dsr.empty.all': 'No requests on file',
  'admin.customers.dsr.empty.all_sub': 'Requests appear here the moment a customer submits one.',
  'admin.customers.dsr.foot': 'Fulfilled requests stay visible read-only under “All”. Every fulfilment records the actor and timestamp in the audit log.',
  'admin.customers.dsr.kind.export': 'Data export',
  'admin.customers.dsr.kind.delete': 'Account deletion',
  'admin.customers.dsr.status.open': 'Open',
  'admin.customers.dsr.status.clarification': 'Awaiting customer',
  'admin.customers.dsr.status.fulfilled': 'Fulfilled',
  'admin.customers.dsr.status.declined': 'Declined',
  'admin.customers.dsr.clock.closed': 'clock closed',
  'admin.customers.dsr.clock.overdue': 'overdue',
  'admin.customers.dsr.clock.left': '{n} d left of {of}',
  'admin.customers.dsr.meta.submitted': 'Submitted {date}',
  'admin.customers.dsr.meta.submitted_lc': 'submitted {date}',
  'admin.customers.dsr.meta.fulfilled': 'fulfilled {date}',
  'admin.customers.dsr.meta.declined': 'declined {date}',
  'admin.customers.dsr.clar': 'Clarification requested {date} — {note}',
  'admin.customers.dsr.declined_note': 'Declined — {note}',
  'admin.customers.dsr.gone': 'Customer record no longer exists',
  'admin.customers.dsr.col.kind': 'Kind',
  'admin.customers.dsr.col.submitted': 'Submitted',
  'admin.customers.dsr.col.due': 'Due',
  'admin.customers.dsr.col.handled': 'Handled',
  'admin.customers.dsr.action.fulfil_export': 'Fulfil — generate export',
  'admin.customers.dsr.action.fulfil_delete': 'Fulfil — anonymize',
  'admin.customers.dsr.action.clarify': 'Request clarification',
  'admin.customers.dsr.action.decline': 'Decline',
  'admin.customers.dsr.dlg.export.title': 'Generate data export for {name}?',
  'admin.customers.dsr.dlg.export.body': 'A machine-readable export of this customer’s profile, bookings and reviews is generated and delivered to their registered contact. The request is closed and audit-logged.',
  'admin.customers.dsr.dlg.export.confirm': 'Generate export',
  'admin.customers.dsr.dlg.delete.title': 'Anonymize {name}?',
  'admin.customers.dsr.dlg.delete.body': 'Name, phone and email are cleared immediately and the customer can no longer sign in. {n, plural, one{Their # booking row is} other{Their # booking rows are}} retained as anonymized ledger entries — booking records are never hard-deleted. Irreversible.',
  'admin.customers.dsr.dlg.delete.confirm': 'Anonymize record',
  'admin.customers.dsr.dlg.clarify.title': 'Request clarification — {id}',
  'admin.customers.dsr.dlg.clarify.body': 'The customer is asked to confirm identity or scope. The statutory clock keeps running, so send it today.',
  'admin.customers.dsr.dlg.clarify.confirm': 'Send request',
  'admin.customers.dsr.dlg.clarify.ph': 'Write what you need clarified',
  'admin.customers.dsr.dlg.decline.title': 'Decline {id}?',
  'admin.customers.dsr.dlg.decline.body': 'The customer is told the request cannot be fulfilled, with your note — for a legal retention hold or an identity that cannot be verified.',
  'admin.customers.dsr.dlg.decline.confirm': 'Decline request',
  'admin.customers.p360.label': 'Customer 360°',
  'admin.customers.p360.error': 'This profile could not be loaded.',
  'admin.customers.p360.retry': 'Try again',
  'admin.customers.p360.no_email': 'no email on file',
  'admin.customers.p360.tag.added': 'Added by admin',
  'admin.customers.p360.tag.repeat': 'Repeat disputes · {n}',
  'admin.customers.p360.blocked': 'Blocked customers cannot place bookings.',
  'admin.customers.p360.blocked_none': 'No reason recorded on the original block.',
  'admin.customers.p360.f.bookings': 'Bookings',
  'admin.customers.p360.f.last': 'last {date}',
  'admin.customers.p360.f.none': 'none yet',
  'admin.customers.p360.f.spend': 'Lifetime spend',
  'admin.customers.p360.f.city': 'City',
  'admin.customers.p360.f.joined': 'Joined',
  'admin.customers.p360.f.by': 'by {name}',
  'admin.customers.p360.f.self': 'self sign-up',
  'admin.customers.p360.f.self_cap': 'Self sign-up',
  'admin.customers.p360.f.disputes': 'Disputes',
  'admin.customers.p360.f.repeat': 'repeat offender context',
  'admin.customers.p360.f.no_pattern': 'no pattern',
  'admin.customers.p360.f.reviews': 'Reviews',
  'admin.customers.p360.f.reviews_sub': 'written on the marketplace',
  'admin.customers.p360.edit': 'Edit details',
  'admin.customers.p360.block': 'Block customer',
  'admin.customers.p360.unblock': 'Unblock customer',
  'admin.customers.p360.merge': 'Merge duplicates',
  'admin.customers.p360.readonly': 'Read-only for your role',
  'admin.customers.p360.disputes': 'Dispute context',
  'admin.customers.p360.dispute.open': 'open',
  'admin.customers.p360.dispute.resolved': 'resolved',
  'admin.customers.p360.history': 'Status history',
  'admin.customers.p360.h.created': 'Account created',
  'admin.customers.p360.h.system': 'System',
  'admin.customers.p360.h.blocked': 'Blocked — {reason}',
  'admin.customers.p360.h.merged': 'Merged into another record — {reason}',
  'admin.customers.p360.h.merged_in': 'Duplicate merged in — {reason}',
  'admin.customers.p360.h.user_blocked': 'Blocked',
  'admin.customers.p360.h.user_merged': 'Merged',
  'admin.customers.p360.h.user_merged_in': 'Duplicate merged in',
  'admin.customers.p360.h.user_unblocked': 'Unblocked',
  'admin.customers.p360.h.customer_created': 'Added by admin',
  'admin.customers.p360.h.customer_contact_updated': 'Contact details edited by admin',
  'admin.customers.p360.h.customer_note_added': 'Internal note added',
  'admin.customers.p360.bookings': 'Bookings · {n}',
  'admin.customers.p360.bookings_empty': 'No bookings placed yet.',
  'admin.customers.p360.bookings_elsewhere': 'Bookings exist in the ledger outside the recent activity — open the Bookings ledger to trace them.',
  'admin.customers.p360.proof': 'proof trail →',
  'admin.customers.p360.reviews': 'Reviews written',
  'admin.customers.p360.reviews_empty': 'No reviews written yet.',
  'admin.customers.p360.dsr': 'PDPA request history',
  'admin.customers.p360.dsr_empty': 'No data-subject requests on file.',
  'admin.customers.p360.notes': 'Internal notes',
  'admin.customers.p360.notes_empty': 'No internal notes yet.',
  'admin.customers.p360.note_ph': 'Add an internal note — attributed to you',
  'admin.customers.p360.note_add': 'Add note',
  'admin.customers.p360.note_required': 'Write the note first',
  'admin.customers.p360.foot': 'No delete action exists on customer records. Blocking, merging and PDPA anonymization are the only removals, and each one is audit-logged.',
  'admin.customers.form.add_title': 'Add customer',
  'admin.customers.form.edit_title': 'Edit — {name}',
  'admin.customers.form.add_sub': 'Manual entry for phone and walk-in customers. The phone number is checked against the directory before the record is created.',
  'admin.customers.form.edit_sub': 'Contact details are editable. Bookings, spend and status are read-only aggregates. Every field change is audit-logged from → to.',
  'admin.customers.form.name': 'Full name — required',
  'admin.customers.form.name_ph': 'e.g. Ruma Begum',
  'admin.customers.form.phone': 'Phone — required',
  'admin.customers.form.phone_hint': 'Mobile format: {example}',
  'admin.customers.form.phone_ok': 'Valid mobile number — no duplicate found.',
  'admin.customers.form.phone_taken': 'This number already belongs to a customer.',
  'admin.customers.form.email': 'Email — optional',
  'admin.customers.form.email_ph': 'name@example.com',
  'admin.customers.form.note': 'Internal note — attributed to you',
  'admin.customers.form.note_ph': 'Context other admins should see',
  'admin.customers.form.dupe_title': 'Customer already exists — open profile',
  'admin.customers.form.open_profile': 'Open profile →',
  'admin.customers.form.ro': 'Read-only aggregates',
  'admin.customers.form.ro.bookings': 'Bookings',
  'admin.customers.form.ro.spend': 'Lifetime spend',
  'admin.customers.form.ro.status': 'Status',
  'admin.customers.form.create': 'Create customer',
  'admin.customers.form.save': 'Save changes',
  'admin.customers.form.add_foot': 'Admin-created customers carry an “Added by admin” tag in their 360° profile, mirroring the manual source on bookings.',
  'admin.customers.form.edit_foot': 'Aggregates cannot be edited by hand — they follow the booking ledger.',
  'admin.customers.form.err.name': 'Name is required',
  'admin.customers.form.err.email': 'Enter a valid email address',
  'admin.customers.form.err.dupe_add': 'That phone already belongs to {name} — open the profile instead',
  'admin.customers.form.err.dupe_edit': 'That phone belongs to {name} — merge the records instead',
  'admin.customers.form.err.conflict': 'That phone already has an account — open or merge that record instead.',
  'admin.customers.merge.title': 'Merge duplicates',
  'admin.customers.merge.sub': 'The duplicate is kept as a redirect record — bookings, spend and reviews move to the survivor. Nothing is deleted.',
  'admin.customers.merge.survivor': 'Survivor — record that is kept',
  'admin.customers.merge.search': 'Duplicate — search by phone or name',
  'admin.customers.merge.joined': 'joined {date}',
  'admin.customers.merge.select': 'select',
  'admin.customers.merge.selected': 'selected',
  'admin.customers.merge.after': 'After merge',
  'admin.customers.merge.preview': '{a} + {b} bookings → {sum} · spend combined {spend}',
  'admin.customers.merge.preview_sub': 'Survivor: {name} · {phone}. {dup} becomes a redirect record — its bookings and reviews move across and nothing is deleted.',
  'admin.customers.merge.reason': 'Reason — required, audit-logged',
  'admin.customers.merge.reason_ph': 'Why are these the same person?',
  'admin.customers.merge.warn': 'Merging cannot be undone from this console. The duplicate stays as a redirect record and the action is written to the audit log with your name.',
  'admin.customers.merge.confirm': 'Merge records',
  'admin.customers.merge.pick_first': 'Pick the duplicate record first',
  'admin.customers.merge.dlg.title': 'Merge {dup} into {survivor}?',
  'admin.customers.merge.dlg.body': 'Irreversible. {n} bookings and {spend} move to {name}. The duplicate is kept as a redirect record — no hard delete.',
  'admin.customers.merge.dlg.confirm': 'Merge — irreversible',
  // ---- end DC100 ----
  // ---- DC77 merchant Offers (design M:2412-2537, M:5704-5934) ----
  'merchant.offers.page_label': 'Offers',
  'merchant.offers.tabs_label': 'Offer lists',
  'merchant.offers.tabs.live': 'Running',
  'merchant.offers.tabs.sched': 'Scheduled',
  'merchant.offers.tabs.ended': 'Ended',
  'merchant.offers.search_ph': 'Search offers',
  'merchant.offers.new_offer': 'New offer',
  'merchant.offers.st.review': 'In review',
  'merchant.offers.st.sched': 'Scheduled',
  'merchant.offers.st.ended': 'Ended',
  'merchant.offers.st.rejected': 'Rejected',
  'merchant.offers.st.draft': 'Draft',
  'merchant.offers.st.paused': 'Paused',
  'merchant.offers.st.taken_down': 'Taken down',
  'merchant.offers.live_dot': 'Live',
  'merchant.offers.attn': '{n, plural, one{# offer rejected} other{# offers rejected}} — {name}',
  'merchant.offers.view': 'View',
  'merchant.offers.empty_list': 'No offers in this list',
  'merchant.offers.empty_hint': 'A discount pushes the service to the top of the offers feed.',
  'merchant.offers.empty_sell': 'Discounts bring new customers — build your first offer',
  'merchant.offers.photo': 'Service photo',
  'merchant.offers.your_service': 'Your service',
  'merchant.offers.days_left': '{n} days left',
  'merchant.offers.starts_in': '{n} days to start',
  'merchant.offers.bookings_n': '{n, plural, one{# booking} other{# bookings}}',
  'merchant.offers.total_bookings': 'Total {n, plural, one{# booking} other{# bookings}}',
  'merchant.offers.rerun': 'Run again',
  'merchant.offers.more': 'More actions for {name}',
  'merchant.offers.edit': 'Edit',
  'merchant.offers.dup': 'Duplicate',
  'merchant.offers.promote': 'Promote',
  'merchant.offers.delete_draft': 'Delete draft',
  'merchant.offers.pause_title': 'Pause this offer?',
  'merchant.offers.pause_body': 'Customers stop seeing the discount until you resume it. Nothing is deleted.',
  'merchant.offers.pause_yes': 'Yes, pause it',
  'merchant.offers.keep': 'Keep running',
  'merchant.offers.toast_submit': 'Offer sent for review · usually a few minutes',
  'merchant.offers.toast_pause': 'Offer paused',
  'merchant.offers.toast_resume': 'Offer running again',
  'merchant.offers.toast_deleted': 'Draft deleted',
  'merchant.offers.err_not_resumable': 'This offer can no longer run — duplicate it to run it again.',
  'merchant.offers.err_invalid': 'That change is not possible for this offer right now.',
  'merchant.offers.err_window': 'Check the dates: the offer must end in the future and run at most 90 days.',
  'merchant.offers.close': 'Close',
  'merchant.offers.mod_kicker': 'Rejected in review',
  'merchant.offers.taken_kicker': 'Taken down by the platform',
  'merchant.offers.pick_svc': 'Which service?',
  'merchant.offers.choose_svc': 'Choose a service',
  'merchant.offers.anchor_help': 'The price comes from your service catalog — the offer must sit below it.',
  'merchant.offers.where': 'Where it applies',
  'merchant.offers.where_service': 'This service only',
  'merchant.offers.where_business': 'Every service at this business',
  'merchant.offers.where_account': 'Every service at all my businesses',
  'merchant.offers.chip_business': 'Whole business',
  'merchant.offers.chip_account': 'All businesses',
  'merchant.offers.svc_running': 'An offer is already running on this service',
  'merchant.offers.no_results': 'No results',
  'merchant.offers.clear': 'Clear',
  'merchant.offers.sec_discount': 'Discount',
  'merchant.offers.mode_pct': 'Percent %',
  'merchant.offers.mode_flat': 'Set price {symbol}',
  'merchant.offers.pct_label': 'Discount percent',
  'merchant.offers.flat_label': 'Offer price',
  'merchant.offers.cust_saves': 'customer saves {amount} · {pct}%',
  'merchant.offers.err_above': 'The offer price must be below the regular price',
  'merchant.offers.err_zero': 'Enter a price',
  'merchant.offers.big70': 'Is such a big discount right?',
  'merchant.offers.big70_hint': 'Above 70% off there is little margin left — continue only if you are sure.',
  'merchant.offers.big70_yes': 'Yes, keep it',
  'merchant.offers.period': 'Validity',
  'merchant.offers.start_label': 'Starts · {date}',
  'merchant.offers.prev_week': 'Previous week',
  'merchant.offers.next_week': 'Next week',
  'merchant.offers.calendar': 'Calendar',
  'merchant.offers.len_label': 'How many days',
  'merchant.offers.days_n': '{n} days',
  'merchant.offers.fewer_days': 'Fewer days',
  'merchant.offers.more_days': 'More days',
  'merchant.offers.ends_on': '→ {date}',
  'merchant.offers.warn30': 'Discounts longer than 30 days usually work less well — short windows create urgency.',
  'merchant.offers.cap90': 'Maximum 90 days',
  'merchant.offers.qty': 'Limited quantity',
  'merchant.offers.qty_hint': 'How many customers',
  'merchant.offers.qty_label': 'First {n} customers',
  'merchant.offers.fewer': 'Fewer',
  'merchant.offers.more_qty': 'More',
  'merchant.offers.preview_title': 'How it will look',
  'merchant.offers.preview_note': '{brand} offers feed',
  'merchant.offers.terms': 'Terms',
  'merchant.offers.submit': 'Send for review',
  'merchant.offers.fix': 'Fix & resubmit',
  'merchant.offers.submit_note': 'Usually a few minutes',
  'merchant.offers.summary': '{name} · -{pct}% · {n} days',
  'merchant.offers.guard_title': 'Unsaved changes',
  'merchant.offers.guard_keep': 'Keep editing',
  'merchant.offers.guard_discard': 'Discard',
  // ---- end DC77 ----
  // ---- DC103 admin Payments ----
  'admin.pay.sub': 'Transaction monitor, refund approvals with SLA timers, fee configuration and revenue split by line.',
  'admin.pay.stat.settled': 'Settled',
  'admin.pay.stat.exceptions': 'Exceptions',
  'admin.pay.stat.refunds': 'Refunds',
  'admin.pay.hero.kicker': 'Payments · direct-payment mediation · live',
  'admin.pay.hero.title': 'Settled this month — all lines',
  'admin.pay.hero.sub': '{brand} does not process, hold, or guarantee direct payments — this console records TRX proofs, verifies them and mediates refunds.',
  'admin.pay.tile.exceptions': 'Exceptions',
  'admin.pay.tile.exceptions_sub': 'pending · rejected',
  'admin.pay.tile.refunds': 'Refunds waiting',
  'admin.pay.tile.refunds_sub': '{amount} at risk · {h} h SLA',
  'admin.pay.tile.refunds_sub_nosla': '{amount} at risk',
  'admin.pay.tile.dupes': 'Duplicate TRX',
  'admin.pay.tile.dupes_sub': 'IDs claimed on 2+ records',
  'admin.pay.tile.subs': 'Subscriptions to verify',
  'admin.pay.tile.subs_sub': 'subscription TRX awaiting a check',
  'admin.pay.tab.refunds': 'Refund approvals',
  'admin.pay.tab.tx': 'Transactions',
  'admin.pay.tab.trx': 'TRX checker',
  'admin.pay.tab.rev': 'Revenue & fees',
  'admin.pay.tab.subs': 'Subscription payments',
  'admin.pay.tabs_label': 'Payments views',
  'admin.pay.queue_clear': 'Queue clear',
  'admin.pay.waiting': '{n} waiting',
  'admin.pay.rq.title': 'Refund approval queue',
  'admin.pay.rq.sub': 'One pipeline for both sources — dispute resolutions and refund claims raised directly by customers. SLA is {h} h. Approvals are restricted to Super Admin and Finance.',
  'admin.pay.rq.sub_nosla': 'One pipeline for both sources — dispute resolutions and refund claims raised directly by customers. Approvals are restricted to Super Admin and Finance.',
  'admin.pay.rq.item_title': '{amount} — {code}',
  'admin.pay.rq.detail': '{merchant} → {customer} · {reason}',
  'admin.pay.rq.meta': '{ref} · refund via {method}',
  'admin.pay.sla.left': '{h} h left',
  'admin.pay.sla.over': 'Overdue {h} h',
  'admin.pay.src.dispute': '⚖ {ref} · {kind}',
  'admin.pay.src.dispute_title': 'Open {ref}',
  'admin.pay.src.claim': '◷ Customer claim · {code}',
  'admin.pay.src.claim_title': 'Open booking {code}',
  'admin.pay.src.admin': 'Admin-initiated',
  'admin.pay.src.admin_reason': '{reason} · Admin-initiated',
  'admin.pay.src.merchant': 'Merchant-initiated',
  'admin.pay.rq.approve': 'Approve refund',
  'admin.pay.rq.decline': 'Decline',
  'admin.pay.finance_only': 'Finance approval required',
  'admin.pay.finance_only_toast': 'Only Super Admin and Finance can decide payments',
  'admin.pay.rq.approved': 'Refund {ref} approved & paid — audit-logged',
  'admin.pay.rq.approved_dispute': 'Refund {ref} approved & paid · {dref} updated — audit-logged',
  'admin.pay.rq.declined': 'Refund declined',
  'admin.pay.rq.declined_dispute': 'Refund declined — {dref} needs re-resolution',
  'admin.pay.rq.decline_title': 'Decline refund {ref}?',
  'admin.pay.rq.decline_body': 'The customer is notified with your reason.',
  'admin.pay.rq.decline_body_dispute': 'The customer is notified with your reason, and {dref} is flagged for re-resolution.',
  'admin.pay.reason': 'Reason — written to the audit log',
  'admin.pay.reason_ph': 'Add a short reason…',
  'admin.pay.reason_required': 'A reason is required',
  'admin.pay.failed': 'That did not go through — {msg}',
  'admin.pay.tx.title': 'Transaction monitor',
  'admin.pay.tx.search': 'Search TRX or merchant…',
  'admin.pay.tx.all_status': 'All statuses',
  'admin.pay.tx.all_line': 'All lines',
  'admin.pay.tx.all_method': 'All methods',
  'admin.pay.tx.col.trx': 'TRX',
  'admin.pay.tx.col.merchant': 'Merchant',
  'admin.pay.tx.col.method': 'Method',
  'admin.pay.tx.col.amount': 'Amount',
  'admin.pay.tx.col.status': 'Status',
  'admin.pay.tx.col.when': 'When',
  'admin.pay.tx.empty': 'No transactions match',
  'admin.pay.tx.clear': 'Clear filters',
  'admin.pay.tx.export': 'Export CSV',
  'admin.pay.tx.page': '{from}–{to} of {n}',
  'admin.pay.tx.prev': 'Prev',
  'admin.pay.tx.next': 'Next',
  'admin.pay.line.subscription': 'Subscription',
  'admin.pay.line.booking_advance': 'Booking advance',
  'admin.pay.method.bkash': 'bKash',
  'admin.pay.method.nagad': 'Nagad',
  'admin.pay.method.rocket': 'Rocket',
  'admin.pay.method.bank': 'Bank',
  'admin.pay.method.gateway': 'Gateway',
  'admin.pay.trx.title': 'TRX ledger lookup',
  'admin.pay.trx.sub': 'Merchants cannot see across each other — only the platform can answer “was this TRX ID used somewhere else?”',
  'admin.pay.trx.ph': 'Paste a TRX ID — e.g. BKH9C4T7L2',
  'admin.pay.trx.clear': 'Clear',
  'admin.pay.trx.dup': '⚠ Duplicate — this TRX ID is claimed on {n} records across {m} merchants',
  'admin.pay.trx.once': '✓ Used once — {ref}',
  'admin.pay.trx.miss': 'No booking or subscription payment on the platform claims “{q}”.',
  'admin.pay.trx.meta': '{amount} · {when} · {kind}',
  'admin.pay.trx.kind.proof': 'booking payment proof',
  'admin.pay.trx.kind.subscription': 'subscription payment',
  'admin.pay.trx.dup_of': 'Duplicate of {ref}',
  'admin.pay.trx.sub_ref': 'Subscription',
  'admin.pay.dupes.title': 'Duplicate TRX report',
  'admin.pay.dupes.sub': 'One TRX ID claimed on more than one record, across merchants.',
  'admin.pay.dupes.claims': '{n} claims',
  'admin.pay.dupes.inspect': 'Inspect',
  'admin.pay.dupes.none': 'No TRX collisions on record.',
  'admin.pay.dupes.row': '{ref} · {merchant} · {amount} · {when}',
  'admin.pay.rev.title': 'Revenue by line — {month}',
  'admin.pay.rev.sub': 'Subscriptions and admin-scheduled sponsored placement, as the revenue split reports them.',
  'admin.pay.rev.col.line': 'Line',
  'admin.pay.rev.col.month': 'This month',
  'admin.pay.rev.col.prev': 'Prev',
  'admin.pay.rev.col.margin': 'Margin',
  'admin.pay.rev.line.subscriptions': 'Subscriptions',
  'admin.pay.rev.line.placement': 'Sponsored placement',
  'admin.pay.rev.empty': 'No revenue recorded yet.',
  'admin.pay.fees.title': 'Per-transaction fees',
  'admin.pay.fees.sub': 'Nothing is charged per booking today; fees unlock with {brand} Pay.',
  'admin.pay.fees.commission': 'Booking commission',
  'admin.pay.fees.convenience': 'Convenience fee',
  'admin.pay.fees.note': 'Enables with {brand} Pay',
  'admin.pay.fees.disabled': 'Disabled',
  'admin.pay.subs.title': 'Subscription payment verification',
  'admin.pay.subs.sub': 'Upgrades and renewals paid to {brand} by bKash, Nagad or bank. Check the TRX against the statement, then verify — the plan activates — or reject with a reason.',
  'admin.pay.subs.item_title': '{amount} — {merchant}',
  'admin.pay.subs.detail': '{plan} · {term}-month term · {price}',
  'admin.pay.subs.meta': '{method} · TRX {trx} · submitted {when}',
  'admin.pay.subs.verify': 'Verify payment',
  'admin.pay.subs.proof': 'Payment screenshot — open full size',
  'admin.pay.subs.reject': 'Reject',
  'admin.pay.subs.verified': 'Payment verified — {merchant} is on {plan}',
  'admin.pay.subs.rejected': 'Payment rejected — the merchant is notified',
  'admin.pay.subs.reject_title': 'Reject payment {trx}?',
  'admin.pay.subs.reject_body': 'The merchant is told your reason and can pay again.',
  'admin.pay.subs.dunning': 'Run renewal reminders',
  'admin.pay.subs.dunning_done': 'Renewal reminders queued.',
  // ---- end DC103 ----
  // ---- DC79 merchant Insights (design M:2751-3114, locked page M:3806-3838) ----
  'dc70.lock.kicker': 'Upgrade to unlock',
  'dc70.lock.needs': 'Needs: {tier}',
  'dc70.lock.from': 'from {price}',
  'dc70.lock.upgrade': 'Upgrade',
  'dc70.lock.preview': 'Preview',
  'merchant.insights.lock_benefit': 'See which services sell and which days get busy, in one place.',
  'merchant.insights.period.label': 'Period',
  'merchant.insights.period.7d': '7 days',
  'merchant.insights.period.30d': '30 days',
  'merchant.insights.period.3m': '3 months',
  'merchant.insights.period.12m': '12 months',
  'merchant.insights.export': 'Export',
  'merchant.insights.csv': 'Download CSV',
  'merchant.insights.pdf': 'Download PDF',
  'merchant.insights.pdf_soon': 'Soon',
  'merchant.insights.toast_export': '{file} ({scope}) — the download has started',
  'merchant.insights.export_failed': 'Could not export. Try again.',
  'merchant.insights.kpi.bookings': 'Total bookings',
  'merchant.insights.kpi.revenue': 'Revenue (confirmed)',
  'merchant.insights.kpi.done': 'Completion rate',
  'merchant.insights.kpi.rating': 'Average rating',
  'merchant.insights.kpi.cancelled': 'cancelled {n}',
  'merchant.insights.kpi.avg': 'avg {amount} per booking',
  'merchant.insights.kpi.done_sub': 'of bookings completed',
  'merchant.insights.kpi.reviews': 'from {n, plural, one{# review} other{# reviews}}',
  'merchant.insights.kpi.no_reviews': 'No reviews in this period',
  'merchant.insights.pct': '{n}%',
  'merchant.insights.vs_prev': 'vs previous period',
  'merchant.insights.trend': 'Booking trend',
  'merchant.insights.now': 'This period',
  'merchant.insights.prev': 'Previous period',
  'merchant.insights.trend_more': '{n}% more bookings than the previous period.',
  'merchant.insights.trend_fewer': '{n}% fewer bookings than the previous period.',
  'merchant.insights.trend_first': 'No bookings in the previous period to compare with.',
  'merchant.insights.chart_label': 'Bookings this period against the previous period',
  'merchant.insights.source': 'Where bookings come from',
  'merchant.insights.bookings_word': 'bookings',
  'merchant.insights.src.marketplace': 'Marketplace',
  'merchant.insights.src.link': 'Your shared link',
  'merchant.insights.src.external': 'Outside site / Facebook',
  'merchant.insights.src_low': 'Only {n}% of bookings come from the marketplace — promoting brings new customers.',
  'merchant.insights.src_link': '{n}% via your link — it works.',
  'merchant.insights.src_none': 'No online bookings in this period yet — share your link.',
  'merchant.insights.popular': 'Popular services',
  'merchant.insights.pop_bookings': '{n} bookings',
  'merchant.insights.pop_fewest': '{name} gets the fewest bookings — try an offer.',
  'merchant.insights.pop_empty': 'No service was booked in this period yet.',
  'merchant.insights.pop_more': 'More actions for {name}',
  'merchant.insights.act.share': 'Share your link',
  'merchant.insights.act.promote': 'Promote',
  'merchant.insights.act.offer': 'Make an offer',
  'merchant.insights.act.sched': 'See schedule',
  'merchant.insights.act.remind': 'Reminder settings',
  'merchant.insights.act.reply': 'Reply',
  'merchant.insights.busy': 'Busy hours',
  'merchant.insights.less': 'Quiet',
  'merchant.insights.more': 'Busy',
  'merchant.insights.busiest': 'Busiest',
  'merchant.insights.quietest': 'Quietest',
  'merchant.insights.per_hour': '~{n} bookings an hour',
  'merchant.insights.quiet_sub': 'A good slot for an offer',
  'merchant.insights.slot': '{days} · {hours}',
  'merchant.insights.hours_same': '{a}–{b_full}',
  'merchant.insights.hours_cross': '{a_full}–{b_full}',
  'merchant.insights.day_range': '{a}–{b}',
  'merchant.insights.cell': '{day} {hour}: {n} bookings',
  'merchant.insights.part.morning': 'mornings',
  'merchant.insights.part.afternoon': 'afternoons',
  'merchant.insights.part.evening': 'evenings',
  'merchant.insights.busy_insight': '{days} {part} are busiest — staff up then.',
  'merchant.insights.quiet_insight': '{days} {part} stay quiet — run an offer for that slot.',
  'merchant.insights.busy_empty': 'Busy hours show up once bookings come in.',
  'merchant.insights.missed': 'No-shows & cancellations',
  'merchant.insights.noshow': 'No-show rate',
  'merchant.insights.cancel': 'Cancellation rate',
  'merchant.insights.noshow_rows': 'No-shows this period',
  'merchant.insights.cancel_rows': 'Cancelled this period',
  'merchant.insights.row_bookings': '{n} bookings',
  'merchant.insights.noshow_high': 'More than {n}% of customers did not show up — turn on SMS reminders.',
  'merchant.insights.noshow_ok': 'No-shows are under control ✓',
  'merchant.insights.rating': 'Rating',
  'merchant.insights.awaiting': '{n} awaiting reply',
  'merchant.insights.reviews_awaiting': '{n, plural, one{# review is} other{# reviews are}} awaiting a reply — replying brings more bookings.',
  'merchant.insights.all_replied': 'Every review has a reply ✓',
  'merchant.insights.no_reviews': 'No reviews yet — they show up here as customers rate you.',
  'merchant.insights.low_data': 'The full picture appears here as more bookings come in',
  'merchant.insights.new_line': 'Insights start collecting after your first booking',
  // ---- end DC79 ----
  // ---- DC101 (admin Pricing control) ----
  'admin.pr.title': 'Pricing Control',
  'admin.pr.sub': 'Edit prices as a draft, then publish. Every published change is recorded in the audit log with your name.',
  'admin.pr.kicker_ro': 'Pricing control · read-only',
  'admin.pr.kicker_draft': 'Pricing control · draft mode',
  'admin.pr.hero.subs_open': 'Launch promo prices are live',
  'admin.pr.hero.subs_open_sub': 'Every first subscription charges the promotional column while the window is open. Merchants inside a paid term keep the price they bought.',
  'admin.pr.hero.subs_closed': 'Regular prices are live',
  'admin.pr.hero.subs_closed_sub': 'Every new subscription charges the regular column. Promotional prices stay staged until the window opens again.',
  'admin.pr.hero.placement': 'Placement is priced per slot per day',
  'admin.pr.hero.placement_sub': 'The rate published here is what the admin-scheduled placement slots sell at. Placement is settled outside the platform.',
  'admin.pr.hero.fees': 'Booking fees are staged, not live',
  'admin.pr.hero.fees_sub': 'Commission and convenience levers stay dormant until {brand} Pay is switched on. Nothing is charged to a booking today.',
  'admin.pr.hero.fees_live': 'Booking fees are live',
  'admin.pr.hero.fees_live_sub': 'Published commission and convenience fees are charged on platform-gateway bookings. Direct-payment bookings are never charged.',
  'admin.pr.stat.live_rows': 'Live price rows',
  'admin.pr.stat.staged': 'Staged, not published',
  'admin.pr.stat.nothing': 'Nothing staged',
  'admin.pr.stat.last': 'Last published · {by}',
  'admin.pr.stat.slots': 'Placement slots',
  'admin.pr.stat.rate': 'Rate per slot · day',
  'admin.pr.stat.commission': 'Commission',
  'admin.pr.stat.convenience': 'Convenience fee',
  'admin.pr.stat.pay': '{brand} Pay',
  'admin.pr.on': 'On',
  'admin.pr.off': 'Off',
  'admin.pr.tabs_label': 'Pricing tabs',
  'admin.pr.tab.subs': 'Subscriptions Plans',
  'admin.pr.tab.placement': 'Placement',
  'admin.pr.tab.fees': 'Booking fees',
  'admin.pr.tab.not_live': 'Not live',
  'admin.pr.mode.editing': 'Editing — changes stage as drafts',
  'admin.pr.mode.read_only': 'Read-only — turn on Edit to change prices',
  'admin.pr.mode.view_only': 'View only — Super Admin and Finance can edit',
  'admin.pr.create': 'Create new',
  'admin.pr.edit': 'Edit',
  'admin.pr.editing': '✓ Editing',
  'admin.pr.publish': 'Publish',
  'admin.pr.discard': 'Discard',
  'admin.pr.win.open_title': 'Launch promo window is open',
  'admin.pr.win.open_body': 'The promotional column is what merchants pay today. Close the window and new purchases move to the regular column — existing paid terms are never repriced.',
  'admin.pr.win.closed_title': 'Launch promo window is closed',
  'admin.pr.win.closed_body': 'The regular column is what merchants pay today. Open the window and first subscriptions move to the promotional column immediately.',
  'admin.pr.win.label': 'Launch promo window',
  'admin.pr.win.until': 'Until {date}',
  'admin.pr.win.ended': 'Ended {date}',
  'admin.pr.win.unset': 'No end date set',
  'admin.pr.win.close_q': 'Close the launch promo window?',
  'admin.pr.win.close_body': 'New purchases will price at the regular rate immediately. Merchants inside a paid term keep their price.',
  'admin.pr.win.open_q': 'Open the launch promo window?',
  'admin.pr.win.open_q_body': 'Promotional prices become the live price for first subscriptions immediately, until the end date you set.',
  'admin.pr.win.ends_field': 'Window ends on',
  'admin.pr.win.confirm': 'Confirm',
  'admin.pr.win.opened': 'Promo window open',
  'admin.pr.win.closed': 'Promo window closed',
  'admin.pr.win.date_required': 'Choose a future end date for the window.',
  'admin.pr.card.per_month': '/month',
  'admin.pr.card.note': 'Lowest per-month rate — {term} months term on the {column} column.',
  'admin.pr.card.col_promo': 'promotional',
  'admin.pr.card.col_regular': 'regular',
  'admin.pr.card.free': 'Free',
  'admin.pr.card.free_note': 'No charge. Bookings are capped once the first {n} months are over.',
  'admin.pr.chip.staged': 'Draft staged',
  'admin.pr.chip.live': 'Live',
  'admin.pr.chip.disabled': 'Disabled',
  'admin.pr.chip.always_free': 'Always free',
  'admin.pr.chip.removing': 'Removing — draft',
  'admin.pr.chip.dirty': 'Draft — unpublished',
  'admin.pr.chip.new': 'Draft — new',
  'admin.pr.col.term': 'Term',
  'admin.pr.col.regular': 'Regular',
  'admin.pr.col.launch': 'Promo',
  'admin.pr.col.loyalty': 'Renewal',
  'admin.pr.months': '{n} months',
  'admin.pr.was': 'was {v}',
  'admin.pr.included': 'Included',
  'admin.pr.price_aria': '{term} · {kind} price',
  'admin.pr.row.edit': 'Edit row',
  'admin.pr.row.duplicate': 'Duplicate as draft',
  'admin.pr.row.disable': 'Disable row',
  'admin.pr.row.undo': 'Undo removal',
  'admin.pr.row.drop': 'Remove draft row',
  'admin.pr.ent.businesses': 'Business profiles',
  'admin.pr.ent.businesses_note': 'Profiles an account may create',
  'admin.pr.ent.services': 'Services per business',
  'admin.pr.ent.services_note': 'Each with its own booking toggle',
  'admin.pr.ent.professionals': 'Professionals',
  'admin.pr.ent.professionals_note': 'Roster held at account level',
  'admin.pr.ent.moderator_seats': 'Moderators',
  'admin.pr.ent.moderator_seats_note': 'Extra logins the merchant manages',
  'admin.pr.ent.bookings': 'Bookings per month',
  'admin.pr.ent.bookings_note_free': 'After the first months; excess arrives blurred',
  'admin.pr.ent.bookings_note_paid': 'No cap',
  'admin.pr.ent.promo_months': 'Unlimited-booking months',
  'admin.pr.ent.promo_months_note': 'From the sign-up date',
  'admin.pr.ent.offers': 'Live offers',
  'admin.pr.ent.offers_note': 'Offers running at once',
  'admin.pr.ent.payments': 'Payment features',
  'admin.pr.ent.payments_on': 'With hard KYC',
  'admin.pr.ent.payments_off': 'Not available',
  'admin.pr.ent.payments_note_on': 'Per business profile',
  'admin.pr.ent.payments_note_off': 'Booking info only',
  'admin.pr.ent.unlimited': 'Unlimited',
  'admin.pr.gate_note': 'Free booking gate — {months} months of unlimited bookings from the sign-up date, then {cap} bookings a month. Bookings past the cap arrive blurred until the merchant upgrades.',
  'admin.pr.meta': 'Last changed by {by} · {when}',
  'admin.pr.meta_subs': 'Last changed by {by} · {when} — entitlements {eby} · {ewhen}',
  'admin.pr.pl.title': 'Placement prices',
  'admin.pr.pl.sub': 'The admin-scheduled placement slots, priced per slot per day.',
  'admin.pr.pl.surface': 'Surface',
  'admin.pr.pl.day': 'Day',
  'admin.pr.pl.rate': 'Rate per slot · day',
  'admin.pr.pl.rate_sub': 'One rate for every sellable slot, settled outside the platform',
  'admin.pr.fee.banner_off': 'Activates with {brand} Pay — commission and convenience fees stay at {zero} until the platform gateway ships. Configure and save drafts now; nothing is charged.',
  'admin.pr.fee.banner_on': '{brand} Pay is live — published commission and convenience fees are charged on platform-gateway bookings. Direct-payment bookings are still never charged.',
  'admin.pr.fee.gw_live': 'Gateway: live',
  'admin.pr.fee.gw_off': 'Gateway: not live',
  'admin.pr.fee.toggle_live': 'Live',
  'admin.pr.fee.toggle_off': 'Off',
  'admin.pr.fee.open_security': 'Open Security →',
  'admin.pr.fee.gw_on_q': 'Switch {brand} Pay live?',
  'admin.pr.fee.gw_on_body': 'Published commission and convenience fees start being charged on platform-gateway bookings immediately. Direct-payment bookings are never charged. This is the same flag as in Roles & Security.',
  'admin.pr.fee.gw_off_q': 'Switch {brand} Pay off?',
  'admin.pr.fee.gw_off_body': 'Commission and convenience fees stop being charged immediately — every booking reverts to direct payment. Configured values stay saved.',
  'admin.pr.fee.gw_switch_on': 'Switch live',
  'admin.pr.fee.gw_switch_off': 'Switch off',
  'admin.pr.fee.gw_done_on': 'Gateway live — published fees are now charged',
  'admin.pr.fee.gw_done_off': 'Gateway off — fees are no longer charged',
  'admin.pr.fee.gw_super_only': 'Only Super Admin can switch the platform gateway',
  'admin.pr.fee.global': 'Global fee defaults',
  'admin.pr.fee.global_sub': 'The platform-wide fallback every booking template inherits.',
  'admin.pr.fee.commission': 'Commission %',
  'admin.pr.fee.commission_sub': 'Share of the booking value taken by the platform. Applies to platform-gateway bookings only.',
  'admin.pr.fee.convenience': 'Convenience fee {cur} / booking',
  'admin.pr.fee.convenience_sub': 'Flat fee added to the customer\'s total, per booking. Applies to platform-gateway bookings only.',
  'admin.pr.fee.minus': 'Lower commission',
  'admin.pr.fee.plus': 'Raise commission',
  'admin.pr.fee.chip_configured': 'Configured — awaiting gateway',
  'admin.pr.fee.chip_not_charged': 'Not charged — {zero}',
  'admin.pr.fee.chip_inherits': 'Inherits global',
  'admin.pr.fee.charged': 'What is charged today',
  'admin.pr.fee.charged_sub_off': 'Every booking is direct payment today, so both levers are inert.',
  'admin.pr.fee.charged_sub_on': 'Platform-gateway bookings pay the published levers.',
  'admin.pr.fee.charged_commission': 'Commission charged today',
  'admin.pr.fee.charged_convenience': 'Convenience fee charged today',
  'admin.pr.fee.direct_note': 'Direct-payment bookings are never charged a commission or convenience fee — the money never touches {brand}. {brand} does not process, hold, or guarantee direct payments.',
  'admin.pr.fee.templates': 'Per-template fee overrides',
  'admin.pr.fee.templates_sub': 'Every booking template from the registry. Override a lever per template, or leave it inheriting the global default.',
  'admin.pr.fee.th_template': 'Template',
  'admin.pr.fee.th_commission': 'Commission',
  'admin.pr.fee.th_convenience': 'Convenience fee',
  'admin.pr.fee.th_state': 'State',
  'admin.pr.fee.override': 'Override',
  'admin.pr.fee.inherit': 'Inherit',
  'admin.pr.fee.no_templates': 'No booking templates to show.',
  'admin.pr.bar.count': '{n} unpublished changes',
  'admin.pr.bar.count_one': '1 unpublished change',
  'admin.pr.bar.sub': 'Draft — goes live on publish',
  'admin.pr.discarded': 'Draft changes discarded',
  'admin.pr.pub.title': 'Publish {n} price changes?',
  'admin.pr.pub.title_one': 'Publish 1 price change?',
  'admin.pr.pub.body': 'This updates live pricing and is recorded in the audit log against {name} ({role}). New purchases and renewals pick the new prices up immediately; existing paid terms are unaffected.',
  'admin.pr.pub.setting': 'Setting',
  'admin.pr.pub.current': 'Current',
  'admin.pr.pub.proposed': 'Proposed',
  'admin.pr.pub.go': 'Publish — go live',
  'admin.pr.pub.done': '{n} price changes published — audit-logged',
  'admin.pr.pub.done_one': '1 price change published — audit-logged',
  'admin.pr.pub.stale': 'Someone published a pricing change since you loaded this page — reload and review your draft.',
  'admin.pr.none_to_publish': 'No unpublished changes',
  'admin.pr.guard.role': 'Only Super Admin and Finance can edit pricing',
  'admin.pr.guard.edit': 'Turn on Edit first — changes stage as drafts',
  'admin.pr.diff.price': '{plan} · {term} · {kind}',
  'admin.pr.diff.ent': '{plan} · {lever}',
  'admin.pr.diff.gate': 'Free gate · {lever}',
  'admin.pr.diff.placement': 'Placement · rate per slot · day',
  'admin.pr.diff.global_commission': 'Global · commission %',
  'admin.pr.diff.global_convenience': 'Global · convenience fee',
  'admin.pr.diff.tpl_commission': '{template} · commission %',
  'admin.pr.diff.tpl_convenience': '{template} · convenience fee',
  'admin.pr.diff.new': 'New term — {plan} · {term}',
  'admin.pr.diff.remove': 'Disable — {plan} · {term}',
  'admin.pr.diff.live': 'live',
  'admin.pr.diff.disabled': 'disabled',
  'admin.pr.rm.title': 'Disable {label}?',
  'admin.pr.rm.body': 'The row leaves live pricing when you publish. Existing paid terms are unaffected. This is a draft action — nothing changes until Publish.',
  'admin.pr.rm.go': 'Disable — add to draft',
  'admin.pr.rm.done': 'Marked for removal — publish to apply',
  'admin.pr.rm.undone': 'Removal cancelled',
  'admin.pr.rm.last': 'A paid plan keeps at least one term',
  'admin.pr.form.title': 'New subscription price row',
  'admin.pr.form.sub': 'Adds a draft term to a plan. Nothing goes live until you publish.',
  'admin.pr.form.plan': 'Plan',
  'admin.pr.form.term': 'Term (months)',
  'admin.pr.form.regular': 'Regular price ({cur} / term)',
  'admin.pr.form.launch': 'Promotional price ({cur} / term)',
  'admin.pr.form.loyalty': 'Loyalty-renewal price ({cur} / term)',
  'admin.pr.form.none': 'Every plan already sells every term — edit a row instead.',
  'admin.pr.form.invalid': 'Promotional and renewal prices cannot exceed the regular price.',
  'admin.pr.form.save': 'Add draft row',
  'admin.pr.form.added': 'Draft row added — publish to go live',
  'admin.pr.form.plan_full': 'Every term of this plan is already priced',
  'admin.pr.close': 'Close',
  // ---- end DC101 ----
  // ---- DC81 My Plan ----
  'merchant.plan.tab.plan': 'Plan',
  'merchant.plan.tab.bill': 'Billing & receipts',
  'merchant.plan.tabs': 'My Plan sections',
  'merchant.plan.current': 'Current plan',
  'merchant.plan.hero.sub_paid': '{n}-month term · paid {kind} price {price}',
  'merchant.plan.kind.launch': 'launch',
  'merchant.plan.kind.regular': 'regular',
  'merchant.plan.kind.loyalty': 'loyalty',
  'merchant.plan.hero.free_sub': 'Free plan · no term',
  'merchant.plan.hero.renews': 'Renews',
  'merchant.plan.hero.loyal': 'Renews at the loyalty price {price}',
  'merchant.plan.hero.renew': 'Renew',
  'merchant.plan.hero.moving': 'Moves to {plan} on {date}',
  'merchant.plan.hero.keep': 'Keep my plan',
  'merchant.plan.toast.kept': 'Downgrade cancelled — your plan stays',
  'merchant.plan.usage': 'Usage',
  'merchant.plan.meter.businesses': 'Businesses',
  'merchant.plan.meter.services': 'Services',
  'merchant.plan.meter.services_note': 'in your busiest business',
  'merchant.plan.meter.professionals': 'Professionals',
  'merchant.plan.meter.bookings': 'Bookings this month',
  'merchant.plan.meter.value': '{used}/{cap}',
  'merchant.plan.meter.unlimited': '{used} · no limit',
  'merchant.plan.meter.upgrade': 'Upgrade',
  'merchant.plan.compare': 'Compare plans',
  'merchant.plan.term': '{n} months',
  'merchant.plan.terms': 'Term',
  'merchant.plan.best_price': 'Best price',
  'merchant.plan.badge.free': 'Free forever',
  'merchant.plan.badge.popular': 'Most popular',
  'merchant.plan.badge.value': 'Best value',
  'merchant.plan.regular': 'Regular {price}',
  'merchant.plan.free_price': 'Free',
  'merchant.plan.per_term': '/ {n} months',
  'merchant.plan.forever': 'forever',
  'merchant.plan.just_per': 'just {price}/month',
  'merchant.plan.ent.businesses': '{n, plural, one{# business} other{# businesses}}',
  'merchant.plan.ent.businesses_unlimited': 'Unlimited businesses',
  'merchant.plan.ent.services': '{n, plural, one{# service} other{# services}}',
  'merchant.plan.ent.services_unlimited': 'Unlimited services',
  'merchant.plan.ent.professionals': '{n, plural, one{# professional} other{# professionals}}',
  'merchant.plan.ent.professionals_unlimited': 'Unlimited professionals',
  'merchant.plan.ent.bookings': '{n} bookings a month',
  'merchant.plan.ent.bookings_unlimited': 'Unlimited bookings',
  'merchant.plan.ent.offers': '{n, plural, one{# live offer} other{# live offers}}',
  'merchant.plan.ent.offers_unlimited': 'Unlimited live offers',
  'merchant.plan.ent.offers_none': 'No live offers',
  'merchant.plan.ent.payments': 'Payment verification',
  'merchant.plan.ent.payments_none': 'No payment verification',
  'merchant.plan.ent.insights': 'Insights',
  'merchant.plan.ent.insights_none': 'No Insights',
  'merchant.plan.not_included': 'Not included',
  'merchant.plan.card.current': 'Current',
  'merchant.plan.card.upgrade': 'Upgrade',
  'merchant.plan.card.downgrade': 'Downgrade',
  'merchant.plan.card.scheduled': 'Downgrade scheduled',
  'merchant.plan.renew_note': 'Stay subscribed and renew at the loyalty price — about {pct}% below regular',
  'merchant.plan.renew_note_plain': 'Stay subscribed and renew at the loyalty price',
  'merchant.plan.contact.title': 'Need more than {plan}?',
  'merchant.plan.contact.body': 'Running more than {n} businesses? Tell us what you need.',
  'merchant.plan.contact.cta': 'Contact us',
  'merchant.plan.up.title_upgrade': 'Upgrade · {plan}',
  'merchant.plan.up.title_renew': 'Renew · {plan}',
  'merchant.plan.up.pick_term': 'Choose a term',
  'merchant.plan.up.note.launch': 'Launch special — first subscription only',
  'merchant.plan.up.note.loyalty': 'You renew at the loyalty price as an active subscriber',
  'merchant.plan.up.note.regular': 'Regular price — the amount is confirmed when you submit',
  'merchant.plan.up.summary': 'Summary',
  'merchant.plan.up.regular': 'Regular',
  'merchant.plan.up.kind.launch': 'Launch special',
  'merchant.plan.up.kind.loyalty': 'Loyalty price',
  'merchant.plan.up.kind.regular': 'Price',
  'merchant.plan.up.per_month': '{price}/month',
  'merchant.plan.up.pay': 'Pay with bKash/Nagad',
  'merchant.plan.up.back': 'Back',
  'merchant.plan.up.method': 'Payment method',
  'merchant.plan.up.pay_hint': 'Send {amount} to {brand} with {method}, then enter the TRX ID',
  'merchant.plan.up.trx': 'TRX ID',
  'merchant.plan.up.trx_hint': 'Type the TRX ID from your {method} SMS exactly',
  'merchant.plan.up.shot_or': 'and',
  'merchant.plan.up.shot': 'Upload payment screenshot',
  'merchant.plan.up.shot_remove': 'Remove screenshot',
  'merchant.plan.up.shot_failed': 'Could not upload the screenshot',
  'merchant.plan.up.submit': 'Submit',
  'merchant.plan.up.verifying': 'Verifying',
  'merchant.plan.up.verify_body': 'We are checking your payment of {amount} — you get an SMS when it is done.',
  'merchant.plan.up.pending': 'A payment is already waiting for verification',
  'merchant.plan.up.failed': 'Could not submit the payment',
  'merchant.plan.up.done': 'Plan is live',
  'merchant.plan.up.done_body': 'The new limits work right away.',
  'merchant.plan.up.close': 'Close',
  'merchant.plan.down.title': 'What happens if you downgrade · {plan}',
  'merchant.plan.down.effective': 'Takes effect on {date}, when your paid term ends.',
  'merchant.plan.down.pay_note': 'To stay on {plan}, pay for it before then — otherwise the plan moves to {free}.',
  'merchant.plan.down.ok': '{used} of {cap} {what} in use — no problem',
  'merchant.plan.down.over': '{over} of {used} {what} over the {plan} limit',
  'merchant.plan.down.over_sub': 'They stay live — you cannot add more until you are under the limit',
  'merchant.plan.down.what.businesses': 'businesses',
  'merchant.plan.down.what.services': 'services',
  'merchant.plan.down.what.professionals': 'professionals',
  'merchant.plan.down.offers': '{n, plural, =0{Live offers pause when the term ends} other{Live offers above # pause when the term ends}}',
  'merchant.plan.down.offers_sub': 'Paused offers do not resume on their own',
  'merchant.plan.down.safe': 'Confirmed bookings always stand',
  'merchant.plan.down.reason_label': 'Reason for downgrading',
  'merchant.plan.down.reason.cost': 'Cutting costs',
  'merchant.plan.down.reason.features': 'Do not need the features',
  'merchant.plan.down.reason.scaling': 'Scaling down',
  'merchant.plan.down.reason.other': 'Other',
  'merchant.plan.down.yes': 'I understand, downgrade',
  'merchant.plan.down.cancel': 'Cancel',
  'merchant.plan.down.failed': 'Could not schedule the downgrade',
  'merchant.plan.toast.down': 'Downgrade to {plan} scheduled for {date}',
  'merchant.plan.bill.payouts': 'Payouts',
  'merchant.plan.bill.soon': 'Coming soon',
  'merchant.plan.bill.gross': 'Gross',
  'merchant.plan.bill.fees': 'Fees',
  'merchant.plan.bill.net': 'Net',
  'merchant.plan.bill.payout_help': 'Customers pay you directly today; when {brand} Pay launches it shows up here.',
  'merchant.plan.bill.receipts': 'Receipts',
  'merchant.plan.bill.empty': 'No receipts yet',
  'merchant.plan.inv.what': '{plan} · {n} months',
  'merchant.plan.inv.sub': '{date} · {id}',
  'merchant.plan.inv.kind.subscription': 'Subscription',
  'merchant.plan.inv.status.paid': 'Paid',
  'merchant.plan.inv.status.due': 'Due',
  'merchant.plan.inv.status.overdue': 'Overdue',
  'merchant.plan.inv.status.void': 'Void',
  'merchant.plan.inv.status.verifying': 'Verifying',
  'merchant.plan.inv.status.refunded': 'Refunded',
  'merchant.plan.inv.download': 'Download receipt',
  'merchant.plan.inv.downloading': 'Downloading receipt',
  'merchant.plan.inv.download_failed': 'Could not download the receipt',
  'merchant.plan.rc.title': 'Receipt',
  'merchant.plan.rc.billed': 'Billed to',
  'merchant.plan.rc.from': 'From',
  'merchant.plan.rc.date': 'Date',
  'merchant.plan.rc.paid_via': 'Paid via',
  'merchant.plan.rc.item': 'For',
  'merchant.plan.rc.amount': 'Amount',
  'merchant.plan.rc.subtotal': 'Subtotal',
  'merchant.plan.rc.vat': 'VAT',
  'merchant.plan.rc.total': 'Total',
  'merchant.plan.rc.pay_details': 'Payment details',
  'merchant.plan.rc.trx': 'TRX ID',
  'merchant.plan.rc.share': 'Share',
  'merchant.plan.rc.pdf': 'Download PDF',
  'merchant.plan.rc.copied': 'Receipt details copied',
  'merchant.plan.rc.share_text': '{brand} receipt {id} · {what} · {amount}',
  'merchant.plan.rc.close': 'Close',
  // ---- end DC81 ----
  // ---- DC82 merchant Account ----
  'merchant.account.sub': 'Your name, phone, email and preferences.',
  'merchant.account.upload_photo': 'Upload photo',
  'merchant.account.photo_hint': 'JPG or PNG, up to 5 MB',
  'merchant.account.photo_saved': 'Profile photo updated',
  'merchant.account.photo_failed': 'Could not upload the photo',
  'merchant.account.photo_uploading': 'Uploading your photo… (1/2)',
  'merchant.account.photo_saving': 'Saving it to your profile… (2/2)',
  'merchant.account.photo_retry': 'Try again',
  'merchant.account.photo_rules': 'Choose a JPG or PNG up to 5 MB',
  'merchant.account.photo_alt': 'Profile photo',
  'merchant.account.verified': 'Verified',
  'merchant.account.profile': 'Profile',
  'merchant.account.name': 'Name',
  'merchant.account.phone': 'Phone',
  'merchant.account.nid': 'NID',
  'merchant.account.email': 'Email',
  'merchant.account.read_only': 'cannot be changed',
  'merchant.account.nid.none': 'Not submitted',
  'merchant.account.nid.submitted': 'Under review',
  'merchant.account.nid.verified': 'Verified',
  'merchant.account.nid.rejected': 'Rejected',
  'merchant.account.save': 'Save changes',
  'merchant.account.saved': 'Account details updated',
  'merchant.account.nothing': 'Nothing to save',
  'merchant.account.save_failed': 'Could not save — try again',
  'merchant.account.name_required': 'Enter your name',
  'merchant.account.email_invalid': 'Enter a valid email',
  'merchant.account.address': 'Address',
  'merchant.account.address_sub': 'Used for billing and correspondence',
  'merchant.account.division': 'Division',
  'merchant.account.city': 'City',
  'merchant.account.area': 'Area',
  'merchant.account.post': 'Postcode',
  'merchant.account.street': 'Street address',
  'merchant.account.choose': 'Choose',
  'merchant.account.address_saved': 'Address saved',
  'merchant.account.notif': 'Notifications',
  'merchant.account.ch.email': 'Email',
  'merchant.account.ch.sms': 'SMS',
  'merchant.account.free': 'free',
  'merchant.account.ev.booking_new': 'New booking',
  'merchant.account.ev.proof_submitted': 'Payment proof',
  'merchant.account.ev.booking_cancelled': 'Cancellation',
  'merchant.account.ev.reminder': 'Reminder',
  'merchant.account.ev.review_new': 'Review',
  'merchant.account.notif_keep': 'Keep at least one channel on for this',
  'merchant.account.notif_failed': 'Could not save the preference',
  'merchant.account.prefs': 'Language & theme',
  'merchant.account.lang_default': 'Default language',
  'merchant.account.theme_default': 'Default theme',
  'merchant.account.lang.bn': 'বাংলা',
  'merchant.account.lang.en': 'English',
  'merchant.account.theme.light': 'Light',
  'merchant.account.theme.dark': 'Dark',
  'merchant.account.danger': 'Deactivate account',
  'merchant.account.danger_body': 'Our team contacts you before anything changes; nothing is deleted. A reason is required.',
  'merchant.account.reason': 'Reason',
  'merchant.account.reason.closing': 'Closing the business',
  'merchant.account.reason.not_now': 'Do not need it now',
  'merchant.account.reason.moving': 'Moving to another platform',
  'merchant.account.reason.other': 'Other',
  'merchant.account.type_confirm': 'Type to confirm — {word}',
  'merchant.account.type_word': 'DEACTIVATE',
  'merchant.account.deactivate': 'Deactivate',
  'merchant.account.deact_sent': 'Deactivation request recorded',
  'merchant.account.deact_sent_note': 'Request sent — our team will contact you.',
  'merchant.account.deact_failed': 'Could not send the request — try again',
  'merchant.account.deact_pick_reason': 'Pick a reason first',
  'merchant.account.deact_message': 'Please deactivate my merchant account. Reason: {reason}',
  // ---- end DC82 ----
  // ---- DC104 admin Reports ----
  'admin.rpt.sub': 'The complete data-of-record for the platform — every merchant, subscription, booking, transaction, message, offer, customer and admin action, down to the single record. Filter, drill in, export; every export is audit-logged.',
  'admin.rpt.stat.ledgers': 'Ledgers',
  'admin.rpt.stat.records': 'Records',
  'admin.rpt.tabs_label': 'Report ledgers',
  'admin.rpt.tab.merchants': 'Merchants',
  'admin.rpt.tab.subs': 'Subscriptions',
  'admin.rpt.tab.bookings': 'Bookings',
  'admin.rpt.tab.trx': 'Transactions',
  'admin.rpt.tab.ads': 'Ads & Promotion',
  'admin.rpt.tab.msg': 'Messaging',
  'admin.rpt.tab.offers': 'Offers',
  'admin.rpt.tab.customers': 'Customers',
  'admin.rpt.tab.audit': 'Audit',
  'admin.rpt.ads_deferred': 'The Ads & Promotion ledger arrives with self-serve promotion — not available yet.',
  'admin.rpt.restricted': 'Restricted — {roles}',
  'admin.rpt.none_title': 'No ledger for your role',
  'admin.rpt.none_body': 'Reports ledgers open for Super admin, Operations and Finance.',
  'admin.rpt.records': '{n} records',
  'admin.rpt.truncated': 'first 1,000 shown',
  'admin.rpt.range_label': 'Period',
  'admin.rpt.range.thisM': 'This month',
  'admin.rpt.range.lastM': 'Last month',
  'admin.rpt.range.ytd': 'YTD',
  'admin.rpt.range.all': 'All time',
  'admin.rpt.export_csv': 'Export CSV',
  'admin.rpt.export_pdf': 'Export PDF',
  'admin.rpt.soon': 'Soon',
  'admin.rpt.pdf_soon_hint': 'PDF export is not available yet — use CSV.',
  'admin.rpt.view_full': 'View full report',
  'admin.rpt.foot_note': 'Every export is audit-logged — the whole ledger for the chosen period.',
  'admin.rpt.export_done': 'Export ready — recorded in the audit log.',
  'admin.rpt.export_failed': 'The export could not be generated. Try again.',
  'admin.rpt.export_slow': 'The export is still being prepared. Try again in a minute.',
  'admin.rpt.export_error': 'Export failed: {msg}',
  'admin.rpt.full.title': 'Full report',
  'admin.rpt.full.meta': 'Generated {date} · {name} ({role}) · {n} records',
  'admin.rpt.close': 'Close',
  'admin.rpt.empty': 'No rows match',
  'admin.rpt.clear_filters': 'Clear filters',
  'admin.rpt.page': '{from}–{to} of {n}',
  'admin.rpt.prev': 'Prev',
  'admin.rpt.next': 'Next',
  'admin.rpt.tier_term': '{plan} · {n} mo',
  'admin.rpt.all.tiers': 'All plans',
  'admin.rpt.all.statuses': 'All statuses',
  'admin.rpt.all.kyc': 'All payment KYC',
  'admin.rpt.all.price_kinds': 'All price kinds',
  'admin.rpt.all.sources': 'All sources',
  'admin.rpt.all.pay_methods': 'All pay modes',
  'admin.rpt.all.lines': 'All lines',
  'admin.rpt.all.methods': 'All methods',
  'admin.rpt.all.purposes': 'All purposes',
  'admin.rpt.all.channels': 'All channels',
  'admin.rpt.all.discount_kinds': 'All discount kinds',
  'admin.rpt.all.roles': 'All roles',
  'admin.rpt.col.merchant': 'Merchant',
  'admin.rpt.col.tier': 'Plan',
  'admin.rpt.col.renewal': 'Renewal',
  'admin.rpt.col.kyc': 'KYC',
  'admin.rpt.col.mrr': 'MRR',
  'admin.rpt.col.bookings': 'Bookings',
  'admin.rpt.col.status': 'Status',
  'admin.rpt.col.id': 'ID',
  'admin.rpt.col.price_applied': 'Price applied',
  'admin.rpt.col.amount': 'Amount',
  'admin.rpt.col.start': 'Start',
  'admin.rpt.col.code': 'Code',
  'admin.rpt.col.when': 'When',
  'admin.rpt.col.advance': 'Advance paid',
  'admin.rpt.col.source': 'Source',
  'admin.rpt.col.trx': 'TRX',
  'admin.rpt.col.line': 'Line',
  'admin.rpt.col.method': 'Method',
  'admin.rpt.col.purpose': 'Purpose',
  'admin.rpt.col.channel': 'Channel',
  'admin.rpt.col.ref': 'Reference',
  'admin.rpt.col.attempts': 'Attempts',
  'admin.rpt.col.date': 'Date',
  'admin.rpt.col.offer': 'Offer',
  'admin.rpt.col.window': 'Window',
  'admin.rpt.col.redeemed': 'Redeemed',
  'admin.rpt.col.customer': 'Customer',
  'admin.rpt.col.spend': 'Lifetime spend',
  'admin.rpt.col.last_booking': 'Last booking',
  'admin.rpt.col.joined': 'Joined',
  'admin.rpt.col.who': 'Who',
  'admin.rpt.col.action': 'Action',
  'admin.rpt.col.target': 'Target',
  'admin.rpt.col.summary': 'Summary',
  'admin.rpt.fact.tier_term': 'Plan · term',
  'admin.rpt.fact.area': 'Area',
  'admin.rpt.fact.lifetime_bookings': 'Bookings (lifetime)',
  'admin.rpt.fact.rating': 'Rating',
  'admin.rpt.fact.rating_v': '{r} · {n} reviews',
  'admin.rpt.fact.invoices': 'Invoices',
  'admin.rpt.fact.slug': 'Page address',
  'admin.rpt.fact.merchant_id': 'Merchant record',
  'admin.rpt.fact.pay_method': 'Pay mode',
  'admin.rpt.fact.created': 'Created',
  'admin.rpt.fact.timestamp': 'Timestamp',
  'admin.rpt.fact.record': 'Record',
  'admin.rpt.fact.sent_at': 'Sent',
  'admin.rpt.fact.discount': 'Discount',
  'admin.rpt.fact.service': 'Service',
  'admin.rpt.fact.services_max': 'Services (largest business)',
  'admin.rpt.fact.professionals': 'Professionals',
  'admin.rpt.fact.role': 'Role',
  'admin.rpt.open.merchant': 'Open merchant',
  'admin.rpt.open.booking': 'Open booking',
  'admin.rpt.open.trx': 'Check TRX',
  'admin.rpt.open.offer': 'Open offer',
  'admin.rpt.open.customer': 'Open customer',
  'admin.rpt.merchants.title': 'Merchant ledger',
  'admin.rpt.merchants.search': 'Search name, area, plan…',
  'admin.rpt.merchants.usage': '{used}/{cap} businesses · {pros} professionals',
  'admin.rpt.merchants.card.total': 'Total merchants',
  'admin.rpt.merchants.card.total_sub': 'Every merchant business on the platform',
  'admin.rpt.merchants.card.paid': 'Paid',
  'admin.rpt.merchants.card.paid_sub': '{mrr} MRR',
  'admin.rpt.merchants.card.split': 'Plan split',
  'admin.rpt.merchants.card.kyc': 'Payment KYC pending',
  'admin.rpt.merchants.card.kyc_sub': 'Under review or more info asked',
  'admin.rpt.merchants.record_body': 'Record {id} · joined {joined}. Related ledgers link below — each full table lives in its own tab.',
  'admin.rpt.subs.title': 'Subscription ledger',
  'admin.rpt.subs.search': 'Search merchant, plan or record…',
  'admin.rpt.subs.card.active': 'Active subscriptions',
  'admin.rpt.subs.card.active_sub': 'of {n} total',
  'admin.rpt.subs.card.mrr_sub': 'Normalised monthly value',
  'admin.rpt.subs.card.paid': 'Paid plans',
  'admin.rpt.subs.card.renewals': 'Renewals in 30 d',
  'admin.rpt.subs.card.renewals_sub': 'Before {date}',
  'admin.rpt.subs.card.overdue': 'Invoices due',
  'admin.rpt.subs.card.overdue_sub': 'of {n} invoices',
  'admin.rpt.subs.record_body': 'Billing history for this subscription. Prices come from the published plan ladder in Pricing control.',
  'admin.rpt.bookings.title': 'Booking ledger',
  'admin.rpt.bookings.search': 'Search code, merchant…',
  'admin.rpt.bookings.card.count': 'Bookings',
  'admin.rpt.bookings.card.value': 'Booked value',
  'admin.rpt.bookings.card.advance': 'Advance paid',
  'admin.rpt.bookings.card.advance_sub': 'Paid directly to merchants',
  'admin.rpt.bookings.card.avg': 'Avg booking',
  'admin.rpt.bookings.record_body': 'The booking record. Direct payments go to the merchant — the platform does not process, hold or guarantee them.',
  'admin.rpt.trx.title': 'Transaction ledger',
  'admin.rpt.trx.search': 'Search TRX id or merchant…',
  'admin.rpt.trx.card.total': 'Total recorded',
  'admin.rpt.trx.card.subs': 'Subscriptions',
  'admin.rpt.trx.card.advance': 'Booking advances',
  'admin.rpt.trx.card.share': '{n}% of the period',
  'admin.rpt.trx.card.pending': 'Awaiting check',
  'admin.rpt.trx.card.pending_sub': 'Submitted, not yet verified',
  'admin.rpt.trx.record_body': 'The money ledger entry as submitted — a subscription payment or a booking payment proof.',
  'admin.rpt.msg.title': 'Messaging ledger',
  'admin.rpt.msg.search': 'Search purpose, channel or reference…',
  'admin.rpt.msg.card.sent': 'Messages',
  'admin.rpt.msg.card.sent_sub': 'Across {n} channels',
  'admin.rpt.msg.card.rate': 'Delivery rate',
  'admin.rpt.msg.card.rate_sub': 'Sent of sent + failed',
  'admin.rpt.msg.card.failed': 'Failed',
  'admin.rpt.msg.card.failed_sub': 'Failed or dead-lettered',
  'admin.rpt.msg.card.queued': 'Queued',
  'admin.rpt.msg.card.queued_sub': 'Waiting to send',
  'admin.rpt.msg.record_body': 'One outbound message from the notification outbox. The recipient is not part of the report.',
  'admin.rpt.outbox.queued': 'Queued',
  'admin.rpt.outbox.sent': 'Sent',
  'admin.rpt.outbox.failed': 'Failed',
  'admin.rpt.outbox.dead_letter': 'Dead letter',
  'admin.rpt.offers.title': 'Offer ledger',
  'admin.rpt.offers.search': 'Search offer or merchant…',
  'admin.rpt.offers.pct_off': '{n}% off',
  'admin.rpt.offers.amount_off': '{amount} off',
  'admin.rpt.offers.kind.percent': 'Percent',
  'admin.rpt.offers.kind.fixed': 'Fixed amount',
  'admin.rpt.offers.card.live': 'Live offers',
  'admin.rpt.offers.card.live_sub': 'of {n} total',
  'admin.rpt.offers.card.redeemed': 'Redeemed',
  'admin.rpt.offers.card.redeemed_sub': 'Bookings on an offer',
  'admin.rpt.offers.card.review': 'In review',
  'admin.rpt.offers.card.review_sub': 'Waiting for moderation',
  'admin.rpt.offers.card.new': 'New this month',
  'admin.rpt.offers.card.new_sub': 'Offers created',
  'admin.rpt.offers.record_body': 'The offer record with its moderation state.',
  'admin.rpt.customers.title': 'Customer ledger',
  'admin.rpt.customers.search': 'Search name…',
  'admin.rpt.customers.card.total': 'Total customers',
  'admin.rpt.customers.card.total_sub': 'Registered accounts',
  'admin.rpt.customers.card.booked': 'Have booked',
  'admin.rpt.customers.card.booked_sub': 'At least one booking',
  'admin.rpt.customers.card.new': 'New this month',
  'admin.rpt.customers.card.blocked': 'Blocked',
  'admin.rpt.customers.card.blocked_sub': 'In this ledger',
  'admin.rpt.customers.record_body': 'Personal data — visible to Super admin and Operations only.',
  'admin.rpt.audit.title': 'Audit ledger',
  'admin.rpt.audit.search': 'Search action, target, person…',
  'admin.rpt.audit.system': 'System',
  'admin.rpt.audit.card.entries': 'Log entries',
  'admin.rpt.audit.card.entries_sub': 'Immutable — no edits, no deletes',
  'admin.rpt.audit.card.pricing': 'Pricing changes',
  'admin.rpt.audit.card.pricing_sub': 'Pricing and plan actions',
  'admin.rpt.audit.card.blocks': 'Suspensions / blocks',
  'admin.rpt.audit.card.blocks_sub': 'Merchant + customer',
  'admin.rpt.audit.card.actors': 'Distinct actors',
  'admin.rpt.audit.card.actors_sub': 'Including the system',
  'admin.rpt.audit.record_body': 'Immutable audit entry {id}.',
  'admin.rpt.role.customer': 'Customer',
  'admin.rpt.role.merchant': 'Merchant',
  'admin.rpt.role.staff': 'Staff',
  'admin.rpt.role.admin': 'Admin',
  'admin.rpt.role.system': 'System',
  'admin.rpt.pay_method.direct': 'Direct',
  'admin.rpt.pay_method.venue': 'At venue',
  'admin.rpt.pay_method.gateway': 'Gateway',
  'admin.rpt.source.online': 'Online',
  'admin.rpt.source.walk_in': 'Walk-in',
  'admin.rpt.source.admin': 'Admin',
  'admin.rpt.source.phone': 'Phone',
  // ---- end DC104 ----
  // ---- DC105 booking templates ----
  'admin.tpl.title': 'Booking templates',
  'admin.tpl.sub': 'Each type is configuration — fields, labels, validation and scheduling. No code deployment.',
  'admin.tpl.stat.live': 'Live types',
  'admin.tpl.stat.live_value': '{live} / {total}',
  'admin.tpl.stat.live_sub': 'of the platform booking registry',
  'admin.tpl.stat.services': 'Total live services',
  'admin.tpl.stat.custom': 'Custom types',
  'admin.tpl.stat.drafts': 'Draft changes',
  'admin.tpl.compose': '＋ Compose new type',
  'admin.tpl.view_only': 'View only',
  'admin.tpl.gate': 'Only Super Admin and Operations can change booking templates',
  'admin.tpl.card.toggle_off': 'Disable — pauses new bookings',
  'admin.tpl.card.toggle_on': 'Enable platform-wide',
  'admin.tpl.card.custom': 'Custom',
  'admin.tpl.card.archived': 'Archived',
  'admin.tpl.card.paused': 'Booking paused',
  'admin.tpl.card.drafts': '{n, plural, one{# draft change} other{# draft changes}}',
  'admin.tpl.card.fields': '{n, plural, one{# field} other{# fields}}',
  'admin.tpl.card.live': '{n} live',
  'admin.tpl.card.edit': 'Edit fields',
  'admin.tpl.card.dup': 'Duplicate',
  'admin.tpl.card.preview': 'Preview form',
  'admin.tpl.card.archive': 'Archive',
  'admin.tpl.card.restore': 'Restore',
  'admin.tpl.empty.title': 'No active booking types',
  'admin.tpl.empty.body': 'Every type is archived. Restore one, or compose a new type from a scheduling model — the public marketplace needs at least one live type to accept bookings.',
  'admin.tpl.disable.title': 'Disable {name}?',
  'admin.tpl.disable.body': '{n} live services will stop accepting NEW bookings (existing bookings unaffected). Services stay visible with “Booking paused”.',
  'admin.tpl.disable.go': 'Disable — pause new bookings',
  'admin.tpl.disable.done': '{name} disabled — {n} services now show “Booking paused”',
  'admin.tpl.enable.done': '{name} enabled — its services accept new bookings again',
  'admin.tpl.archived_toggle': 'Archived types cannot be enabled — restore it first',
  'admin.tpl.archive.title': 'Archive {name}?',
  'admin.tpl.archive.body': 'Custom types are never hard-deleted. Archiving hides the type from new services; its history, bookings and audit trail stay intact and it can be restored.',
  'admin.tpl.archive.go': 'Archive type',
  'admin.tpl.archive.done': '{name} archived — audit-logged',
  'admin.tpl.restore.done': '{name} restored — live again',
  'admin.tpl.fact.model': 'Scheduling model',
  'admin.tpl.fact.services': 'Live services',
  'admin.tpl.fact.existing': 'Existing bookings',
  'admin.tpl.fact.existing_kept': 'Unaffected — honoured as booked',
  'admin.tpl.fact.unaffected': 'Unaffected',
  'admin.tpl.fact.reason': 'Reason',
  'admin.tpl.fact.reason_v': 'Required · audit-logged',
  'admin.tpl.fact.composed': 'Composed from',
  'admin.tpl.fact.blank': 'Blank',
  'admin.tpl.fact.reversible': 'Reversible',
  'admin.tpl.fact.reversible_v': 'Yes — restore any time',
  'admin.tpl.dlg.cancel': 'Cancel',
  'admin.tpl.dlg.close': 'Close',
  'admin.tpl.dlg.reason': 'Reason — written to the audit log',
  'admin.tpl.dlg.reason_ph': 'Add a short reason…',
  'admin.tpl.dlg.reason_required': 'A reason is required — it is recorded in the audit log',
  'admin.tpl.dlg.setting': 'Setting',
  'admin.tpl.dlg.current': 'Current',
  'admin.tpl.dlg.proposed': 'Proposed',
  'admin.tpl.ed.sub': 'Field editor — labels, validation, order and lifecycle for this booking type. Changes are a draft until you publish; publishing updates the live public booking form instantly.',
  'admin.tpl.ed.sub_new': 'Field editor for a new type. It is a draft until you publish it as version 1; customers can book it only once a category maps to it.',
  'admin.tpl.ed.stat.drafts': 'Draft changes',
  'admin.tpl.ed.stat.unpublished': 'unpublished',
  'admin.tpl.ed.stat.in_sync': 'in sync with live',
  'admin.tpl.ed.stat.fields': 'Customer fields',
  'admin.tpl.ed.stat.services': 'Live services',
  'admin.tpl.ed.stat.model': 'Model',
  'admin.tpl.ed.back': '← All booking templates',
  'admin.tpl.ed.not_found': 'This booking type is not in the registry.',
  'admin.tpl.common.title': 'Common fields — every template',
  'admin.tpl.common.note': 'Common fields apply to every template and are edited platform-wide, not here.',
  'admin.tpl.common.name': 'Full name',
  'admin.tpl.common.phone': 'Mobile number',
  'admin.tpl.common.email': 'Email',
  'admin.tpl.common.notes': 'Notes for the business',
  'admin.tpl.common.pay': 'Payment method',
  'admin.tpl.common.consent': 'Terms & privacy consent',
  'admin.tpl.req': 'Required',
  'admin.tpl.opt': 'Optional',
  'admin.tpl.fields.title': 'Customer-facing fields',
  'admin.tpl.fields.note': 'Drag to reorder — this is the order the customer sees. Both labels are required.',
  'admin.tpl.fields.add': '＋ Add field',
  'admin.tpl.fields.none': 'No customer fields yet — add one from the palette.',
  'admin.tpl.field.handle': 'Drag to reorder',
  'admin.tpl.field.label_en_ph': 'Customer label — English',
  'admin.tpl.field.label_bn_ph': 'Customer label — Bangla',
  'admin.tpl.field.label_en': '{label} — English label',
  'admin.tpl.field.label_bn': '{label} — Bangla label',
  'admin.tpl.field.rule': '{label} — validation',
  'admin.tpl.field.required': '{label} — required',
  'admin.tpl.field.menu': '{label} — field actions',
  'admin.tpl.field.who_title': 'Who fills this field in',
  'admin.tpl.field.who.customer': 'Customer',
  'admin.tpl.field.who.merchant': 'Merchant',
  'admin.tpl.field.who.system': 'System',
  'admin.tpl.field.rule_none': 'No validation',
  'admin.tpl.field.options': 'Edit options ({n})',
  'admin.tpl.field.dirty': 'Draft — unpublished',
  'admin.tpl.field.error.label': 'Both labels are required',
  'admin.tpl.field.error.server': 'Refused by the field schema: {code}',
  'admin.tpl.menu.options': 'Edit options',
  'admin.tpl.menu.up': 'Move up',
  'admin.tpl.menu.down': 'Move down',
  'admin.tpl.menu.remove': 'Remove field',
  'admin.tpl.menu.cancel': 'Cancel',
  'admin.tpl.remove.title': 'Remove “{label}”?',
  'admin.tpl.remove.body': 'The field leaves the draft only. Nothing changes on the live booking form until you publish, and past bookings keep the answers customers already gave.',
  'admin.tpl.remove.go': 'Remove — add to draft',
  'admin.tpl.remove.done': 'Field removed from the draft',
  'admin.tpl.opts.title': 'Options — {label}',
  'admin.tpl.opts.note': 'Both languages are shown to customers. Each option keeps a stable value — the answer stored on the booking.',
  'admin.tpl.opts.done': '✓ Done',
  'admin.tpl.opts.en_ph': 'Option — English',
  'admin.tpl.opts.bn_ph': 'Option — Bangla',
  'admin.tpl.opts.add': '＋ Add option',
  'admin.tpl.opts.remove': 'Remove option {n}',
  'admin.tpl.opts.source': 'Choices come from the merchant’s {source} — no fixed list.',
  'admin.tpl.opts.source.catalog': 'services',
  'admin.tpl.opts.source.staff': 'staff',
  'admin.tpl.opts.source.units': 'bookable units',
  'admin.tpl.model.title': 'Scheduling model',
  'admin.tpl.model.locked': 'Locked — {n} live services depend on this model. Editable on drafts and custom types with 0 live services.',
  'admin.tpl.model.desc.date_range': 'Customer picks a start and an end; inventory is counted per night or per day.',
  'admin.tpl.model.desc.time_slot': 'A day is cut into fixed slots; one booking consumes one slot per resource.',
  'admin.tpl.model.desc.serial_session': 'No clock time — a session issues numbered serials in order of booking.',
  'admin.tpl.model.desc.seat_inventory': 'A dated session sells priced tiers, optionally down to individual seats.',
  'admin.tpl.model.desc.capacity_session': 'A session has N places; bookings decrement the remaining capacity.',
  'admin.tpl.model.desc.block_booking': 'A whole block of time is reserved exclusively — half day, full day or an hourly range.',
  'admin.tpl.life.title': 'Lifecycle defaults',
  'admin.tpl.life.note': 'What a new service on this type inherits. A merchant can tighten these, never loosen them.',
  'admin.tpl.life.confirm': 'Confirmation',
  'admin.tpl.life.cancel': 'Cancellation policy default',
  'admin.tpl.confirm.instant': 'Instant',
  'admin.tpl.confirm.approval': 'Owner approval',
  'admin.tpl.cancel.flexible': 'Flexible',
  'admin.tpl.cancel.moderate': 'Moderate',
  'admin.tpl.cancel.strict': 'Strict',
  'admin.tpl.reminder': '{h}-hour reminder',
  'admin.tpl.pv.title': 'Live preview',
  'admin.tpl.pv.note': 'The customer booking form, rendered from this field config in real time.',
  'admin.tpl.pv.err_show': 'Show sample validation errors',
  'admin.tpl.pv.err_on': '✓ Showing validation errors',
  'admin.tpl.pv.foot': 'This is the dynamic form renderer — the marketplace, merchant dashboard and admin all read the same config.',
  'admin.tpl.pv.lang_en': 'EN',
  'admin.tpl.pv.lang_bn': 'বাং',
  'admin.tpl.pv.langs': 'Preview language',
  'admin.tpl.pvm.title': '{name} — booking form',
  'admin.tpl.pvm.note': 'Exactly what a customer sees on the public marketplace, rendered from the current field config.',
  'admin.tpl.form.kicker': 'Book now',
  'admin.tpl.form.continue': 'Continue',
  'admin.tpl.form.disclaimer': '{brand} does not process, hold, or guarantee direct payments.',
  'admin.tpl.form.common': 'Your details — common to every template',
  'admin.tpl.form.required': 'Required',
  'admin.tpl.form.auto': 'auto',
  'admin.tpl.form.merchant': 'merchant fills',
  'admin.tpl.form.from_list': 'from service',
  'admin.tpl.form.date_ph': 'dd / mm / yyyy',
  'admin.tpl.form.optional': 'optional',
  'admin.tpl.kind.text': 'TEXT',
  'admin.tpl.kind.number': 'NUMBER',
  'admin.tpl.kind.date': 'DATE',
  'admin.tpl.kind.time': 'TIME',
  'admin.tpl.kind.select': 'SELECT',
  'admin.tpl.kind.multi_select': 'MULTI-SELECT',
  'admin.tpl.kind.counter': 'COUNTER',
  'admin.tpl.kind.textarea': 'TEXTAREA',
  'admin.tpl.kind.toggle': 'TOGGLE',
  'admin.tpl.kind.file': 'FILE',
  'admin.tpl.kind.seat_picker': 'SEAT-PICKER',
  'admin.tpl.kind.staff_picker': 'STAFF-PICKER',
  'admin.tpl.kind_desc.text': 'Short free text',
  'admin.tpl.kind_desc.number': 'Whole number',
  'admin.tpl.kind_desc.date': 'Calendar date',
  'admin.tpl.kind_desc.time': 'Slot from the schedule',
  'admin.tpl.kind_desc.select': 'One choice from a list',
  'admin.tpl.kind_desc.multi_select': 'Many choices from a list',
  'admin.tpl.kind_desc.counter': 'Stepper with min / max',
  'admin.tpl.kind_desc.textarea': 'Long text',
  'admin.tpl.kind_desc.toggle': 'Yes / no switch',
  'admin.tpl.kind_desc.file': 'Customer upload',
  'admin.tpl.kind_desc.seat_picker': 'Seat map selection',
  'admin.tpl.kind_desc.staff_picker': 'Staff member of the service',
  'admin.tpl.kind_hint.text': 'e.g. 2–60 characters',
  'admin.tpl.kind_hint.number': 'e.g. 0–120',
  'admin.tpl.kind_hint.date': 'e.g. Today or later',
  'admin.tpl.kind_hint.time': 'e.g. Slot must be open',
  'admin.tpl.kind_hint.select': 'e.g. One choice required',
  'admin.tpl.kind_hint.multi_select': 'e.g. At least one',
  'admin.tpl.kind_hint.counter': 'e.g. 1–10',
  'admin.tpl.kind_hint.textarea': 'e.g. Max 500 characters',
  'admin.tpl.kind_hint.toggle': 'No validation',
  'admin.tpl.kind_hint.file': 'e.g. JPG / PDF · max 5 MB',
  'admin.tpl.kind_hint.seat_picker': 'e.g. Needs an uploaded seat map',
  'admin.tpl.kind_hint.staff_picker': 'e.g. Needs per-staff scheduling',
  'admin.tpl.pal.title': 'Add a field',
  'admin.tpl.pal.note': 'Pick a primitive the dynamic form renderer supports. The field lands at the bottom of the draft — set its labels, validation and required flag there.',
  'admin.tpl.pal.new_en': 'New {kind} field',
  'admin.tpl.pal.new_bn': 'নতুন ফিল্ড',
  'admin.tpl.pal.option_en': 'Option one',
  'admin.tpl.pal.option_bn': 'অপশন এক',
  'admin.tpl.pal.added': '{kind} field added to the draft',
  'admin.tpl.pal.seat': 'A seat picker needs the Seat / tier inventory model',
  'admin.tpl.pal.staff': 'A staff picker needs per-staff scheduling on this template',
  'admin.tpl.new.title': 'Compose a new booking type',
  'admin.tpl.new.sub': 'A new type is configuration: a name, one scheduling model and a field set. It is a draft until you publish it, and customers can book it only once a category maps to it.',
  'admin.tpl.new.step1': 'Name & start point',
  'admin.tpl.new.step2': 'Scheduling model',
  'admin.tpl.new.step3': 'Fields & preview',
  'admin.tpl.new.name_en': 'Name — English',
  'admin.tpl.new.name_bn': 'Name — বাংলা',
  'admin.tpl.new.name_en_ph': 'e.g. Photography session',
  'admin.tpl.new.name_bn_ph': 'যেমন ফটোগ্রাফি সেশন',
  'admin.tpl.new.from': 'Start from',
  'admin.tpl.new.blank': 'Blank',
  'admin.tpl.new.dup': 'Duplicate existing',
  'admin.tpl.new.dup_label': 'Template to duplicate',
  'admin.tpl.new.dup_opt': '{name} · {model} · {n, plural, one{# field} other{# fields}}',
  'admin.tpl.new.copy': '{name} (copy)',
  'admin.tpl.new.used': 'Used by {names}',
  'admin.tpl.new.used_more': 'Used by {names} +{n}',
  'admin.tpl.new.unused': 'Not used yet',
  'admin.tpl.new.cancel': 'Cancel',
  'admin.tpl.new.back': 'Back',
  'admin.tpl.new.next': 'Next — pick a model',
  'admin.tpl.new.create': 'Create draft & build fields',
  'admin.tpl.new.names_required': 'Both the English and Bangla name are required',
  'admin.tpl.new.created': '“{name}” composed as a draft — step 3: build its fields',
  'admin.tpl.bar.count': '{n, plural, one{# unpublished change} other{# unpublished changes}}',
  'admin.tpl.bar.sub': '{name} · draft',
  'admin.tpl.bar.new': 'New type — not yet published',
  'admin.tpl.bar.discard': 'Discard',
  'admin.tpl.bar.publish': 'Publish',
  'admin.tpl.pub.title': '{n, plural, one{Publish # change?} other{Publish # changes?}}',
  'admin.tpl.pub.title_new': 'Publish “{name}” as version 1?',
  'admin.tpl.pub.body': 'Published changes update the live public booking form instantly for all {n} services on this type. Every change is written to the audit log against {name} ({role}). Bookings already made keep the version they were booked on.',
  'admin.tpl.pub.body_new': 'The new type goes live as version 1 under the id {id}. No service uses it until a category maps to it. Written to the audit log against {name} ({role}).',
  'admin.tpl.pub.go': 'Publish — go live',
  'admin.tpl.pub.done': '{n, plural, one{# change published} other{# changes published}} — live booking form updated',
  'admin.tpl.pub.done_new': '“{name}” published as version 1',
  'admin.tpl.pub.stale': 'Someone published this template since you loaded it — reload and re-apply your changes.',
  'admin.tpl.pub.labels': 'Every field needs both an English and a Bangla label.',
  'admin.tpl.pub.none': 'Nothing to publish — the draft matches the live template.',
  'admin.tpl.discarded': 'Draft changes discarded',
  'admin.tpl.save.saving': 'Saving draft…',
  'admin.tpl.save.error': 'Draft not saved — {reason}',
  'admin.tpl.save.stale': 'The live template changed since this draft was made — discard it to start from the new version.',
  'admin.tpl.diff.new_field': 'New field · {f}',
  'admin.tpl.diff.label_en': '{f} · EN label',
  'admin.tpl.diff.label_bn': '{f} · BN label',
  'admin.tpl.diff.required': '{f} · flag',
  'admin.tpl.diff.rule': '{f} · validation',
  'admin.tpl.diff.who': '{f} · filled by',
  'admin.tpl.diff.options': '{f} · options',
  'admin.tpl.diff.position': '{f} · position',
  'admin.tpl.diff.removed_field': 'Removed field · {f}',
  'admin.tpl.diff.name_en': 'Name — English',
  'admin.tpl.diff.name_bn': 'Name — Bangla',
  'admin.tpl.diff.model': 'Scheduling model',
  'admin.tpl.diff.confirm': 'Confirmation',
  'admin.tpl.diff.cancellation': 'Cancellation policy',
  'admin.tpl.diff.reminder': '{f}-hour reminder',
  'admin.tpl.diff.none': 'none',
  'admin.tpl.diff.kind_req': '{kind} · {req}',
  'admin.tpl.diff.req': 'required',
  'admin.tpl.diff.opt': 'optional',
  'admin.tpl.diff.n_options': '{n, plural, one{# option} other{# options}}',
  'admin.tpl.diff.pos': '#{n}',
  'admin.tpl.diff.removed': 'Removed',
  'admin.tpl.diff.on': 'On',
  'admin.tpl.diff.off': 'Off',
  'admin.tpl.diff.unset': 'Not set',
  // ---- end DC105 ----
  // CMS & growth (DC107).
  'admin.cm.sub': 'Everything the public sees that isn’t a service: banners, featured rails, stats, codes, announcements, pages.',
  'admin.cm.tab.home': 'Homepage',
  'admin.cm.tab.codes': 'Promo codes',
  'admin.cm.tab.ann': 'Announcements & referrals',
  'admin.cm.tab.pages': 'Pages & slots',
  'admin.cm.tab.blog': 'Blog',
  'admin.cm.bl.title': 'Blog posts',
  'admin.cm.bl.empty': 'No posts yet.',
  'admin.cm.bl.has_bn': 'বাংলা ✓',
  'admin.cm.bl.edit_named': 'Edit {title}',
  'admin.cm.bl.delete': 'Delete',
  'admin.cm.bl.delete_title': 'Delete this post?',
  'admin.cm.bl.delete_body': '“{title}” is removed from the website at once. This cannot be undone.',
  'admin.cm.bl.toast_saved': 'Draft saved — audit-logged',
  'admin.cm.bl.toast_published': 'Post published — audit-logged',
  'admin.cm.bl.toast_deleted': 'Post deleted — audit-logged',
  'admin.cm.stat.banners_live': 'Banners live',
  'admin.cm.stat.active_codes': 'Active codes',
  'admin.cm.stat.scheduled': 'Scheduled items',
  'admin.cm.stat.leads': 'Leads',
  'admin.cm.stat.pages_month': 'Pages updated this month',
  'admin.cm.status.live': 'Live',
  'admin.cm.status.active': 'Active',
  'admin.cm.status.published': 'Published',
  'admin.cm.status.scheduled': 'Scheduled',
  'admin.cm.status.ended': 'Ended',
  'admin.cm.status.paused': 'Paused',
  'admin.cm.status.draft': 'Draft',
  'admin.cm.status.archived': 'Archived',
  'admin.cm.act.menu': 'More actions',
  'admin.cm.act.edit': 'Edit',
  'admin.cm.act.duplicate': 'Duplicate',
  'admin.cm.act.end_now': 'End now',
  'admin.cm.act.end': 'End',
  'admin.cm.act.archive': 'Archive',
  'admin.cm.act.pause': 'Pause',
  'admin.cm.act.resume': 'Resume',
  'admin.cm.reason': 'Reason — written to the audit log',
  'admin.cm.reason_ph': 'Add a short reason…',
  'admin.cm.reason_required': 'A reason is required — it is recorded in the audit log',
  'admin.cm.required': 'Required',
  'admin.cm.starts': 'Starts',
  'admin.cm.ends': 'Ends',
  'admin.cm.place.home_hero': 'Homepage hero',
  'admin.cm.place.home_top': 'Homepage strip',
  'admin.cm.bn.title': 'Homepage banners',
  'admin.cm.bn.sub': 'Status derives from the schedule — Draft, Scheduled, Live, Ended. Nothing is hard-deleted.',
  'admin.cm.bn.add': '＋ Add banner',
  'admin.cm.bn.empty': 'No banners yet — add one to fill the homepage hero.',
  'admin.cm.bn.no_window': 'No schedule',
  'admin.cm.bn.queued': 'Overlaps “{headline}” on the same placement — this banner queues until it ends ({end}).',
  'admin.cm.bn.end_title': 'End “{headline}” now?',
  'admin.cm.bn.end_body': 'The banner disappears from the public homepage immediately. The change is written to the audit log.',
  'admin.cm.bn.end_action': 'End banner',
  'admin.cm.bn.toast_dup': 'Duplicated as a draft — edit it to schedule',
  'admin.cm.bn.toast_end': 'Banner ended — audit-logged',
  'admin.cm.bn.toast_archive': 'Banner archived — audit-logged',
  'admin.cm.bn.toast_saved': 'Banner saved — audit-logged',
  'admin.cm.bn.toast_created': 'Banner created — audit-logged',
  'admin.cm.bn.new_title': 'New banner',
  'admin.cm.bn.edit_title': 'Edit banner',
  'admin.cm.bn.form_sub': 'Both headline languages are required — the public homepage renders the visitor’s session language. Status derives from the schedule.',
  'admin.cm.bn.headline_en': 'Headline (EN)',
  'admin.cm.bn.headline_bn': 'Headline (BN)',
  'admin.cm.bn.ph_headline_en': 'Headline (EN)',
  'admin.cm.bn.ph_headline_bn': 'শিরোনাম (BN)',
  'admin.cm.bn.placement': 'Placement',
  'admin.cm.bn.form_overlap': 'Overlaps “{headline}” ({window}) on {placement} — the later banner queues until the earlier one ends.',
  'admin.cm.bn.save': 'Save banner',
  'admin.cm.bn.err_required': 'Both headline languages are required',
  'admin.cm.bn.err_dates': 'Pick a start and an end date',
  'admin.cm.bn.err_order': 'The end date can’t be before the start date',
  'admin.cm.ft.title': 'Featured businesses',
  'admin.cm.ft.sub': 'Featured is editorial and free — paid placement is sold separately; Trending and New are always data-driven.',
  'admin.cm.ft.counter': '{n} featured',
  'admin.cm.ft.counter_cap': '{n}/{cap} featured',
  'admin.cm.ft.ph': 'Search live businesses to feature…',
  'admin.cm.ft.editorial': 'Editorial',
  'admin.cm.ft.reviews': '{n} reviews',
  'admin.cm.ft.remove': 'Remove from featured',
  'admin.cm.ft.remove_named': 'Remove {name} from featured',
  'admin.cm.ft.empty': 'Nothing featured yet — search above to add a business.',
  'admin.cm.ft.toast_cap': 'Featured is capped at {cap} — remove one first',
  'admin.cm.ft.toast_added': '{name} featured — audit-logged',
  'admin.cm.ft.toast_removed': '{name} removed from featured',
  'admin.cm.st.title': 'Platform stats',
  'admin.cm.st.sub': 'Manual values show on the public homepage exactly as entered — keep them honest.',
  'admin.cm.st.counter': '{manual} manual · {live} live',
  'admin.cm.st.bookings_made': 'Bookings made',
  'admin.cm.st.bookings_today': 'Ticker — bookings today',
  'admin.cm.st.merchant_reviews': 'Verified reviews',
  'admin.cm.st.facilitated_value_minor': 'Booking value facilitated',
  'admin.cm.st.auto': 'AUTO',
  'admin.cm.st.manual': 'MANUAL',
  'admin.cm.st.manual_chip': 'Manual override',
  'admin.cm.st.computed': 'Computed from live data — read-only',
  'admin.cm.st.super_only': 'Only a Super admin can override platform stats.',
  'admin.cm.st.ask_title': 'Manual override — {stat}',
  'admin.cm.st.ask_body': 'The manual value replaces the live computed figure ({live}) on the public homepage. A reason is required and audit-logged.',
  'admin.cm.st.value': 'Manual value',
  'admin.cm.st.value_money': 'Manual value ({symbol})',
  'admin.cm.st.apply': 'Apply override',
  'admin.cm.st.err_value': 'Enter the value to show — whole numbers only',
  'admin.cm.st.toast_manual': '{stat} overridden — audit-logged',
  'admin.cm.st.toast_auto': '{stat} back to live data — audit-logged',
  'admin.cm.cd.title': 'Platform promo codes',
  'admin.cm.cd.sub': 'Platform-level codes customers apply at checkout — merchant offers live in Offers. Ended codes are kept for reporting; nothing is hard-deleted.',
  'admin.cm.cd.add': '＋ Add code',
  'admin.cm.cd.empty': 'No promo codes yet.',
  'admin.cm.cd.off_pct': '{v}% off',
  'admin.cm.cd.off_flat': '{v} off',
  'admin.cm.cd.all_categories': 'All categories',
  'admin.cm.cd.scope_platform': 'Platform-wide',
  'admin.cm.cd.scope_participating': 'Participating businesses',
  'admin.cm.cd.scope_platform_note': 'Applies at every business',
  'admin.cm.cd.scope_participating_note': 'Applies only where the business opted in',
  'admin.cm.cd.per_user_line': 'per-user limit {n}',
  'admin.cm.cd.usage': '{used} of {cap} redemptions',
  'admin.cm.cd.usage_nocap': '{used} redemptions · no cap',
  'admin.cm.cd.until': 'Until {date}',
  'admin.cm.cd.from': 'From {date}',
  'admin.cm.cd.no_window': 'No end date',
  'admin.cm.cd.end_title': 'End {code}?',
  'admin.cm.cd.end_body': 'The code stops working immediately. Ended codes are kept for reporting — no hard delete. The change is written to the audit log.',
  'admin.cm.cd.end_action': 'End code',
  'admin.cm.cd.toast_paused': '{code} paused — audit-logged',
  'admin.cm.cd.toast_resumed': '{code} resumed — audit-logged',
  'admin.cm.cd.toast_dup': 'Duplicated as a paused copy — {code}',
  'admin.cm.cd.toast_ended': '{code} ended — audit-logged',
  'admin.cm.cd.toast_created': '{code} created — live now, audit-logged',
  'admin.cm.cd.toast_saved': '{code} saved — audit-logged',
  'admin.cm.cd.new_title': 'New promo code',
  'admin.cm.cd.edit_title': 'Edit {code}',
  'admin.cm.cd.form_sub': 'One form for every platform code. Codes apply at the customer checkout; a merchant’s own discounts are Offers.',
  'admin.cm.cd.code': 'Code',
  'admin.cm.cd.ph_code': 'FIRSTBOOK',
  'admin.cm.cd.code_note': 'Uppercase, unique',
  'admin.cm.cd.code_fixed': 'The code itself can’t change after creation',
  'admin.cm.cd.scope': 'Participation',
  'admin.cm.cd.kind': 'Benefit type',
  'admin.cm.cd.kind_pct': '% off',
  'admin.cm.cd.kind_flat': 'Flat amount',
  'admin.cm.cd.value_pct': 'Value (%)',
  'admin.cm.cd.value_flat': 'Value ({symbol})',
  'admin.cm.cd.category': 'Scope',
  'admin.cm.cd.cap': 'Redemption cap',
  'admin.cm.cd.per_user': 'Per-user limit',
  'admin.cm.cd.caps_at': 'caps at {n}',
  'admin.cm.cd.save': 'Save code',
  'admin.cm.cd.err_code': 'Enter a code',
  'admin.cm.cd.err_unique': 'That code already exists — codes must be unique',
  'admin.cm.cd.err_value': 'Enter a benefit value (1–100 for a percentage)',
  'admin.cm.cd.err_cap': 'Enter a redemption cap',
  'admin.cm.an.title': 'Segment announcements',
  'admin.cm.an.sub': 'Written in both languages — recipients get their session language.',
  'admin.cm.an.deferred': 'Announcements are not available yet — sending to segments is planned for a later release.',
  'admin.cm.rf.title': 'Referral campaigns',
  'admin.cm.rf.deferred': 'Referral campaigns are not available yet — they are planned for a later release.',
  'admin.cm.pg.title': 'Static pages',
  'admin.cm.pg.sub': 'Bilingual content. Every publish is audit-logged and keeps the previous copy as a version you can restore.',
  'admin.cm.pg.updated': 'Last updated {date}',
  'admin.cm.pg.edit_named': 'Edit {title}',
  'admin.cm.pg.empty': 'No static pages yet.',
  'admin.cm.pg.back': '← All pages',
  'admin.cm.pg.lang_label': 'Language',
  'admin.cm.pg.tab_en': 'English',
  'admin.cm.pg.tab_bn': 'বাংলা',
  'admin.cm.pg.body_en': 'Page text (English)',
  'admin.cm.pg.body_bn': 'Page text (Bangla)',
  'admin.cm.pg.hint_dirty': 'Unpublished changes — publish to go live. Every publish is audit-logged.',
  'admin.cm.pg.hint_clean': 'No unpublished changes.',
  'admin.cm.pg.discard': 'Discard',
  'admin.cm.pg.publish': 'Publish',
  'admin.cm.pg.err_en': 'The English text is required',
  'admin.cm.pg.toast_published': '{title} published — audit-logged',
  'admin.cm.pg.toast_discarded': 'Changes discarded',
  'admin.cm.pg.versions': 'Version history',
  'admin.cm.pg.no_versions': 'No earlier versions yet — each publish saves the one it replaces.',
  'admin.cm.pg.v_saved': 'Saved copy',
  'admin.cm.pg.v_superseded': 'Replaced by a later publish',
  'admin.cm.pg.restore': 'Restore',
  'admin.cm.pg.restore_title': 'Restore this version?',
  'admin.cm.pg.restore_body': 'The copy saved {when} goes live now. The current page is saved as a version first, so this can be undone.',
  'admin.cm.pg.toast_restored': 'Version restored — the previous copy was saved first',
  'admin.cm.sl.title': 'Ad slot registry',
  'admin.cm.sl.banner': 'Slots and prices are managed in Pricing control →',
  'admin.cm.sl.pricing': 'Pricing control',
  'admin.cm.sl.available': 'Available',
  'admin.cm.sl.filled': 'Filled',
  'admin.cm.sl.filled_by': 'Filled by {name}',
  'admin.cm.sl.disabled': 'Disabled',
  'admin.cm.ld.title': 'Leads',
  'admin.cm.ld.kind_app_notify': 'App notify-me',
  'admin.cm.ld.kind_deal_alert': 'Deal alert',
  'admin.cm.ld.kind_newsletter': 'Newsletter',
  'admin.cm.ld.empty': 'No leads captured yet.',
  'admin.cm.ld.foot': 'Every lead carries the consent that permits contacting it.',
  'admin.nf.sub': 'Everything the platform told you, everything you have chosen to hear, and every message the platform sends merchants and customers on your behalf.',
  'admin.nf.stat.unread': 'Unread',
  'admin.nf.stat.oldest': 'oldest · {when}',
  'admin.nf.stat.caught_up': 'All caught up',
  'admin.nf.stat.inbox': 'Inbox',
  'admin.nf.stat.off': 'Categories off',
  'admin.nf.stat.off_sub': 'no bell for these',
  'admin.nf.stat.on_sub': 'everything reaches you',
  'admin.nf.tab.inbox': 'Inbox',
  'admin.nf.tab.alerts': 'My alerts',
  'admin.nf.tab.messages': 'Platform messages',
  'admin.nf.tab.off': '{n} off',
  'admin.nf.filter.category': 'Category',
  'admin.nf.filter.all_categories': 'All categories',
  'admin.nf.filter.state': 'State',
  'admin.nf.filter.all': 'All',
  'admin.nf.filter.unread': 'Unread',
  'admin.nf.filter.read': 'Read',
  'admin.nf.filter.when': 'When',
  'admin.nf.filter.any_time': 'Any time',
  'admin.nf.filter.today': 'Today',
  'admin.nf.filter.d7': 'Last 7 days',
  'admin.nf.filter.d30': 'Last 30 days',
  'admin.nf.selected': '{n} selected',
  'admin.nf.mark_read': 'Mark read',
  'admin.nf.dismiss': 'Dismiss',
  'admin.nf.clear': 'Clear',
  'admin.nf.mark_all': 'Mark all read',
  'admin.nf.empty': 'Nothing new',
  'admin.nf.empty_unread': 'All caught up ✓',
  'admin.nf.footnote': 'Dismissing a row clears it from this inbox only — the underlying booking, dispute or claim stays exactly where it is in its queue.',
  'admin.nf.select': 'Select',
  'admin.nf.dismiss_title': 'Dismiss — the work itself stays in its queue',
  'admin.nf.toast.marked': 'Marked read',
  'admin.nf.toast.all_marked': 'All notifications marked read',
  'admin.nf.toast.dismissed': 'Notification dismissed',
  'admin.nf.toast.dismissed_n': 'Notifications dismissed',
  'admin.nf.toast.failed': 'Could not save — try again',
  'admin.nf.action.verify': 'Verify',
  'admin.nf.action.track': 'Track',
  'admin.nf.action.fix': 'Fix',
  'admin.nf.action.review': 'Review',
  'admin.nf.action.moderate': 'Moderate',
  'admin.nf.action.pay_out': 'Pay out',
  'admin.nf.action.open': 'Open',
  'admin.nf.action.chase': 'Chase',
  'admin.nf.action.triage': 'Triage',
  'admin.nf.action.check': 'Check',
  'admin.nf.action.handle': 'Handle',
  'admin.nf.al.title': 'What reaches me',
  'admin.nf.al.self': '{name} · {role}',
  'admin.nf.al.sub': 'Personal delivery preferences — saved the moment you tap, never drafted, never published. Rows your role cannot act on are not listed. SLA and system-health alerts always stay on in-app.',
  'admin.nf.al.stored': 'Email and SMS alerts, thresholds and quiet hours are saved now; delivery by them starts in a later release — today every alert reaches you in-app.',
  'admin.nf.group.moderation': 'Moderation',
  'admin.nf.group.money': 'Money',
  'admin.nf.group.merchant': 'Merchant',
  'admin.nf.group.platform': 'Platform',
  'admin.nf.group.customer': 'Customer',
  'admin.nf.al.locked': 'always in-app',
  'admin.nf.lock.sla': 'Always reaches you in-app — an unanswered queue is how the marketplace breaks.',
  'admin.nf.lock.health': 'Always reaches you in-app — gateway, SMS and push failures need an operator, not an inbox.',
  'admin.nf.lock.generic': 'Always reaches you in-app.',
  'admin.nf.thr.proofs': 'Notify me when payment proofs waiting exceeds',
  'admin.nf.thr.proofs_unit': 'proofs',
  'admin.nf.thr.dispute_sla': 'Notify me when a dispute is under',
  'admin.nf.thr.dispute_sla_unit': 'h to SLA',
  'admin.nf.thr.label': 'Threshold for {category}',
  'admin.nf.ch.inapp': 'In-app',
  'admin.nf.ch.email': 'Email',
  'admin.nf.ch.sms': 'SMS',
  'admin.nf.cell.on': 'On — click to switch off',
  'admin.nf.cell.off': 'Off — click to switch on',
  'admin.nf.on': 'on',
  'admin.nf.off': 'off',
  'admin.nf.toast.channel': '{channel} · {category} — {state} · saved',
  'admin.nf.toast.threshold': 'Threshold saved',
  'admin.nf.quiet.title': 'Quiet hours',
  'admin.nf.quiet.from': 'From',
  'admin.nf.quiet.to': 'To',
  'admin.nf.quiet.note': 'Email and SMS pause between these hours. Urgent SLA breaches and system-health warnings ignore quiet hours — they reach you anyway.',
  'admin.nf.toast.quiet': 'Quiet hours — {state}',
  'admin.nf.msg.title': 'What merchants and customers receive',
  'admin.nf.msg.deferred': 'Platform messages are fixed in the platform’s code for now — their wording, channels and SMS billing cannot be viewed or changed here yet.',

  // DC56 — website content pages + mobile search flow.
  'dc56.blog.kicker': 'Blog',
  'dc56.blog.title1': 'Booking,',
  'dc56.blog.title2': 'decoded.',
  'dc56.blog.sub': 'Guides and stories from {region}’s booking marketplace — hotels, turfs, restaurants, offers and the people who run them.',
  'dc56.blog.featured': 'Featured',
  'dc56.blog.read_article': 'Read article',
  'dc56.blog.min_read': '{n} min read',
  'dc56.blog.empty': 'No articles yet — check back soon.',
  'dc56.post.team_name': '{brand} Team',
  'dc56.post.team_bio': 'We write from what we run — {region}’s multi-vertical booking marketplace, from doctor serials to wedding venues.',
  'dc56.post.related': 'Related articles',
  'dc56.help.title': 'Help Center',
  'dc56.help.sub': 'Answers to the most common questions from customers and merchants.',
  'dc56.about.product_of': 'A {company_short} product',
  'dc56.about.book_label': 'What you can book',
  'dc56.about.cta_title': 'Put your business on the map.',
  'dc56.about.cta_sub': 'List free in minutes — bookings can start today.',
  'dc56.about.cta_list': 'Add your business — free',
  'dc56.about.cta_offers': 'Browse offers',
  'dc56.help.search_ph': 'Search a question… (e.g. refund, serial, bKash)',
  'dc56.help.search_label': 'Search the help center',
  'dc56.help.topics_label': 'Topics',
  'dc56.help.topic_all': 'All',
  'dc56.help.no_results': 'Nothing matches that — try another word, or ask us directly below.',
  'dc56.help.still_title': 'Still stuck?',
  'dc56.help.still_sub': 'Real humans, Bangla or English — we usually reply within a few minutes during the day.',
  'dc56.help.whatsapp': 'WhatsApp us',
  'dc56.status.title': 'Platform status',
  'dc56.status.all_ok': 'All systems operational',
  'dc56.status.operational': 'Operational',
  'dc56.contact.lets': 'Let’s',
  'dc56.contact.talk': 'talk.',
  'dc56.contact.sub': 'We reply within one business day — Bangla or English, your choice.',
  'dc56.contact.info_title': 'Contact information',
  'dc56.contact.info_sub': 'Questions about a booking, a listing or an offer? Same inbox, real humans.',
  'dc56.contact.email_label': 'Email · replies within a day',
  'dc56.contact.first_reply': 'Avg. first reply: under 4 hours',
  'dc56.contact.your_name': 'Your name',
  'dc56.contact.reach': 'Email or WhatsApp number',
  'dc56.contact.topic': 'Topic',
  'dc56.contact.message': 'Message',
  'dc56.contact.ph_name': 'Farzana Hoque',
  'dc56.contact.ph_reach': 'you@mail.com / +880…',
  'dc56.contact.ph_message': 'How can we help?',
  'dc56.contact.send': 'Send message',
  'dc56.contact.sending': 'Sending…',
  'dc56.contact.err_name': 'Please add your name',
  'dc56.contact.err_reach': 'Enter a valid email or WhatsApp number.',
  'dc56.contact.err_msg': 'Write a short message (at least 10 characters)',
  'dc56.contact.err_send': 'We could not send your message. Check your details and try again.',
  'dc56.contact.sent_title': 'Message received',
  'dc56.contact.sent_body': 'Ticket {id} · {topic} — we’ll reply to {reach} within one business day.',
  'dc56.contact.send_another': 'Send another message',
  'dc56.contact.view_help': 'Browse the Help Center',
  'dc56.app.coming_soon': 'Coming soon',
  'dc56.app.title': '{brand} apps — launching soon',
  'dc56.app.sub': 'Leave your number and we’ll SMS you the download link the day we launch on iOS and Android.',
  'dc56.app.phone_label': 'Your phone number',
  'dc56.app.notify': 'Notify me',
  'dc56.app.consent': 'Text me once when the app launches.',
  'dc56.app.on_list': 'You’re on the list — we’ll SMS {phone}',
  'dc56.app.no_spam': 'No spam — one SMS at launch, that’s it.',
  'dc56.app.err_phone': 'Enter a valid mobile number',
  'dc56.app.err_consent': 'Tick the box so we may text you',
  'dc56.ms.title_service': 'Service or business',
  'dc56.ms.title_location': 'Location',
  'dc56.ms.title_when': 'Date & Time',
  'dc56.ms.back': 'Back',
  'dc56.ms.close': 'Close search',
  'dc56.ms.ph': 'Salon, doctor, turf…',
  'dc56.ms.skip_all': 'Skip — search everything',
  'dc56.ms.skip': 'Skip',
  'dc56.ms.search': 'Search',
  'dc56.ms.step': 'Step {n} of {total}',

  // DC123 · accessibility labels (screen-reader names for controls that had none).
  'a11y.gallery': 'Photos of {name}',

  // app-user (customer mobile app) strings — packages/i18n/app-strings.ts
  ...appEn,
  ...appEnPlatform,
  ...appEnBook,
  ...appEnExtras,
  ...appEnBookings,
  ...appEnSearch,
  ...appEnBusiness,
  ...appEnOffers,
  ...appEnAccount,
  ...appEnHome,
  ...appEnUi,
  ...appEnShell,
  ...siteChromeEn,
  ...blogPageEn,
  ...lrMerchantEn,
  ...lrAdminEn,
  // dash-admin — Turf setup mirror + DR-77 platform settings (FR-B28; API-241, API-192) — inline so the build's prune step can drop it per surface (DC123).
  // Merchant 360 › Turf setup section
  'admin.turf.title': 'Turf setup',
  'admin.turf.sub': 'Fields, slot grid, payment mode and policies, edited on the merchant’s behalf. Every save is audit-logged.',
  'admin.turf.read_only': 'Read-only for your role',
  'admin.turf.fields': 'Fields',
  'admin.turf.add_field': 'Add field',
  'admin.turf.discard_field': 'Discard',
  'admin.turf.conflict': 'This turf setup was changed elsewhere since you opened it. Reload to see the latest — your unsaved edits will be lost.',
  'admin.turf.reload': 'Reload',
  'admin.turf.discard.title': 'Discard your unsaved turf setup changes?',
  'admin.turf.discard.body': 'Nothing has been saved or audit-logged yet. Leaving now loses these edits.',
  'admin.turf.discard.cta': 'Discard changes',
  'admin.turf.no_fields': 'No fields yet — add the first one.',
  'admin.turf.f.untitled': 'Field {n}',
  'admin.turf.f.new': 'New — starts in draft',
  'admin.turf.f.name_en': 'Name (English)',
  'admin.turf.f.name_bn': 'Name (Bangla)',
  'admin.turf.f.type': 'Field type',
  'admin.turf.f.size': 'Size (sq ft)',
  'admin.turf.f.price': 'Base price per slot',
  'admin.turf.f.peak': 'Peak window and price',
  'admin.turf.f.peak_from': 'Peak from (hour, 0–23)',
  'admin.turf.f.peak_to': 'Peak to (hour, 1–24)',
  'admin.turf.f.peak_price': 'Peak price per slot',
  'admin.turf.f.paused': 'Paused — keeps its bookings, takes no new ones',
  'admin.turf.f.paused_chip': 'Paused',
  'admin.turf.f.saved_hint': 'A saved field is not removed here: pause it, or archive it in Services.',
  'admin.turf.f.services_link': 'Open in Services',
  'admin.turf.grid': 'Slot grid',
  'admin.turf.g.slot_min': 'Slot length',
  'admin.turf.g.minutes': '{n} min',
  'admin.turf.g.buffer': 'Buffer between slots (min)',
  'admin.turf.g.min_slots': 'Min slots per booking',
  'admin.turf.g.max_slots': 'Max slots per booking',
  'admin.turf.g.hours': 'Weekday',
  'admin.turf.g.open_day': 'Open this weekday',
  'admin.turf.g.closed': 'closed',
  'admin.turf.g.first': 'First slot starts',
  'admin.turf.g.last': 'Last slot starts',
  'admin.turf.g.next_day': 'next day',
  'admin.turf.g.preview': 'Generated slots — what the customer sees',
  'admin.turf.g.count': '{n} slots',
  'admin.turf.g.empty': 'No slots on this weekday.',
  'admin.turf.g.peak_tag': 'Peak',
  'admin.turf.g.manual': 'Manual',
  'admin.turf.g.price_edit': 'Slot price override for',
  'admin.turf.g.remove_slot': 'Remove slot at',
  'admin.turf.g.removed': 'Removed slots',
  'admin.turf.g.restore': 'restore',
  'admin.turf.g.add_start': 'Add a slot starting at',
  'admin.turf.g.add_slot': 'Add slot',
  'admin.turf.pay': 'Payment mode',
  'admin.turf.pay.venue_only': 'Venue only — no online payment; the booking confirms instantly',
  'admin.turf.pay.online_optional': 'Online optional — pay at the venue, or pay now to the merchant’s wallet with proof',
  'admin.turf.pay.advance_required': 'Advance required — the slot locks only with the advance sent with proof',
  'admin.turf.pay.not_allowed': 'Merchant cannot take online payments',
  'admin.turf.pay.override': 'The merchant has no approved payment KYC or no payments entitlement, so they could not pick this mode themselves. Saving applies it anyway (admin override, audited).',
  'admin.turf.policies': 'Policies',
  'admin.turf.p.tier': 'Cancellation tier',
  'admin.turf.p.sla': 'Proof-verification SLA (hours)',
  'admin.turf.p.expire': 'Auto-expiry (hours)',
  'admin.turf.p.default': 'Platform default: {n}',
  'admin.turf.note': 'Saved on the merchant’s behalf — written to the audit log as business.turf_setup_updated with the diff.',
  'admin.turf.save': 'Save turf setup — audit-logged',
  'admin.turf.saved': 'Turf setup saved for {name}',
  'admin.turf.failed': 'The turf setup could not be saved',
  'admin.turf.err.name': 'Every field needs an English name',
  'admin.turf.err.price': 'Enter a base price of 0 or more',
  'admin.turf.err.name_len': 'Field names are at most 80 characters',
  'admin.turf.err.type': 'Field type is at most 40 characters',
  'admin.turf.err.size': 'Size must be a whole number from 1 to 1,000,000 sq ft',
  'admin.turf.err.slot_min': 'Slot length must be 90 or 60 minutes',
  'admin.turf.err.buffer': 'Buffer must be between 0 and 60 minutes',
  'admin.turf.err.range': 'Slots per booking must satisfy 1 ≤ min ≤ max ≤ 8',
  'admin.turf.err.hours': 'A weekday’s first slot must not start after its last slot',
  'admin.turf.err.peak': 'The peak window must start before it ends, within 0–24 h, with a peak price of 0 or more',
  'admin.turf.err.sla': 'Proof-verification SLA must be 1–168 hours, or empty for the platform default',
  'admin.turf.err.expire': 'Auto-expiry must be 1–336 hours, or empty for the platform default',

  // Roles & security › Settings — DR-77 structured keys (API-192)
  'admin.sec.set.bookable_categories': 'Bookable categories',
  'admin.sec.set.bookable_categories_desc': 'Categories whose listings take online bookings. Every other category renders contact-only with “Notify me when booking opens”.',
  'admin.sec.set.turf_defaults': 'Turf defaults',
  'admin.sec.set.turf_defaults_desc': 'The starting values of a new turf field (slot length, buffer, slots per booking, first and last slot start) and the fallback advance % when a listing sets none.',
  'admin.sec.set.cancellation_tiers': 'Cancellation tiers',
  'admin.sec.set.cancellation_tiers_desc': 'Hours before the first slot during which a cancellation is free, per tier. Later cancellations forfeit the advance.',
  'admin.sec.turf.first_slot_min': 'Default first slot starts',
  'admin.sec.turf.last_slot_min': 'Default last slot starts',
  'admin.sec.turf.free_until': '{tier} — free until (hours before)',
  'admin.sec.turf.no_cats': 'No live categories to choose from.',
  'admin.sec.turf.advance_fallback_pct': 'Fallback advance (%)',
  'admin.sec.turf.err': 'Enter whole numbers within range: slot 90 or 60, buffer 0–60, 1 ≤ min ≤ max ≤ 8, advance 1–100 %.',
  'admin.sec.turf.err_tiers': 'Free-cancel windows must be whole hours from 0 to 168.',
  // Website — turf booking modal (PRD §7.3.1, DR-77, FR-B28) — inline so the build's prune step can drop it per surface (DC123).
  'turfweb.title': 'Book a slot',
  'turfweb.close': 'Close booking',
  'turfweb.step.slot': 'Slot',
  'turfweb.step.details': 'Details',
  'turfweb.step.payment': 'Payment',
  'turfweb.step.review': 'Review',
  'turfweb.step.done': 'Done',
  'turfweb.hold_chip': 'Held {time}',
  'turfweb.hold_gone': 'Hold expired',
  // step 1 — date · field · slots
  'turfweb.pick_date': 'Pick a date',
  'turfweb.calendar': 'Booking calendar',
  'turfweb.prev_month': 'Previous month',
  'turfweb.next_month': 'Next month',
  'turfweb.fields_title': 'Choose a field',
  'turfweb.field_from': 'from {price}/slot',
  'turfweb.field_size': '{n} sq ft',
  'turfweb.field_paused': 'Paused',
  'turfweb.field_paused_note': '{name} is paused and takes no new bookings.',
  'turfweb.all_paused': 'Every field is paused right now — please check back later.',
  'turfweb.slots_title': 'Pick your slots',
  'turfweb.slots_hint': 'Select {min}–{max} back-to-back slots on this field.',
  'turfweb.slots_hint_single': 'Select up to {max} back-to-back slots on this field.',
  'turfweb.slot_booked': 'Booked',
  'turfweb.slot_held': 'Held',
  'turfweb.slot_closed': 'Closed',
  'turfweb.peak': 'Peak',
  'turfweb.slots_loading': 'Checking open slots for this date…',
  'turfweb.no_slots': 'No slots on this day — try another date.',
  'turfweb.selected_count': '{n, plural, one{# slot} other{# slots}} selected',
  'turfweb.need_min': 'Select at least {n, plural, one{# slot} other{# slots}} to continue.',
  'turfweb.max_reached': 'You can book up to {n, plural, one{# slot} other{# slots}} in one booking.',
  'turfweb.hint_slot': 'Pick a slot to continue',
  'turfweb.slots_line': '{n, plural, one{# slot} other{# slots}}',
  // step 2 — details
  'turfweb.details_title': 'Your details',
  'turfweb.note_label': 'Note for the venue (optional)',
  'turfweb.note_ph': 'e.g. we need bibs and a referee',
  // step 3 — payment per mode
  'turfweb.pay_title': 'How will you pay?',
  'turfweb.venue_only_title': 'No online payment',
  'turfweb.venue_only_body': 'This venue takes no online payment — pay the full amount when you arrive. Your slot confirms instantly.',
  'turfweb.pay_choice_venue': 'Pay at venue',
  'turfweb.pay_choice_venue_sub': 'Pay the full amount at the venue — confirms instantly',
  'turfweb.pay_choice_now': 'Pay now',
  'turfweb.pay_choice_now_sub': 'Send the advance to the venue’s own wallet and attach your proof',
  'turfweb.advance_title': 'Advance required',
  'turfweb.advance_body': 'Send {advance} now to lock the slot. The remaining {due} is due at the venue.',
  'turfweb.pay_now_due': 'Send {amount} to the venue:',
  'turfweb.copy': 'Copy',
  'turfweb.copied': 'Copied',
  'turfweb.copy_number': 'Copy {label} number',
  'turfweb.trx_or_shot': 'Add either the TRX ID or a screenshot — one is enough.',
  'turfweb.cancel_free': 'Free cancellation until {h} hours before kick-off ({tier} policy).',
  'turfweb.hint_pay': 'Choose how you’ll pay',
  'turfweb.hint_proof': 'Add the TRX ID or a screenshot of your payment',
  // step 4 — review
  'turfweb.review_title': 'Review your booking',
  'turfweb.row.turf': 'Turf · field',
  'turfweb.row.when': 'When',
  'turfweb.row.booked_by': 'Booked by',
  'turfweb.row.players': 'Players',
  'turfweb.row.slot_price': 'Slot price',
  'turfweb.row.at_venue': 'At venue',
  'turfweb.pay_at_venue_full': 'Full amount at the venue',
  // step 5 — done
  'turfweb.done_title': 'Slot booked!',
  'turfweb.done_title_verify': 'Proof sent',
  'turfweb.done_body_instant': 'Your slot is confirmed. Show the QR code at the venue.',
  'turfweb.done_body_verify': 'The venue is verifying your payment. You’ll get an SMS the moment it is confirmed.',
  'turfweb.done_body_pending': 'The venue will confirm your slot shortly. You’ll get an SMS.',
  'turfweb.tl_created': 'Booking placed',
  'turfweb.tl_proof': 'Proof submitted',
  'turfweb.tl_verify': 'Venue verifies payment',
  'turfweb.tl_confirmed': 'Confirmed',
  'turfweb.next_title': 'Next steps',
  'turfweb.next_sms': 'Your booking code is on its way by SMS.',
  'turfweb.next_arrive': 'Arrive 10 minutes early and show the QR code at the gate.',
  'turfweb.next_pay_venue': 'Pay {amount} at the venue.',
  'turfweb.next_cancel': 'Cancel free of charge up to {h} hours before kick-off.',
  'turfweb.next_proof_later': 'Forgot the proof? Add it from your booking page.',
  'turfweb.view_booking': 'View booking',
  'turfweb.done_close': 'Done',
  // listing page book card
  // the per-slot price suffix of a turf field (API-240 forces `/slot`), via price-unit-label.ts
  'turfweb.unit.slot': '/slot',
  // errors from the hold (API-70 turf details)
  'turfweb.err.out_of_range': 'Pick between {min} and {max} slots.',
  'turfweb.err.not_contiguous': 'Slots must be back-to-back on one field.',
  'turfweb.err.field_paused': 'This field is paused — pick another field.',
  'turfweb.err.not_bookable': 'Online booking isn’t open for this venue yet.',
  // dash-merchant — My Business › Turf setup (FR-B28, DR-77; API-239/240) — inline so the build's prune step can drop it per surface (DC123).
  'turf.merchant.tab': 'Turf setup',
  'turf.merchant.load_failed': 'Could not load the turf setup.',
  'turf.merchant.dirty': 'Unsaved changes',
  'turf.merchant.save': 'Save turf setup',
  'turf.merchant.saved': 'Turf setup saved.',
  'turf.merchant.saved_review': 'Turf setup saved. The new price goes live after review.',
  'turf.merchant.err.moderation': 'A price change is still under review. Try again once the review is done.',
  'turf.merchant.err.save': 'Could not save the turf setup. Try again.',
  'turf.merchant.err.invalid': 'Fix the highlighted fields and try again.',
  'turf.merchant.err.payments': 'Online payment is not enabled for this business yet. Keep "Pay at venue only".',
  'turf.merchant.err.conflict': 'The turf setup changed elsewhere. Reload it to continue.',
  'turf.merchant.conflict.body': 'This turf setup was changed elsewhere since you opened it. Reload to see the latest — your unsaved edits will be lost.',
  'turf.merchant.conflict.reload': 'Reload',

  // (1) Fields
  'turf.merchant.fields.title': 'Fields',
  'turf.merchant.fields.hint': 'Each field is a service of this business. A new field starts as a draft and goes through review.',
  'turf.merchant.fields.add': 'Add field',
  'turf.merchant.fields.empty': 'No fields yet. Add the first one.',
  'turf.merchant.fields.max': 'Up to 20 fields per business.',
  'turf.merchant.fields.all_paused': 'Your fields are paused, so customers can’t book online yet. Review each field, switch it to “Taking bookings” and save to open booking.',
  'turf.merchant.field.new': 'New field',
  'turf.merchant.field.unsaved': 'Not saved yet',
  'turf.merchant.field.discard': 'Discard',
  'turf.merchant.field.name_en': 'Field name (English)',
  'turf.merchant.field.name_bn': 'Field name (Bangla)',
  'turf.merchant.field.type': 'Field type',
  'turf.merchant.field.type_hint': 'e.g. 5-a-side',
  'turf.merchant.ftype.5': '5-a-side',
  'turf.merchant.ftype.7': '7-a-side',
  'turf.merchant.ftype.9': '9-a-side',
  'turf.merchant.ftype.11': '11-a-side',
  'turf.merchant.field.size': 'Size (sq ft)',
  'turf.merchant.field.price': 'Price per slot',
  'turf.merchant.field.price_review': 'Changing the price sends this field back to review before customers see it.',
  'turf.merchant.field.peak_on': 'Peak pricing',
  'turf.merchant.field.peak_from': 'Peak from',
  'turf.merchant.field.peak_to': 'Peak to',
  'turf.merchant.field.peak_price': 'Peak price per slot',
  'turf.merchant.field.pause': 'Pause field',
  'turf.merchant.field.pause_help': 'A paused field keeps its bookings and takes no new ones.',
  'turf.merchant.field.paused': 'Paused',
  'turf.merchant.field.taking': 'Taking bookings',
  'turf.merchant.field.photos': 'Photos are managed in Services.',
  'turf.merchant.field.photos_link': 'Open in Services',
  'turf.merchant.field.photos_count': '{n} photos',

  // (2) Slot grid
  'turf.merchant.grid.title': 'Slot grid',
  'turf.merchant.grid.hint': 'Generated from the first and last slot start of each day. Edit any slot or add one by hand; a hand-made slot wins over the generated one.',
  'turf.merchant.grid.field': 'Field',
  'turf.merchant.grid.slot_len': 'Slot length',
  'turf.merchant.grid.min': '{n} min',
  'turf.merchant.grid.buffer': 'Buffer between slots (min)',
  'turf.merchant.grid.min_slots': 'Min slots per booking',
  'turf.merchant.grid.max_slots': 'Max slots per booking',
  'turf.merchant.grid.decrease': 'Decrease {what}',
  'turf.merchant.grid.increase': 'Increase {what}',
  'turf.merchant.grid.open': 'Open',
  'turf.merchant.grid.closed': 'Closed',
  'turf.merchant.grid.first': 'First slot',
  'turf.merchant.grid.last': 'Last slot',
  'turf.merchant.grid.next_day': 'next day',
  'turf.merchant.grid.preview': 'Preview · {day}',
  'turf.merchant.grid.preview_saved': 'As saved',
  'turf.merchant.grid.preview_local': 'Unsaved preview',
  'turf.merchant.grid.empty': 'No slots on this day.',
  'turf.merchant.grid.peak_tag': 'Peak',
  'turf.merchant.grid.manual_tag': 'Manual',
  'turf.merchant.grid.slot_off': 'Remove slot {time}',
  'turf.merchant.grid.slot_price': 'Price for slot {time}',
  'turf.merchant.grid.removed': 'Removed slots',
  'turf.merchant.grid.restore': 'Restore slot {time}',
  'turf.merchant.grid.restore_btn': 'Restore',
  'turf.merchant.grid.day_pick': 'Preview day',
  'turf.merchant.grid.add': 'Add slot',
  'turf.merchant.grid.add_start': 'Start time',
  'turf.merchant.grid.add_btn': 'Add',
  'turf.merchant.grid.add_exists': 'A slot already starts at that time.',
  'turf.merchant.grid.rows_max': 'Up to 200 hand-made slot rows per field.',

  // (3) Payment
  'turf.merchant.pay.title': 'Payment',
  'turf.merchant.pay.venue_only': 'Pay at venue only',
  'turf.merchant.pay.venue_only_help': 'No online payment is offered; the booking confirms instantly.',
  'turf.merchant.pay.online_optional': 'Online optional',
  'turf.merchant.pay.online_optional_help': 'Pay at the venue, or pay now to your own bKash/Nagad with proof; a proof booking enters Payment Verification.',
  'turf.merchant.pay.advance_required': 'Advance required',
  'turf.merchant.pay.advance_required_help': 'The slot locks only once the advance is sent with proof; the balance is due at the venue.',
  'turf.merchant.pay.locked': 'Online payment needs an approved payment application and a plan with payments. Only "Pay at venue only" can be chosen until then.',
  'turf.merchant.pay.link': 'Payment numbers and the advance amount are set in the Payments tab.',
  'turf.merchant.pay.link_btn': 'Open Payments',

  // (4) Policies
  'turf.merchant.pol.title': 'Policies',
  'turf.merchant.pol.tier': 'Cancellation tier',
  'turf.merchant.pol.flexible': 'Flexible',
  'turf.merchant.pol.moderate': 'Moderate',
  'turf.merchant.pol.strict': 'Strict',
  'turf.merchant.pol.sla': 'Proof verification SLA (hours)',
  'turf.merchant.pol.expire': 'Proof auto-expiry (hours)',
  'turf.merchant.pol.default': 'Platform default · {n}',
  'turf.merchant.pol.clear': 'Use platform default',

  // Validation (mirrors API-240)
  'turf.merchant.v.name': 'Enter a field name.',
  'turf.merchant.v.price': 'Enter a price of 0 or more.',
  'turf.merchant.v.moderation': 'A price change is already under review. Change the price again once the review is done.',
  'turf.merchant.v.size': 'Size must be a whole number of 1 or more.',
  'turf.merchant.v.peak': 'Peak end must be after peak start.',
  'turf.merchant.v.peak_price': 'Enter the peak price.',
  'turf.merchant.v.minmax': 'Min slots cannot exceed max slots.',
  'turf.merchant.v.hours': '{day}: the last slot must start at or after the first.',
  'turf.merchant.v.sla': 'SLA must be between 1 and 168 hours.',
  'turf.merchant.v.expire': 'Auto-expiry must be between 1 and 336 hours.',
  // One phone field for every surface (packages/i18n/phone.ts).
  'phone.label': 'Mobile number',
  'phone.dial_label': 'Country code {dial}',
  'phone.invalid': 'Enter a valid mobile number — {n} digits after {dial}, starting with {lead}',
} as const satisfies Record<string, string>;
